import React, { useCallback, Dispatch, SetStateAction, RefObject, MutableRefObject, useRef, useEffect, useState } from 'react';
import type {
  Point, Wall, ElementType, FloorPlanElement, Room, WallType,
  ResizingRoomInfo, TextBox, RoomCorner, CornerDragPreview, WindowElement
} from '../types';
import type { WallWithTempId } from '../types';
import type { RetrofitSurveyData } from '../utils/retrofitSurveyStorage';
import {
  SNAP_THRESHOLD, GRID_SIZE, POINT_SNAP_THRESHOLD, CM_SNAP_SIZE,
  WALL_RESIZE_HOVER_TOLERANCE, WALL_RESIZE_TOUCH_TOLERANCE, INITIAL_TOUCH_SELECTION_RADIUS,
  TOUCH_DRAG_THRESHOLD, TOUCH_LONG_PRESS_DELAY, SNAP_ANGLE_TOLERANCE_PX,
  SCALE_FACTOR, MAX_SCALE, INITIAL_SCALE, POINT_MATCH_TOLERANCE, PIXELS_PER_METER,
  MIN_CLICK_TOLERANCE, MIN_INTERACTION_TOLERANCE,
  HOVER_TOLERANCE, WALL_THICKNESS, RADIATOR_WALL_OFFSET, BOILER_WALL_OFFSET
} from '../constants/floorPlanConstants';
import {
  snapPointToGrid, snapToFineGridOrEndpoints as snapToFineGridOrEndpointsUtil,
  getElementBounds, getLineIntersectionPoint,
  snapToEndpointsOrGrid as snapToEndpointsOrGridUtil,
  generateStableWallId, // Added import
  adjustExtrusionWidth  // Add this import
} from '../utils/floorPlanUtils';
import { analyzeWallSegmentation, findSegmentAtPoint, updateSegmentType, segmentsToWalls } from '../utils/wallSegmentation';
import { calculateResizedWalls, getUniqueCornerPoints, synchronizeSharedWallsAfterResize } from '../utils/floorPlanGeometry';
import { findWallForRoom, findWallSafeForResize } from '../utils/enhancedWallLookupSystem';
import { performSafeWallResize, validateWallResize } from '../utils/resizeOperationSafetySystem';
import { 
  getResizeEligibleWalls, 
  createSafeWallArrayForResize, 
  validatePostResize,
  filterWallsByRoomOwnership 
} from '../utils/wallOwnershipEnforcement';
import { findElementRoomWithConsolidation } from '../services/roomSyncService';
import { 
  synchronizeSharedWallsWithOwnership,
  validateSynchronizationRespectedBoundaries 
} from '../utils/ownershipAwareSharedWallSync';
import { applySmartCoordinateOffsets } from '../utils/coordinateOffsetSystem';
import { splitWallAndProcessState, WallSplitResult, isInteractionPointEligibleForTool, getExposedSegments, pointsAreEffectivelyEqual, createWallSections, findSectionAtPoint, updateSectionType } from '../utils/wallUtils';




import { checkRectangleWallIntersectionUniqueName } from '../utils/intersectionUtils'; // Import the new unique name
import { constrainViewOffset as constrainViewOffsetUtil } from '../utils/viewUtils';
import { ROOM_AUTOLABEL_AREA_THRESHOLD_SQ_METERS } from '../constants/floorPlanConstants';
import { 
  distance, 
  subtractPoints, 
  addPoints, 
  scalePoint, 
  dotProductPoints, 
  normalizePoint 
} from '../utils/geometryUtils';
import { 
  calculateArea, getRoomCenter, getRoomBounds, isNearPoint, distanceToLine,
  doLinesIntersect, isPointInsideRoom, findAlignedCorners, calculateWallLength,
  findWallEndpoint, doesWallIntersect, shouldSnapToWall, shouldSnapToEndpoint,
  pointToLineDistance, getCollinearNeighbors, snapToFineGridOrEndpoints,
  snapToEndpointsOrGrid, isPointOnWall, getRoomCenter as getRoomCenterImpl,
  findRoomAtPoint, findElementAtPoint, getResizeHandle, pointsAreEqual, calculateRooms, getPersistentRoomId,
  migrateRoomLabels, RoomCalculationContext, getWallResizeDirection, orientation,
  snapRoomPointToNearbyStructures, snapRoomForAdjacentCreation, // Add new imports
  analyzeWallClick, findWallForProfessionalCAD // Professional CAD functions
} from '../utils/floorPlanUtils';
import { memoizedCalculateRooms } from '../utils/performanceOptimizations';
import { decoupleSharedPointsForDrag } from '../utils/pointDecoupling';
import { handlePartialWallDrag } from '../utils/partialRoomDrag';
import { 
  enforceWallRoomMembership, 
  propagateRoomMembership, 
  preserveWallMetadataForResize, 
  batchPreserveWallMetadataForResize 
} from '../utils/roomMembershipEnforcement';
import { 
  checkConnectorExists, 
  createSafeConnector, 
  filterDuplicateConnectors 
} from '../utils/connectorDuplicationPrevention';
import { 
  calculateSharedWallMovementEffect, 
  updateSharedWallSystemAfterRoomChange,
  getWallKey,
  type SharedWallSystemState 
} from '../utils/sharedWallSystem';
// 🆕 ENHANCED: Replace complex snapping with bulletproof simple system
import { snapToWalls, snapToWallsWithLineIntersection, type SimpleSnapResult } from '../utils/simpleWallSnapping';
import { 
  handleSmartWallDrawing, 
  getWallConnectionPreview 
} from '../utils/wallConnectionSystem';
import { detectRoomResizeCollision, resetConnectionLock } from '../utils/roomCollisionDetection';



// Add this debugging function near the top of the file
const debugConnectorWalls = (walls: Wall[], context: string) => {
  const connectorWalls = walls.filter(w => (w as any).isExtrusionConnector);
  const mainSegments = walls.filter(w => (w as any).isMainExtrusionSegment);
  
  console.log(`🔍 [${context}] Connector Wall Debug:`, {
    connectorWallsCount: connectorWalls.length,
    mainSegmentsCount: mainSegments.length,
    connectorWalls: connectorWalls.map(w => ({
      tempId: (w as any).tempId,
      start: w.start,
      end: w.end,
      isExtrusionConnector: (w as any).isExtrusionConnector
    })),
    mainSegments: mainSegments.map(w => ({
      tempId: (w as any).tempId,
      start: w.start,
      end: w.end,
      isMainExtrusionSegment: (w as any).isMainExtrusionSegment
    }))
  });
};

// In the section where you identify connector walls, add:
const identifyConnectorWalls = (walls: Wall[], mainSegmentId: string) => {
  const connectors = walls.filter(w => 
    w.tempId !== mainSegmentId && 
    (w as any).isExtrusionConnector
  );
  
  console.log(`🔍 Identifying Connector Walls:`, {
    mainSegmentId,
    totalWalls: walls.length,
    connectorWalls: connectors.map(w => ({
      tempId: (w as any).tempId,
      start: w.start,
      end: w.end,
      isExtrusionConnector: (w as any).isExtrusionConnector,
      isMainExtrusionSegment: (w as any).isMainExtrusionSegment
    }))
  });
  
  return connectors;
};

// Define the structure for undo/redo state (can be imported if defined globally)
type UndoRedoState = {
  floorPlans: Record<number, Wall[]>;
  elements: FloorPlanElement[];
  textBoxes: TextBox[];
  roomCustomLabels: Record<string, string>;
  propertyAddress: string;
  windowCount: { single: number, double: number };
  lightCount: { nonLed: number, led: number };
  currentFloor: number;
};

export interface UseFloorPlanInteractionsProps {
  // States
  currentWalls: WallWithTempId[];
  elements: FloorPlanElement[];
  scale: number;
  viewOffset: Point;
  currentTool: ElementType; 
  selectedRoom: Room | null;
  selectedElement: FloorPlanElement | null;
  tempWalls: WallWithTempId[] | null;
  resizeHandle: string | null;
  isResizing: boolean;
  isDragging: boolean;
  isDraggingRoom: boolean;
  roomStart: Point | null;
  roomEnd: Point | null;
  selectedWall: WallWithTempId | null;
  splitPoint: Point | null;
  floorPlans: Record<number, Wall[]>; // This is the current state of floorPlans
  currentFloor: number;
  undoStack: UndoRedoState[]; // Updated type
  redoStack: UndoRedoState[]; // Updated type
  textBoxes: TextBox[];
  selectedTextBox: TextBox | null;
  roomCustomLabels: Record<string, string>;
  resizingRoomInfo: ResizingRoomInfo | null;
  activelyResizedWalls: Wall[] | null;
  isPanning: boolean;
  panStart: Point | null;
  touchStartPos: Point | null;
  isLongPress: boolean;
  longPressTimer: NodeJS.Timeout | null;
  touchMoved: boolean;
  isDraggingCorner: boolean;
  hoveredCorner: Point | null;
  isMultiTouching: boolean; 
  resizingRoomLiveDimensions: { width: number, height: number, area: number } | null; 
  splitPreviewDimensions: { distToStart: number; distToEnd: number } | null;
  cornerDragPreview: CornerDragPreview | null;
  isWallSplitting: boolean; // Added for performance optimization
  
  // Survey data synchronization
  surveyData?: RetrofitSurveyData;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData) => void;
  siteNotesData?: any; // For extension deletion
  setSiteNotesData?: Dispatch<SetStateAction<any>>; // For extension deletion

  // Setters
  setScale: Dispatch<SetStateAction<number>>;
  setViewOffset: Dispatch<SetStateAction<Point>>;
  setFloorPlans: Dispatch<SetStateAction<Record<number, Wall[]>>>;
  setElements: Dispatch<SetStateAction<FloorPlanElement[]>>;
  setUndoStack: Dispatch<SetStateAction<UndoRedoState[]>>; // Updated type
  setRedoStack: Dispatch<SetStateAction<UndoRedoState[]>>; // Updated type
  setSelectedWall: Dispatch<SetStateAction<WallWithTempId | null>>;
  setCurrentTool: Dispatch<SetStateAction<ElementType>>;
  setShowTextInput?: Dispatch<SetStateAction<boolean>>;
  setCurrentPoint: Dispatch<SetStateAction<Point | null>>;
  setDoorPreview?: Dispatch<SetStateAction<any | null>>;
  setSelectedElement: Dispatch<SetStateAction<FloorPlanElement | null>>;
  setIsDragging: Dispatch<SetStateAction<boolean>>;
  setIsPanning: Dispatch<SetStateAction<boolean>>;
  setPanStart: Dispatch<SetStateAction<Point | null>>;
  setIsDraggingRoom: Dispatch<SetStateAction<boolean>>;
  setRoomStart: Dispatch<SetStateAction<Point | null>>;
  setRoomEnd: Dispatch<SetStateAction<Point | null>>;
  setSelectedRoom: Dispatch<SetStateAction<Room | null>>;
  setResizeHandle: Dispatch<SetStateAction<string | null>>;
  setTempWalls: Dispatch<SetStateAction<WallWithTempId[] | null>>;
  setHoveredHandle?: Dispatch<SetStateAction<string | null>>;
  setSplitPoint: Dispatch<SetStateAction<Point | null>>;
  setTextBoxes: Dispatch<SetStateAction<TextBox[]>>;
  setSelectedTextBox: Dispatch<SetStateAction<TextBox | null>>;
  setRoomCustomLabels: Dispatch<SetStateAction<Record<string, string>>>;
  setResizingRoomInfo: Dispatch<SetStateAction<ResizingRoomInfo | null>>;
  setActivelyResizedWalls: Dispatch<SetStateAction<Wall[] | null>>;
  setTouchStartPos: Dispatch<SetStateAction<Point | null>>;
  setIsLongPress: Dispatch<SetStateAction<boolean>>;
  setLongPressTimer: Dispatch<SetStateAction<NodeJS.Timeout | null>>;
  setTouchMoved: Dispatch<SetStateAction<boolean>>;
  setIsResizing: Dispatch<SetStateAction<boolean>>;
  setHoveredCorner: Dispatch<SetStateAction<Point | null>>;
  setIsDraggingCorner: Dispatch<SetStateAction<boolean>>;
  setResizingRoomLiveDimensions: Dispatch<SetStateAction<{ width: number, height: number, area: number } | null>>; 
  setSplitPreviewDimensions: Dispatch<SetStateAction<{ distToStart: number; distToEnd: number } | null>>; 
  setCornerDragPreview: Dispatch<SetStateAction<CornerDragPreview | null>>;
  setIsWallSplitting: Dispatch<SetStateAction<boolean>>; // Added for performance optimization
  setCurrentSnapResult: Dispatch<SetStateAction<SimpleSnapResult | null>>; // 🆕 ENHANCED: Visual feedback for wall snapping

  // Refs
  canvasRef: RefObject<HTMLCanvasElement>;
  isResizingRef: MutableRefObject<boolean>;
  activeCornerInfoRef: MutableRefObject<{
    originalCornerWorldPoint: Point;
    affectedWallsData: Array<{ tempId: string; isStartPointCorner: boolean }>;
  } | null>;
  lastRawDraggedCornerPosRef: MutableRefObject<Point | null>;
  draggedWallSegmentRef: MutableRefObject<Wall | null>; 
  isMouseDownRef: MutableRefObject<boolean>;
  eraserPreviewPointRef: MutableRefObject<Point | null>;
  justResizedRef: MutableRefObject<boolean>;
  currentToolInternalRef: MutableRefObject<ElementType>; 
  lastOperationTypeRef: MutableRefObject<RoomCalculationContext>;
  roomMigrationMapRef: MutableRefObject<Record<string, string>>; // ADDED
  selectedWallRef: MutableRefObject<WallWithTempId | null>;
  newlyAddedWallsRef: MutableRefObject<Wall[]>; // Track newly added walls for endpoint room creation

  // Callbacks/Utilities
  getCanvasCoordinates: (event: MouseEvent | TouchEvent | Touch | { clientX: number, clientY: number }) => Point;
  onDebug: (msg: string, obj?: any) => void; 
  recordUndoState?: () => void; // Optional undo recording
  roomSync: {
    updateLightCount: (roomName: string, lightType: string, count: number, floor: number) => void;
    syncAllFloorsLightsToAdditionalInfo: () => void;
    updateWindowDimensions: (roomName: string, width: number, height: number, trickleVents: boolean, floor: number) => void;
    smartSyncWindows: (windows: any[], rooms: any[], floor: number) => void;
    syncAllFloorsWindows: (windows: any[], floorPlans: any, roomLabels: any) => void;
    updateRadiatorDimensions: (roomName: string, width: number, height: number, radiatorType: string, floor: number) => void;
    syncDoorUndercuts: (doors: any[], rooms: any[], floor: number) => void;
    syncAllFloorsDoorUndercuts: (doors: any[], floorPlans: any, roomLabels: any) => void;
    cleanupDuplicateRooms: () => void;
    syncAllFloorsRadiatorsToHeatingSystem: () => void;
    syncAllRoomsWithConsolidation: (rooms: any[], floor: number) => void;
  }; // Room sync object with all methods
  triggerAutosave?: () => void; // Autosave trigger callback
  onWallOperation?: () => void; // Wall operation timestamp callback
  startBatchOperation?: () => void; // Optional batch operation starter to prevent rapid autosave triggers
  markFloorPlanChanged?: () => void; // Mark floor plan as changed callback
  onWallCreated?: () => void; // Wall creation callback
  onRoomCreated?: () => void; // Room creation callback
  onRoomDeleted?: () => void; // Room deletion callback
  updateSurveyDataAfterResize?: (resizedRooms: Room[]) => void; // Survey data update callback after room resize
  

}

// Helper function to calculate a point on a new line segment corresponding to a point on an old line segment
const getCorrespondingPointOnLine = (
  pointOnOldLine: Point,
  oldLineStart: Point,
  oldLineEnd: Point,
  newLineStart: Point,
  newLineEnd: Point,
  onDebug?: (event: string, data: any) => void // Added onDebug parameter
): Point => {
  const oldLineDx = oldLineEnd.x - oldLineStart.x;
  const oldLineDy = oldLineEnd.y - oldLineStart.y;
  const oldLineLength = Math.hypot(oldLineDx, oldLineDy);

  onDebug?.('GET_CORRESPONDING_POINT_DEBUG', { pointOnOldLine, oldLineStart, oldLineEnd, newLineStart, newLineEnd, oldLineLength });


  if (pointsAreEqual(oldLineStart, oldLineEnd)) { // oldLine is a point
    onDebug?.('GET_CORRESPONDING_POINT_DEBUG', { message: "Old line is a point, returning newLineStart."});
    if (pointsAreEqual(pointOnOldLine, oldLineStart)) {
      return newLineStart;
    }
    return newLineStart;
  }
  
  if (pointsAreEqual(pointOnOldLine, oldLineStart)) {
    onDebug?.('GET_CORRESPONDING_POINT_DEBUG', { message: "Point is oldLineStart, returning newLineStart."});
    return newLineStart;
  }
  if (pointsAreEqual(pointOnOldLine, oldLineEnd)) {
    onDebug?.('GET_CORRESPONDING_POINT_DEBUG', { message: "Point is oldLineEnd, returning newLineEnd."});
    return newLineEnd;
  }

  const vecStartToPointX = pointOnOldLine.x - oldLineStart.x;
  const vecStartToPointY = pointOnOldLine.y - oldLineStart.y;

  const dotProduct = vecStartToPointX * oldLineDx + vecStartToPointY * oldLineDy;
  const t = dotProduct / (oldLineLength * oldLineLength);
  
  const clampedT = Math.max(0, Math.min(1, t));

  onDebug?.('GET_CORRESPONDING_POINT_DEBUG', { originalT: t, clampedT });

  return {
    x: newLineStart.x + clampedT * (newLineEnd.x - newLineStart.x),
    y: newLineStart.y + clampedT * (newLineEnd.y - newLineStart.y)
  };
};

// Helper function to determine the side of a point relative to a directed line segment
const determinePointSide = (point: Point, lineStart: Point, lineEnd: Point): number => {
  return (point.x - lineStart.x) * (lineEnd.y - lineStart.y) - (point.y - lineStart.y) * (lineEnd.x - lineStart.x);
};

// Helper function to find which point of wall1 (connector) is attached to wall2 (baseWall)
const getAttachmentPointOnWall = (
    connectorWall: WallWithTempId, 
    baseWall: WallWithTempId, 
    scale: number, // Added scale parameter
    onDebug?: (event: string, data: any) => void, // Added onDebug parameter
    tolerance: number = POINT_MATCH_TOLERANCE + 0.5 
): Point | null => {
    // Check if connector's start point is on or near the baseWall's ENDPOINTS
    if (isNearPoint(connectorWall.start, baseWall.start, tolerance) || 
        isNearPoint(connectorWall.start, baseWall.end, tolerance)) {
      onDebug?.('GET_ATTACHMENT_DEBUG', { type: 'near_endpoint', point: connectorWall.start, wall: baseWall.tempId });
      return connectorWall.start;
    }
    // Check if connector's end point is on or near the baseWall's ENDPOINTS
    if (isNearPoint(connectorWall.end, baseWall.start, tolerance) || 
        isNearPoint(connectorWall.end, baseWall.end, tolerance)) {
      onDebug?.('GET_ATTACHMENT_DEBUG', { type: 'near_endpoint', point: connectorWall.end, wall: baseWall.tempId });
      return connectorWall.end;
    }

    // Check if connector's start point is ON THE SEGMENT of the baseWall
    // Use a slightly more generous tolerance for isPointOnWall segment check here
    // as it's crucial for finding the connection.
    const segmentCheckTolerance = Math.max(tolerance, SNAP_THRESHOLD / scale, 2); // Ensure at least 2px world tolerance // Replaced props.scale with scale
    if (isPointOnWall(connectorWall.start, baseWall, segmentCheckTolerance)) { 
      onDebug?.('GET_ATTACHMENT_DEBUG', { type: 'on_segment', point: connectorWall.start, wall: baseWall.tempId, toleranceUsed: segmentCheckTolerance });
      return connectorWall.start;
    }
    // Check if connector's end point is ON THE SEGMENT of the baseWall
    if (isPointOnWall(connectorWall.end, baseWall, segmentCheckTolerance)) { 
      onDebug?.('GET_ATTACHMENT_DEBUG', { type: 'on_segment', point: connectorWall.end, wall: baseWall.tempId, toleranceUsed: segmentCheckTolerance });
      return connectorWall.end;
    }
    onDebug?.('GET_ATTACHMENT_DEBUG_FAIL', { connectorId: connectorWall.tempId, baseWallId: baseWall.tempId, tolerance });
    return null;
};

// Type guard to check if an element has an id property
const hasId = (element: FloorPlanElement): element is FloorPlanElement & { id: string } => {
  return 'id' in element && typeof (element as any).id === 'string';
};

/**
 * Detects if a wall completion might create an enclosed room
 * Returns true if the wall closes a cycle and potentially creates a new enclosed space
 */
const detectPotentialRoomCreation = (newWall: Wall, allWalls: Wall[]): boolean => {
  try {
    // Enhanced heuristic: Check if the new wall connects two points that are part of existing wall networks
    // If both endpoints connect to existing walls, it might close a cycle
    
    const connectionTolerance = 2; // Increased tolerance for better connection detection
    
    const startConnections = allWalls.filter(wall => 
      wall !== newWall && (
        (Math.abs(wall.start.x - newWall.start.x) < connectionTolerance && Math.abs(wall.start.y - newWall.start.y) < connectionTolerance) || 
        (Math.abs(wall.end.x - newWall.start.x) < connectionTolerance && Math.abs(wall.end.y - newWall.start.y) < connectionTolerance)
      )
    );
    
    const endConnections = allWalls.filter(wall => 
      wall !== newWall && (
        (Math.abs(wall.start.x - newWall.end.x) < connectionTolerance && Math.abs(wall.start.y - newWall.end.y) < connectionTolerance) || 
        (Math.abs(wall.end.x - newWall.end.x) < connectionTolerance && Math.abs(wall.end.y - newWall.end.y) < connectionTolerance)
      )
    );
    
    // 🔧 ENHANCED DETECTION: Also check if this creates a closed loop
    const mightCloseLoop = startConnections.length > 0 && endConnections.length > 0;
    
    // 🔧 ADDITIONAL CHECK: If we have multiple connections, it's more likely to be room creation
    const hasMultipleConnections = startConnections.length >= 1 && endConnections.length >= 1;
    
    // 🔧 IMPROVED LOGIC: More permissive detection for better room creation recognition
    const likelyRoomCreation = mightCloseLoop || hasMultipleConnections;
    
    console.log('🔧 ROOM DETECTION: Analyzing wall completion', {
      startConnections: startConnections.length,
      endConnections: endConnections.length,
      mightCloseLoop,
      hasMultipleConnections,
      likelyRoomCreation,
      newWallStart: newWall.start,
      newWallEnd: newWall.end,
      tolerance: connectionTolerance
    });
    
    return likelyRoomCreation;
  } catch (error) {
    console.warn('🔧 ROOM DETECTION: Error in room detection, defaulting to true for safety', error);
    return true; // Default to true to err on the side of room creation detection
  }
};

export const useFloorPlanInteractions = (props: UseFloorPlanInteractionsProps) => {
  // Extract survey synchronization props
  const { surveyData, onDataUpdate, siteNotesData, setSiteNotesData } = props;
  const {
    currentWalls, elements, scale, viewOffset, currentTool, selectedRoom, selectedElement,
    tempWalls, resizeHandle, isResizing, isDragging,
    isDraggingRoom, roomStart, roomEnd,
    selectedWall, splitPoint, floorPlans, currentFloor, undoStack, redoStack,
    textBoxes, selectedTextBox, roomCustomLabels, resizingRoomInfo, activelyResizedWalls,
    isPanning, panStart, touchStartPos, isLongPress, longPressTimer, touchMoved,
    isDraggingCorner, hoveredCorner,
    isMultiTouching, resizingRoomLiveDimensions, splitPreviewDimensions, cornerDragPreview,
    isWallSplitting, // Added for performance optimization

    setScale, setViewOffset, setFloorPlans, setElements, setUndoStack, setRedoStack,
    setSelectedWall, setCurrentTool, setCurrentPoint, setSelectedElement, setIsDragging,
    setIsPanning, setPanStart, setIsDraggingRoom, setRoomStart, setRoomEnd,
    setSelectedRoom, setResizeHandle, setTempWalls, setSplitPoint, setTextBoxes,
    setSelectedTextBox, setRoomCustomLabels, setResizingRoomInfo, setActivelyResizedWalls,
    setTouchStartPos, setIsLongPress, setLongPressTimer, setTouchMoved, setIsResizing,
    setHoveredCorner, setIsDraggingCorner, setResizingRoomLiveDimensions, setSplitPreviewDimensions,
    setCornerDragPreview, setIsWallSplitting, setCurrentSnapResult, // 🆕 ENHANCED: Visual feedback

    canvasRef, isResizingRef, activeCornerInfoRef, lastRawDraggedCornerPosRef,
    draggedWallSegmentRef, isMouseDownRef, eraserPreviewPointRef, justResizedRef,
    currentToolInternalRef, lastOperationTypeRef,
    roomMigrationMapRef, // ADDED
    selectedWallRef,
    newlyAddedWallsRef, // Extract newly added walls ref

    getCanvasCoordinates, onDebug, recordUndoState, roomSync, triggerAutosave, onWallOperation,
    markFloorPlanChanged, onWallCreated, onRoomCreated, onRoomDeleted, updateSurveyDataAfterResize,
  } = props;



  const isDraggingRoomRef = useRef(isDraggingRoom);
  const roomStartRef = useRef(roomStart);
  const roomEndRef = useRef(roomEnd);
  const activeResizeOriginalWallTempIdsRef = useRef<string[] | null>(null); 
  const initialWallsForResizeRef = useRef<WallWithTempId[] | null>(null); 

  // Refs for extrusion dragging
  const isDraggingExtrusionRef = useRef<boolean>(false);
  const extrusionSegmentRef = useRef<WallWithTempId | null>(null);
  const originalWallLineForExtrusionRef = useRef<{ start: Point; end: Point } | null>(null);
  const extrusionDragStartPointRef = useRef<Point | null>(null);
  const isExtrudingRoomSegmentRef = useRef<boolean>(false);
  const isResizingExtrusionWidthRef = useRef<boolean>(false);
  const lastUpdateTimeRef = useRef<number>(0); // For throttling updates during dragging
  
  // Store intersection data for partial wall dragging and shared wall movement
  const intersectionDataRef = useRef<{
    intersectionPoints: Point[];
    intersectedWalls: WallWithTempId[];
    requiresPartialDrag: boolean;
    draggedWall: WallWithTempId;
    // 🔗 NEW: Professional shared wall movement data
    sharedWallMovement?: {
      movementType: 'shared' | 'independent' | 'connected';
      affectedRooms: string[];
      preserveRoomIds: boolean;
      sharedWallSystem: SharedWallSystemState;
    };
  } | null>(null);

  // 🎯 WALL DRAG MODE - Professional CAD-like individual wall dragging
  const [isWallDragMode, setIsWallDragMode] = useState(false);
  const wallDragModeRef = useRef(false);
  const originalToolRef = useRef<string | null>(null);
  
  // Wall drag state for independent wall manipulation
  const wallDragDataRef = useRef<{
    draggedWall: WallWithTempId;
    originalWallPosition: { start: Point; end: Point };
    independentWalls: WallWithTempId[];
    connectedWalls: WallWithTempId[];
    dragStartPoint: Point;
    intersectionPoints: Point[];
  } | null>(null);

  // Refs for instant responsive room resizing
  const roomResizeStartPointRef = useRef<Point | null>(null);
  const pendingRoomForResizeRef = useRef<Room | null>(null);
  const dragThresholdReachedRef = useRef<boolean>(false);

  // Enhanced element dragging state
  const [dragStartPoint, setDragStartPoint] = useState<Point | null>(null);
  const [elementDragOffset, setElementDragOffset] = useState<Point | null>(null);

  // Add refs for dragging state  
  const isDraggingElementRef = useRef(false);
  const dragStartPointRef = useRef<Point | null>(null);

  // 🔧 ENHANCED WALL-ROOM MEMBERSHIP ENFORCEMENT: Aggressive room association preservation
  const preserveWallMetadata = useCallback((wall: WallWithTempId): Wall => {
    const { tempId, ...baseWall } = wall;
    
    // 🛡️ DEFENSIVE: Ensure room membership is never lost
    let roomOwnership = {
      originatingRoomId: wall.originatingRoomId,
      assignedRoomIds: wall.assignedRoomIds ? [...wall.assignedRoomIds] : undefined,
      parentRoomIds: wall.parentRoomIds ? [...wall.parentRoomIds] : undefined
    };
    
    // 🔧 CRITICAL FIX: If wall lacks room ownership but has parent info, inherit it
    if (!roomOwnership.originatingRoomId && !roomOwnership.assignedRoomIds && wall.parentRoomIds?.length) {
      roomOwnership.originatingRoomId = wall.parentRoomIds[0];
      roomOwnership.assignedRoomIds = [...wall.parentRoomIds];
      console.log('🛡️ ROOM_MEMBERSHIP_REPAIR: Inherited room ownership from parentRoomIds', {
        wallId: tempId,
        wallCoords: `(${wall.start.x},${wall.start.y})->(${wall.end.x},${wall.end.y})`,
        inheritedFrom: wall.parentRoomIds,
        newOriginatingRoomId: roomOwnership.originatingRoomId
      });
    }
    
    // 🔧 CRITICAL CONNECTOR WALL FIX: Ensure connector walls are assigned to BOTH rooms they connect
    if (wall.isExtrusionConnector) {
      // Connector walls need to be assigned to BOTH rooms they connect
      let connectorRoomIds = new Set<string>();
      
      // 1. Preserve existing room assignments if they exist
      if (roomOwnership.originatingRoomId) {
        connectorRoomIds.add(roomOwnership.originatingRoomId);
      }
      if (roomOwnership.assignedRoomIds) {
        roomOwnership.assignedRoomIds.forEach(id => connectorRoomIds.add(id));
      }
      if (roomOwnership.parentRoomIds) {
        roomOwnership.parentRoomIds.forEach(id => connectorRoomIds.add(id));
      }
      
      // 2. Find parent wall and inherit its room ownership
      const parentWall = currentWalls.find(w => 
        (w as any).tempId === wall.parentWallId ||
        (w as any).isMainExtrusionSegment
      );
      
      if (parentWall) {
        if ((parentWall as any).originatingRoomId) {
          connectorRoomIds.add((parentWall as any).originatingRoomId);
        }
        if ((parentWall as any).assignedRoomIds) {
          (parentWall as any).assignedRoomIds.forEach((id: string) => connectorRoomIds.add(id));
        }
      }
      
      // 3. Find walls that share endpoints with this connector (indicating room boundaries)
      const connectorStart = { x: wall.start.x, y: wall.start.y };
      const connectorEnd = { x: wall.end.x, y: wall.end.y };
      const tolerance = 5; // pixels
      
      const connectedWalls = currentWalls.filter(w => {
        if ((w as any).tempId === tempId) return false; // Skip self
        
        const wallStart = { x: w.start.x, y: w.start.y };
        const wallEnd = { x: w.end.x, y: w.end.y };
        
        // Check if walls share endpoints (within tolerance)
        const shareStartEnd = Math.abs(wallStart.x - connectorEnd.x) < tolerance && Math.abs(wallStart.y - connectorEnd.y) < tolerance;
        const shareEndStart = Math.abs(wallEnd.x - connectorStart.x) < tolerance && Math.abs(wallEnd.y - connectorStart.y) < tolerance;
        const shareStartStart = Math.abs(wallStart.x - connectorStart.x) < tolerance && Math.abs(wallStart.y - connectorStart.y) < tolerance;
        const shareEndEnd = Math.abs(wallEnd.x - connectorEnd.x) < tolerance && Math.abs(wallEnd.y - connectorEnd.y) < tolerance;
        
        return shareStartEnd || shareEndStart || shareStartStart || shareEndEnd;
      });
      
      // 4. Inherit room ownership from connected walls
      connectedWalls.forEach(connectedWall => {
        if ((connectedWall as any).originatingRoomId) {
          connectorRoomIds.add((connectedWall as any).originatingRoomId);
        }
        if ((connectedWall as any).assignedRoomIds) {
          (connectedWall as any).assignedRoomIds.forEach((id: string) => connectorRoomIds.add(id));
        }
      });
      
      // 5. Apply the collected room assignments
      const roomIdsArray = Array.from(connectorRoomIds);
      if (roomIdsArray.length > 0) {
        roomOwnership.originatingRoomId = roomIdsArray[0]; // Use first as primary
        roomOwnership.assignedRoomIds = roomIdsArray; // Assign to all connected rooms
        roomOwnership.parentRoomIds = roomIdsArray; // Also set as parent rooms
        
        console.log('🔗 CONNECTOR_WALL_MULTI_ROOM_ASSIGNMENT', {
          connectorId: tempId,
          wallCoords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
          connectedWallsFound: connectedWalls.length,
          roomsAssigned: roomIdsArray.length,
          roomIds: roomIdsArray.map(id => id.substring(0, 30) + '...'),
          preservationStrategy: 'multi-room-assignment'
        });
      } else {
        console.warn('🚨 CONNECTOR_WALL_NO_ROOMS_FOUND: Connector wall has no room assignments', {
          connectorId: tempId,
          wallCoords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
          parentWallFound: !!parentWall,
          connectedWallsFound: connectedWalls.length
        });
      }
    }
    
    // 🚫 CRITICAL SPLIT WALL FIX: Preserve parentWallId for split walls at all costs
    let splitWallMetadata = {
      isSplitOrigin: wall.isSplitOrigin,
      parentWallId: wall.parentWallId
    };
    
    // 🛡️ DEFENSIVE: If split wall is missing parentWallId, try to recover it
    if (wall.isSplitOrigin && !splitWallMetadata.parentWallId) {
      // Try to find the original parent wall by looking at wall naming patterns or geometry
      const potentialParentWall = currentWalls.find(w => {
        // Look for walls that might be the original parent
        const wallId = (w as any).tempId || '';
        const currentWallId = tempId || '';
        
        // Check if this might be the parent based on ID patterns (e.g., wall-1 vs wall-1-3)
        const baseIdMatch = wallId.includes('-') && currentWallId.includes('-') && 
          wallId.split('-').slice(0, 2).join('-') === currentWallId.split('-').slice(0, 2).join('-') &&
          wallId !== currentWallId;
        
        return baseIdMatch;
      });
      
      if (potentialParentWall) {
        splitWallMetadata.parentWallId = (potentialParentWall as any).tempId;
        console.log('🛡️ SPLIT_WALL_PARENT_RECOVERY: Recovered missing parentWallId', {
          splitWallId: tempId,
          recoveredParentId: splitWallMetadata.parentWallId,
          wallCoords: `(${wall.start.x},${wall.start.y})->(${wall.end.x},${wall.end.y})`
        });
      } else {
        console.warn('🚨 SPLIT_WALL_PARENT_RECOVERY_FAILED: Could not recover parentWallId', {
          splitWallId: tempId,
          isSplitOrigin: wall.isSplitOrigin,
          availableWalls: currentWalls.length,
          wallCoords: `(${wall.start.x},${wall.start.y})->(${wall.end.x},${wall.end.y})`
        });
      }
    }
    
    const result = {
      ...baseWall,
      // 🎯 CRITICAL FIX: ALWAYS preserve tempId for ALL walls (not just split walls)
      // This is essential for connector walls, split walls, and regular walls to maintain identity
      ...(tempId && { tempId: tempId }),
      // 🚫 CRITICAL SPLIT WALL FIX: Always preserve split wall metadata including recovered parentWallId
      ...(splitWallMetadata.isSplitOrigin && { isSplitOrigin: splitWallMetadata.isSplitOrigin }),
      ...(splitWallMetadata.parentWallId && { parentWallId: splitWallMetadata.parentWallId }),
      // Preserve other wall metadata
      ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
      ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
      ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector }),
      // 🛡️ ENFORCED ROOM OWNERSHIP: Always preserve room associations
      ...(roomOwnership.originatingRoomId && { originatingRoomId: roomOwnership.originatingRoomId }),
      ...(roomOwnership.assignedRoomIds && { assignedRoomIds: roomOwnership.assignedRoomIds }),
      ...(roomOwnership.parentRoomIds && { parentRoomIds: roomOwnership.parentRoomIds }),
      // Ensure floor is always set
      floor: wall.floor || currentFloor
    };
    
    // 🔧 DEBUG: Log room membership preservation
    if (wall.isSplitOrigin || wall.isExtrusionConnector) {
      console.log('🛡️ WALL_ROOM_MEMBERSHIP_PRESERVED', {
        wallType: wall.isSplitOrigin ? 'split' : (wall.isExtrusionConnector ? 'connector' : 'regular'),
        originalTempId: tempId,
        preservedTempId: (result as any).tempId,
        wallCoords: `(${wall.start.x},${wall.start.y})->(${wall.end.x},${wall.end.y})`,
        roomMembership: {
          originatingRoomId: (result as any).originatingRoomId,
          assignedRoomIds: (result as any).assignedRoomIds,
          parentRoomIds: (result as any).parentRoomIds
        },
        membershipPreserved: !!(result as any).originatingRoomId || !!(result as any).assignedRoomIds
      });
    }
    
    return result;
  }, [currentFloor, currentWalls]);
  const elementDragOffsetRef = useRef<Point | null>(null);

  // Add a ref to track which walls have already been used for extrusion creation
  const hasCreatedExtrusionRef = useRef<Set<string>>(new Set());

  // 🔧 COMPREHENSIVE EXTRUSION BUG FIXES

  // 1. **ROBUST WALL IDENTITY SYSTEM** - Stable UUIDs instead of changing tempIds
  const generateStableWallUUID = (wall: { start: Point; end: Point }): string => {
    // Create a stable UUID based on wall geometry and timestamp for uniqueness
    const geometryHash = generateStableWallId(wall.start, wall.end);
    const uuid = `wall_${geometryHash}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    return uuid;
  };

  // 2. **EXTRUSION STATE MACHINE** - Comprehensive state tracking
  interface ExtrusionState {
    wallId: string;
    stableUUID: string;
    hasCreatedExtrusion: boolean;
    extrusionType: 'none' | 'first-creation' | 'existing-main' | 'existing-connector';
    mainSegmentUUID?: string;
    connectorUUIDs?: string[];
    createdTimestamp: number;
  }

  const extrusionStateMapRef = useRef<Map<string, ExtrusionState>>(new Map());

  // 3. **GUARD FUNCTIONS** - Prevent duplicate extrusion creation
  const hasWallAlreadyCreatedExtrusion = (wallId: string): boolean => {
    const state = extrusionStateMapRef.current.get(wallId);
    return state?.hasCreatedExtrusion === true;
  };

  const isWallPartOfExistingExtrusion = (wallId: string, allWalls: WallWithTempId[]): boolean => {
    // Check if wall is already marked as main segment or connector
    const wall = allWalls.find(w => w.tempId === wallId);
    if (!wall) return false;
    
    return !!(wall as any).isMainExtrusionSegment || !!(wall as any).isExtrusionConnector;
  };

  const findExistingExtrusionForWall = (wallId: string, allWalls: WallWithTempId[]): {
    mainSegments: WallWithTempId[];
    connectors: WallWithTempId[];
  } | null => {
    const wall = allWalls.find(w => w.tempId === wallId);
    if (!wall) return null;

    // If this is already part of an extrusion, find all related walls
    if ((wall as any).isMainExtrusionSegment || (wall as any).isExtrusionConnector) {
      const mainSegments = allWalls.filter(w => (w as any).isMainExtrusionSegment);
      const connectors = allWalls.filter(w => (w as any).isExtrusionConnector);
      
      return { mainSegments, connectors };
    }
    
    return null;
  };

  // 4. **COMPREHENSIVE DEBUG LOGGING**
  const debugExtrusionOperation = (operation: string, data: any) => {
    const timestamp = new Date().toISOString();
    console.log(`🔧 [EXTRUSION-${operation}] ${timestamp}:`, {
      ...data,
      extrusionStateMap: Array.from(extrusionStateMapRef.current.entries()).map(([key, value]) => ({
        wallId: key,
        state: value
      }))
    });
    
    // Also call the main debug function
    onDebug(`EXTRUSION_${operation}`, data);
  };

  // 5. **STATE MANAGEMENT FUNCTIONS**
  const setExtrusionState = (wallId: string, state: Partial<ExtrusionState>) => {
    const existing = extrusionStateMapRef.current.get(wallId) || {
      wallId,
      stableUUID: generateStableWallUUID({ start: { x: 0, y: 0 }, end: { x: 0, y: 0 } }),
      hasCreatedExtrusion: false,
      extrusionType: 'none' as const,
      createdTimestamp: Date.now()
    };
    
    const updated = { ...existing, ...state };
    extrusionStateMapRef.current.set(wallId, updated);
    
    debugExtrusionOperation('STATE_UPDATE', {
      wallId,
      previousState: existing,
      newState: updated
    });
  };

  const getExtrusionState = (wallId: string): ExtrusionState | null => {
    return extrusionStateMapRef.current.get(wallId) || null;
  };

  // 6. **BULLETPROOF EXTRUSION CREATION GUARD**
  const shouldAllowExtrusionCreation = (wallId: string, allWalls: WallWithTempId[]): {
    allowed: boolean;
    reason: string;
    action: 'create-first' | 'modify-existing' | 'standard-resize' | 'blocked';
  } => {
    const wall = allWalls.find(w => w.tempId === wallId);
    if (!wall) {
      return { allowed: false, reason: 'Wall not found', action: 'blocked' };
    }

    // Check if wall is already part of an extrusion system
    if ((wall as any).isMainExtrusionSegment) {
      return { 
        allowed: true, 
        reason: 'Wall is existing main extrusion segment - allow modification', 
        action: 'modify-existing' 
      };
    }

    if ((wall as any).isExtrusionConnector) {
      return { 
        allowed: true, 
        reason: 'Wall is existing connector - allow width modification', 
        action: 'modify-existing' 
      };
    }

    // 🔧 FIX: Allow split segments to be resized multiple times
    if ((wall as any).isSplitOrigin || (wall as any).parentWallId) {
      return { 
        allowed: true, 
        reason: 'Split segment wall always eligible for resize operations', 
        action: 'standard-resize' 
      };
    }

    // Check our comprehensive state tracking
    const state = getExtrusionState(wallId);
    if (state?.hasCreatedExtrusion) {
      return { 
        allowed: false, 
        reason: 'Wall already used for extrusion creation - use standard resize', 
        action: 'standard-resize' 
      };
    }

    // Check legacy tracking
    if (hasCreatedExtrusionRef.current.has(wallId)) {
      return { 
        allowed: false, 
        reason: 'Wall already used for extrusion (legacy tracking) - use standard resize', 
        action: 'standard-resize' 
      };
    }

    return { 
      allowed: false, 
      reason: 'Wall not eligible for extrusion creation', 
      action: 'standard-resize' 
    };
  };

  // 7. **CLEANUP AND MAINTENANCE FUNCTIONS**
  const resetExtrusionStateForOperation = () => {
    isExtrudingRoomSegmentRef.current = false;
    isResizingExtrusionWidthRef.current = false;
    
    debugExtrusionOperation('STATE_RESET', {
      reason: 'Operation complete - resetting extrusion flags'
    });
  };

  const clearObsoleteExtrusionStates = (currentWallIds: string[]) => {
    const currentStates = Array.from(extrusionStateMapRef.current.keys());
    const obsoleteStates = currentStates.filter(wallId => !currentWallIds.includes(wallId));
    
    obsoleteStates.forEach(wallId => {
      extrusionStateMapRef.current.delete(wallId);
      hasCreatedExtrusionRef.current.delete(wallId);
    });

    if (obsoleteStates.length > 0) {
      debugExtrusionOperation('CLEANUP_OBSOLETE_STATES', {
        removedStates: obsoleteStates.length,
        removedWallIds: obsoleteStates
      });
    }
  };

  useEffect(() => {
    isDraggingRoomRef.current = isDraggingRoom;
  }, [isDraggingRoom]);

  useEffect(() => {
    roomStartRef.current = roomStart;
  }, [roomStart]);

  useEffect(() => {
    roomEndRef.current = roomEnd;
  }, [roomEnd]);

  // 🚨 CRITICAL FIX: Sync isResizing state to isResizingRef for touch move detection
  useEffect(() => {
    isResizingRef.current = isResizing;

  }, [isResizing]);

  // 🎯 WALL DRAG MODE UTILITIES - Professional CAD-like individual wall manipulation
  
  /**
   * Switch to wall drag mode when partial dragging is detected
   */
  const activateWallDragMode = useCallback((
    draggedWall: WallWithTempId,
    intersectionPoints: Point[],
    dragStartPoint: Point,
    allWalls: WallWithTempId[]
  ) => {
    console.log('🎯 ACTIVATING WALL DRAG MODE', {
      draggedWallId: draggedWall.tempId,
      intersectionCount: intersectionPoints.length,
      dragStartPoint
    });
    
    // Store original tool and switch to wall drag mode
    originalToolRef.current = currentToolInternalRef.current;
    setIsWallDragMode(true);
    wallDragModeRef.current = true;
    
    // Calculate which walls are independent vs connected
    const independentWalls = allWalls.filter(wall => 
      wall.tempId !== draggedWall.tempId
    );
    
    const connectedWalls = allWalls.filter(wall => 
      wall.tempId === draggedWall.tempId
    );
    
    // Store wall drag data
    wallDragDataRef.current = {
      draggedWall,
      originalWallPosition: {
        start: { ...draggedWall.start },
        end: { ...draggedWall.end }
      },
      independentWalls,
      connectedWalls,
      dragStartPoint,
      intersectionPoints
    };
    
    onDebug('WALL_DRAG_MODE_ACTIVATED', {
      draggedWallId: draggedWall.tempId,
      originalTool: originalToolRef.current,
      independentWallCount: independentWalls.length,
      connectedWallCount: connectedWalls.length
    });
  }, [onDebug, currentToolInternalRef]);
  
  /**
   * Deactivate wall drag mode and return to original tool
   */
  const deactivateWallDragMode = useCallback(() => {
    console.log('🎯 DEACTIVATING WALL DRAG MODE');
    
    setIsWallDragMode(false);
    wallDragModeRef.current = false;
    wallDragDataRef.current = null;
    intersectionDataRef.current = null;
    originalToolRef.current = null;
    
    onDebug('WALL_DRAG_MODE_DEACTIVATED', {});
  }, [onDebug]);
  
  /**
   * Handle wall drag movement in professional CAD style
   */
  const handleWallDragMove = useCallback((
    currentPoint: Point,
    draggedWall: WallWithTempId,
    intersectionPoints: Point[],
    allWalls: WallWithTempId[]
  ): WallWithTempId[] => {
    const dragData = wallDragDataRef.current;
    if (!dragData) return allWalls;
    
    const deltaX = currentPoint.x - dragData.dragStartPoint.x;
    const deltaY = currentPoint.y - dragData.dragStartPoint.y;
    
    // Calculate new wall position
    const newWallPosition = {
      start: {
        x: dragData.originalWallPosition.start.x + deltaX,
        y: dragData.originalWallPosition.start.y + deltaY
      },
      end: {
        x: dragData.originalWallPosition.end.x + deltaX,
        y: dragData.originalWallPosition.end.y + deltaY
      }
    };
    
    // Update only the dragged wall, leave others independent
    const updatedWalls = allWalls.map(wall => {
      if (wall.tempId === draggedWall.tempId) {
        return {
          ...wall,
          start: newWallPosition.start,
          end: newWallPosition.end
        };
      }
      return wall;
    });
    
    console.log('🎯 WALL DRAG MOVE', {
      draggedWallId: draggedWall.tempId,
      deltaX: deltaX.toFixed(1),
      deltaY: deltaY.toFixed(1),
      newStart: `(${newWallPosition.start.x.toFixed(1)},${newWallPosition.start.y.toFixed(1)})`,
      newEnd: `(${newWallPosition.end.x.toFixed(1)},${newWallPosition.end.y.toFixed(1)})`
    });
    
    return updatedWalls;
  }, []);

  const snapToEndpointsOrGrid = useCallback((point: Point, isTouchEvent = false): Point => {
    const plainWalls = currentWalls.map(({ tempId, ...wall }) => ({
      ...wall,
      // Preserve split wall metadata
      ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
      ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
      ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
      ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
      ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
      ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
    }));
    
    // Reduce snap threshold for touch events on iOS
    const adjustedSnapThreshold = isTouchEvent ? SNAP_THRESHOLD * 0.5 : SNAP_THRESHOLD;
    
    // Always try to snap to existing wall endpoints first
    for (const wall of plainWalls) {
      if (isNearPoint(point, wall.start, adjustedSnapThreshold)) {
        console.log('🔧 Snap Debug: Snapped to wall start', {
          originalPoint: point,
          snappedTo: wall.start,
          distance: Math.hypot(point.x - wall.start.x, point.y - wall.start.y),
          threshold: adjustedSnapThreshold
        });
        return wall.start;
      }
      if (isNearPoint(point, wall.end, adjustedSnapThreshold)) {
        console.log('🔧 Snap Debug: Snapped to wall end', {
          originalPoint: point,
          snappedTo: wall.end,
          distance: Math.hypot(point.x - wall.end.x, point.y - wall.end.y),
          threshold: adjustedSnapThreshold
        });
        return wall.end;
      }
    }
    
    // Skip grid snapping for touch events to maintain precise touch input
    if (isTouchEvent) {
      console.log('🔧 Snap Debug: Touch event - skipping grid snap', {
        originalPoint: point,
        finalPoint: point,
        reason: 'Touch events bypass grid snapping for better precision'
      });
      return point;
    }
    
    // Grid snapping only for mouse events
    const gridSize = 30;
    const snappedX = Math.round(point.x / gridSize) * gridSize;
    const snappedY = Math.round(point.y / gridSize) * gridSize;
    const snappedPoint = { x: snappedX, y: snappedY };
    
    console.log('🔧 Snap Debug: Snapped to grid', {
      originalPoint: point,
      snappedTo: snappedPoint,
      gridSize
    });
    
    return snappedPoint;
  }, [currentWalls]);

  const checkIfPerpendicularDrag = (wall: Wall, handle: string | null): boolean => {
    if (!handle) return false;
    // Handles like 'n', 's', 'e', 'w' are for edge drags.
    // Handles like 'nw', 'ne', 'sw', 'se' are for corner drags.
    // Extrusion typically happens from dragging an edge perpendicularly.
    if (handle.length > 1) return false; // Corner handles are not perpendicular wall drags

    const isHorizontal = Math.abs(wall.start.y - wall.end.y) < Math.abs(wall.start.x - wall.end.x);
    if (isHorizontal) {
        return handle === 'n' || handle === 's';
    } else { // Vertical
        return handle === 'e' || handle === 'w';
    }
  };
  // Fast simplified extrusion preview for performance during dragging
  const createSimplifiedExtrusionPreview = (
    roomWalls: Wall[],
    draggedWall: WallWithTempId,
    targetPoint: Point,
    resizeHandle: string,
    allCurrentWalls?: Wall[] // ⚡ NEW: Include current walls to detect existing connectors
  ): Wall[] | null => {
    
    // Debug logging disabled during dragging preview for performance
    // console.log('🚨 [DIRECT] createSimplifiedExtrusionPreview CALLED!', {
    //   draggedWallCoords: `(${draggedWall.start.x.toFixed(1)},${draggedWall.start.y.toFixed(1)})->(${draggedWall.end.x.toFixed(1)},${draggedWall.end.y.toFixed(1)})`,
    //   roomWallsCount: roomWalls.length,
    //   targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
    //   resizeHandle,
    //   timestamp: Date.now()
    // });
    
    // Debug logging disabled during dragging preview for performance
    // onDebug('🔧 [createSimplifiedExtrusionPreview] ENTRY', {
    //   draggedWallCoords: `(${draggedWall.start.x.toFixed(1)},${draggedWall.start.y.toFixed(1)})->(${draggedWall.end.x.toFixed(1)},${draggedWall.end.y.toFixed(1)})`,
    //   draggedWallFlags: {
    //     isSplitOrigin: (draggedWall as any).isSplitOrigin,
    //     isMainExtrusionSegment: (draggedWall as any).isMainExtrusionSegment,
    //     parentWallId: (draggedWall as any).parentWallId
    //   },
    //   roomWallsCount: roomWalls.length,
    //   targetPoint: `(${targetPoint.x.toFixed(1)},${targetPoint.y.toFixed(1)})`,
    //   resizeHandle,
    //   context: 'This is likely the function creating the unwanted connectors!'
    // });
    
    try {
      // Create a simple extrusion preview by moving the dragged wall perpendicular to its direction
      const segmentVector = { 
        x: draggedWall.end.x - draggedWall.start.x, 
        y: draggedWall.end.y - draggedWall.start.y 
      };
      const segmentLength = Math.hypot(segmentVector.x, segmentVector.y);
      
      if (segmentLength === 0) return null;
      
      // Calculate perpendicular direction
      const perpVector = { 
        x: -segmentVector.y / segmentLength, 
        y: segmentVector.x / segmentLength 
      };
      
      // Calculate how far to move the wall
      const midPoint = { 
        x: (draggedWall.start.x + draggedWall.end.x) / 2, 
        y: (draggedWall.start.y + draggedWall.end.y) / 2 
      };
      const dragVector = { x: targetPoint.x - midPoint.x, y: targetPoint.y - midPoint.y };
      const displacement = (dragVector.x * perpVector.x) + (dragVector.y * perpVector.y);
      
      // Create the moved wall (this will become the main extrusion segment)
      const movedWall: Wall = {
        start: {
          x: draggedWall.start.x + perpVector.x * displacement,
          y: draggedWall.start.y + perpVector.y * displacement
        },
        end: {
          x: draggedWall.end.x + perpVector.x * displacement,
          y: draggedWall.end.y + perpVector.y * displacement
        },
        type: draggedWall.type,
        floor: draggedWall.floor || currentFloor,
        // 🔧 CRITICAL FIX: Inherit ownership metadata from draggedWall to prevent unowned walls
        originatingRoomId: (draggedWall as any).originatingRoomId,
        assignedRoomIds: (draggedWall as any).assignedRoomIds ? [...(draggedWall as any).assignedRoomIds] : undefined
      };
      
      // 🔧 CRITICAL FIX: Apply the "original room endpoints never use connectors" rule
      
      // Find the split point (where segments from the same parent wall meet)
      const findSplitPoint = (): Point | null => {
        const draggedWallTyped = draggedWall as any;
        if (!draggedWallTyped.parentWallId) {
          console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: No parentWallId found - this should not happen after the fix');
          
          // 🔧 FIXED: If there's no parentWallId, this is likely not a proper split wall from manual splitting
          // Don't force split point detection - let both endpoints be treated as original room endpoints
          // This allows proper wall extension behavior instead of creating unwanted fixed connectors
          if (draggedWallTyped.isSplitOrigin) {
            console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: Wall has isSplitOrigin but no parentWallId - treating as regular wall');
            console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: Both endpoints will be treated as original room endpoints for wall extension');
          }
          
          return null;
        }
        
        console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: Starting detection', {
          draggedParentId: draggedWallTyped.parentWallId,
          draggedTempId: draggedWallTyped.tempId,
          roomWallsCount: roomWalls.length
        });
        
        // 🔧 HYBRID APPROACH: Use original room walls for split point detection
        // (to find where segments used to connect) but current state for connector extension
        // Include any existing connector walls for comprehensive detection
        const connectorWallsNotInRoom = currentWalls.filter(w => {
          if (!(w as any).isExtrusionConnector) return false;
          
          // Check if this connector wall is already in roomWalls by coordinates
          return !roomWalls.some(roomWall => 
            (pointsAreEqual(roomWall.start, w.start, 0.1) && pointsAreEqual(roomWall.end, w.end, 0.1)) ||
            (pointsAreEqual(roomWall.start, w.end, 0.1) && pointsAreEqual(roomWall.end, w.start, 0.1))
          );
        });
        
        const allCurrentWalls = [...roomWalls, ...connectorWallsNotInRoom];
        
        console.log('🔧 HYBRID_SPLIT_POINT_DETECTION:', {
          originalRoomWallsCount: roomWalls.length,
          existingConnectorWalls: connectorWallsNotInRoom.length,
          totalWallsForDetection: allCurrentWalls.length,
          reason: 'Using original room walls + existing connectors for split point detection'
        });
        
        // Find sibling segments (walls with the same parent wall ID but different coordinates)
        const siblingSegments = allCurrentWalls.filter(wall => {
          const wallTyped = wall as any;
          
          // Check if this wall has the same parent ID
          const hasExactParentMatch = wallTyped.parentWallId === draggedWallTyped.parentWallId;
          
          // 🔧 CRITICAL FIX: Also check for similar parent IDs that might be from the same original wall
          // Sometimes split segments get slightly different parent IDs but are from the same original wall
          const wallParentId = wallTyped.parentWallId;
          const draggedParentId = draggedWallTyped.parentWallId;
          
          const hasSimilarParentMatch = wallParentId && draggedParentId && 
            wallParentId.startsWith('original_') && draggedParentId.startsWith('original_') &&
            Math.abs(parseInt(wallParentId.split('_')[1]) - parseInt(draggedParentId.split('_')[1])) <= 1;
          
          // 🔧 NEW: Also look for walls that might be connected through connector walls
          // If we can't find direct sibling through parent ID, look for walls connected to the same split point
          const hasConnectorRelationship = !hasExactParentMatch && !hasSimilarParentMatch && wallTyped.isExtrusionConnector &&
            (pointsAreEqual(wall.start, draggedWall.start, 0.1) || pointsAreEqual(wall.start, draggedWall.end, 0.1) ||
             pointsAreEqual(wall.end, draggedWall.start, 0.1) || pointsAreEqual(wall.end, draggedWall.end, 0.1));
          
          console.log('🔧 SIBLING_PARENT_ID_CHECK:', {
            wallTempId: wallTyped.tempId,
            wallParentId: wallParentId,
            draggedParentId: draggedParentId,
            hasExactParentMatch: hasExactParentMatch,
            hasSimilarParentMatch: hasSimilarParentMatch,
            hasConnectorRelationship: hasConnectorRelationship,
            isExtrusionConnector: wallTyped.isExtrusionConnector,
            willInclude: hasExactParentMatch || hasSimilarParentMatch || hasConnectorRelationship
          });
          
          if (!hasExactParentMatch && !hasSimilarParentMatch && !hasConnectorRelationship) {
            return false;
          }
          
          // More robust way to exclude self - use coordinate comparison since tempId might be unreliable
          const isSameWall = (
            pointsAreEqual(draggedWall.start, wall.start, 0.1) && 
            pointsAreEqual(draggedWall.end, wall.end, 0.1)
          ) || (
            pointsAreEqual(draggedWall.start, wall.end, 0.1) && 
            pointsAreEqual(draggedWall.end, wall.start, 0.1)
          );
          
          const isSibling = !isSameWall;
          
          if (isSibling) {
            console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: Found sibling segment', {
              siblingTempId: wallTyped.tempId,
              siblingCoords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
              parentMatches: hasExactParentMatch,
              similarParentMatch: hasSimilarParentMatch,
              connectorRelationship: hasConnectorRelationship,
              parentMatchType: hasExactParentMatch ? 'exact' : (hasSimilarParentMatch ? 'similar' : (hasConnectorRelationship ? 'connector' : 'none')),
              isNotSameWall: !isSameWall
            });
          }
          return isSibling;
        });
        
        console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: Found siblings', { count: siblingSegments.length });
        
        // Check which endpoint is shared with sibling segments
        for (const sibling of siblingSegments) {
          // 🔧 CRITICAL FIX: Use larger tolerance to account for connector walls that may have moved
          const tolerance = 20.0; // Increased tolerance to handle split points that moved during previous resizes
          
          // Check if draggedWall.start is close to either endpoint of sibling
          if (pointsAreEqual(draggedWall.start, sibling.start, tolerance) || pointsAreEqual(draggedWall.start, sibling.end, tolerance)) {
            // 🔧 ENHANCED SPLIT POINT CALCULATION: Check if this is a connector adjustment scenario
            const parentWallId = (draggedWall as any).parentWallId;
            const hasExistingConnectors = allCurrentWalls.some((wall: any) => 
              wall.isExtrusionConnector && wall.parentWallId === parentWallId
            );
            
            if (hasExistingConnectors) {
              // 🔧 CRITICAL FIX: For connector adjustment, use the dragged wall's new endpoint as split point
              // This ensures connectors extend to exactly where the dragged segment moved to
              // 🔧 BACKWARD COMPATIBILITY: Ensure movedWall has valid coordinates
              if (movedWall && movedWall.start && typeof movedWall.start.x === 'number' && typeof movedWall.start.y === 'number') {
                console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: P1 is split point (CONNECTOR ADJUSTMENT)', {
                  originalPoint: `(${draggedWall.start.x.toFixed(1)},${draggedWall.start.y.toFixed(1)})`,
                  actualSplitPoint: `(${movedWall.start.x.toFixed(1)},${movedWall.start.y.toFixed(1)})`,
                  sharedWith: `(${sibling.start.x.toFixed(1)},${sibling.start.y.toFixed(1)})->(${sibling.end.x.toFixed(1)},${sibling.end.y.toFixed(1)})`,
                  reason: 'Using dragged wall new endpoint for connector adjustment - ensures gap closure'
                });
                return movedWall.start; // Use the new position, not the old sibling position
              } else {
                console.warn('🔧 SPLIT_POINT_DETECTION: Invalid movedWall coordinates, falling back to original logic');
                // Fall through to original logic
              }
            } else {
              // 🔧 ORIGINAL LOGIC: For new connector creation, use sibling's current position  
              const distToSiblingStart = Math.sqrt(
                Math.pow(draggedWall.start.x - sibling.start.x, 2) + 
                Math.pow(draggedWall.start.y - sibling.start.y, 2)
              );
              const distToSiblingEnd = Math.sqrt(
                Math.pow(draggedWall.start.x - sibling.end.x, 2) + 
                Math.pow(draggedWall.start.y - sibling.end.y, 2)
              );
              
              const actualSplitPoint = distToSiblingStart < distToSiblingEnd ? sibling.start : sibling.end;
              console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: P1 is split point (NEW CONNECTOR)', {
                originalPoint: `(${draggedWall.start.x.toFixed(1)},${draggedWall.start.y.toFixed(1)})`,
                actualSplitPoint: `(${actualSplitPoint.x.toFixed(1)},${actualSplitPoint.y.toFixed(1)})`,
                sharedWith: `(${sibling.start.x.toFixed(1)},${sibling.start.y.toFixed(1)})->(${sibling.end.x.toFixed(1)},${sibling.end.y.toFixed(1)})`,
                reason: 'Using current split point position from sibling wall for new connector'
              });
              return actualSplitPoint;
            }
          }
          
          // Check if draggedWall.end is close to either endpoint of sibling
          if (pointsAreEqual(draggedWall.end, sibling.start, tolerance) || pointsAreEqual(draggedWall.end, sibling.end, tolerance)) {
            // 🔧 ENHANCED SPLIT POINT CALCULATION: Check if this is a connector adjustment scenario
            const parentWallId = (draggedWall as any).parentWallId;
            const hasExistingConnectors = allCurrentWalls.some((wall: any) => 
              wall.isExtrusionConnector && wall.parentWallId === parentWallId
            );
            
            if (hasExistingConnectors) {
              // 🔧 CRITICAL FIX: For connector adjustment, use the dragged wall's new endpoint as split point
              // This ensures connectors extend to exactly where the dragged segment moved to
              // 🔧 BACKWARD COMPATIBILITY: Ensure movedWall has valid coordinates
              if (movedWall && movedWall.end && typeof movedWall.end.x === 'number' && typeof movedWall.end.y === 'number') {
                console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: P2 is split point (CONNECTOR ADJUSTMENT)', {
                  originalPoint: `(${draggedWall.end.x.toFixed(1)},${draggedWall.end.y.toFixed(1)})`,
                  actualSplitPoint: `(${movedWall.end.x.toFixed(1)},${movedWall.end.y.toFixed(1)})`,
                  sharedWith: `(${sibling.start.x.toFixed(1)},${sibling.start.y.toFixed(1)})->(${sibling.end.x.toFixed(1)},${sibling.end.y.toFixed(1)})`,
                  reason: 'Using dragged wall new endpoint for connector adjustment - ensures gap closure'
                });
                return movedWall.end; // Use the new position, not the old sibling position
              } else {
                console.warn('🔧 SPLIT_POINT_DETECTION: Invalid movedWall coordinates, falling back to original logic');
                // Fall through to original logic
              }
            } else {
              // 🔧 ORIGINAL LOGIC: For new connector creation, use sibling's current position
              const distToSiblingStart = Math.sqrt(
                Math.pow(draggedWall.end.x - sibling.start.x, 2) + 
                Math.pow(draggedWall.end.y - sibling.start.y, 2)
              );
              const distToSiblingEnd = Math.sqrt(
                Math.pow(draggedWall.end.x - sibling.end.x, 2) + 
                Math.pow(draggedWall.end.y - sibling.end.y, 2)
              );
              
              const actualSplitPoint = distToSiblingStart < distToSiblingEnd ? sibling.start : sibling.end;
              console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: P2 is split point (NEW CONNECTOR)', {
                originalPoint: `(${draggedWall.end.x.toFixed(1)},${draggedWall.end.y.toFixed(1)})`,
                actualSplitPoint: `(${actualSplitPoint.x.toFixed(1)},${actualSplitPoint.y.toFixed(1)})`,
                sharedWith: `(${sibling.start.x.toFixed(1)},${sibling.start.y.toFixed(1)})->(${sibling.end.x.toFixed(1)},${sibling.end.y.toFixed(1)})`,
                reason: 'Using current split point position from sibling wall for new connector'
              });
              return actualSplitPoint;
            }
          }
        }
        
        console.log('🚨 [DIRECT] SPLIT_POINT_DETECTION: No split point found');
        return null;
      };
      
      // Check if an endpoint is an original room endpoint (connected to walls from different parents or regular walls)
      const isOriginalRoomEndpoint = (point: Point): boolean => {
        const tolerance = 0.1;
        const draggedWallTyped = draggedWall as any;
        const draggedParentId = draggedWallTyped.parentWallId;
        
        // 🔧 CRITICAL FIX: Include connector walls that aren't already in roomWalls
        // This ensures that connector walls created during previous resize operations are considered
        // but avoids duplicates that could cause incorrect endpoint classification
        const connectorWallsNotInRoomForEndpoint = currentWalls.filter(w => {
          if (!w.isExtrusionConnector) return false;
          
          // Check if this connector wall is already in roomWalls by coordinates
          return !roomWalls.some(roomWall => 
            (pointsAreEqual(roomWall.start, w.start, 0.1) && pointsAreEqual(roomWall.end, w.end, 0.1)) ||
            (pointsAreEqual(roomWall.start, w.end, 0.1) && pointsAreEqual(roomWall.end, w.start, 0.1))
          );
        });
        
        const allWallsForEndpointCheck = [...roomWalls, ...connectorWallsNotInRoomForEndpoint];
        
        // Find all walls connected to this endpoint
        const connectedWalls = allWallsForEndpointCheck.filter(wall => {
          // Skip the dragged wall itself
          if (wall === draggedWall) return false;
          
          return pointsAreEqual(wall.start, point, tolerance) || pointsAreEqual(wall.end, point, tolerance);
        });
        
        console.log('🚨 [DIRECT] ORIGINAL_ENDPOINT_CHECK', {
          point: `(${point.x.toFixed(1)},${point.y.toFixed(1)})`,
          connectedWallsCount: connectedWalls.length,
          draggedParentId
        });
        
        // Check if any connected wall has a different parent ID or no parent ID (regular wall)
        for (const connectedWall of connectedWalls) {
          const connectedWallTyped = connectedWall as any;
          const connectedParentId = connectedWallTyped.parentWallId;
          
          console.log('🚨 [DIRECT] ORIGINAL_ENDPOINT_CHECK: Connected wall', {
            connectedWallCoords: `(${connectedWall.start.x.toFixed(1)},${connectedWall.start.y.toFixed(1)})->(${connectedWall.end.x.toFixed(1)},${connectedWall.end.y.toFixed(1)})`,
            connectedParentId,
            draggedParentId,
            isRegularWall: !connectedParentId,
            isDifferentParent: connectedParentId && connectedParentId !== draggedParentId
          });
          
          // If connected to a regular wall (no parent ID), this is an original endpoint
          if (!connectedParentId) {
            console.log('🚨 [DIRECT] ORIGINAL_ENDPOINT_CONFIRMED: Connected to regular wall');
            return true;
          }
          
          // If connected to a wall from a different parent, this is an original endpoint
          if (connectedParentId !== draggedParentId) {
            console.log('🚨 [DIRECT] ORIGINAL_ENDPOINT_CONFIRMED: Connected to different parent wall');
            return true;
          }
        }
        
        console.log('🚨 [DIRECT] ORIGINAL_ENDPOINT_REJECTED: Only connected to same parent walls');
        return false;
      };
      
      // Identify endpoints
      const P1_orig = draggedWall.start;
      const P2_orig = draggedWall.end;
      
      console.log('🚨 [DIRECT] BEFORE_SPLIT_POINT_CALL', {
        P1_coords: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
        P2_coords: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
        timestamp: Date.now()
      });
      
      let splitPoint;
      try {
        splitPoint = findSplitPoint();
        console.log('🚨 [DIRECT] SPLIT_POINT_CALL_SUCCESS', {
          splitPoint: splitPoint ? `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})` : 'Not found'
        });
      } catch (error) {
        console.error('🚨 [DIRECT] SPLIT_POINT_CALL_ERROR', error);
        splitPoint = null;
      }
      
      console.log('🚨 [DIRECT] BEFORE_ENDPOINT_ANALYSIS', {
        aboutToCallIsOriginalRoomEndpoint: true,
        timestamp: Date.now()
      });
      
      let P1_isOriginal, P2_isOriginal;
      try {
        P1_isOriginal = isOriginalRoomEndpoint(P1_orig);
        console.log('🚨 [DIRECT] P1_ENDPOINT_CHECK_COMPLETE', { P1_isOriginal });
      } catch (error) {
        console.error('🚨 [DIRECT] P1_ENDPOINT_CHECK_ERROR', error);
        P1_isOriginal = false;
      }
      
      try {
        P2_isOriginal = isOriginalRoomEndpoint(P2_orig);
        console.log('🚨 [DIRECT] P2_ENDPOINT_CHECK_COMPLETE', { P2_isOriginal });
      } catch (error) {
        console.error('🚨 [DIRECT] P2_ENDPOINT_CHECK_ERROR', error);
        P2_isOriginal = false;
      }
      
      console.log('🚨 [DIRECT] ENDPOINT_ANALYSIS', {
        splitPoint: splitPoint ? `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})` : 'Not found',
        P1_isOriginal,
        P2_isOriginal,
        P1_coords: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
        P2_coords: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
        willApplyRule: 'Only create connectors for split points, extend walls for original endpoints'
      });
      
      // Collect connectors and walls to extend
      const connectorsToAdd: Wall[] = [];
      
      // 🔍 COMPREHENSIVE LOGGING: Track wall transformation before mapping
      console.log('🔍 [roomWalls.map] WALL_TRANSFORMATION_START:', {
        roomWallsCount: roomWalls.length,
        draggedWallCoords: `(${draggedWall.start.x.toFixed(1)},${draggedWall.start.y.toFixed(1)})->(${draggedWall.end.x.toFixed(1)},${draggedWall.end.y.toFixed(1)})`,
        movedWallCoords: `(${movedWall.start.x.toFixed(1)},${movedWall.start.y.toFixed(1)})->(${movedWall.end.x.toFixed(1)},${movedWall.end.y.toFixed(1)})`,
        stackTrace: new Error().stack?.split('\n').slice(0, 5),
        inputWallsOwnership: roomWalls.map(w => ({
          coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
          hasOwnership: !!((w as any).originatingRoomId && (w as any).assignedRoomIds),
          originatingRoomId: (w as any).originatingRoomId || 'NONE',
          assignedRoomIds: (w as any).assignedRoomIds || 'NONE'
        }))
      });
      
      const wallsToReturn = roomWalls.map((wall, index) => {
        // 🔍 COMPREHENSIVE LOGGING: Track each wall transformation
        const wallCoords = `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`;
        
        // Replace the dragged wall with the moved wall
        if ((pointsAreEqual(wall.start, draggedWall.start, 0.1) && pointsAreEqual(wall.end, draggedWall.end, 0.1)) ||
            (pointsAreEqual(wall.start, draggedWall.end, 0.1) && pointsAreEqual(wall.end, draggedWall.start, 0.1))) {
          console.log(`🔍 [roomWalls.map] REPLACING_DRAGGED_WALL[${index}]:`, {
            originalCoords: wallCoords,
            newCoords: `(${movedWall.start.x.toFixed(1)},${movedWall.start.y.toFixed(1)})->(${movedWall.end.x.toFixed(1)},${movedWall.end.y.toFixed(1)})`,
            originalOwnership: {
              originatingRoomId: (wall as any).originatingRoomId || 'NONE',
              assignedRoomIds: (wall as any).assignedRoomIds || 'NONE'
            },
            movedWallOwnership: {
              originatingRoomId: (movedWall as any).originatingRoomId || 'NONE',
              assignedRoomIds: (movedWall as any).assignedRoomIds || 'NONE'
            }
          });
          return movedWall;
        }
        
        console.log(`🔍 [roomWalls.map] PRESERVING_WALL[${index}]:`, {
          coords: wallCoords,
          hasOwnership: !!((wall as any).originatingRoomId && (wall as any).assignedRoomIds),
          originatingRoomId: (wall as any).originatingRoomId || 'NONE',
          assignedRoomIds: (wall as any).assignedRoomIds || 'NONE'
        });
        
        return wall;
      });
      
      // 🔍 COMPREHENSIVE LOGGING: Track final transformation results
      console.log('🔍 [roomWalls.map] WALL_TRANSFORMATION_COMPLETE:', {
        outputWallsCount: wallsToReturn.length,
        outputWallsOwnership: wallsToReturn.map((w, i) => ({
          index: i,
          coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
          hasOwnership: !!((w as any).originatingRoomId && (w as any).assignedRoomIds),
          originatingRoomId: (w as any).originatingRoomId || 'NONE',
          assignedRoomIds: (w as any).assignedRoomIds || 'NONE'
        })),
        unownedWalls: wallsToReturn
          .filter(w => !((w as any).originatingRoomId && (w as any).assignedRoomIds))
          .map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
      });
      
      // Handle P1 (start point)
      const P1_isSplitPoint = splitPoint && pointsAreEqual(P1_orig, splitPoint, 0.1);
      const P1_isOriginalEndpoint = isOriginalRoomEndpoint(P1_orig);
      
      console.log('🚨 [DIRECT] P1_DECISION_ANALYSIS', {
        P1_coords: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
        P1_isSplitPoint,
        P1_isOriginalEndpoint,
        splitPointCoords: splitPoint ? `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})` : 'none',
        splitPointExists: !!splitPoint,
        pointsEqualCheck: splitPoint ? pointsAreEqual(P1_orig, splitPoint, 0.1) : 'no-split-point',
        P1_exact: `x:${P1_orig.x}, y:${P1_orig.y}`,
        splitPoint_exact: splitPoint ? `x:${splitPoint.x}, y:${splitPoint.y}` : 'none',
        distance: splitPoint ? Math.sqrt((P1_orig.x - splitPoint.x)**2 + (P1_orig.y - splitPoint.y)**2) : 'N/A',
        decision: P1_isSplitPoint ? 'FLUID_SPLIT_POINT_MOVEMENT' : P1_isOriginalEndpoint ? 'EXTEND_WALLS' : 'FALLBACK_EXTEND_WALLS'
      });
      
      console.log('🚨 [DIRECT] ABOUT_TO_PROCESS_P1_LOGIC');
      
      if (P1_isSplitPoint) {
        // 🔧 FLEXIBLE SPLIT POINT MOVEMENT: Check if we should extend siblings or create connectors
        console.log('🚨 [DIRECT] P1_SPLIT_POINT_FLUID_MOVEMENT', {
          originalPoint: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
          newPoint: `(${movedWall.start.x.toFixed(1)},${movedWall.start.y.toFixed(1)})`,
          reason: 'Split point moves naturally, determining best handling approach'
        });
        
        // 🔧 CRITICAL FIX: Check if there are existing connectors for this split wall
        // Instead of looking at exact coordinates (which may have moved), look for connectors
        // that belong to the same parent wall as this split segment
        const parentWallId = (draggedWall as any).parentWallId || (draggedWall as any).tempId;
        
        // ⚡ CRITICAL FIX: Expand search scope to include all available walls for connector detection
        const wallsToSearch = allCurrentWalls && allCurrentWalls.length > 0 
          ? [...wallsToReturn, ...allCurrentWalls]
          : wallsToReturn;
          
        const existingConnectorsAtSplitPoint = wallsToSearch.filter((wall: any) => {
          if (!wall.isExtrusionConnector) return false;
          
          // Look for connectors that share the same parent wall
          const wallParentId = wall.parentWallId;
          if (wallParentId === parentWallId) return true;
          
          // Fallback: Also check coordinate-based detection with larger tolerance for moved split points
          const tolerance = 5.0; // Increased tolerance to account for moved split points
          return (Math.abs(wall.start.x - P1_orig.x) < tolerance && Math.abs(wall.start.y - P1_orig.y) < tolerance) ||
                 (Math.abs(wall.end.x - P1_orig.x) < tolerance && Math.abs(wall.end.y - P1_orig.y) < tolerance);
        });
        
        console.log('🔍 [createSimplifiedExtrusionPreview] P1_CONNECTOR_SEARCH_SCOPE', {
          searchScope: allCurrentWalls ? `${wallsToReturn.length} room walls + ${allCurrentWalls.length} additional = ${wallsToSearch.length} total` : `${wallsToReturn.length} room walls only`,
          parentWallId,
          connectorsFound: existingConnectorsAtSplitPoint.length,
          connectorsDetails: existingConnectorsAtSplitPoint.map(c => ({
            tempId: (c as any).tempId,
            coords: `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`,
            parentWallId: c.parentWallId
          }))
        });
        
        const hasExistingConnectors = existingConnectorsAtSplitPoint.length > 0;
        
        console.log('🚨 [DIRECT] P1_SPLIT_POINT_CONNECTOR_CHECK', {
          existingConnectorsFound: hasExistingConnectors,
          connectorCount: existingConnectorsAtSplitPoint.length,
          parentWallId: parentWallId,
          connectorsFound: existingConnectorsAtSplitPoint.map(c => ({
            coords: `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`,
            parentWallId: c.parentWallId,
            foundBy: c.parentWallId === parentWallId ? 'PARENT_WALL_ID' : 'COORDINATES'
          })),
          approach: hasExistingConnectors ? 'EXTEND_SIBLINGS_TO_MATCH' : 'CREATE_CONNECTOR_GAP'
        });
        
        let P1_wallsExtended = 0;
        
        if (hasExistingConnectors) {
          // 🔧 FLUID BEHAVIOR: Extend sibling walls to match new position (no new connectors)
          console.log('🚨 [DIRECT] P1_SPLIT_SIBLINGS_EXTEND_TO_MATCH', {
            reason: 'Existing connectors found - extending sibling walls for fluid movement',
            originalSplitPoint: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
            newSplitPoint: `(${movedWall.start.x.toFixed(1)},${movedWall.start.y.toFixed(1)})`
          });
          
          // Find and extend sibling walls that connect to this split point
                const tolerance = 5.0; // 🔧 CRITICAL FIX: Match the tolerance used for connector detection
                const connectedWalls = wallsToReturn.filter((wall: any) => {
                  if (wall.tempId === draggedWall.tempId) return false; // Skip the wall being moved
                  // 🔧 CRITICAL FIX: Do NOT skip connectors - they need to be extended too!
                  // The connector wall is what bridges the gap and must move with the split point

                  const connectsAtStart = Math.abs(wall.start.x - P1_orig.x) < tolerance && Math.abs(wall.start.y - P1_orig.y) < tolerance;
                  const connectsAtEnd = Math.abs(wall.end.x - P1_orig.x) < tolerance && Math.abs(wall.end.y - P1_orig.y) < tolerance;

                  return connectsAtStart || connectsAtEnd;
                });
          
          // 🔧 CRITICAL FIX: Prioritize connector walls first, then other walls
          const connectorWalls = connectedWalls.filter((wall: any) => wall.isExtrusionConnector);
          const regularWalls = connectedWalls.filter((wall: any) => !wall.isExtrusionConnector);
          const wallsToExtend = [...connectorWalls, ...regularWalls]; // Connectors first!
          
          console.log('🚨 [DIRECT] aP1_WALLS_TO_EXTEND_ANALYSIS', {
            totalConnected: connectedWalls.length,
            connectorWalls: connectorWalls.length,
            regularWalls: regularWalls.length,
            connectorDetails: connectorWalls.map(w => ({
              coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
              tempId: w.tempId,
              isExtrusionConnector: w.isExtrusionConnector
            }))
          });

          wallsToExtend.forEach((wall: any) => {
            const wallConnectsAtStart = Math.abs(wall.start.x - P1_orig.x) < tolerance && Math.abs(wall.start.y - P1_orig.y) < tolerance;
            const wallConnectsAtEnd = Math.abs(wall.end.x - P1_orig.x) < tolerance && Math.abs(wall.end.y - P1_orig.y) < tolerance;
            
            if (wallConnectsAtStart) {
              // Calculate proper perpendicular extension for the wall
              const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
              const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
              
              if (wallLength > 0) {
                // Normalize the wall direction
                const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
                
                // Calculate movement vector from original to new endpoint
                const movementVector = { x: movedWall.start.x - P1_orig.x, y: movedWall.start.y - P1_orig.y };
                
                // Project movement onto wall direction to get proper extension
                const projectedLength = movementVector.x * wallDirection.x + movementVector.y * wallDirection.y;
                
                // Move the start point along the wall's direction by the projected amount
                wall.start.x = P1_orig.x + projectedLength * wallDirection.x;
                wall.start.y = P1_orig.y + projectedLength * wallDirection.y;
                
                console.log('🚨 [DIRECT] P1_SIBLING_WALL_START_EXTENDED_PROPERLY', {
                  wallTempId: wall.tempId || 'unknown',
                  wallType: wall.isExtrusionConnector ? 'CONNECTOR' : 'REGULAR',
                  originalStart: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
                  newStart: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})`,
                  projectedExtension: projectedLength.toFixed(2),
                  reason: 'Start point extended along wall direction for perpendicular extension'
                });
              } else {
                // Fallback for zero-length walls
                wall.start.x = movedWall.start.x;
                wall.start.y = movedWall.start.y;
              }
              P1_wallsExtended++;
            } else if (wallConnectsAtEnd) {
              // Calculate proper perpendicular extension for the wall
              const wallVector = { x: wall.start.x - wall.end.x, y: wall.start.y - wall.end.y };
              const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
              
              if (wallLength > 0) {
                // Normalize the wall direction (from end to start)
                const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
                
                // Calculate movement vector from original to new endpoint
                const movementVector = { x: movedWall.start.x - P1_orig.x, y: movedWall.start.y - P1_orig.y };
                
                // Project movement onto wall direction to get proper extension
                const projectedLength = movementVector.x * wallDirection.x + movementVector.y * wallDirection.y;
                
                // Move the end point along the wall's direction by the projected amount
                wall.end.x = P1_orig.x + projectedLength * wallDirection.x;
                wall.end.y = P1_orig.y + projectedLength * wallDirection.y;
                
                console.log('🚨 [DIRECT] P1_SIBLING_WALL_END_EXTENDED_PROPERLY', {
                  wallTempId: wall.tempId || 'unknown',
                  wallType: wall.isExtrusionConnector ? 'CONNECTOR' : 'REGULAR',
                  originalEnd: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
                  newEnd: `(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
                  projectedExtension: projectedLength.toFixed(2),
                  reason: 'End point extended along wall direction for perpendicular extension'
                });
              } else {
                // Fallback for zero-length walls
                wall.end.x = movedWall.start.x;
                wall.end.y = movedWall.start.y;
              }
              P1_wallsExtended++;
            }
          });
        } else {
          // 🔧 FIRST RESIZE: Create connector gap (original behavior)
          console.log('🚨 [DIRECT] P1_SPLIT_SIBLINGS_STAY_PUT', {
            reason: 'No existing connectors - sibling walls remain in original position, connector bridges gap',
            originalSplitPoint: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
            newSplitPoint: `(${movedWall.start.x.toFixed(1)},${movedWall.start.y.toFixed(1)})`
          });
          
          // Check if we need a connector due to significant displacement
          const displacement = Math.sqrt(
            Math.pow(movedWall.start.x - P1_orig.x, 2) + 
            Math.pow(movedWall.start.y - P1_orig.y, 2)
          );
          
          const CONNECTOR_THRESHOLD = 2.0; // Create connector for gaps > 2 pixels
          const needsConnector = displacement > CONNECTOR_THRESHOLD;
          
          if (needsConnector) {
            // Create connector with duplication prevention (Phase 2 fix)
            const connectorData = {
              start: { ...P1_orig },
              end: { ...movedWall.start },
              type: draggedWall.type,
              floor: draggedWall.floor || currentFloor,
              originatingRoomId: (draggedWall as any).originatingRoomId,
              assignedRoomIds: (draggedWall as any).assignedRoomIds ? [...(draggedWall as any).assignedRoomIds] : undefined,
              parentRoomIds: (draggedWall as any).parentRoomIds ? [...(draggedWall as any).parentRoomIds] : undefined,
              parentWallId: (draggedWall as any).parentWallId || (draggedWall as any).tempId
            };
            
            const safeConnectorResult = createSafeConnector(
              connectorData, 
              [...wallsToReturn, ...connectorsToAdd],
              1.0 // 1 pixel tolerance for duplication detection
            );
            
            if (safeConnectorResult.wasCreated && safeConnectorResult.connector) {
              connectorsToAdd.push(safeConnectorResult.connector);
              console.log('✅ [SAFE] P1_CONNECTOR_CREATED_FOR_GAP', {
                connector: `(${safeConnectorResult.connector.start.x.toFixed(1)},${safeConnectorResult.connector.start.y.toFixed(1)})->(${safeConnectorResult.connector.end.x.toFixed(1)},${safeConnectorResult.connector.end.y.toFixed(1)})`,
                displacement: displacement.toFixed(2),
                splitPointMoved: true,
                tempId: (safeConnectorResult.connector as any).tempId
              });
            } else {
              console.log('🚫 [DUPLICATE_PREVENTED] P1_CONNECTOR_SKIPPED', {
                reason: safeConnectorResult.reason,
                proposedConnector: `(${connectorData.start.x.toFixed(1)},${connectorData.start.y.toFixed(1)})->(${connectorData.end.x.toFixed(1)},${connectorData.end.y.toFixed(1)})`,
                displacement: displacement.toFixed(2)
              });
            }
          }
        }
        
        console.log('🚨 [DIRECT] P1_SPLIT_POINT_PROCESSED', { 
          siblingsExtended: P1_wallsExtended, 
          connectorCreated: !hasExistingConnectors,
          approach: hasExistingConnectors ? 'EXTEND_SIBLINGS' : 'CREATE_CONNECTOR',
          behavior: hasExistingConnectors ? 'fluid_movement' : 'siblings_stay_put_connector_bridges_gap'
        });
              } else {
        console.log('🚨 [DIRECT] P1_IS_NOT_SPLIT_POINT - Extending walls instead');
        
        // P1 is original room endpoint OR fallback case - extend connected walls naturally
        console.log('🚨 [DIRECT] EXTENDING_WALLS_FOR_ENDPOINT_P1', {
          originalPoint: `(${P1_orig.x.toFixed(1)},${P1_orig.y.toFixed(1)})`,
          newPoint: `(${movedWall.start.x.toFixed(1)},${movedWall.start.y.toFixed(1)})`,
          reason: P1_isOriginalEndpoint ? 'P1 is original room endpoint - no connector needed' : 'P1 endpoint type unclear - defaulting to wall extension (safer)'
        });
        
        // Extend walls connected to P1
        let P1_wallsExtended = 0;
        for (let i = 0; i < wallsToReturn.length; i++) {
          const wall = wallsToReturn[i];
          if (pointsAreEqual(wall.start, P1_orig, 0.1)) {
            wallsToReturn[i] = { ...wall, start: { ...movedWall.start } };
            P1_wallsExtended++;
          } else if (pointsAreEqual(wall.end, P1_orig, 0.1)) {
            wallsToReturn[i] = { ...wall, end: { ...movedWall.start } };
            P1_wallsExtended++;
          }
        }
        console.log('🚨 [DIRECT] P1_WALLS_EXTENDED', { count: P1_wallsExtended });
      }
      
      // Handle P2 (end point)
      const P2_isSplitPoint = splitPoint && pointsAreEqual(P2_orig, splitPoint, 0.1);
      const P2_isOriginalEndpoint = isOriginalRoomEndpoint(P2_orig);
      
      console.log('🚨 [DIRECT] P2_DECISION_ANALYSIS', {
        P2_coords: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
        P2_isSplitPoint,
        P2_isOriginalEndpoint,
        splitPointCoords: splitPoint ? `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})` : 'none',
        decision: P2_isSplitPoint ? 'FLUID_SPLIT_POINT_MOVEMENT' : P2_isOriginalEndpoint ? 'EXTEND_WALLS' : 'FALLBACK_EXTEND_WALLS'
      });
      
      if (P2_isSplitPoint) {
        // 🔧 FLEXIBLE SPLIT POINT MOVEMENT: Check if we should extend siblings or create connectors
        console.log('🚨 [DIRECT] P2_SPLIT_POINT_FLUID_MOVEMENT', {
          originalPoint: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
          newPoint: `(${movedWall.end.x.toFixed(1)},${movedWall.end.y.toFixed(1)})`,
          reason: 'Split point moves naturally, determining best handling approach'
        });
        
        // 🔧 CRITICAL FIX: Check if there are existing connectors for this split wall
        // Instead of looking at exact coordinates (which may have moved), look for connectors
        // that belong to the same parent wall as this split segment
        const parentWallId = (draggedWall as any).parentWallId || (draggedWall as any).tempId;
        
        // ⚡ FIX: Search for existing connectors in BOTH current walls AND room walls
        const wallsToSearch = allCurrentWalls ? [...wallsToReturn, ...allCurrentWalls] : wallsToReturn;
        const existingConnectorsAtSplitPointP2 = wallsToSearch.filter((wall: any) => {
          if (!wall.isExtrusionConnector) return false;
          
          // Look for connectors that share the same parent wall
          const wallParentId = wall.parentWallId;
          if (wallParentId === parentWallId) return true;
          
          // Fallback: Also check coordinate-based detection with larger tolerance for moved split points
          const tolerance = 5.0; // Increased tolerance to account for moved split points
          return (Math.abs(wall.start.x - P2_orig.x) < tolerance && Math.abs(wall.start.y - P2_orig.y) < tolerance) ||
                 (Math.abs(wall.end.x - P2_orig.x) < tolerance && Math.abs(wall.end.y - P2_orig.y) < tolerance);
        });
        
        const hasExistingConnectorsP2 = existingConnectorsAtSplitPointP2.length > 0;
        
        console.log('🚨 [DIRECT] P2_SPLIT_POINT_CONNECTOR_CHECK', {
          existingConnectorsFound: hasExistingConnectorsP2,
          connectorCount: existingConnectorsAtSplitPointP2.length,
          parentWallId: parentWallId,
          searchScope: allCurrentWalls ? `${wallsToReturn.length} room walls + ${allCurrentWalls.length} current walls` : `${wallsToReturn.length} room walls only`,
          totalWallsSearched: wallsToSearch.length,
          wallsSearchedBreakdown: {
            roomWalls: wallsToReturn.length,
            currentWalls: allCurrentWalls ? allCurrentWalls.length : 0,
            wallsWithExtrusionConnectorFlag: wallsToSearch.filter(w => (w as any).isExtrusionConnector).length,
            allConnectorWalls: wallsToSearch.filter(w => (w as any).isExtrusionConnector || (w as any).tempId?.includes('connector')).length
          },
          connectorsFound: existingConnectorsAtSplitPointP2.map(c => ({
            coords: `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`,
            parentWallId: c.parentWallId,
            tempId: (c as any).tempId,
            foundBy: c.parentWallId === parentWallId ? 'PARENT_WALL_ID' : 'COORDINATES'
          })),
          approach: hasExistingConnectorsP2 ? 'EXTEND_SIBLINGS_TO_MATCH' : 'CREATE_CONNECTOR_GAP'
        });
        
        let P2_wallsExtended = 0;
        
        if (hasExistingConnectorsP2) {
          // 🔧 FLUID BEHAVIOR: Extend sibling walls to match new position (no new connectors)
          console.log('🚨 [DIRECT] P2_SPLIT_SIBLINGS_EXTEND_TO_MATCH', {
            reason: 'Existing connectors found - extending sibling walls for fluid movement',
            originalSplitPoint: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
            newSplitPoint: `(${movedWall.end.x.toFixed(1)},${movedWall.end.y.toFixed(1)})`
          });
          
          // Find and extend sibling walls that connect to this split point
          const tolerance = 5.0; // 🔧 CRITICAL FIX: Match the tolerance used for connector detection
          const connectedWalls = wallsToReturn.filter((wall: any) => {
            if (wall.tempId === draggedWall.tempId) return false; // Skip the wall being moved
            // 🔧 CRITICAL FIX: Do NOT skip connectors - they need to be extended too!
            // The connector wall is what bridges the gap and must move with the split point
            
            const connectsAtStart = Math.abs(wall.start.x - P2_orig.x) < tolerance && Math.abs(wall.start.y - P2_orig.y) < tolerance;
            const connectsAtEnd = Math.abs(wall.end.x - P2_orig.x) < tolerance && Math.abs(wall.end.y - P2_orig.y) < tolerance;
            
            return connectsAtStart || connectsAtEnd;
          });
          
          // 🔧 CRITICAL FIX: Prioritize connector walls first, then other walls
          const connectorWalls = connectedWalls.filter((wall: any) => wall.isExtrusionConnector);
          const regularWalls = connectedWalls.filter((wall: any) => !wall.isExtrusionConnector);
          const wallsToExtend = [...connectorWalls, ...regularWalls]; // Connectors first!
          
          console.log('🚨 [DIRECT] P2_WALLS_TO_EXTEND_ANALYSIS', {
            totalConnected: connectedWalls.length,
            connectorWalls: connectorWalls.length,
            regularWalls: regularWalls.length,
            connectorDetails: connectorWalls.map(w => ({
              coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
              tempId: w.tempId,
              isExtrusionConnector: w.isExtrusionConnector
            }))
          });

          wallsToExtend.forEach((wall: any) => {
            const wallConnectsAtStart = Math.abs(wall.start.x - P2_orig.x) < tolerance && Math.abs(wall.start.y - P2_orig.y) < tolerance;
            const wallConnectsAtEnd = Math.abs(wall.end.x - P2_orig.x) < tolerance && Math.abs(wall.end.y - P2_orig.y) < tolerance;
            
            if (wallConnectsAtStart) {
              // Calculate proper perpendicular extension for the wall
              const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
              const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
              
              if (wallLength > 0) {
                // Normalize the wall direction
                const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
                
                // Calculate movement vector from original to new endpoint
                const movementVector = { x: movedWall.end.x - P2_orig.x, y: movedWall.end.y - P2_orig.y };
                
                // Project movement onto wall direction to get proper extension
                const projectedLength = movementVector.x * wallDirection.x + movementVector.y * wallDirection.y;
                
                // Move the start point along the wall's direction by the projected amount
                wall.start.x = P2_orig.x + projectedLength * wallDirection.x;
                wall.start.y = P2_orig.y + projectedLength * wallDirection.y;
                
                console.log('🚨 [DIRECT] P2_SIBLING_WALL_START_EXTENDED_PROPERLY', {
                  wallTempId: wall.tempId || 'unknown',
                  wallType: wall.isExtrusionConnector ? 'CONNECTOR' : 'REGULAR',
                  originalStart: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
                  newStart: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})`,
                  projectedExtension: projectedLength.toFixed(2),
                  reason: 'Start point extended along wall direction for perpendicular extension'
                });
              } else {
                // Fallback for zero-length walls
                wall.start.x = movedWall.end.x;
                wall.start.y = movedWall.end.y;
              }
              P2_wallsExtended++;
            } else if (wallConnectsAtEnd) {
              // Calculate proper perpendicular extension for the wall
              const wallVector = { x: wall.start.x - wall.end.x, y: wall.start.y - wall.end.y };
              const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
              
              if (wallLength > 0) {
                // Normalize the wall direction (from end to start)
                const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
                
                // Calculate movement vector from original to new endpoint
                const movementVector = { x: movedWall.end.x - P2_orig.x, y: movedWall.end.y - P2_orig.y };
                
                // Project movement onto wall direction to get proper extension
                const projectedLength = movementVector.x * wallDirection.x + movementVector.y * wallDirection.y;
                
                // Move the end point along the wall's direction by the projected amount
                wall.end.x = P2_orig.x + projectedLength * wallDirection.x;
                wall.end.y = P2_orig.y + projectedLength * wallDirection.y;
                
                console.log('🚨 [DIRECT] P2_SIBLING_WALL_END_EXTENDED_PROPERLY', {
                  wallTempId: wall.tempId || 'unknown',
                  wallType: wall.isExtrusionConnector ? 'CONNECTOR' : 'REGULAR',
                  originalEnd: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
                  newEnd: `(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
                  projectedExtension: projectedLength.toFixed(2),
                  reason: 'End point extended along wall direction for perpendicular extension'
                });
              } else {
                // Fallback for zero-length walls
                wall.end.x = movedWall.end.x;
                wall.end.y = movedWall.end.y;
              }
              P2_wallsExtended++;
            }
          });
        } else {
          // 🔧 FIRST RESIZE: Create connector gap (original behavior)
          console.log('🚨 [DIRECT] P2_SPLIT_SIBLINGS_STAY_PUT', {
            reason: 'No existing connectors - sibling walls remain in original position, connector bridges gap',
            originalSplitPoint: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
            newSplitPoint: `(${movedWall.end.x.toFixed(1)},${movedWall.end.y.toFixed(1)})`
          });
          
          // P2_wallsExtended already initialized to 0 above
          
          // Check if we need a connector due to significant displacement
          const displacement = Math.sqrt(
            Math.pow(movedWall.end.x - P2_orig.x, 2) + 
            Math.pow(movedWall.end.y - P2_orig.y, 2)
          );
          
          const CONNECTOR_THRESHOLD = 2.0; // Create connector for gaps > 2 pixels
          const needsConnector = displacement > CONNECTOR_THRESHOLD;
          
          if (needsConnector) {
            // Create connector with duplication prevention (Phase 2 fix)
            const connectorData = {
              start: { ...P2_orig },
              end: { ...movedWall.end },
              type: draggedWall.type,
              floor: draggedWall.floor || currentFloor,
              originatingRoomId: (draggedWall as any).originatingRoomId,
              assignedRoomIds: (draggedWall as any).assignedRoomIds ? [...(draggedWall as any).assignedRoomIds] : undefined,
              parentRoomIds: (draggedWall as any).parentRoomIds ? [...(draggedWall as any).parentRoomIds] : undefined,
              parentWallId: (draggedWall as any).parentWallId || (draggedWall as any).tempId
            };
            
            const safeConnectorResult = createSafeConnector(
              connectorData, 
              [...wallsToReturn, ...connectorsToAdd],
              1.0 // 1 pixel tolerance for duplication detection
            );
            
            if (safeConnectorResult.wasCreated && safeConnectorResult.connector) {
              connectorsToAdd.push(safeConnectorResult.connector);
              console.log('✅ [SAFE] P2_CONNECTOR_CREATED_FOR_GAP', {
                connector: `(${safeConnectorResult.connector.start.x.toFixed(1)},${safeConnectorResult.connector.start.y.toFixed(1)})->(${safeConnectorResult.connector.end.x.toFixed(1)},${safeConnectorResult.connector.end.y.toFixed(1)})`,
                displacement: displacement.toFixed(2),
                splitPointMoved: true,
                tempId: (safeConnectorResult.connector as any).tempId
              });
            } else {
              console.log('🚫 [DUPLICATE_PREVENTED] P2_CONNECTOR_SKIPPED', {
                reason: safeConnectorResult.reason,
                proposedConnector: `(${connectorData.start.x.toFixed(1)},${connectorData.start.y.toFixed(1)})->(${connectorData.end.x.toFixed(1)},${connectorData.end.y.toFixed(1)})`,
                displacement: displacement.toFixed(2)
              });
            }
          }
        }
        
        console.log('🚨 [DIRECT] P2_SPLIT_POINT_PROCESSED', { 
          siblingsExtended: P2_wallsExtended, 
          connectorCreated: !hasExistingConnectorsP2,
          approach: hasExistingConnectorsP2 ? 'EXTEND_SIBLINGS' : 'CREATE_CONNECTOR',
          behavior: hasExistingConnectorsP2 ? 'fluid_movement' : 'siblings_stay_put_connector_bridges_gap'
        });
      } else {
        // P2 is original room endpoint OR fallback case - extend connected walls naturally  
        console.log('🚨 [DIRECT] EXTENDING_WALLS_FOR_ENDPOINT_P2', {
          originalPoint: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
          newPoint: `(${movedWall.end.x.toFixed(1)},${movedWall.end.y.toFixed(1)})`,
          reason: P2_isOriginalEndpoint ? 'P2 is original room endpoint - no connector needed' : 'P2 endpoint type unclear - defaulting to wall extension (safer)'
        });
        
        // Check if P2 needs a connector instead of wall extension (perpendicular movement)
        const movementVector = { x: movedWall.end.x - P2_orig.x, y: movedWall.end.y - P2_orig.y };
        const movementDistance = Math.sqrt(movementVector.x * movementVector.x + movementVector.y * movementVector.y);
        
        let P2_needsConnector = false;
        let P2_maxProjectedLength = 0;
        
        // First pass: check if any connected walls have significant projection
        for (let i = 0; i < wallsToReturn.length; i++) {
          const wall = wallsToReturn[i];
          if (pointsAreEqual(wall.start, P2_orig, 0.1) || pointsAreEqual(wall.end, P2_orig, 0.1)) {
            const wallVector = pointsAreEqual(wall.start, P2_orig, 0.1) 
              ? { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y }
              : { x: wall.start.x - wall.end.x, y: wall.start.y - wall.end.y };
            
            const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
            
            if (wallLength > 0) {
              const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
              const projectedLength = Math.abs(movementVector.x * wallDirection.x + movementVector.y * wallDirection.y);
              P2_maxProjectedLength = Math.max(P2_maxProjectedLength, projectedLength);
            }
          }
        }
        
        // If movement is mostly perpendicular to connected walls, create connector
        if (movementDistance > 5 && P2_maxProjectedLength < movementDistance * 0.3) {
          P2_needsConnector = true;
          
          console.log('🚨 [DIRECT] P2_PERPENDICULAR_MOVEMENT_DETECTED', {
            movementDistance: movementDistance.toFixed(2),
            maxProjectedLength: P2_maxProjectedLength.toFixed(2),
            ratio: (P2_maxProjectedLength / movementDistance).toFixed(2),
            decision: 'CREATE_CONNECTOR_FOR_P2'
          });
          
          // Create connector for P2
          const p2Connector = {
            start: { ...P2_orig },
            end: { ...movedWall.end },
            tempId: `connector-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
            type: 'None' as WallType,
            floor: movedWall.floor || 1,
            parentWallId: movedWall.parentWallId || 'unknown',
            originatingRoomId: movedWall.originatingRoomId,
            assignedRoomIds: movedWall.assignedRoomIds || [],
            parentRoomIds: movedWall.parentRoomIds || [],
            isExtrusionConnector: true
          };
          
          connectorsToAdd.push(p2Connector);
          
          console.log('🚨 [DIRECT] P2_CONNECTOR_CREATED', {
            connector: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})->(${movedWall.end.x.toFixed(1)},${movedWall.end.y.toFixed(1)})`,
            tempId: p2Connector.tempId,
            reason: 'Movement is perpendicular to connected walls - connector needed'
          });
        }
        
        // Extend walls connected to P2 (only if not creating connector)
        let P2_wallsExtended = 0;
        if (!P2_needsConnector) {
          for (let i = 0; i < wallsToReturn.length; i++) {
            const wall = wallsToReturn[i];
            if (pointsAreEqual(wall.start, P2_orig, 0.1)) {
              // Calculate proper perpendicular extension for the wall
              const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
              const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
              
              if (wallLength > 0) {
                // Normalize the wall direction
                const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
                
                // Project movement onto wall direction to get proper extension
                const projectedLength = movementVector.x * wallDirection.x + movementVector.y * wallDirection.y;
                
                // Move the start point along the wall's direction by the projected amount
                const newStart = {
                  x: P2_orig.x + projectedLength * wallDirection.x,
                  y: P2_orig.y + projectedLength * wallDirection.y
                };
                
                wallsToReturn[i] = { ...wall, start: newStart };
                
                console.log('🚨 [DIRECT] P2_REGULAR_WALL_START_EXTENDED_PROPERLY', {
                  wallIndex: i,
                  originalStart: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
                  newStart: `(${newStart.x.toFixed(1)},${newStart.y.toFixed(1)})`,
                  projectedExtension: projectedLength.toFixed(2),
                  reason: 'Start point extended along wall direction for perpendicular extension'
                });
              } else {
                // Fallback for zero-length walls
                wallsToReturn[i] = { ...wall, start: { ...movedWall.end } };
              }
              P2_wallsExtended++;
            } else if (pointsAreEqual(wall.end, P2_orig, 0.1)) {
              // Calculate proper perpendicular extension for the wall
              const wallVector = { x: wall.start.x - wall.end.x, y: wall.start.y - wall.end.y };
              const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
              
              if (wallLength > 0) {
                // Normalize the wall direction (from end to start)
                const wallDirection = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
                
                // Project movement onto wall direction to get proper extension
                const projectedLength = movementVector.x * wallDirection.x + movementVector.y * wallDirection.y;
                
                // Move the end point along the wall's direction by the projected amount
                const newEnd = {
                  x: P2_orig.x + projectedLength * wallDirection.x,
                  y: P2_orig.y + projectedLength * wallDirection.y
                };
                
                wallsToReturn[i] = { ...wall, end: newEnd };
                
                console.log('🚨 [DIRECT] P2_REGULAR_WALL_END_EXTENDED_PROPERLY', {
                  wallIndex: i,
                  originalEnd: `(${P2_orig.x.toFixed(1)},${P2_orig.y.toFixed(1)})`,
                  newEnd: `(${newEnd.x.toFixed(1)},${newEnd.y.toFixed(1)})`,
                  projectedExtension: projectedLength.toFixed(2),
                  reason: 'End point extended along wall direction for perpendicular extension'
                });
              } else {
                // Fallback for zero-length walls
                wallsToReturn[i] = { ...wall, end: { ...movedWall.end } };
              }
              P2_wallsExtended++;
            }
          }
        }
        console.log('🚨 [DIRECT] P2_WALLS_EXTENDED', { count: P2_wallsExtended });
      }
      
      console.log('🚨 [DIRECT] EXTRUSION_RESULT', {
        connectorsCreated: connectorsToAdd.length,
        originalRoomWalls: roomWalls.length,
        finalWallsCount: wallsToReturn.length + connectorsToAdd.length,
        message: connectorsToAdd.length === 0 ? 'SUCCESS: Split point moved naturally - no gap connectors needed!' : `SUCCESS: Split point moved naturally + ${connectorsToAdd.length} gap connector(s) created (siblings stay put)!`
      });
      
      // Return the walls with connectors (only for split points)
      const finalResult = wallsToReturn.concat(connectorsToAdd);
      console.log('🚨 [DIRECT] CREATETIMPLIFIEDEXTRUSIONPREVIEW_COMPLETE:', {
        originalWallsCount: wallsToReturn.length,
        connectorsToAddCount: connectorsToAdd.length,
        finalResultCount: finalResult.length,
        connectorsAdded: connectorsToAdd.map(c => ({
          coords: `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`,
          isExtrusionConnector: (c as any).isExtrusionConnector,
          hasOwnership: !!((c as any).originatingRoomId || (c as any).assignedRoomIds)
        })),
        finalWalls: finalResult.map((w, i) => ({
          index: i,
          coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
          isConnector: (w as any).isExtrusionConnector === true
        }))
      });
      return finalResult;
      
    } catch (error) {
      onDebug('SIMPLIFIED_EXTRUSION_PREVIEW_ERROR', { 
        error: error instanceof Error ? error.message : String(error) 
      });
      return null;
    }
  };

  // Function to delete a selected room
  const deleteSelectedRoom = useCallback(() => {
    if (!selectedRoom) {
      onDebug('DELETE_ROOM_NO_SELECTION', { message: 'No room selected for deletion' });
      return;
    }

    const roomName = (selectedRoom.stableId ? roomCustomLabels[selectedRoom.stableId] : null) || selectedRoom.name || 'Unnamed Room';
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete the room "${roomName}"? This will remove all walls that form this room and cannot be undone.`)) {
      return;
    }

    onDebug('DELETE_ROOM_INITIATED', { 
      roomId: selectedRoom.stableId, 
      roomName: roomName,
      wallCount: selectedRoom.walls.length 
    });

    // Start batch operation to prevent rapid autosave triggers
    if (props.startBatchOperation) {
      props.startBatchOperation();
    }

    // Record undo state before deletion
    recordUndoState?.();

    // Get wall IDs that form the selected room
    const roomWallIds = new Set<string>();
    selectedRoom.walls.forEach(wall => {
      // Find the corresponding wall with tempId in currentWalls
      const matchingWall = currentWalls.find(cw => 
        (isNearPoint(cw.start, wall.start, 0.1) && isNearPoint(cw.end, wall.end, 0.1)) ||
        (isNearPoint(cw.start, wall.end, 0.1) && isNearPoint(cw.end, wall.start, 0.1))
      );
      if (matchingWall && matchingWall.tempId) {
        roomWallIds.add(matchingWall.tempId);
      }
    });

    // Remove walls that form the room
    const remainingWalls = currentWalls.filter(wall => wall.tempId && !roomWallIds.has(wall.tempId));
    
    // Update floor plans
    setFloorPlans(prev => ({
      ...prev,
      [currentFloor]: remainingWalls.map(({ tempId, ...w }) => ({ ...w, floor: w.floor || currentFloor }))
    }));

    // Remove room label
    if (selectedRoom.stableId && roomCustomLabels[selectedRoom.stableId]) {
      setRoomCustomLabels(prev => {
        const updated = { ...prev };
        delete updated[selectedRoom.stableId!];
        return updated;
      });
    }

    // Remove elements (doors, windows, etc.) that were attached to the deleted room's walls
    const elementsToRemove = elements.filter(element => {
      if ('wallId' in element && element.wallId) {
        return roomWallIds.has(element.wallId);
      }
      return false;
    });

    if (elementsToRemove.length > 0) {
      const remainingElements = elements.filter(element => {
        if ('wallId' in element && element.wallId) {
          return !roomWallIds.has(element.wallId);
        }
        return true;
      });
      setElements(remainingElements);
      
      onDebug('DELETE_ROOM_ELEMENTS_REMOVED', { 
        removedElementsCount: elementsToRemove.length,
        elementTypes: elementsToRemove.map(el => el.type)
      });
    }

    // Clear selection
    setSelectedElement(null);
    setSelectedRoom(null);
    setSelectedWall(null);
    setResizeHandle(null);
    setTempWalls(null);
    setSplitPoint(null);
    setActivelyResizedWalls(null);
    setResizingRoomInfo(null);
    activeResizeOriginalWallTempIdsRef.current = null;
    initialWallsForResizeRef.current = null; // 🔧 FIX: Clear resize tracking refs to prevent stale references

    // 🔄 SYNC: Remove room from survey data to keep Room Details page synchronized
    if (surveyData && onDataUpdate && selectedRoom.stableId) {
      console.log('🔄 [deleteSelectedRoom] Syncing room deletion to survey data:', {
        roomId: selectedRoom.stableId,
        roomName: roomName
      });
      
      onDataUpdate((prev: RetrofitSurveyData) => {
        const existingRooms = prev.room_details?.rooms || [];
        const updatedRooms = existingRooms.filter((r: any) => r.id !== selectedRoom.stableId);
        
        console.log('🗑️ [deleteSelectedRoom] Removing room from survey data:', {
          originalCount: existingRooms.length,
          newCount: updatedRooms.length,
          removedRoomId: selectedRoom.stableId
        });
        
        return {
          ...prev,
          room_details: {
            ...prev.room_details,
            rooms: updatedRooms
          }
        };
      });
    }

    onDebug('DELETE_ROOM_COMPLETED', { 
      roomId: selectedRoom.stableId,
      roomName: roomName,
      deletedWallsCount: roomWallIds.size,
      deletedElementsCount: elementsToRemove.length,
      remainingWallsCount: remainingWalls.length
    });

  }, [
    selectedRoom, roomCustomLabels, onDebug, recordUndoState, currentWalls, setFloorPlans, currentFloor,
    setRoomCustomLabels, elements, setElements, setSelectedRoom, setResizeHandle, setIsResizing, 
    isResizingRef, setTempWalls, setResizingRoomInfo, setActivelyResizedWalls, draggedWallSegmentRef,
    activeResizeOriginalWallTempIdsRef, isNearPoint, surveyData, onDataUpdate
  ]);

  const roomHasExistingExtrusions = (room: Room | null, allCurrentWalls: WallWithTempId[]): boolean => {
    if (!room || !room.walls || room.walls.length === 0) return false;
    for (const roomWall of room.walls) { // roomWall is a plain Wall object
        // Find this roomWall in allCurrentWalls to check its flags
        const fullWallInfo = allCurrentWalls.find(cw =>
            (pointsAreEqual(cw.start, roomWall.start, POINT_MATCH_TOLERANCE) && pointsAreEqual(cw.end, roomWall.end, POINT_MATCH_TOLERANCE)) ||
            (pointsAreEqual(cw.start, roomWall.end, POINT_MATCH_TOLERANCE) && pointsAreEqual(cw.end, roomWall.start, POINT_MATCH_TOLERANCE))
        );
        if (fullWallInfo && (fullWallInfo.isMainExtrusionSegment || fullWallInfo.isExtrusionConnector)) {
            onDebug('[roomHasExistingExtrusions] Found existing extrusion part in room', { roomId: room.stableId, wallId: fullWallInfo.tempId, isMain: fullWallInfo.isMainExtrusionSegment, isConnector: fullWallInfo.isExtrusionConnector });
            return true;
        }
    }
    return false;
  };

  const checkForNewRoomsAndCommit = useCallback((newWallsForCurrentFloor: Wall[], previousFloorPlanStateForUndo?: Record<number, Wall[]>) => {
    // previousFloorPlanStateForUndo is optional; recordUndoState handles the full snapshot
    if (recordUndoState) recordUndoState(); // Call before actual state modification
    // Removed fallback logic that manually constructed UndoRedoState parts,
    // as recordUndoState from props should always be used.
    
    // 🔥 ROOM OWNERSHIP: Assign walls to their originating room during creation
    const wallsWithOwnership = newWallsForCurrentFloor.map(({ tempId, floor, ...wall }) => {
      const processedWall = {
        ...wall, 
        floor: floor || currentFloor,
        // Preserve split wall metadata
        ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
        ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
        ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
        ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
        ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
        ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector }),
        // Preserve existing room ownership
        ...(wall.originatingRoomId && { originatingRoomId: wall.originatingRoomId }),
        ...(wall.assignedRoomIds && { assignedRoomIds: wall.assignedRoomIds })
      };
      
      // 🏠 ASSIGN ROOM OWNERSHIP: If this wall doesn't have ownership and we're drawing a room
      if (!processedWall.originatingRoomId && isDraggingRoom) {
        // Calculate the rooms to find the target room this wall should belong to
        const existingRooms = calculateRooms(newWallsForCurrentFloor, roomCustomLabels, 'geometryUpdate', currentFloor);
        
        // 🔧 CRITICAL FIX: Use canonical stableId instead of human-readable name
        // Find which room this wall should belong to based on spatial containment
        let targetRoomStableId = null;
        
        // Try to find the room that would contain this wall
        if (existingRooms.length > 0) {
          // For room creation, assign to the room being actively drawn
          // This typically corresponds to the last/newest room
          const targetRoom = existingRooms[existingRooms.length - 1];
          targetRoomStableId = targetRoom.stableId;
        }
        
        // Fallback: generate a canonical stableId if no room found
        if (!targetRoomStableId) {
          // Create a temporary canonical ID using wall coordinates
          const wallCoords = `${processedWall.start.x.toFixed(2)},${processedWall.start.y.toFixed(2)}-${processedWall.end.x.toFixed(2)},${processedWall.end.y.toFixed(2)}`;
          targetRoomStableId = `floor_${currentFloor}_temp_${wallCoords}`;
        }
        
        console.log(`🏠 ASSIGNING OWNERSHIP: Wall assigned canonical stableId`, {
          wallCoords: `(${processedWall.start.x.toFixed(1)},${processedWall.start.y.toFixed(1)})->(${processedWall.end.x.toFixed(1)},${processedWall.end.y.toFixed(1)})`,
          targetRoomStableId,
          existingRoomsCount: existingRooms.length
        });
        
        return {
          ...processedWall,
          originatingRoomId: targetRoomStableId,  // ✅ Use canonical stableId that matches room.stableId
          assignedRoomIds: [targetRoomStableId]    // ✅ Use canonical stableId that matches room.stableId
        };
      }
      
      return processedWall;
    });
    
    // setRedoStack([]); // This is handled by recordUndoState in FloorPlanCreator
    setFloorPlans(prevFloorPlans => ({
      ...prevFloorPlans,
      [currentFloor]: wallsWithOwnership
    }));
  }, [recordUndoState, setFloorPlans, currentFloor, lastOperationTypeRef, triggerAutosave, isDraggingRoom, roomCustomLabels, calculateRooms]);

  const eraseAtPoint = useCallback((point: Point) => {
    onDebug('ERASE_ATTEMPT', { point, timestamp: new Date().toISOString() });
    
    // Enhanced eraser tolerance for touch devices
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const eraserTolerance = isTouchDevice ? SNAP_THRESHOLD * 2.5 : SNAP_THRESHOLD;
    
    // 🔧 NEW: Check if erasing within an extension boundary
    if (siteNotesData?.floors && setSiteNotesData && onDataUpdate) {
      const extensionFloors = siteNotesData.floors.filter((f: any) => {
        const buildingType = (f.buildingType || '').toLowerCase();
        return buildingType.includes('ext') || buildingType === 'extension';
      });
      
      for (const extFloor of extensionFloors) {
        // Check if point is within extension's highlight area
        const highlightBreakdown = extFloor.highlighterBreakdown;
        if (highlightBreakdown?.dimensions) {
          const { x, y, width, height } = highlightBreakdown.dimensions;
          
          if (point.x >= x && point.x <= x + width &&
              point.y >= y && point.y <= y + height) {
            
            // User clicked inside extension - confirm deletion
            const extensionName = extFloor.buildingType || 'extension';
            const confirmed = window.confirm(
              `Delete ${extensionName}?\n\nThis will:\n` +
              `- Remove the extension from the floor plan\n` +
              `- Remove it from the Extensions form\n` +
              `- Restore area back to main building`
            );
            
            if (confirmed) {
              console.log(`🗑️ [Eraser] Deleting extension: ${extensionName}`);
              
              recordUndoState?.();
              
              // 1. Remove from siteNotesData.floors
              const updatedFloors = siteNotesData.floors.filter((f: any) => 
                f.id !== extFloor.id
              );
              
              setSiteNotesData((prev: any) => ({
                ...prev,
                floors: updatedFloors
              }));
              
              // 2. Remove from surveyData.extensions.existingExtensions
              onDataUpdate(prev => {
                const existingExtensions = prev.extensions?.existingExtensions || [];
                const updatedExtensions = existingExtensions.filter((ext: any) => {
                  const extType = (ext.type || '').toLowerCase();
                  const extName = extensionName.toLowerCase();
                  return extType !== extName && !extType.includes(extName);
                });
                
                console.log(`✅ [Eraser] Extension "${extensionName}" deleted successfully`, {
                  beforeCount: existingExtensions.length,
                  afterCount: updatedExtensions.length
                });
                
                return {
                  ...prev,
                  extensions: {
                    ...prev.extensions,
                    existingExtensions: updatedExtensions,
                    numberOfExtensions: String(updatedExtensions.length)
                  }
                };
              });
              
              triggerAutosave?.();
              return; // Exit after deletion
            }
          }
        }
      }
    }
    
    // 🔧 NEW: First check if user tapped on a split wall for restoration
    let splitWallToRestore = null;
    let minSplitDistance = eraserTolerance;
    
    for (const wall of currentWalls) {
      if ((wall as any).isSplitOrigin && (wall as any).parentWallId) {
        const distance = distanceToLine(point, wall.start, wall.end);
        if (distance < minSplitDistance) {
          minSplitDistance = distance;
          splitWallToRestore = wall;
        }
      }
    }
    
    // 🔧 NEW: If user tapped on a split wall, offer to restore the original wall
    if (splitWallToRestore) {
      const parentWallId = (splitWallToRestore as any).parentWallId;
      const allSplitSegments = currentWalls.filter(wall => 
        (wall as any).isSplitOrigin && (wall as any).parentWallId === parentWallId
      );
      
      onDebug('SPLIT_WALL_RESTORATION_DETECTED', {
        splitWallClicked: {
          tempId: splitWallToRestore.tempId,
          start: splitWallToRestore.start,
          end: splitWallToRestore.end,
          parentWallId
        },
        allRelatedSegments: allSplitSegments.map(w => ({
          tempId: w.tempId,
          start: w.start,
          end: w.end
        }))
      });
      
      if (allSplitSegments.length >= 2) {
        // Restore the original wall by merging split segments
        const restoredWall = restoreSplitWall(allSplitSegments, parentWallId);
        if (restoredWall) {
          onDebug('SPLIT_WALL_RESTORED', {
            originalSegmentsCount: allSplitSegments.length,
            restoredWall: {
              start: restoredWall.start,
              end: restoredWall.end,
              type: restoredWall.type
            }
          });
          
          if (lastOperationTypeRef) lastOperationTypeRef.current = 'geometryUpdate';
          recordUndoState?.(); // Record state before restoration
          
          // Remove all split segments and extrusion connectors
          const wallsToRemove = new Set();
          allSplitSegments.forEach(segment => wallsToRemove.add(segment.tempId));
          
          // Also remove any extrusion connectors associated with this parent wall
          currentWalls.forEach(wall => {
            if ((wall as any).isExtrusionConnector && (wall as any).parentWallId === parentWallId) {
              wallsToRemove.add(wall.tempId);
            }
          });
          
          const wallsAfterRemoval = currentWalls.filter(wall => !wallsToRemove.has(wall.tempId));
          
          // Add the restored wall with a new tempId
          const restoredWallWithTempId: WallWithTempId = {
            ...restoredWall,
            tempId: `restored-${parentWallId}-${Date.now()}`
          };
          
          const newWalls = [...wallsAfterRemoval, restoredWallWithTempId];
          const newWallsPlain = newWalls.map(({ tempId, ...w }) => ({ 
            ...w, 
            floor: w.floor || currentFloor
          }));
          
          // Update room labels if needed
          const labelsBeforeRestore = { ...roomCustomLabels };
          const migrationResult = migrateRoomLabels(
            currentWalls.map(({ tempId, ...w }) => ({ ...w, floor: w.floor || currentFloor })),
            newWallsPlain,
            labelsBeforeRestore,
            currentFloor
          );
          
          setRoomCustomLabels(migrationResult.labels);
          setFloorPlans(prev => ({ ...prev, [currentFloor]: newWallsPlain }));
          
          console.log(`🔧 Split wall restored: ${allSplitSegments.length} segments → 1 original wall`);
          return;
        }
      }
    }
    
    // Find the closest wall within tolerance (original eraser logic)
    let wallToErase = null;
    let minDistance = eraserTolerance;
    
    for (const wall of currentWalls) {
      const distance = distanceToLine(point, wall.start, wall.end);
      if (distance < minDistance) {
        minDistance = distance;
        wallToErase = wall;
      }
    }

    if (wallToErase) {
      // 🔧 ENHANCED DEBUG: Log detailed wall information before processing
      console.log('🔍 ERASER DEBUG: Wall found for erasing', {
        wallId: wallToErase.tempId,
        wallType: wallToErase.type,
        typeExists: !!wallToErase.type,
        isTypeNone: wallToErase.type === 'None',
        wallCoords: `(${wallToErase.start.x.toFixed(1)},${wallToErase.start.y.toFixed(1)})->(${wallToErase.end.x.toFixed(1)},${wallToErase.end.y.toFixed(1)})`,
        willRemoveType: !!(wallToErase.type && wallToErase.type !== 'None')
      });

      // 🔍 WALL TYPE ASSIGNMENT DEBUG: Let's trace when walls get their types
      const wallsWithSpecialSections = currentWalls.filter(w => 
        w.sections && w.sections.some(s => s.type && s.type !== 'None')
      );
      
      console.log('🎨 WALL TYPE ASSIGNMENT DEBUG: Check all walls on this floor for special types', {
        totalWalls: currentWalls.length,
        wallsByMainType: {
          'None': currentWalls.filter(w => w.type === 'None' || !w.type).length,
          'HLP': currentWalls.filter(w => w.type === 'HLP').length,
          'PW': currentWalls.filter(w => w.type === 'PW').length,
          'SW': currentWalls.filter(w => w.type === 'SW').length,
        },
        wallsWithSpecialSections: wallsWithSpecialSections.length,
        specialTypeWalls: currentWalls.filter(w => w.type && w.type !== 'None').map(w => ({
          id: w.tempId,
          type: w.type,
          coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`
        })),
        wallsWithSpecialSectionsDetail: wallsWithSpecialSections.map(w => ({
          id: w.tempId,
          mainType: w.type,
          sections: w.sections?.map(s => ({ type: s.type, exposure: s.exposureLevel })),
          coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`
        }))
      });

      // 🔄 NEW: Check if wall has a type other than 'None' OR has special wall sections - if so, remove the type first
      const hasSpecialWallType = wallToErase.type && wallToErase.type !== 'None';
      const hasSpecialSections = wallToErase.sections && wallToErase.sections.some(section => 
        section.type && section.type !== 'None'
      );
      
      console.log('🔍 ERASER SECTION CHECK: Wall sections analysis', {
        wallId: wallToErase.tempId,
        mainWallType: wallToErase.type,
        hasSpecialWallType: hasSpecialWallType,
        sectionsCount: wallToErase.sections?.length || 0,
        sections: wallToErase.sections?.map(s => ({ type: s.type, exposure: s.exposureLevel })) || [],
        hasSpecialSections: hasSpecialSections,
        shouldRemoveTypeFirst: hasSpecialWallType || hasSpecialSections
      });
      
      if (hasSpecialWallType || hasSpecialSections) {
        console.log('🎨 WALL TYPE REMOVAL: Changing wall type to None instead of deleting', {
          wallId: wallToErase.tempId,
          oldType: wallToErase.type,
          newType: 'None'
        });

        onDebug('WALL_TYPE_REMOVED', { 
          wall: wallToErase, 
          oldType: wallToErase.type, 
          timestamp: new Date().toISOString() 
        });
        
        if (lastOperationTypeRef) lastOperationTypeRef.current = 'geometryUpdate';
        recordUndoState?.(); // Record state before type removal
        
        // Remove wall type by setting it to 'None' AND clearing special wall sections
        const updatedWalls = currentWalls.map(wall => {
          if (wall.tempId === wallToErase.tempId) {
            const updatedWall = { ...wall, type: 'None' as WallType };
            
            // If wall has sections with special types, clear them to 'None'
            if (wall.sections && wall.sections.some(s => s.type && s.type !== 'None')) {
              updatedWall.sections = wall.sections.map(section => ({
                ...section,
                type: 'None' as WallType
              }));
              
              console.log('🎨 WALL SECTIONS CLEARED: Reset all wall sections to None', {
                wallId: wall.tempId,
                originalSections: wall.sections.length,
                clearedSections: updatedWall.sections.length
              });
            }
            
            return updatedWall;
          }
          return wall;
        });
        
        const updatedWallsPlain = updatedWalls.map(({ tempId, ...w }) => {
          console.log('🎨 WALL TYPE MAPPING DEBUG: Mapping wall in eraser type removal', {
            wallId: tempId,
            originalType: w.type,
            hasType: !!w.type,
            coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
            allProperties: Object.keys(w)
          });
          
          return { 
            ...w, 
            floor: w.floor || currentFloor,
            // Preserve extrusion properties
            ...(w.isExtrusionConnector && { isExtrusionConnector: true }),
            ...(w.isMainExtrusionSegment && { isMainExtrusionSegment: true }),
            ...(w.isSplitOrigin && { isSplitOrigin: true })
          };
        });
        
        console.log('🔍 BEFORE SETFLOORPLANS: Wall types before state update', {
          totalWalls: updatedWallsPlain.length,
          wallsByType: {
            'None': updatedWallsPlain.filter(w => w.type === 'None' || !w.type).length,
            'HLP': updatedWallsPlain.filter(w => w.type === 'HLP').length,
            'PW': updatedWallsPlain.filter(w => w.type === 'PW').length,
            'SW': updatedWallsPlain.filter(w => w.type === 'SW').length,
          },
          specialTypeWalls: updatedWallsPlain.filter(w => w.type && w.type !== 'None').map(w => ({
            type: w.type,
            coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`
          }))
        });
        
        setFloorPlans(prev => ({ ...prev, [currentFloor]: updatedWallsPlain }));
        console.log(`🎨 Wall type removed: ${wallToErase.type} → None`);
        return;
      }
      
      // If wall type is already 'None' or undefined, proceed with wall deletion
      console.log('🗑️ WALL DELETION: Wall type is None or undefined, proceeding with deletion', {
        wallId: wallToErase.tempId,
        wallType: wallToErase.type,
        typeIsNone: wallToErase.type === 'None',
        typeIsUndefined: wallToErase.type === undefined,
        willDeleteWall: true
      });

      onDebug('WALL_ERASED', { wall: wallToErase, timestamp: new Date().toISOString() });
      if (lastOperationTypeRef) lastOperationTypeRef.current = 'geometryUpdate';
      recordUndoState?.(); // Record state before erase
      
      const wallsBeforeErase = currentWalls.map(({ tempId, ...w }) => ({ 
        ...w, 
        floor: w.floor || currentFloor,
        // Preserve extrusion properties
        ...(w.isExtrusionConnector && { isExtrusionConnector: true }),
        ...(w.isMainExtrusionSegment && { isMainExtrusionSegment: true }),
        ...(w.isSplitOrigin && { isSplitOrigin: true })
      }));
      const labelsBeforeErase = { ...roomCustomLabels };

      const newCurrentFloorWallsWithTempId = currentWalls.filter(wall => wall.tempId !== wallToErase.tempId);
      const newCurrentFloorWallsPlain = newCurrentFloorWallsWithTempId.map(({ tempId, ...w }) => ({ 
        ...w, 
        floor: w.floor || currentFloor,
        // Preserve extrusion properties
        ...(w.isExtrusionConnector && { isExtrusionConnector: true }),
        ...(w.isMainExtrusionSegment && { isMainExtrusionSegment: true }),
        ...(w.isSplitOrigin && { isSplitOrigin: true })
      }));

      const migrationResult = migrateRoomLabels(wallsBeforeErase, newCurrentFloorWallsPlain, labelsBeforeErase, currentFloor); // Fixed: Added currentFloor parameter
      setRoomCustomLabels(migrationResult.labels); // MODIFIED

      setFloorPlans(prev => ({ ...prev, [currentFloor]: newCurrentFloorWallsPlain }));
      // Undo/Redo stack updated by recordUndoState in FloorPlanCreator
      return;
    }

    const elementToErase = findElementAtPointInternal(point, elements);
    if (elementToErase) {
      onDebug('ELEMENT_ERASED', { element: elementToErase, timestamp: new Date().toISOString() });
      recordUndoState?.(); // Record state before erase
      
      // 🔄 If erasing a light, decrement the light count in surveyor data
      if (elementToErase.type === 'light') {
        const lightElement = elementToErase as any; // Type assertion to access lightType
        const currentRooms = memoizedCalculateRooms(currentWalls.map(({tempId, ...w}) => ({...w, ...(w.isSplitOrigin && {isSplitOrigin: w.isSplitOrigin}), ...(w.parentWallId && {parentWallId: w.parentWallId})})), roomCustomLabels, 'geometryUpdate');
        const roomContainingLight = findRoomAtPoint(lightElement.position, currentRooms);
        
        if (roomContainingLight && roomContainingLight.name && roomSync) {
          roomSync.updateLightCount(roomContainingLight.name, lightElement.lightType, -1, currentFloor);
          console.log(`💡 Light removed from room "${roomContainingLight.name}" - decremented ${lightElement.lightType} light count`);
        }
      }

      // 🔄 If erasing a radiator, clear the radiator dimensions in surveyor data
      if (elementToErase.type === 'radiator') {
        const radiatorElement = elementToErase as any; // Type assertion to access radiator properties
        const currentRooms = memoizedCalculateRooms(currentWalls.map(({tempId, ...w}) => ({...w, ...(w.isSplitOrigin && {isSplitOrigin: w.isSplitOrigin}), ...(w.parentWallId && {parentWallId: w.parentWallId})})), roomCustomLabels, 'geometryUpdate');
        const roomContainingRadiator = findRoomAtPoint(radiatorElement.position, currentRooms);
        
        if (roomContainingRadiator && roomContainingRadiator.name && roomSync) {
          // Clear radiator dimensions by setting them to empty strings
          roomSync.updateRadiatorDimensions(roomContainingRadiator.name, 0, 0, '', currentFloor);
          console.log(`🔥 Radiator removed from room "${roomContainingRadiator.name}" - cleared radiator dimensions`);
        }
      }

      // 🪟 If erasing a window, properly sync the window removal to site notes
      if (elementToErase.type === 'window') {
        const windowElement = elementToErase as any; // Type assertion to access window properties
        const currentRooms = memoizedCalculateRooms(currentWalls.map(({tempId, ...w}) => ({...w, ...(w.isSplitOrigin && {isSplitOrigin: w.isSplitOrigin}), ...(w.parentWallId && {parentWallId: w.parentWallId})})), roomCustomLabels, 'geometryUpdate');
        const roomContainingWindow = findElementRoomWithConsolidation(windowElement.position, currentRooms, 'window');
        
        console.log('🪟 DEBUG Window deletion sync via eraser:', {
          roomContainingWindow: roomContainingWindow?.name || 'NO_ROOM',
          hasRoomSync: !!roomSync,
          windowPosition: windowElement.position,
          deletedWindowId: windowElement.id
        });
        
        if (roomContainingWindow && roomContainingWindow.name && roomSync) {
          // Get the updated elements list after removal for proper sync
          const updatedElements = elements.filter(el => el !== elementToErase);
          const allWindows = updatedElements.filter(el => el.type === 'window') as unknown as WindowElement[];
          
          // Use smart sync to handle window removal (same as dedicated delete button)
          roomSync.smartSyncWindows(allWindows, currentRooms, currentFloor);
          
          // Sync overall window counts across ALL floors for site notes
          roomSync.syncAllFloorsWindows(allWindows, floorPlans, roomCustomLabels);
          
          console.log(`🪟 Window erased from room "${roomContainingWindow.name}" - synced removal to site notes`);
        }
      }

      // 🚪 If erasing a door, properly sync the door removal to room survey data and site notes
      if (elementToErase.type === 'door') {
        const doorElement = elementToErase as any; // Type assertion to access door properties
        const currentRooms = memoizedCalculateRooms(currentWalls.map(({tempId, ...w}) => ({...w, ...(w.isSplitOrigin && {isSplitOrigin: w.isSplitOrigin}), ...(w.parentWallId && {parentWallId: w.parentWallId})})), roomCustomLabels, 'geometryUpdate');
        
        // Use manually assigned roomId if available, otherwise fall back to auto-detection
        let roomContainingDoor;
        if (doorElement.roomId) {
          roomContainingDoor = currentRooms.find(r => (r.stableId || r.id) === doorElement.roomId);
          console.log('🚪 DEBUG Door deletion (eraser): Using manually assigned room:', roomContainingDoor?.name);
        } else {
          roomContainingDoor = findRoomAtPoint(doorElement.position, currentRooms);
          console.log('🚪 DEBUG Door deletion (eraser): Using auto-detected room:', roomContainingDoor?.name);
        }
        
        console.log('🚪 DEBUG Door deletion sync via eraser:', {
          roomContainingDoor: roomContainingDoor?.name || 'NO_ROOM',
          hasRoomSync: !!roomSync,
          doorPosition: doorElement.position,
          deletedDoorId: doorElement.id,
          hadManualRoomId: !!doorElement.roomId
        });
        
        if (roomContainingDoor && roomContainingDoor.name && roomSync) {
          // Get the updated elements list after removal for proper sync
          const updatedElements = elements.filter(el => el !== elementToErase);
          const allDoors = updatedElements.filter(el => el.type === 'door') as any[];
          
          // Sync door undercuts for current floor
          roomSync.syncDoorUndercuts(allDoors, currentRooms, currentFloor);
          
          // Sync overall door counts across ALL floors for site notes
          roomSync.syncAllFloorsDoorUndercuts(allDoors, floorPlans, roomCustomLabels);
          
          // 🔧 CRITICAL: Also sync to room_details to update doorPresent status
          if (surveyData && onDataUpdate) {
            console.log(`🚪 [Eraser] BEFORE deletion - room_details rooms:`, surveyData.room_details?.rooms?.map(r => ({
              id: r.id,
              name: r.name,
              doorPresent: (r as any).doorPresent,
              doorUndercutSize: (r as any).doorUndercutSize
            })));
            
            onDataUpdate(prev => {
              const existingRooms = prev.room_details?.rooms || [];
              const updatedRooms = existingRooms.map(room => {
                if (room.id === roomContainingDoor.stableId) {
                  console.log(`🚪 [Eraser] UPDATING room ${room.name} - BEFORE:`, {
                    doorPresent: (room as any).doorPresent,
                    doorUndercutSize: (room as any).doorUndercutSize
                  });
                  const updated = {
                    ...room,
                    doorPresent: false,
                    doorUndercutSize: '',
                    doorPhotos: []
                  };
                  console.log(`🚪 [Eraser] UPDATING room ${room.name} - AFTER:`, {
                    doorPresent: updated.doorPresent,
                    doorUndercutSize: updated.doorUndercutSize
                  });
                  return updated;
                }
                return room;
              });
              
              console.log(`🚪 [Eraser] AFTER deletion - updated rooms:`, updatedRooms.map(r => ({
                id: r.id,
                name: r.name,
                doorPresent: (r as any).doorPresent,
                doorUndercutSize: (r as any).doorUndercutSize
              })));
              
              return {
                ...prev,
                room_details: {
                  ...prev.room_details,
                  rooms: updatedRooms
                }
              };
            });
          }
          
          console.log(`🚪 Door erased from room "${roomContainingDoor.name}" - synced removal to site notes and room survey`);
        }
      }
      
      setElements(prev => prev.filter(el => el !== elementToErase));
      // Undo/Redo stack updated by recordUndoState in FloorPlanCreator
    }
  }, [currentWalls, elements, floorPlans, currentFloor, setFloorPlans, setElements, recordUndoState, onDebug, lastOperationTypeRef, roomCustomLabels, setRoomCustomLabels, migrateRoomLabels, triggerAutosave]);

  // Enhanced findElementAtPointInternal with better hit detection
  // 🔧 NEW: Function to restore split wall from segments
  const restoreSplitWall = (splitSegments: WallWithTempId[], parentWallId: string): Wall | null => {
    if (splitSegments.length < 2) {
      console.warn('[restoreSplitWall] Need at least 2 segments to restore a wall');
      return null;
    }
    
    onDebug('RESTORE_SPLIT_WALL_ATTEMPT', {
      segmentsCount: splitSegments.length,
      parentWallId,
      segments: splitSegments.map(s => ({
        tempId: s.tempId,
        start: s.start,
        end: s.end,
        type: s.type
      }))
    });
    
    // Sort segments to find the correct order and endpoints
    // First, find all unique endpoints
    const allPoints: Point[] = [];
    splitSegments.forEach(segment => {
      allPoints.push(segment.start, segment.end);
    });
    
    // Find the two endpoints that appear only once (the true start and end of the original wall)
    const pointCounts = new Map<string, { point: Point, count: number }>();
    allPoints.forEach(point => {
      const key = `${Math.round(point.x * 100) / 100}_${Math.round(point.y * 100) / 100}`;
      if (!pointCounts.has(key)) {
        pointCounts.set(key, { point, count: 0 });
      }
      pointCounts.get(key)!.count++;
    });
    
    const endpoints = Array.from(pointCounts.values())
      .filter(entry => entry.count === 1)
      .map(entry => entry.point);
    
    if (endpoints.length !== 2) {
      console.warn('[restoreSplitWall] Could not identify exactly 2 endpoints', {
        endpointsFound: endpoints.length,
        endpoints,
        pointCounts: Array.from(pointCounts.entries())
      });
      
      // Fallback: use the extreme points of all segments
      if (splitSegments.length === 2) {
        // For 2 segments, find the endpoints that don't connect
        const seg1 = splitSegments[0];
        const seg2 = splitSegments[1];
        
        const tolerance = 0.1;
        const seg1StartConnectsToSeg2 = 
          Math.abs(seg1.start.x - seg2.start.x) < tolerance && Math.abs(seg1.start.y - seg2.start.y) < tolerance ||
          Math.abs(seg1.start.x - seg2.end.x) < tolerance && Math.abs(seg1.start.y - seg2.end.y) < tolerance;
        const seg1EndConnectsToSeg2 = 
          Math.abs(seg1.end.x - seg2.start.x) < tolerance && Math.abs(seg1.end.y - seg2.start.y) < tolerance ||
          Math.abs(seg1.end.x - seg2.end.x) < tolerance && Math.abs(seg1.end.y - seg2.end.y) < tolerance;
        
        let originalStart: Point, originalEnd: Point;
        if (seg1StartConnectsToSeg2) {
          originalStart = seg1.end;
          originalEnd = seg1StartConnectsToSeg2 && Math.abs(seg1.start.x - seg2.start.x) < tolerance ? seg2.end : seg2.start;
        } else if (seg1EndConnectsToSeg2) {
          originalStart = seg1.start;
          originalEnd = seg1EndConnectsToSeg2 && Math.abs(seg1.end.x - seg2.start.x) < tolerance ? seg2.end : seg2.start;
        } else {
          // Segments don't connect - use extreme points
          const allX = allPoints.map(p => p.x);
          const allY = allPoints.map(p => p.y);
          const minX = Math.min(...allX);
          const maxX = Math.max(...allX);
          const minY = Math.min(...allY);
          const maxY = Math.max(...allY);
          
          // Determine if the wall is more horizontal or vertical
          const deltaX = maxX - minX;
          const deltaY = maxY - minY;
          
          if (deltaX > deltaY) {
            // More horizontal - use leftmost and rightmost points
            originalStart = allPoints.find(p => p.x === minX) || allPoints[0];
            originalEnd = allPoints.find(p => p.x === maxX) || allPoints[1];
          } else {
            // More vertical - use topmost and bottommost points
            originalStart = allPoints.find(p => p.y === minY) || allPoints[0];
            originalEnd = allPoints.find(p => p.y === maxY) || allPoints[1];
          }
        }
        
        endpoints.push(originalStart, originalEnd);
      }
    }
    
    if (endpoints.length < 2) {
      console.error('[restoreSplitWall] Failed to determine original wall endpoints');
      return null;
    }
    
    const originalStart = endpoints[0];
    const originalEnd = endpoints[1];
    
    // Use the type and other properties from the first segment
    const referenceSegment = splitSegments[0];
    
    const restoredWall: Wall = {
      start: {
        x: Math.round(originalStart.x * 100) / 100,
        y: Math.round(originalStart.y * 100) / 100
      },
      end: {
        x: Math.round(originalEnd.x * 100) / 100,
        y: Math.round(originalEnd.y * 100) / 100
      },
      type: referenceSegment.type,
      floor: referenceSegment.floor || currentFloor,
      // Remove split-specific properties
      // isSplitOrigin: undefined,
      // parentWallId: undefined,
      // requiresExtrusionOnMove: undefined
    };
    
    onDebug('RESTORE_SPLIT_WALL_SUCCESS', {
      originalSegmentsCount: splitSegments.length,
      restoredWall: {
        start: restoredWall.start,
        end: restoredWall.end,
        type: restoredWall.type,
        length: Math.hypot(
          restoredWall.end.x - restoredWall.start.x,
          restoredWall.end.y - restoredWall.start.y
        ).toFixed(2)
      }
    });
    
    return restoredWall;
  };

  const findElementAtPointInternal = useCallback((point: Point, currentElements: FloorPlanElement[]): FloorPlanElement | null => {
    // Enhanced element detection for touch devices with scale-aware tolerance
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // 🎯 IMPROVED: Use minimum screen-space tolerance to ensure consistent click detection at all zoom levels
    // The tolerance should never get too small when zoomed in
    const baseTolerance = isTouchDevice ? MIN_INTERACTION_TOLERANCE : MIN_CLICK_TOLERANCE;
    const worldTolerance = baseTolerance / scale; // Convert screen pixels to world coordinates
    const minWorldTolerance = 3; // Minimum world space tolerance
    
    // Use the LARGER of the two - this ensures good UX at both high and low zoom
    const scaledTouchTolerance = Math.max(worldTolerance, minWorldTolerance);
    
    // Filter elements to current floor first
    const elementsOnCurrentFloor = currentElements.filter(element => {
      const el = element as any;
      return el.floor === currentFloor || el.floor === undefined; // Include elements without floor specified
    });
    
    // 🎯 IMPROVED SELECTION: Find all elements that contain the click point
    const candidateElements: Array<{ element: FloorPlanElement; distance: number }> = [];
    
    for (let i = elementsOnCurrentFloor.length - 1; i >= 0; i--) {
        const element = elementsOnCurrentFloor[i];
        const bounds = getElementBounds(element, undefined, scale); // Use scale-aware touch-friendly bounds
        
        // Expand bounds for touch devices with scale-aware tolerance
        const expandedBounds = {
          minX: bounds.minX - scaledTouchTolerance,
          minY: bounds.minY - scaledTouchTolerance,
          maxX: bounds.maxX + scaledTouchTolerance,
          maxY: bounds.maxY + scaledTouchTolerance
        };
        
        if (point.x >= expandedBounds.minX && point.x <= expandedBounds.maxX && 
            point.y >= expandedBounds.minY && point.y <= expandedBounds.maxY) {
            
            // Calculate distance from click point to element center
            const elementCenter = {
              x: (bounds.minX + bounds.maxX) / 2,
              y: (bounds.minY + bounds.maxY) / 2
            };
            
            // For elements with position property, use that for more accurate distance
            if ((element as any).position) {
              const elementPos = (element as any).position;
              elementCenter.x = elementPos.x;
              elementCenter.y = elementPos.y;
            }
            
            const distance = Math.hypot(point.x - elementCenter.x, point.y - elementCenter.y);
            candidateElements.push({ element, distance });
        }
    }
    
    // If no elements found, return null
    if (candidateElements.length === 0) {
      return null;
    }
    
    // 🏆 CLOSEST WINS: If multiple elements found, return the closest one
    // This solves the issue of selecting the correct window when multiple windows are on the same wall
    if (candidateElements.length > 1) {
      // Sort by distance and return the closest
      candidateElements.sort((a, b) => a.distance - b.distance);
      
      onDebug('ELEMENT_SELECTION_MULTIPLE_CANDIDATES', {
        clickPoint: point,
        candidatesCount: candidateElements.length,
        closestElement: {
          type: candidateElements[0].element.type,
          id: hasId(candidateElements[0].element) ? candidateElements[0].element.id : 'no-id',
          distance: candidateElements[0].distance.toFixed(2)
        },
        allCandidates: candidateElements.map(c => ({
          type: c.element.type,
          id: hasId(c.element) ? c.element.id : 'no-id',
          distance: c.distance.toFixed(2)
        }))
      });
      
      return candidateElements[0].element;
    }
    
    // Single element found, return it
    return candidateElements[0].element;
  }, [currentFloor, scale, onDebug]);

  // 🔗 COORDINATED ROOM RESIZE: Handle shared wall movement with both rooms
  const handleCoordinatedRoomResize = useCallback((point: Point, coordinatedInfo: any) => {
    const { primaryRoom, secondaryRooms, sharedWallId, affectedRooms } = coordinatedInfo;
    
    console.log('🔗 COORDINATED RESIZE START', {
      primaryRoom: primaryRoom.stableId,
      secondaryRoomsCount: secondaryRooms.length,
      sharedWallId,
      targetPoint: `(${point.x.toFixed(1)},${point.y.toFixed(1)})`
    });
    
    try {
      // Get the shared wall being dragged
      const sharedWall = draggedWallSegmentRef.current;
      if (!sharedWall) {
        console.log('❌ COORDINATED RESIZE: No shared wall reference found');
        return { handled: false };
      }
      
      // Get the original wall position
      const originalWall = initialWallsForResizeRef.current?.find(w => w.tempId === sharedWall.tempId);
      if (!originalWall) {
        console.log('❌ COORDINATED RESIZE: Original wall not found');
        return { handled: false };
      }
      
      // Snap the point for precise movement
      const snappedPoint = snapPointToGrid(point, CM_SNAP_SIZE);
      
      // Calculate the movement delta based on the shared wall direction
      let deltaX = 0, deltaY = 0;
      
      if (resizeHandle === 'w' || resizeHandle === 'e') {
        // Horizontal movement - the shared wall is vertical
        deltaX = snappedPoint.x - originalWall.start.x;
        console.log('🔗 HORIZONTAL MOVEMENT', {
          originalX: originalWall.start.x,
          newX: snappedPoint.x,
          deltaX: deltaX.toFixed(2)
        });
      } else if (resizeHandle === 'n' || resizeHandle === 's') {
        // Vertical movement - the shared wall is horizontal
        deltaY = snappedPoint.y - originalWall.start.y;
        console.log('🔗 VERTICAL MOVEMENT', {
          originalY: originalWall.start.y,
          newY: snappedPoint.y,
          deltaY: deltaY.toFixed(2)
        });
      }
      
      // Get current walls for processing
      const currentWallsForResize = tempWalls || currentWalls;
      const secondaryRoom = secondaryRooms[0];
      
      // 🏗️ PROFESSIONAL CAD APPROACH: Treat shared wall as constraint, update connected walls
      // Step 1: Define the new position of the shared wall (this is our constraint)
      const newSharedWallPosition = {
        start: { ...originalWall.start },
        end: { ...originalWall.end }
      };
      
      // Move the shared wall to the target position
      if (resizeHandle === 'w' || resizeHandle === 'e') {
        newSharedWallPosition.start.x = snappedPoint.x;
        newSharedWallPosition.end.x = snappedPoint.x;
      } else if (resizeHandle === 'n' || resizeHandle === 's') {
        newSharedWallPosition.start.y = snappedPoint.y;
        newSharedWallPosition.end.y = snappedPoint.y;
      }
      
      console.log('🔗 SHARED WALL CONSTRAINT ESTABLISHED', {
        originalPos: `(${originalWall.start.x.toFixed(1)},${originalWall.start.y.toFixed(1)})->(${originalWall.end.x.toFixed(1)},${originalWall.end.y.toFixed(1)})`,
        newPos: `(${newSharedWallPosition.start.x.toFixed(1)},${newSharedWallPosition.start.y.toFixed(1)})->(${newSharedWallPosition.end.x.toFixed(1)},${newSharedWallPosition.end.y.toFixed(1)})`
      });
      
      // Step 2: Update all walls while maintaining connectivity to the shared wall
      const updatedWalls = currentWallsForResize.map(wall => {
        const wallCopy = { ...wall, start: { ...wall.start }, end: { ...wall.end } };
        
        // Check if this is the shared wall itself
        if (wall.tempId === sharedWall.tempId) {
          // Apply the constraint - this is the new position of the shared wall
          wallCopy.start = { ...newSharedWallPosition.start };
          wallCopy.end = { ...newSharedWallPosition.end };
          
          console.log('🔗 SHARED WALL CONSTRAINT APPLIED', {
            wallId: wall.tempId,
            newPos: `(${wallCopy.start.x.toFixed(1)},${wallCopy.start.y.toFixed(1)})->(${wallCopy.end.x.toFixed(1)},${wallCopy.end.y.toFixed(1)})`
          });
          
          return wallCopy;
        }
        
        // Check if this wall is connected to the shared wall
        const isConnectedToSharedWall = (
          pointsAreEqual(wall.start, originalWall.start) ||
          pointsAreEqual(wall.start, originalWall.end) ||
          pointsAreEqual(wall.end, originalWall.start) ||
          pointsAreEqual(wall.end, originalWall.end)
        );
        
        if (isConnectedToSharedWall) {
          // This wall is connected to the shared wall - update its connection points
          if (pointsAreEqual(wall.start, originalWall.start)) {
            wallCopy.start = { ...newSharedWallPosition.start };
          } else if (pointsAreEqual(wall.start, originalWall.end)) {
            wallCopy.start = { ...newSharedWallPosition.end };
          }
          
          if (pointsAreEqual(wall.end, originalWall.start)) {
            wallCopy.end = { ...newSharedWallPosition.start };
          } else if (pointsAreEqual(wall.end, originalWall.end)) {
            wallCopy.end = { ...newSharedWallPosition.end };
          }
          
          console.log('🔗 CONNECTED WALL UPDATED', {
            wallId: wall.tempId,
            originalPos: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
            newPos: `(${wallCopy.start.x.toFixed(1)},${wallCopy.start.y.toFixed(1)})->(${wallCopy.end.x.toFixed(1)},${wallCopy.end.y.toFixed(1)})`
          });
        }
        
        return wallCopy;
      });
      
      // Update the walls
      const finalWalls = updatedWalls.map((wall, index) => ({
        ...wall,
        tempId: wall.tempId || `coordinated-${Date.now()}-${index}`,
        floor: currentFloor
      }));
      
      setTempWalls(finalWalls);
      setActivelyResizedWalls(finalWalls.map(preserveWallMetadata));
      
      // Update live dimensions for the primary room
      const primaryRoomWalls = finalWalls.filter(wall =>
        primaryRoom.walls.some((roomWall: Wall) => 
          (roomWall as any).tempId === wall.tempId ||
          (pointsAreEqual(roomWall.start, wall.start) && pointsAreEqual(roomWall.end, wall.end))
        )
      ).map(preserveWallMetadata);
      
      if (primaryRoomWalls.length > 0) {
        const bounds = getRoomBounds(primaryRoomWalls);
        const widthPx = bounds.maxX - bounds.minX;
        const heightPx = bounds.maxY - bounds.minY;
        const widthM = Math.round((widthPx / PIXELS_PER_METER) * 100) / 100;
        const heightM = Math.round((heightPx / PIXELS_PER_METER) * 100) / 100;
        const areaM2 = widthM * heightM;
        setResizingRoomLiveDimensions({ width: widthM, height: heightM, area: areaM2 });
      }
      
      console.log('✅ COORDINATED RESIZE COMPLETE', {
        updatedWallsCount: finalWalls.length,
        primaryRoomWallsCount: primaryRoomWalls.length,
        deltaApplied: { deltaX: deltaX.toFixed(2), deltaY: deltaY.toFixed(2) }
      });
      
      return { handled: true };
      
    } catch (error) {
      console.error('❌ COORDINATED RESIZE ERROR:', error);
      return { handled: false };
    }
  }, [resizeHandle, tempWalls, currentWalls, currentFloor, draggedWallSegmentRef, initialWallsForResizeRef, setTempWalls, setActivelyResizedWalls, setResizingRoomLiveDimensions]);

  // 🎯 PROFESSIONAL CAD: Handle constraint-based shared wall movement
  const handleProfessionalCADConstraints = useCallback((point: Point, coordinatedInfo: any): { handled: boolean } => {
    console.log('🎯 PROFESSIONAL CAD: Starting constraint-based shared wall handling', {
      targetPoint: `(${point.x.toFixed(1)},${point.y.toFixed(1)})`,
      sharedWallId: coordinatedInfo.sharedWallId,
      affectedRooms: coordinatedInfo.affectedRooms,
      sharedWallSystem: !!coordinatedInfo.sharedWallSystem
    });

    if (!selectedRoom || !draggedWallSegmentRef.current || !coordinatedInfo.sharedWallSystem) {
      onDebug('PROFESSIONAL_CAD_CONSTRAINT_GUARD_FAIL', {
        selectedRoom: !!selectedRoom,
        draggedWall: !!draggedWallSegmentRef.current,
        sharedWallSystem: !!coordinatedInfo.sharedWallSystem
      });
      return { handled: false };
    }

    const draggedWall = draggedWallSegmentRef.current;
    const sharedWallSystem = coordinatedInfo.sharedWallSystem;
    const currentWallsForConstraint = tempWalls || currentWalls;

    // 🎯 PROFESSIONAL CAD APPROACH: Use the same constraint-based logic as the coordinated resize
    // but with enhanced overlap detection and auto-splitting capabilities
    
    // Get the shared wall being dragged
    const originalWall = initialWallsForResizeRef.current?.find(w => w.tempId === draggedWall.tempId);
    if (!originalWall) {
      console.log('🎯 PROFESSIONAL CAD: Original wall not found');
      return { handled: false };
    }
    
    // Snap the point for precise movement
    const snappedPoint = snapPointToGrid(point, CM_SNAP_SIZE);
    
    // 🎯 PROFESSIONAL CAD ENHANCEMENT: Check if movement extends beyond shared segment
    const sharedSegment = sharedWallSystem.sharedSegments.find((segment: any) => 
      segment.originalWallIds.includes(draggedWall.tempId!) || 
      segment.originalWallIds.includes(coordinatedInfo.sharedWallId)
    );

    if (!sharedSegment) {
      console.log('🎯 PROFESSIONAL CAD: No shared segment found - using standard constraint approach');
      // Fall back to the same logic as coordinated resize
      return { handled: false };
    }

    // 🎯 PROFESSIONAL CAD LOGIC: Apply constraint-based movement with overlap detection
    // Define the new position of the shared wall (this is our constraint)
    const newSharedWallPosition = {
      start: { ...originalWall.start },
      end: { ...originalWall.end }
    };
    
    // Move the shared wall to the target position
    if (resizeHandle === 'w' || resizeHandle === 'e') {
      newSharedWallPosition.start.x = snappedPoint.x;
      newSharedWallPosition.end.x = snappedPoint.x;
    } else if (resizeHandle === 'n' || resizeHandle === 's') {
      newSharedWallPosition.start.y = snappedPoint.y;
      newSharedWallPosition.end.y = snappedPoint.y;
    }
    
    console.log('🎯 PROFESSIONAL CAD: Constraint-based shared wall movement', {
      originalPos: `(${originalWall.start.x.toFixed(1)},${originalWall.start.y.toFixed(1)})->(${originalWall.end.x.toFixed(1)},${originalWall.end.y.toFixed(1)})`,
      newPos: `(${newSharedWallPosition.start.x.toFixed(1)},${newSharedWallPosition.start.y.toFixed(1)})->(${newSharedWallPosition.end.x.toFixed(1)},${newSharedWallPosition.end.y.toFixed(1)})`,
      sharedSegment: {
        start: sharedSegment.start,
        end: sharedSegment.end
      }
    });
    
    // Apply the same professional CAD constraint logic
    const updatedWalls = currentWallsForConstraint.map(wall => {
      const wallCopy = { ...wall, start: { ...wall.start }, end: { ...wall.end } };
      
      // Check if this is the shared wall itself
      if (wall.tempId === draggedWall.tempId) {
        // Apply the constraint - this is the new position of the shared wall
        wallCopy.start = { ...newSharedWallPosition.start };
        wallCopy.end = { ...newSharedWallPosition.end };
        
        console.log('🎯 PROFESSIONAL CAD: Shared wall constraint applied', {
          wallId: wall.tempId,
          newPos: `(${wallCopy.start.x.toFixed(1)},${wallCopy.start.y.toFixed(1)})->(${wallCopy.end.x.toFixed(1)},${wallCopy.end.y.toFixed(1)})`
        });
        
        return wallCopy;
      }
      
      // Check if this wall is connected to the shared wall
      const isConnectedToSharedWall = (
        pointsAreEqual(wall.start, originalWall.start) ||
        pointsAreEqual(wall.start, originalWall.end) ||
        pointsAreEqual(wall.end, originalWall.start) ||
        pointsAreEqual(wall.end, originalWall.end)
      );
      
      if (isConnectedToSharedWall) {
        // This wall is connected to the shared wall - update its connection points
        if (pointsAreEqual(wall.start, originalWall.start)) {
          wallCopy.start = { ...newSharedWallPosition.start };
        } else if (pointsAreEqual(wall.start, originalWall.end)) {
          wallCopy.start = { ...newSharedWallPosition.end };
        }
        
        if (pointsAreEqual(wall.end, originalWall.start)) {
          wallCopy.end = { ...newSharedWallPosition.start };
        } else if (pointsAreEqual(wall.end, originalWall.end)) {
          wallCopy.end = { ...newSharedWallPosition.end };
        }
        
        console.log('🎯 PROFESSIONAL CAD: Connected wall updated', {
          wallId: wall.tempId,
          originalPos: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
          newPos: `(${wallCopy.start.x.toFixed(1)},${wallCopy.start.y.toFixed(1)})->(${wallCopy.end.x.toFixed(1)},${wallCopy.end.y.toFixed(1)})`
        });
      }
      
      return wallCopy;
    });
    
    // Update the walls with constraint-based result
    const finalWalls = updatedWalls.map((wall, index) => ({
      ...wall,
      tempId: wall.tempId || `professional-cad-${Date.now()}-${index}`,
      floor: currentFloor
    }));
    
    setTempWalls(finalWalls);
    setActivelyResizedWalls(finalWalls.map(({tempId, ...w}) => w));
    
    // Update live dimensions for the primary room
    const primaryRoomWalls = finalWalls.filter(wall =>
      coordinatedInfo.primaryRoom.walls.some((roomWall: Wall) => 
        (roomWall as any).tempId === wall.tempId ||
        (pointsAreEqual(roomWall.start, wall.start) && pointsAreEqual(roomWall.end, wall.end))
      )
    ).map(({tempId, ...w}) => w);
    
    if (primaryRoomWalls.length > 0) {
      const bounds = getRoomBounds(primaryRoomWalls);
      const widthPx = bounds.maxX - bounds.minX;
      const heightPx = bounds.maxY - bounds.minY;
      const widthM = Math.round((widthPx / PIXELS_PER_METER) * 100) / 100;
      const heightM = Math.round((heightPx / PIXELS_PER_METER) * 100) / 100;
      const areaM2 = widthM * heightM;
      setResizingRoomLiveDimensions({ width: widthM, height: heightM, area: areaM2 });
    }
    
    console.log('✅ PROFESSIONAL CAD: Constraint-based movement complete', {
      updatedWallsCount: finalWalls.length,
      primaryRoomWallsCount: primaryRoomWalls.length,
      constraintsMaintained: true,
      sharedWallPreserved: true
    });
    
    return { handled: true };
  }, [selectedRoom, draggedWallSegmentRef, tempWalls, currentWalls, initialWallsForResizeRef, resizeHandle, currentFloor, setTempWalls, setActivelyResizedWalls, setResizingRoomLiveDimensions, onDebug]);

  const handleRoomResize = useCallback((point: Point) => {
    // Debug logging disabled during dragging preview for performance
    // console.log('🚨 [DIRECT] handleRoomResize CALLED!', {
    //   point: `(${point.x.toFixed(1)},${point.y.toFixed(1)})`,
    //   selectedRoom: !!selectedRoom,
    //   selectedRoomId: selectedRoom?.stableId || selectedRoom?.id,
    //   selectedRoomName: selectedRoom?.name,
    //   resizeHandle,
    //   draggedWallRef: !!draggedWallSegmentRef.current,
    //   isDragging,
    //   timestamp: Date.now()
    // });
    
    // 🔗 COORDINATED MOVEMENT: Check if this is a coordinated shared wall movement
    const coordinatedInfo = (window as any).coordinatedMovementInfo;
    if (coordinatedInfo && coordinatedInfo.isCoordinatedMovement) {
        // Debug logging disabled during dragging preview for performance
        // console.log('🔗 COORDINATED MOVEMENT DETECTED in handleRoomResize', {
        //     primaryRoom: coordinatedInfo.primaryRoom.stableId,
        //     affectedRooms: coordinatedInfo.affectedRooms,
        //     sharedWallId: coordinatedInfo.sharedWallId,
        //     targetPoint: `(${point.x.toFixed(1)},${point.y.toFixed(1)})`
        // });
        
        // 🎯 PROFESSIONAL CAD: Check for constraint-based movement
        if (coordinatedInfo.constraintBasedMovement && coordinatedInfo.autoSplitSharedWalls) {
            console.log('🎯 PROFESSIONAL CAD: Implementing constraint-based shared wall movement', {
                autoSplitSharedWalls: coordinatedInfo.autoSplitSharedWalls,
                maintainSharedConstraints: coordinatedInfo.maintainSharedConstraints,
                sharedWallSystem: !!coordinatedInfo.sharedWallSystem
            });
            
            // Handle professional CAD constraint-based movement
            const result = handleProfessionalCADConstraints(point, coordinatedInfo);
            if (result.handled) {
                return; // Professional CAD constraint handling completed
            }
        } else {
            // Handle legacy coordinated movement of both rooms
            const result = handleCoordinatedRoomResize(point, coordinatedInfo);
            if (result.handled) {
                return; // Coordinated movement handled, skip normal processing
            }
        }
    }
    
    // 🚨 DEBUG: Entry point debug to verify function is being called
    onDebug('HANDLE_ROOM_RESIZE_ENTRY_POINT', { 
      point: { x: point.x.toFixed(2), y: point.y.toFixed(2) },
      selectedRoom: !!selectedRoom,
      resizeHandle,
      draggedWallSegmentRef: !!draggedWallSegmentRef.current,
      isDragging,
      isResizingExtrusionWidth: isResizingExtrusionWidthRef.current
    });
    
    if (!selectedRoom || !resizeHandle || !draggedWallSegmentRef.current) {
        console.log('🚨 [DIRECT] HANDLE_ROOM_RESIZE_GUARD_FAIL!', {
          selectedRoom: !!selectedRoom,
          selectedRoomDetails: selectedRoom ? {
            id: selectedRoom.id,
            stableId: selectedRoom.stableId,
            name: selectedRoom.name,
            wallsCount: selectedRoom.walls?.length || 0
          } : 'NO_ROOM',
          resizeHandle,
          draggedWallSegmentRefCurrent: !!draggedWallSegmentRef.current,
          draggedWallDetails: draggedWallSegmentRef.current ? 'HAS_WALL' : 'NO_WALL'
        });
        onDebug('HANDLE_ROOM_RESIZE_GUARD_FAIL', { selectedRoom, resizeHandle, draggedWallSegmentRefCurrent: draggedWallSegmentRef.current });
        return;
    }
    
    // 🚀 ENHANCED: Simple wall snapping for room resize handles
    let snappedPoint = snapPointToGrid(point, CM_SNAP_SIZE);
    
    // Apply simple wall snapping if conditions are met
    if (selectedRoom && currentWalls && currentWalls.length > 0) {
      try {
        // Get walls for snapping (exclude current room walls)
        const targetWalls = currentWalls.filter(wall => 
          !selectedRoom.walls.some(roomWall => roomWall.tempId === wall.tempId)
        ).map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
        
        // Check if we're resizing with edge handles (not corners)
        const isEdgeHandle = resizeHandle === 'n' || resizeHandle === 's' || resizeHandle === 'e' || resizeHandle === 'w';
        const isCornerHandle = resizeHandle === 'nw' || resizeHandle === 'ne' || resizeHandle === 'sw' || resizeHandle === 'se';
        
        let snapResult;
        
        // CRITICAL: Corner handles should NOT use wall snapping at all
        // They start AT intersections, causing validation to fail (distance=0, cosAngle=0)
        // Only edge handles benefit from line intersection snapping
        if (isEdgeHandle && draggedWallSegmentRef.current) {
          // Use line intersection snapping for edge handles only
          const isVerticalResize = resizeHandle === 'n' || resizeHandle === 's';
          const roomBounds = getRoomBounds(selectedRoom.walls);
          const resizeStartPoint = {
            x: resizeHandle === 'n' ? roomBounds.minX : roomBounds.maxX,
            y: resizeHandle === 'n' ? roomBounds.minY : roomBounds.maxY
          };
          
          onDebug('ROOM_RESIZE_EDGE_LINE_INTERSECTION', {
            resizeHandle,
            resizeStartPoint,
            targetPoint: point,
            isVerticalResize,
            targetWallsCount: targetWalls.length
          });
          
          snapResult = snapToWallsWithLineIntersection(point, targetWalls, resizeStartPoint, scale, false, true);
          
          if (snapResult.type !== 'none') {
            snappedPoint = snapResult.point;
          }
        } else if (isCornerHandle) {
          // For corner handles, use grid snapping ONLY - no wall snapping
          // This prevents validation failures when corner starts at an intersection
          console.log('🎯 CORNER RESIZE: Using grid snapping only (no wall snapping)');
          // snappedPoint already set to grid snap above, so just skip wall snapping
        } else {
          // Fallback for any other resize handles
          snapResult = snapToWalls(point, targetWalls, scale, false);
          
          if (snapResult.type !== 'none') {
            snappedPoint = snapResult.point;
          }
        }
        
        // Only apply wall snap result for non-corner handles
        if (!isCornerHandle && snapResult && snapResult.type !== 'none') {
          snappedPoint = snapResult.point;
          onDebug('ROOM_RESIZE_SNAP_APPLIED', {
            originalPoint: point,
            gridSnappedPoint: snapPointToGrid(point, CM_SNAP_SIZE),
            wallSnappedPoint: snapResult.point,
            snapType: snapResult.type,
            resizeHandle,
            message: `Room resize handle snapped to ${snapResult.type}`
          });
        }
      } catch (error) {
        console.warn('⚠️ Simple wall snapping failed in handleRoomResize:', error);
        // Fallback to grid snapping only
        snappedPoint = snapPointToGrid(point, CM_SNAP_SIZE);
      }
    }

    

    // Performance optimization during active dragging
    if (isDragging) {
      onDebug('HANDLE_ROOM_RESIZE_OPTIMIZED_MODE', { 
        isDragging: true,
        isResizingExtrusionWidth: isResizingExtrusionWidthRef.current,
        isExtrudingRoomSegment: isExtrudingRoomSegmentRef.current,
        hasIntersectionData: !!intersectionDataRef.current
      });
      
      // 🎯 PARTIAL WALL DRAG: Check if we have intersection data and should use partial dragging
      if (intersectionDataRef.current && intersectionDataRef.current.requiresPartialDrag) {
        console.log('🎯 USING PARTIAL WALL DRAG', {
          intersectionCount: intersectionDataRef.current.intersectionPoints.length,
          draggedWall: `${intersectionDataRef.current.draggedWall.start.x.toFixed(1)},${intersectionDataRef.current.draggedWall.start.y.toFixed(1)} -> ${intersectionDataRef.current.draggedWall.end.x.toFixed(1)},${intersectionDataRef.current.draggedWall.end.y.toFixed(1)}`,
          targetPoint: `${snappedPoint.x.toFixed(1)},${snappedPoint.y.toFixed(1)}`
        });
        
        // Calculate movement delta
        const draggedWall = intersectionDataRef.current.draggedWall;
        const originalWall = initialWallsForResizeRef.current?.find(w => w.tempId === draggedWall.tempId);
        
        if (originalWall && selectedRoom) {
          const deltaX = snappedPoint.x - (resizeHandle === 'e' ? originalWall.end.x : originalWall.start.x);
          const deltaY = snappedPoint.y - (resizeHandle === 'n' ? originalWall.start.y : originalWall.end.y);
          
                     // Use the partial wall drag system
           // 🔧 CRITICAL DRAG PHASE ROOM MERGE FIX: Use complete floor plan walls for live preview
           // During drag, tempWalls may only contain ~4 walls from resizing room
           // but room calculations need ALL floor plan walls to maintain accurate room counts during preview
           const completeWallsForDragPreview = floorPlans[currentFloor] || [];
           const partialDragResult = handlePartialWallDrag(
             draggedWall,
             intersectionDataRef.current.intersectionPoints,
             selectedRoom,
             memoizedCalculateRooms(completeWallsForDragPreview, {}, 'geometryUpdate', currentFloor),
             tempWalls || currentWalls,
             deltaX,
             deltaY,
             snappedPoint
           );
          
          console.log('🎯 PARTIAL DRAG RESULT', {
            partialMovementApplied: partialDragResult.partialMovementApplied,
            movableSegments: partialDragResult.movableSegments,
            fixedSegments: partialDragResult.fixedSegments,
            totalResultWalls: partialDragResult.resultWalls.length
          });
          
          if (partialDragResult.partialMovementApplied) {
            // Update the walls with the partial movement result
            const updatedWalls = partialDragResult.resultWalls.map((wall, index) => ({
              ...wall,
              tempId: `temp-${Date.now()}-${index}`,
              floor: currentFloor
            }));
            
            setTempWalls(updatedWalls);
            setActivelyResizedWalls(partialDragResult.resultWalls);
            
            // Update live dimensions
            const bounds = getRoomBounds(partialDragResult.resultWalls);
            const widthPx = bounds.maxX - bounds.minX;
            const heightPx = bounds.maxY - bounds.minY;
            const widthM = Math.round((widthPx / PIXELS_PER_METER) * 100) / 100;
            const heightM = Math.round((heightPx / PIXELS_PER_METER) * 100) / 100;
            const areaM2 = widthM * heightM;
            setResizingRoomLiveDimensions({ width: widthM, height: heightM, area: areaM2 });
            
            return; // Skip all other processing
          }
        }
      }
      
      // 🔗 PROFESSIONAL SHARED WALL HANDLING: Check if we have shared wall movement data
      if (intersectionDataRef.current && intersectionDataRef.current.sharedWallMovement) {
        const sharedWallData = intersectionDataRef.current.sharedWallMovement;
        
        console.log('🔗 USING PROFESSIONAL SHARED WALL MOVEMENT', {
          movementType: sharedWallData.movementType,
          affectedRooms: sharedWallData.affectedRooms,
          preserveRoomIds: sharedWallData.preserveRoomIds,
          targetPoint: `${snappedPoint.x.toFixed(1)},${snappedPoint.y.toFixed(1)}`
        });
        
        // Calculate movement delta
        const draggedWall = intersectionDataRef.current.draggedWall;
        const originalWall = initialWallsForResizeRef.current?.find(w => w.tempId === draggedWall.tempId);
        
        if (originalWall && selectedRoom) {
          // Calculate movement delta based on resize handle
          const deltaX = snappedPoint.x - (resizeHandle === 'e' ? originalWall.end.x : 
                                         resizeHandle === 'w' ? originalWall.start.x : 
                                         (originalWall.start.x + originalWall.end.x) / 2);
          const deltaY = snappedPoint.y - (resizeHandle === 's' ? originalWall.end.y : 
                                         resizeHandle === 'n' ? originalWall.start.y : 
                                         (originalWall.start.y + originalWall.end.y) / 2);
          
          // 🔧 CRITICAL DRAG PHASE ROOM MERGE FIX: Use complete floor plan walls for shared wall calculation
          // During drag, tempWalls may only contain ~4 walls from resizing room 
          // but room calculations need ALL floor plan walls to maintain accurate room counts during preview
          const currentRooms = memoizedCalculateRooms(
            (floorPlans[currentFloor] || []).map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor})), 
            roomCustomLabels, 
            'geometryUpdate', 
            currentFloor
          );
          
                     // Apply shared wall movement effect
           // 🔧 ENHANCED: Use the exact same coordinate key generation as shared wall system
           const draggedWallCoordinateKey = getWallKey(draggedWall);
           const sharedWallResult = calculateSharedWallMovementEffect(
             draggedWall.tempId || draggedWallCoordinateKey,
             snappedPoint, // new start (using target point for now)
             snappedPoint, // new end (using target point for now) 
             sharedWallData.sharedWallSystem,
             currentRooms
           );
          
          console.log('🔗 SHARED WALL MOVEMENT RESULT', {
            movementApplied: sharedWallResult.movementType === 'shared',
            affectedRooms: sharedWallResult.affectedRooms,
            newWallsCount: (tempWalls || currentWalls).length
          });
          
          if (sharedWallResult.movementType === 'shared') {
            // For shared walls, we need to update all affected rooms proportionally
            // This is where the professional CAD behavior happens
            
            const currentWallsArray = tempWalls || currentWalls;
            const updatedWalls = currentWallsArray.map(wall => {
              const wallKey = wall.tempId || `${wall.start.x},${wall.start.y}-${wall.end.x},${wall.end.y}`;
              
              // Check if this wall is affected by the shared wall movement
              const isAffectedWall = sharedWallResult.affectedRooms.some(roomId => 
                currentRooms.some(room => 
                  room.stableId === roomId && 
                  room.walls.some(roomWall => 
                    Math.abs(roomWall.start.x - wall.start.x) < 0.1 && 
                    Math.abs(roomWall.start.y - wall.start.y) < 0.1 &&
                    Math.abs(roomWall.end.x - wall.end.x) < 0.1 && 
                    Math.abs(roomWall.end.y - wall.end.y) < 0.1
                  )
                )
              );
              
              if (isAffectedWall) {
                // Apply proportional movement to affected walls
                const updatedWall = { ...wall };
                
                // If this is the dragged wall, move it to the target point
                if (wall.tempId === draggedWall.tempId) {
                  if (resizeHandle === 'e') {
                    updatedWall.end = { ...snappedPoint };
                  } else if (resizeHandle === 'w') {
                    updatedWall.start = { ...snappedPoint };
                  } else if (resizeHandle === 'n') {
                    updatedWall.start = { ...snappedPoint };
                  } else if (resizeHandle === 's') {
                    updatedWall.end = { ...snappedPoint };
                  }
                } else {
                  // For other walls in shared rooms, apply proportional movement
                  updatedWall.start = {
                    x: wall.start.x + (deltaX * 0.5), // Proportional movement
                    y: wall.start.y + (deltaY * 0.5)
                  };
                  updatedWall.end = {
                    x: wall.end.x + (deltaX * 0.5),
                    y: wall.end.y + (deltaY * 0.5)
                  };
                }
                
                return updatedWall;
              }
              
              return wall;
            });
            
            setTempWalls(updatedWalls);
            setActivelyResizedWalls(updatedWalls.filter(w => w.tempId === draggedWall.tempId).map(({tempId, ...w}) => w));
            
            // Update live dimensions for the primary room
            if (selectedRoom) {
              const selectedRoomWalls = updatedWalls.filter(wall => 
                selectedRoom.walls.some(roomWall => 
                  Math.abs(roomWall.start.x - wall.start.x) < 0.1 && 
                  Math.abs(roomWall.start.y - wall.start.y) < 0.1 &&
                  Math.abs(roomWall.end.x - wall.end.x) < 0.1 && 
                  Math.abs(roomWall.end.y - wall.end.y) < 0.1
                )
              ).map(preserveWallMetadata);
              
              if (selectedRoomWalls.length > 0) {
                const bounds = getRoomBounds(selectedRoomWalls);
                const widthPx = bounds.maxX - bounds.minX;
                const heightPx = bounds.maxY - bounds.minY;
                const widthM = Math.round((widthPx / PIXELS_PER_METER) * 100) / 100;
                const heightM = Math.round((heightPx / PIXELS_PER_METER) * 100) / 100;
                const areaM2 = widthM * heightM;
                setResizingRoomLiveDimensions({ width: widthM, height: heightM, area: areaM2 });
              }
            }
            
            return; // Skip all other processing
          }
        }
      }
      
      // 🚨 CRITICAL FIX: Check for extrusion width adjustment FIRST and completely skip standard logic
      if (isResizingExtrusionWidthRef.current) {
        onDebug('HANDLE_ROOM_RESIZE_EXTRUSION_WIDTH_DURING_DRAG', { 
          message: 'Extrusion width adjustment detected during drag - skipping ALL standard logic and going directly to specialized logic' 
        });
        // Skip ALL standard drag logic and go directly to the extrusion width adjustment logic below
        // This prevents the wall count from dropping from 7 to 3
        // Jump directly to the extrusion width logic without any standard processing
        // IMMEDIATELY SKIP TO EXTRUSION WIDTH LOGIC
        // Don't execute any standard resize calculation
      } else {
        // Only execute standard logic when NOT in extrusion width mode
      
      // Check if we're moving a split wall - if so, set extrusion flags
      // 🔧 ENHANCED: Detect split wall movement that needs connector adjustment
      const draggedWall = draggedWallSegmentRef.current as WallWithTempId;
      const isSplitWallMovement = draggedWall && (draggedWall as any).isSplitOrigin && !(draggedWall as any).isMainExtrusionSegment;
      
      // 🔧 NEW: Also check if we're in extrusion mode due to existing connectors
      const isExtrusionModeForConnectors = isExtrudingRoomSegmentRef.current && draggedWall && (draggedWall as any).isSplitOrigin;
      
      // 🚨 DIRECT DEBUG: Log split wall detection
                console.log('🚨 [DIRECT] SPLIT_WALL_DETECTION', {
            draggedWallTempId: draggedWall?.tempId,
            isSplitOrigin: (draggedWall as any)?.isSplitOrigin,
            isMainExtrusionSegment: (draggedWall as any)?.isMainExtrusionSegment,
            isSplitWallMovement,
            isExtrusionModeForConnectors,
            isExtrudingRoomSegment: isExtrudingRoomSegmentRef.current,
            willTriggerExtrusion: isSplitWallMovement || isExtrusionModeForConnectors,
            timestamp: Date.now()
          });
      
      if (isSplitWallMovement || isExtrusionModeForConnectors) {
          // Optimized throttling for smooth snapping - prioritize immediate updates for small movements
          const now = Date.now();
          const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
          
          // Immediate update for first movement or significant position changes
          const shouldUpdate = !lastUpdateTimeRef.current || timeSinceLastUpdate > 8; // Reduced to 8ms for smoother snapping
          
          // Reduce debug logging frequency during active dragging for performance
                      if (shouldUpdate) {
              onDebug('ROOM_RESIZE_SPLIT_WALL_EXTRUSION_DURING_DRAG', { 
                  draggedWallId: draggedWall.tempId, 
                  targetPoint: snappedPoint,
                  message: isExtrusionModeForConnectors 
                      ? 'Split wall with existing connectors detected during drag - adjusting connectors'
                      : 'Split wall movement detected during drag - using optimized extrusion preview',
                  isExtrusionModeForConnectors,
                  isExtrudingRoomSegment: isExtrudingRoomSegmentRef.current
              });
            }
          
          // 🔧 SIMPLIFIED RULE: Respect the decision made in touch/mouse handlers
          // Don't force extrusion mode here - use whatever was decided at touch start
          // isExtrudingRoomSegmentRef.current = true; // REMOVED - respects simplified rule
          // isResizingExtrusionWidthRef.current = false; // REMOVED - respects simplified rule
          
          // For split wall extrusions during drag, use a simplified preview calculation
          // instead of the expensive full extrusion calculation
          
          // 🚨 DIRECT DEBUG: About to call createSimplifiedExtrusionPreview
          console.log('🚨 [DIRECT] ABOUT_TO_CALL_createSimplifiedExtrusionPreview', {
            draggedWallTempId: draggedWall.tempId,
            selectedRoomWallsCount: selectedRoom.walls.length,
            snappedPoint: `(${snappedPoint.x.toFixed(1)},${snappedPoint.y.toFixed(1)})`,
            resizeHandle,
            timestamp: Date.now()
          });
          
          // ⚡ CRITICAL FIX: Include tempWalls in search scope to detect connectors from previous drags
          const wallsForConnectorSearch = tempWalls && tempWalls.length > 0 
            ? [...currentWalls, ...tempWalls] 
            : currentWalls;
            
          const simplifiedExtrusionPreview = createSimplifiedExtrusionPreview(
            selectedRoom.walls.map(w => ({...w, floor: w.floor || currentFloor})),
            draggedWall,
            snappedPoint,
            resizeHandle!,
            wallsForConnectorSearch // ⚡ FIX: Pass combined walls to detect existing connectors
          );
          
          if (simplifiedExtrusionPreview && shouldUpdate) {
            lastUpdateTimeRef.current = now;
            
            // 🛡️ PRESERVE METADATA: Get original walls for metadata preservation during resize
            const originalWallsForPreservation = selectedRoom.walls;
            
            const newlyResizedWallsWithTempIds: WallWithTempId[] = simplifiedExtrusionPreview.map((wall, index) => {
              const originalTempId = activeResizeOriginalWallTempIdsRef.current![index];
              let wallWithTempId: WallWithTempId;
              
              if (!originalTempId) {
                // 🔧 CRITICAL FIX: This is likely a connector wall created by createSimplifiedExtrusionPreview
                // Generate a proper connector wall temp ID and preserve its metadata
                const isConnectorWall = (wall as any).isExtrusionConnector;
                const connectorTempId = isConnectorWall 
                  ? `extrusion-connector-${Date.now()}-${index}`
                  : `temp-${Date.now()}-${index}`;
                
                console.log('🔧 CONNECTOR_WALL_TEMP_ID_ASSIGNMENT:', {
                  wallIndex: index,
                  isConnectorWall,
                  connectorTempId,
                  hasOwnership: !!((wall as any).originatingRoomId || (wall as any).assignedRoomIds),
                  wallCoords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`
                });
                
                wallWithTempId = { 
                  ...wall, 
                  tempId: connectorTempId, 
                  floor: currentFloor,
                  // 🔧 CRITICAL: Preserve all connector wall metadata
                  ...(isConnectorWall && {
                    isExtrusionConnector: true,
                    parentWallId: (wall as any).parentWallId,
                    originatingRoomId: (wall as any).originatingRoomId,
                    assignedRoomIds: (wall as any).assignedRoomIds,
                    parentRoomIds: (wall as any).parentRoomIds
                  })
                };
              } else {
                wallWithTempId = { ...wall, tempId: originalTempId, floor: currentFloor };
              }
              
              // 🛡️ CRITICAL: Preserve wall metadata from original wall during resize
              const originalWall = originalWallsForPreservation.find(orig => 
                (orig as any).tempId === originalTempId ||
                originalWallsForPreservation[index] // fallback to index matching
              );
              
              if (originalWall) {
                return preserveWallMetadataForResize(wallWithTempId, originalWall);
              } else {
                console.warn('⚠️ NO_ORIGINAL_WALL_FOR_METADATA_PRESERVATION', {
                  wallIndex: index,
                  tempId: originalTempId,
                  wallCoords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`
                });
                return wallWithTempId;
              }
            });
            
            // 🔧 FIX: Use tempWalls (current state with extrusions) instead of currentWalls for multiple resizes
            const wallsToFilterFrom = tempWalls || currentWalls;
            const otherWalls = wallsToFilterFrom.filter(wall => 
              !activeResizeOriginalWallTempIdsRef.current?.includes(wall.tempId!)
            );
            
            setTempWalls([...otherWalls, ...newlyResizedWallsWithTempIds]);
            setActivelyResizedWalls(simplifiedExtrusionPreview);
            
            // Update live dimensions for the preview
            const bounds = getRoomBounds(simplifiedExtrusionPreview);
            const widthPx = bounds.maxX - bounds.minX;
            const heightPx = bounds.maxY - bounds.minY;
            const widthM = Math.round((widthPx / PIXELS_PER_METER) * 100) / 100;
            const heightM = Math.round((heightPx / PIXELS_PER_METER) * 100) / 100;
            const areaM2 = widthM * heightM;
            setResizingRoomLiveDimensions({ width: widthM, height: heightM, area: areaM2 });
          }
          
          return; // Skip all other processing for split wall extrusions
      }
      
      // Standard resize calculation for non-split walls
      const plainDraggedWall = (({ tempId, floor, ...rest }) => ({
        ...rest, 
        floor: floor || currentFloor,
        // Preserve split wall metadata
        ...(rest.isSplitOrigin && { isSplitOrigin: rest.isSplitOrigin }),
        ...(rest.parentWallId && { parentWallId: rest.parentWallId }),
        ...(rest.parentRoomIds && { parentRoomIds: rest.parentRoomIds }),
        ...(rest.requiresExtrusionOnMove && { requiresExtrusionOnMove: rest.requiresExtrusionOnMove }),
        ...(rest.isMainExtrusionSegment && { isMainExtrusionSegment: rest.isMainExtrusionSegment }),
        ...(rest.isExtrusionConnector && { isExtrusionConnector: rest.isExtrusionConnector })
      }))(draggedWallSegmentRef.current as WallWithTempId);
      
      // 🛡️ COLLISION DETECTION: Prevent room overlaps during resize
      let constrainedPoint = snappedPoint;
      
      // Get all rooms for collision detection
      const currentRoomsForCollision = (() => {
        try {
          return memoizedCalculateRooms(
            // 🔧 CRITICAL FIX: Preserve ALL wall metadata (especially room ownership for extrusion connectors)
            [...(tempWalls || currentWalls)].map(w => preserveWallMetadata(w)),
            roomCustomLabels,
            'geometryUpdate',
            currentFloor
          );
        } catch (error) {
          console.warn('Failed to calculate rooms for collision detection:', error);
          return [];
        }
      })();
      
      // 🎯 IMMEDIATE SNAP DETECTION: Check for immediate snapping without throttling
      let hasImmediateSnap = false;
      
      if (currentRoomsForCollision.length > 1) {
        try {
          const collisionResult = detectRoomResizeCollision(
            selectedRoom,
            resizeHandle!,
            plainDraggedWall,
            snappedPoint,
            currentRoomsForCollision,
            0 // Allow rooms to touch each other
          );
          
          // Check if this is a snap vs collision
          if (collisionResult.hasCollision) {
            const snapDistance = Math.hypot(
              snappedPoint.x - collisionResult.constrainedPoint.x,
              snappedPoint.y - collisionResult.constrainedPoint.y
            );
            
            // If the constraint point is very close, treat it as a snap
            if (snapDistance <= 25) { // Within our snap zone
              hasImmediateSnap = true;
              constrainedPoint = collisionResult.constrainedPoint;
              onDebug('IMMEDIATE_SNAP_DETECTED', {
                originalPoint: snappedPoint,
                snapPoint: collisionResult.constrainedPoint,
                snapDistance: snapDistance.toFixed(1)
              });
            } else {
              // Regular collision constraint
              constrainedPoint = collisionResult.constrainedPoint;
              onDebug('COLLISION_CONSTRAINT_APPLIED', {
                originalPoint: snappedPoint,
                constrainedPoint,
                collidingRooms: collisionResult.collisionRooms.map(r => r.name),
                maxAllowedResize: collisionResult.maxAllowedResize
              });
            }
          }
        } catch (error) {
          console.warn('Collision detection failed, using original point:', error);
          // Fallback to original behavior if collision detection fails
        }
      }

      // ⚡ CRITICAL FIX: Include tempWalls and currentWalls for connector detection during wall splits
      const wallsForConnectorDetection = tempWalls && tempWalls.length > 0 
        ? [...currentWalls, ...tempWalls] 
        : currentWalls;

      // 🔒 ENHANCED WALL OWNERSHIP: Filter walls to only include those belonging to this room
      // This prevents coordinate collision issues where walls from other rooms get affected
      const safeWallArray = createSafeWallArrayForResize(selectedRoom, wallsForConnectorDetection);
      
      if (safeWallArray.warnings.length > 0) {
        console.warn('⚠️ WALL OWNERSHIP WARNINGS:', {
          roomId: selectedRoom.stableId,
          warnings: safeWallArray.warnings
        });
      }

      console.log('🔒 WALL OWNERSHIP FILTERING APPLIED:', {
        roomId: selectedRoom.stableId,
        originalWallCount: wallsForConnectorDetection.length,
        safeWallCount: safeWallArray.safeWalls.length,
        excludedWallCount: safeWallArray.excludedWalls.length
      });

      // 🛡️ ENHANCED RESIZE VALIDATION: Validate resize operation before execution
      const draggedWallForValidation = draggedWallSegmentRef.current;
      
      try {
        // Validate the resize operation to prevent coordinate collisions and disconnections
        const validation = validateWallResize(
          draggedWallForValidation,
          constrainedPoint, // Using constrained point as new start
          { x: draggedWallForValidation.end.x + (constrainedPoint.x - draggedWallForValidation.start.x), 
            y: draggedWallForValidation.end.y + (constrainedPoint.y - draggedWallForValidation.start.y) }, // Calculate new end
          selectedRoom.stableId || selectedRoom.id || 'unknown',
          currentWalls,
          [selectedRoom] // Pass current room for validation
        );

        if (!validation.canProceed) {
          console.warn('🚫 RESIZE OPERATION BLOCKED:', {
            roomId: selectedRoom.stableId,
            wallId: draggedWallForValidation.tempId,
            blockers: validation.blockers,
            point: constrainedPoint
          });
          // Don't proceed with resize if validation fails
          return;
        }

        if (validation.warnings.length > 0) {
          console.warn('⚠️ RESIZE WARNINGS:', {
            roomId: selectedRoom.stableId,
            wallId: draggedWallForValidation.tempId,
            warnings: validation.warnings
          });
        }

        console.log('✅ RESIZE VALIDATION PASSED:', {
          roomId: selectedRoom.stableId,
          wallId: draggedWallForValidation.tempId,
          affectedRooms: validation.affectedRooms,
          sharedWallsCount: validation.sharedWallsCount
        });

      } catch (error) {
        console.error('❌ RESIZE VALIDATION ERROR:', error);
        // Continue with standard resize as fallback
      }

      // 🔒 ENHANCED RESIZE: Use only walls that belong to this room to prevent coordinate collisions
      const roomSpecificWallsForResize = getResizeEligibleWalls(selectedRoom, safeWallArray.safeWalls);
      
      console.log('🔍 BEFORE_CALCULATE_RESIZED_WALLS:', {
        roomName: selectedRoom.name,
        roomWallsCount: selectedRoom.walls.length,
        roomSpecificWallsCount: roomSpecificWallsForResize.length,
        safeWallsCount: safeWallArray.safeWalls.length,
        roomSpecificWallIds: roomSpecificWallsForResize.map(w => w.tempId || 'NO_ID')
      });
      
      let newlyResizedPlainWalls = calculateResizedWalls(
        roomSpecificWallsForResize.map(w => ({...w, floor: w.floor || currentFloor})), 
        plainDraggedWall,   
        resizeHandle!, 
        constrainedPoint, // Use constrained point instead of snapped point
        false, // isFinalAdjustment
        false, // maintainSquareness
        undefined, // originalRoomAspectRatio
        // Only force standard resize for isSplitOrigin walls when room has existing extrusions
        // Never force it for requiresExtrusionOnMove walls as they need extrusion logic
        !isExtrudingRoomSegmentRef.current && !isResizingExtrusionWidthRef.current && !(plainDraggedWall as any).requiresExtrusionOnMove, // forceStandardResize
        roomSpecificWallsForResize // 🔒 ENHANCED: Use room-specific walls to prevent coordinate collisions
      );
      
      console.log('🔍 AFTER_CALCULATE_RESIZED_WALLS:', {
        roomName: selectedRoom.name,
        inputWallsCount: roomSpecificWallsForResize.length,
        outputWallsCount: newlyResizedPlainWalls.length,
        wallCountChange: newlyResizedPlainWalls.length - roomSpecificWallsForResize.length,
        outputWallIds: newlyResizedPlainWalls.map((w, i) => `${i}: ${(w as any).tempId || 'NO_ID'}`)
      });

      // 🔒 POST-RESIZE VALIDATION: Ensure only the correct room's walls were affected
      const postResizeValidation = validatePostResize(
        wallsForConnectorDetection,
        newlyResizedPlainWalls,
        selectedRoom.stableId || selectedRoom.id || 'unknown'
      );

      if (!postResizeValidation.isValid) {
        console.warn('⚠️ POST-RESIZE VALIDATION WARNING:', {
          roomId: selectedRoom.stableId,
          unexpectedChanges: postResizeValidation.unexpectedChanges.length,
          summary: postResizeValidation.summary
        });
      } else {
        console.log('✅ POST-RESIZE VALIDATION PASSED:', {
          roomId: selectedRoom.stableId,
          summary: postResizeValidation.summary
        });
      }

      // 🔒 OWNERSHIP-AWARE SHARED WALL SYNC: Synchronize walls while respecting room boundaries
      // This ensures only walls belonging to the target room are affected during resize
      const currentAllWalls = tempWalls || currentWalls;
      const ownershipAwareSyncResult = synchronizeSharedWallsWithOwnership(
        newlyResizedPlainWalls, 
        currentAllWalls, 
        selectedRoom.stableId || selectedRoom.id || 'unknown',
        0.1 // tolerance
      );

      // Validate that room boundaries were respected
      const boundaryValidation = validateSynchronizationRespectedBoundaries(
        ownershipAwareSyncResult,
        selectedRoom.stableId || selectedRoom.id || 'unknown'
      );

      if (!boundaryValidation.isValid) {
        console.warn('⚠️ SYNC BOUNDARY VIOLATION:', {
          roomId: selectedRoom.stableId,
          violations: boundaryValidation.violations,
          summary: boundaryValidation.summary
        });
      } else {
        console.log('✅ SYNC BOUNDARY VALIDATION PASSED:', {
          roomId: selectedRoom.stableId,
          coordinateCollisionsRespected: ownershipAwareSyncResult.coordinateCollisionsRespected,
          summary: boundaryValidation.summary
        });
      }

      const synchronizedAllWalls = ownershipAwareSyncResult.synchronizedWalls;
      
      // Update the wall list to include synchronized walls from other rooms
      if (synchronizedAllWalls.length !== currentAllWalls.length || 
          synchronizedAllWalls.some((wall, i) => 
            wall.start.x !== currentAllWalls[i]?.start.x || 
            wall.start.y !== currentAllWalls[i]?.start.y ||
            wall.end.x !== currentAllWalls[i]?.end.x ||
            wall.end.y !== currentAllWalls[i]?.end.y
          )) {
        console.log('🔗 SHARED WALL SYNC: Wall synchronization detected changes', {
          originalWallCount: currentAllWalls.length,
          synchronizedWallCount: synchronizedAllWalls.length,
          resizedRoomWallCount: newlyResizedPlainWalls.length
        });
        
        // Apply synchronized walls to prevent stagnant walls in connected rooms
        console.log('🔍 SET_TEMP_WALLS_SYNCHRONIZED:', {
          beforeCount: tempWalls?.length || 0,
          afterCount: synchronizedAllWalls.length,
          wallCountChange: synchronizedAllWalls.length - (tempWalls?.length || 0),
          synchronizedWallIds: synchronizedAllWalls.map(w => w.tempId || 'NO_ID').slice(0, 30)
        });
        setTempWalls(synchronizedAllWalls as WallWithTempId[]);
      }

      if (!activeResizeOriginalWallTempIdsRef.current) return;

      const newlyResizedWallsWithTempIds: WallWithTempId[] = newlyResizedPlainWalls.map((resizedWall, index) => {
        const originalTempId = activeResizeOriginalWallTempIdsRef.current![index]; 
        if (!originalTempId) {
          return { ...resizedWall, tempId: `temp-${Date.now()}-${index}`, floor: currentFloor };
        }
        
        // 🔧 FIX: Find original wall to preserve extrusion metadata
        const originalWall = (tempWalls || currentWalls).find(w => w.tempId === originalTempId);
        
        // Preserve extrusion metadata and ownership to keep walls draggable after resize
        const preservedMetadata = originalWall ? {
          isMainExtrusionSegment: (originalWall as any).isMainExtrusionSegment,
          isExtrusionConnector: (originalWall as any).isExtrusionConnector,
          originatingRoomId: (originalWall as any).originatingRoomId,
          assignedRoomIds: (originalWall as any).assignedRoomIds ? [...(originalWall as any).assignedRoomIds] : undefined,
          // Preserve other metadata that might be important
          requiresExtrusionOnMove: (originalWall as any).requiresExtrusionOnMove,
          parentWallId: (originalWall as any).parentWallId,
          parentRoomIds: (originalWall as any).parentRoomIds ? [...(originalWall as any).parentRoomIds] : undefined
        } : {};
        
        return { 
          ...resizedWall, 
          tempId: originalTempId, 
          floor: currentFloor,
          ...preservedMetadata
        };
      });
      
      // 🔧 FIX: Use tempWalls (current state with extrusions) instead of currentWalls for multiple resizes
      const wallsToFilterFrom = tempWalls || currentWalls;
      
      console.log('🔍 FILTERING_OTHER_WALLS:', {
        wallsToFilterFromCount: wallsToFilterFrom.length,
        activeResizeOriginalWallTempIdsCount: activeResizeOriginalWallTempIdsRef.current?.length || 0,
        activeResizeOriginalWallTempIds: activeResizeOriginalWallTempIdsRef.current || [],
        wallsToFilterFromIds: wallsToFilterFrom.map(w => w.tempId || 'NO_ID')
      });
      
      const otherWalls = wallsToFilterFrom.filter(wall => 
        !activeResizeOriginalWallTempIdsRef.current?.includes(wall.tempId!)
      );
      
      console.log('🔍 OTHER_WALLS_FILTERED:', {
        otherWallsCount: otherWalls.length,
        expectedCount: wallsToFilterFrom.length - (activeResizeOriginalWallTempIdsRef.current?.length || 0),
        difference: otherWalls.length - (wallsToFilterFrom.length - (activeResizeOriginalWallTempIdsRef.current?.length || 0))
      });
      
      // 🎯 IMMEDIATE UPDATE: Apply walls immediately if snap detected, otherwise use normal throttling
      if (hasImmediateSnap) {
        // Update immediately for smooth snapping
        const combinedWalls = [...otherWalls, ...newlyResizedWallsWithTempIds];
        console.log('🔍 SET_TEMP_WALLS_IMMEDIATE_SNAP:', {
          beforeCount: tempWalls?.length || 0,
          afterCount: combinedWalls.length,
          otherWallsCount: otherWalls.length,
          resizedWallsCount: newlyResizedWallsWithTempIds.length,
          wallCountChange: combinedWalls.length - (tempWalls?.length || 0)
        });
        setTempWalls(combinedWalls);
        setActivelyResizedWalls(newlyResizedPlainWalls);
        
        // Update last update time to prevent redundant updates
        if (lastUpdateTimeRef.current !== undefined) {
          lastUpdateTimeRef.current = Date.now();
        }
        
        onDebug('IMMEDIATE_SNAP_UPDATE_APPLIED', {
          wallsUpdated: newlyResizedWallsWithTempIds.length,
          snapPoint: constrainedPoint
        });
      } else {
        // Normal throttled update
        const combinedWalls = [...otherWalls, ...newlyResizedWallsWithTempIds];
        console.log('🔍 SET_TEMP_WALLS_NORMAL:', {
          beforeCount: tempWalls?.length || 0,
          afterCount: combinedWalls.length,
          otherWallsCount: otherWalls.length,
          resizedWallsCount: newlyResizedWallsWithTempIds.length,
          wallCountChange: combinedWalls.length - (tempWalls?.length || 0)
        });
        setTempWalls(combinedWalls);
        setActivelyResizedWalls(newlyResizedPlainWalls);
      }

      // Update live dimensions
      if (newlyResizedPlainWalls && newlyResizedPlainWalls.length > 0) {
        const bounds = getRoomBounds(newlyResizedPlainWalls);
        const widthPx = bounds.maxX - bounds.minX;
        const heightPx = bounds.maxY - bounds.minY;
        const widthM = Math.round((widthPx / PIXELS_PER_METER) * 100) / 100;
        const heightM = Math.round((heightPx / PIXELS_PER_METER) * 100) / 100;
        
        // 🔧 CRITICAL FIX: Use proper polygon-based area calculation instead of bounding box
        const areaM2 = calculateArea(newlyResizedPlainWalls);
        console.log('🔧 RESIZE AREA CALCULATION:', {
          boundingBoxArea: (widthM * heightM).toFixed(2),
          actualPolygonArea: areaM2.toFixed(2),
          wallCount: newlyResizedPlainWalls.length,
          method: 'polygon-based'
        });
        
        setResizingRoomLiveDimensions({ width: widthM, height: heightM, area: areaM2 });
      }
      
      return; // Skip the complex extrusion logic for standard resize cases
      } // End of else block for standard drag logic
    }

    // Check if we're moving a split wall - if so, always create extrusions
    // 🔧 ENHANCED: Detect split wall movement that needs connector adjustment
    const draggedWall = draggedWallSegmentRef.current as WallWithTempId;
    const isSplitWallMovement = draggedWall && (draggedWall as any).isSplitOrigin && !(draggedWall as any).isMainExtrusionSegment;
    
    // 🔧 NEW: Also check if we're in extrusion mode due to existing connectors
    const isExtrusionModeForConnectors = isExtrudingRoomSegmentRef.current && draggedWall && (draggedWall as any).isSplitOrigin;
    
    if (isSplitWallMovement || isExtrusionModeForConnectors) {
        onDebug('ROOM_RESIZE_SPLIT_WALL_EXTRUSION', { 
            draggedWallId: draggedWall.tempId, 
            targetPoint: snappedPoint,
            message: isExtrusionModeForConnectors 
                ? 'Split wall with existing connectors detected - adjusting connectors' 
                : 'Split wall movement detected - creating extrusion instead of angling connected walls',
            isSplitOrigin: (draggedWall as any).isSplitOrigin,
            isMainExtrusionSegment: (draggedWall as any).isMainExtrusionSegment,
            isExtrusionConnector: (draggedWall as any).isExtrusionConnector,
            isExtrusionModeForConnectors,
            isExtrudingRoomSegment: isExtrudingRoomSegmentRef.current
        });
        
        // 🔧 SIMPLIFIED RULE: Respect the decision made in touch/mouse handlers
        // Don't force extrusion mode here - use whatever was decided at touch start
        // The touch/mouse handlers already implement our simplified rule
        // isExtrudingRoomSegmentRef.current = true; // REMOVED - respects simplified rule  
        // isResizingExtrusionWidthRef.current = false; // REMOVED - respects simplified rule
    } else if (draggedWall && (draggedWall as any).isMainExtrusionSegment) {
        onDebug('ROOM_RESIZE_EXISTING_EXTRUSION_SEGMENT', { 
            draggedWallId: draggedWall.tempId, 
            targetPoint: snappedPoint,
            message: 'Main extrusion segment detected - updating existing extrusion'
        });
        // For existing main extrusion segments, maintain extrusion mode but don't force it
        // The logic should already be set from the wall type detection in touch/mouse handlers
    }
    
    if (isResizingExtrusionWidthRef.current) {
        onDebug('ROOM_RESIZE_EXTRUSION_WIDTH_ADJUSTMENT_ACTIVE', { draggedConnectorId: draggedWallSegmentRef.current.tempId, targetPoint: snappedPoint });
        const draggedConnector = draggedWallSegmentRef.current as WallWithTempId;
        
        if (!draggedConnector || !draggedConnector.isExtrusionConnector) {
            onDebug('EXTRUSION_WIDTH_ADJUST_ERROR', { message: "Dragged segment is not a connector for width adjustment."});
            setTempWalls(null); setActivelyResizedWalls(null); return;
        }

        // Find the extrusion parts related to this connector
        const originalExtrusionParts = (() => {
            let ms: WallWithTempId | null = null;
            const cons: WallWithTempId[] = [];
            // Use initialWallsForResizeRef if available and populated, otherwise currentWalls
            // This is for finding the *original structure* of the extrusion
            const wallsToSearch = initialWallsForResizeRef.current || currentWalls;
            
            console.log('🔧 EXTRUSION_PARTS_DEBUG: Searching for main segment', {
                draggedConnectorId: draggedConnector.tempId,
                draggedConnectorCoords: `(${draggedConnector.start.x.toFixed(1)},${draggedConnector.start.y.toFixed(1)})->(${draggedConnector.end.x.toFixed(1)},${draggedConnector.end.y.toFixed(1)})`,
                wallsToSearchCount: wallsToSearch.length,
                wallsBreakdown: wallsToSearch.map((w, i) => ({
                    index: i,
                    tempId: w.tempId,
                    coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
                    isMainExtrusionSegment: (w as any).isMainExtrusionSegment,
                    isExtrusionConnector: (w as any).isExtrusionConnector,
                    isSplitOrigin: (w as any).isSplitOrigin,
                    parentWallId: (w as any).parentWallId
                }))
            });

            // First try to find explicitly marked main extrusion segment
            for (const wall of wallsToSearch) {
                if (wall.isMainExtrusionSegment) {
                    // Check if this main segment is connected to the draggedConnector
                    const draggedConnectorInSearchList = wallsToSearch.find(w => w.tempId === draggedConnector.tempId);
                    if (draggedConnectorInSearchList && (
                        pointsAreEqual(wall.start, draggedConnectorInSearchList.start, POINT_MATCH_TOLERANCE) || pointsAreEqual(wall.end, draggedConnectorInSearchList.start, POINT_MATCH_TOLERANCE) ||
                        pointsAreEqual(wall.start, draggedConnectorInSearchList.end, POINT_MATCH_TOLERANCE) || pointsAreEqual(wall.end, draggedConnectorInSearchList.end, POINT_MATCH_TOLERANCE))
                    ) {
                        ms = wall;
                    }
                }
            }
            
            // 🔧 CRITICAL FIX: If no explicit main segment found, look for split walls that should be main segments
            if (!ms) {
                console.log('🔧 FALLBACK_MAIN_SEGMENT_SEARCH: No explicit main segment found, searching for split walls connected to connector');
                
                const draggedConnectorInSearchList = wallsToSearch.find(w => w.tempId === draggedConnector.tempId);
                if (draggedConnectorInSearchList) {
                    // Look for walls that:
                    // 1. Share an endpoint with the connector
                    // 2. Have the same parentWallId (indicating they're from the same split operation)
                    // 3. Are NOT connectors themselves
                    for (const wall of wallsToSearch) {
                        const isSameParent = (wall as any).parentWallId === (draggedConnectorInSearchList as any).parentWallId;
                        const isConnectedToConnector = (
                            pointsAreEqual(wall.start, draggedConnectorInSearchList.start, POINT_MATCH_TOLERANCE) || 
                            pointsAreEqual(wall.end, draggedConnectorInSearchList.start, POINT_MATCH_TOLERANCE) ||
                            pointsAreEqual(wall.start, draggedConnectorInSearchList.end, POINT_MATCH_TOLERANCE) || 
                            pointsAreEqual(wall.end, draggedConnectorInSearchList.end, POINT_MATCH_TOLERANCE)
                        );
                        const isNotConnector = !(wall as any).isExtrusionConnector;
                        
                        if (isSameParent && isConnectedToConnector && isNotConnector) {
                            console.log('🔧 FALLBACK_MAIN_SEGMENT_FOUND:', {
                                wallId: wall.tempId,
                                coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
                                parentWallId: (wall as any).parentWallId,
                                isSplitOrigin: (wall as any).isSplitOrigin
                            });
                            ms = wall;
                            // Mark it as main extrusion segment for future use
                            (ms as any).isMainExtrusionSegment = true;
                            break;
                        }
                    }
                }
            }
            if (ms) {
                // First, find explicit extrusion connectors
                for (const wall of wallsToSearch) {
                    if (wall.isExtrusionConnector) {
                        if (pointsAreEqual(wall.start, ms.start, POINT_MATCH_TOLERANCE) || pointsAreEqual(wall.end, ms.start, POINT_MATCH_TOLERANCE) ||
                            pointsAreEqual(wall.start, ms.end, POINT_MATCH_TOLERANCE) || pointsAreEqual(wall.end, ms.end, POINT_MATCH_TOLERANCE)) {
                            cons.push(wall);
                        }
                    }
                }
                
                // 🔧 CRITICAL FIX: If we don't have 2 connectors, look for regular walls that connect to main segment endpoints
                if (cons.length < 2) {
                    console.log('🔧 CONNECTOR_FALLBACK_SEARCH: Only found', cons.length, 'explicit connectors, searching for regular connecting walls');
                    
                    for (const wall of wallsToSearch) {
                        // Skip if already found as connector, skip if it's the main segment itself
                        if (cons.find(c => c.tempId === wall.tempId) || wall.tempId === ms.tempId) {
                            continue;
                        }
                        
                        // Check if this wall connects to either end of the main segment
                        const connectsToMainSegment = (
                            pointsAreEqual(wall.start, ms.start, POINT_MATCH_TOLERANCE) || 
                            pointsAreEqual(wall.end, ms.start, POINT_MATCH_TOLERANCE) ||
                            pointsAreEqual(wall.start, ms.end, POINT_MATCH_TOLERANCE) || 
                            pointsAreEqual(wall.end, ms.end, POINT_MATCH_TOLERANCE)
                        );
                        
                        if (connectsToMainSegment) {
                            console.log('🔧 FALLBACK_CONNECTOR_FOUND:', {
                                wallId: wall.tempId,
                                coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
                                connectsToMainStart: pointsAreEqual(wall.start, ms.start, POINT_MATCH_TOLERANCE) || pointsAreEqual(wall.end, ms.start, POINT_MATCH_TOLERANCE),
                                connectsToMainEnd: pointsAreEqual(wall.start, ms.end, POINT_MATCH_TOLERANCE) || pointsAreEqual(wall.end, ms.end, POINT_MATCH_TOLERANCE)
                            });
                            
                            // Temporarily mark it as a connector for width adjustment
                            (wall as any).isExtrusionConnector = true;
                            cons.push(wall);
                            
                            // If we have 2 connectors now, stop searching
                            if (cons.length >= 2) {
                                break;
                            }
                        }
                    }
                }
            }
            return { mainSegment: ms, connectors: cons };
        })();

        const mainSegment = originalExtrusionParts.mainSegment;
        console.log('🔧 MAIN_SEGMENT_SEARCH_RESULT:', {
            mainSegmentFound: !!mainSegment,
            mainSegmentId: mainSegment?.tempId,
            mainSegmentCoords: mainSegment ? `(${mainSegment.start.x.toFixed(1)},${mainSegment.start.y.toFixed(1)})->(${mainSegment.end.x.toFixed(1)},${mainSegment.end.y.toFixed(1)})` : 'none',
            connectorsFound: originalExtrusionParts.connectors.length,
            connectorIds: originalExtrusionParts.connectors.map(c => c.tempId),
            connectorsDetails: originalExtrusionParts.connectors.map(c => ({
                id: c.tempId,
                coords: `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`,
                isExplicitConnector: (c as any).isExtrusionConnector === true
            }))
        });
        
        if (!mainSegment) {
            onDebug('EXTRUSION_WIDTH_ADJUST_ERROR', { 
                message: "Could not find original main segment.", 
                draggedConnectorId: draggedConnector.tempId,
                searchedWallsCount: (initialWallsForResizeRef.current || currentWalls).length
            });
            setTempWalls(null); setActivelyResizedWalls(null); return;
        }

        console.log('🔧 CONNECTOR_COUNT_CHECK:', {
            minConnectors: 2,
            actualConnectors: originalExtrusionParts.connectors.length,
            willProceed: originalExtrusionParts.connectors.length >= 2,
            connectorsList: originalExtrusionParts.connectors.map(c => ({
                id: c.tempId,
                coords: `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`,
                isExplicitConnector: (c as any).isExtrusionConnector === true
            }))
        });
        
        if (originalExtrusionParts.connectors.length < 2) {
             onDebug('EXTRUSION_WIDTH_ADJUST_ERROR', { 
                message: `Need at least 2 connectors, found ${originalExtrusionParts.connectors.length}.`, 
                mainSegmentId: mainSegment.tempId,
                connectorsFound: originalExtrusionParts.connectors.map(c => ({
                    id: c.tempId,
                    coords: `(${c.start.x.toFixed(1)},${c.start.y.toFixed(1)})->(${c.end.x.toFixed(1)},${c.end.y.toFixed(1)})`
                }))
            });
             setTempWalls(null); setActivelyResizedWalls(null); return;
        }
        
        console.log('🎉 CONNECTOR_COUNT_CHECK_PASSED: Proceeding with width adjustment logic');

        const originalDraggedConnectorInstance = originalExtrusionParts.connectors.find(c => c.tempId === draggedConnector.tempId);
        const otherConnectors = originalExtrusionParts.connectors.filter(c => c.tempId !== draggedConnector.tempId);
        
        if (!originalDraggedConnectorInstance || otherConnectors.length === 0) {
            onDebug('EXTRUSION_WIDTH_ADJUST_ERROR', { 
                message: "Could not find connector instances for width adjustment.",
                draggedConnectorFound: !!originalDraggedConnectorInstance,
                otherConnectorsCount: otherConnectors.length
            });
            setTempWalls(null); setActivelyResizedWalls(null); return;
        }
        
        console.log('🔧 MULTIPLE_CONNECTOR_SUPPORT:', {
            draggedConnector: originalDraggedConnectorInstance.tempId,
            otherConnectorsCount: otherConnectors.length,
            otherConnectorIds: otherConnectors.map(c => c.tempId)
        });
        
        // Determine which end of the main segment to move based on which connector was dragged
        const draggedConnectsToStart = pointsAreEqual(originalDraggedConnectorInstance.start, mainSegment.start, POINT_MATCH_TOLERANCE) || 
                                       pointsAreEqual(originalDraggedConnectorInstance.end, mainSegment.start, POINT_MATCH_TOLERANCE);
        const draggedConnectsToEnd = pointsAreEqual(originalDraggedConnectorInstance.start, mainSegment.end, POINT_MATCH_TOLERANCE) || 
                                     pointsAreEqual(originalDraggedConnectorInstance.end, mainSegment.end, POINT_MATCH_TOLERANCE);

        if (!draggedConnectsToStart && !draggedConnectsToEnd) {
            onDebug('EXTRUSION_WIDTH_ADJUST_ERROR', { message: "Dragged connector does not connect to main segment." });
            setTempWalls(null); setActivelyResizedWalls(null); return;
        }

        // COMPLETELY REWRITTEN WIDTH ADJUSTMENT LOGIC
        let newMainSegment: WallWithTempId;
        let newDraggedConnector: WallWithTempId;
        let newOtherConnectors: WallWithTempId[];

        if (draggedConnectsToStart) {
            // Moving the start end of the main segment
            // For width adjustment, project the snapped point to maintain the main segment's orientation
            const mainSegmentVector = {
                x: mainSegment.end.x - mainSegment.start.x,
                y: mainSegment.end.y - mainSegment.start.y
            };
            const mainSegmentLength = Math.sqrt(mainSegmentVector.x ** 2 + mainSegmentVector.y ** 2);
            const mainSegmentUnit = {
                x: mainSegmentVector.x / mainSegmentLength,
                y: mainSegmentVector.y / mainSegmentLength
            };
            
            // Calculate how far to move along the main segment's direction
            const deltaFromOriginal = {
                x: snappedPoint.x - mainSegment.start.x,
                y: snappedPoint.y - mainSegment.start.y
            };
            const projectedDistance = deltaFromOriginal.x * mainSegmentUnit.x + deltaFromOriginal.y * mainSegmentUnit.y;
            
            // Calculate the new start point by moving along the main segment's direction
            const newStartPoint = {
                x: mainSegment.start.x + projectedDistance * mainSegmentUnit.x,
                y: mainSegment.start.y + projectedDistance * mainSegmentUnit.y
            };
            
            newMainSegment = {
                ...mainSegment,
                start: { ...newStartPoint },
                end: { ...mainSegment.end }
            };

            // Calculate how much the connection point moved
            const deltaX = newStartPoint.x - mainSegment.start.x;
            const deltaY = newStartPoint.y - mainSegment.start.y;
            
            // Update dragged connector by translating it to maintain its orientation
            newDraggedConnector = {
                ...originalDraggedConnectorInstance,
                start: {
                    x: originalDraggedConnectorInstance.start.x + deltaX,
                    y: originalDraggedConnectorInstance.start.y + deltaY
                },
                end: {
                    x: originalDraggedConnectorInstance.end.x + deltaX,
                    y: originalDraggedConnectorInstance.end.y + deltaY
                }
            };

            // Other connectors don't move since their connection point (end) didn't move
            newOtherConnectors = otherConnectors.map(connector => ({
                ...connector
            }));
        } else {
            // Moving the end of the main segment
            // For width adjustment, project the snapped point to maintain the main segment's orientation
            const mainSegmentVector = {
                x: mainSegment.end.x - mainSegment.start.x,
                y: mainSegment.end.y - mainSegment.start.y
            };
            const mainSegmentLength = Math.sqrt(mainSegmentVector.x ** 2 + mainSegmentVector.y ** 2);
            const mainSegmentUnit = {
                x: mainSegmentVector.x / mainSegmentLength,
                y: mainSegmentVector.y / mainSegmentLength
            };
            
            // Calculate how far to move along the main segment's direction
            const deltaFromOriginal = {
                x: snappedPoint.x - mainSegment.end.x,
                y: snappedPoint.y - mainSegment.end.y
            };
            const projectedDistance = deltaFromOriginal.x * mainSegmentUnit.x + deltaFromOriginal.y * mainSegmentUnit.y;
            
            // Calculate the new end point by moving along the main segment's direction
            const newEndPoint = {
                x: mainSegment.end.x + projectedDistance * mainSegmentUnit.x,
                y: mainSegment.end.y + projectedDistance * mainSegmentUnit.y
            };
            
            newMainSegment = {
                ...mainSegment,
                start: { ...mainSegment.start },
                end: { ...newEndPoint }
            };
            
            // Calculate how much the connection point moved
            const deltaX = newEndPoint.x - mainSegment.end.x;
            const deltaY = newEndPoint.y - mainSegment.end.y;
            
            // Update dragged connector by translating it to maintain its orientation
            newDraggedConnector = {
                ...originalDraggedConnectorInstance,
                start: {
                    x: originalDraggedConnectorInstance.start.x + deltaX,
                    y: originalDraggedConnectorInstance.start.y + deltaY
                },
                end: {
                    x: originalDraggedConnectorInstance.end.x + deltaX,
                    y: originalDraggedConnectorInstance.end.y + deltaY
                }
            };

            // Other connectors don't move since their connection point (start) didn't move
            newOtherConnectors = otherConnectors.map(connector => ({
                ...connector
            }));
        }

        onDebug('[ExtrusionWidthAdjust] Simplified width adjustment', { 
            draggedConnectsToStart,
            draggedConnectsToEnd,
            snappedPoint,
            originalMainSegmentLength: Math.hypot(mainSegment.end.x - mainSegment.start.x, mainSegment.end.y - mainSegment.start.y),
            newMainSegmentLength: Math.hypot(newMainSegment.end.x - newMainSegment.start.x, newMainSegment.end.y - newMainSegment.start.y)
        });

        // Add detailed debugging for the updated connectors
        onDebug('[ExtrusionWidthAdjust] Updated connectors', {
            originalDraggedConnector: { 
                tempId: originalDraggedConnectorInstance.tempId,
                start: originalDraggedConnectorInstance.start, 
                end: originalDraggedConnectorInstance.end 
            },
            newDraggedConnector: { 
                tempId: newDraggedConnector.tempId,
                start: newDraggedConnector.start, 
                end: newDraggedConnector.end 
            },
            otherConnectorsCount: otherConnectors.length,
            originalOtherConnectors: otherConnectors.map(c => ({ 
                tempId: c.tempId,
                start: c.start, 
                end: c.end 
            })),
            newOtherConnectors: newOtherConnectors.map(c => ({ 
                tempId: c.tempId,
                start: c.start, 
                end: c.end 
            }))
        });

        // ADD DETAILED COORDINATE DEBUGGING
        onDebug('[ExtrusionWidthAdjust] COORDINATE_DETAILS', {
            snappedPoint: { x: snappedPoint.x, y: snappedPoint.y },
            originalMainSegment: {
                start: { x: mainSegment.start.x, y: mainSegment.start.y },
                end: { x: mainSegment.end.x, y: mainSegment.end.y }
            },
            newMainSegment: {
                start: { x: newMainSegment.start.x, y: newMainSegment.start.y },
                end: { x: newMainSegment.end.x, y: newMainSegment.end.y }
            },
            originalDraggedConnector: {
                start: { x: originalDraggedConnectorInstance.start.x, y: originalDraggedConnectorInstance.start.y },
                end: { x: originalDraggedConnectorInstance.end.x, y: originalDraggedConnectorInstance.end.y }
            },
            newDraggedConnector: {
                start: { x: newDraggedConnector.start.x, y: newDraggedConnector.start.y },
                end: { x: newDraggedConnector.end.x, y: newDraggedConnector.end.y }
            },
            originalOtherConnectors: otherConnectors.map(c => ({
                start: { x: c.start.x, y: c.start.y },
                end: { x: c.end.x, y: c.end.y }
            })),
            newOtherConnectors: newOtherConnectors.map(c => ({
                start: { x: c.start.x, y: c.start.y },
                end: { x: c.end.x, y: c.end.y }
            })),
            connectionsCheck: {
                draggedConnectsToStart,
                draggedConnectsToEnd,
                draggedOriginalConnectionPoint: draggedConnectsToStart ? mainSegment.start : mainSegment.end,
                newConnectionPoint: snappedPoint
            }
        });

        // ADD EXPLICIT COORDINATE DEBUGGING WITH STRING FORMATTING
        onDebug('[ExtrusionWidthAdjust] COORDINATE_VALUES_EXPLICIT', {
            snappedPoint: `(${snappedPoint.x.toFixed(2)}, ${snappedPoint.y.toFixed(2)})`,
            
            originalMainSegment: `Start(${mainSegment.start.x.toFixed(2)}, ${mainSegment.start.y.toFixed(2)}) -> End(${mainSegment.end.x.toFixed(2)}, ${mainSegment.end.y.toFixed(2)})`,
            newMainSegment: `Start(${newMainSegment.start.x.toFixed(2)}, ${newMainSegment.start.y.toFixed(2)}) -> End(${newMainSegment.end.x.toFixed(2)}, ${newMainSegment.end.y.toFixed(2)})`,
            
            originalDraggedConnector: `Start(${originalDraggedConnectorInstance.start.x.toFixed(2)}, ${originalDraggedConnectorInstance.start.y.toFixed(2)}) -> End(${originalDraggedConnectorInstance.end.x.toFixed(2)}, ${originalDraggedConnectorInstance.end.y.toFixed(2)})`,
            newDraggedConnector: `Start(${newDraggedConnector.start.x.toFixed(2)}, ${newDraggedConnector.start.y.toFixed(2)}) -> End(${newDraggedConnector.end.x.toFixed(2)}, ${newDraggedConnector.end.y.toFixed(2)})`,
            
            originalOtherConnectors: otherConnectors.map(c => `Start(${c.start.x.toFixed(2)}, ${c.start.y.toFixed(2)}) -> End(${c.end.x.toFixed(2)}, ${c.end.y.toFixed(2)})`),
            newOtherConnectors: newOtherConnectors.map(c => `Start(${c.start.x.toFixed(2)}, ${c.start.y.toFixed(2)}) -> End(${c.end.x.toFixed(2)}, ${c.end.y.toFixed(2)})`),
            
            movementAnalysis: {
                mainSegmentStartMoved: `${Math.abs(mainSegment.start.x - newMainSegment.start.x) > 0.1 || Math.abs(mainSegment.start.y - newMainSegment.start.y) > 0.1}`,
                mainSegmentEndMoved: `${Math.abs(mainSegment.end.x - newMainSegment.end.x) > 0.1 || Math.abs(mainSegment.end.y - newMainSegment.end.y) > 0.1}`,
                draggedConnectorMoved: `${Math.abs(originalDraggedConnectorInstance.start.x - newDraggedConnector.start.x) > 0.1 || Math.abs(originalDraggedConnectorInstance.start.y - newDraggedConnector.start.y) > 0.1 || Math.abs(originalDraggedConnectorInstance.end.x - newDraggedConnector.end.x) > 0.1 || Math.abs(originalDraggedConnectorInstance.end.y - newDraggedConnector.end.y) > 0.1}`,
                otherConnectorsMoved: otherConnectors.map((original, i) => {
                    const newConnector = newOtherConnectors[i];
                    return `${Math.abs(original.start.x - newConnector.start.x) > 0.1 || Math.abs(original.start.y - newConnector.start.y) > 0.1 || Math.abs(original.end.x - newConnector.end.x) > 0.1 || Math.abs(original.end.y - newConnector.end.y) > 0.1}`;
                })
            }
        });

        // 🔧 CRITICAL FIX: Ensure we maintain exact wall count during extrusion width resize
        // First, identify which walls we're replacing to avoid double-filtering or missing walls
        const wallsToReplace = new Set([
            originalDraggedConnectorInstance.tempId,
            ...otherConnectors.map(c => c.tempId),
            mainSegment.tempId
        ]);

        // Filter out the original walls that we're replacing
        const otherWalls = currentWalls.filter(w => !wallsToReplace.has(w.tempId!));

        // 🔧 CRITICAL FIX: Instead of filtering out zero-length walls, fix them to maintain wall count integrity
        const updatedExtrusionWalls = [newMainSegment, newDraggedConnector, ...newOtherConnectors].map(wall => {
            // Validate wall exists and has valid coordinates
            if (!wall || !wall.start || !wall.end || 
                typeof wall.start.x !== 'number' || typeof wall.start.y !== 'number' ||
                typeof wall.end.x !== 'number' || typeof wall.end.y !== 'number') {
                
                onDebug('EXTRUSION_WIDTH_ADJUST_WALL_INVALID', {
                    wallId: wall?.tempId || 'unknown',
                    reason: 'Invalid coordinates - using fallback wall',
                    wall: wall
                });
                
                // Return a minimal valid wall as fallback to prevent wall count reduction
                return {
                    tempId: wall?.tempId || `fallback-${Date.now()}`,
                    start: { x: 0, y: 0 },
                    end: { x: 1, y: 0 },
                    type: 'None',
                    floor: currentFloor
                } as WallWithTempId;
            }
            
            // Check if wall is zero-length and fix it
            if (pointsAreEqual(wall.start, wall.end, 0.1)) {
                onDebug('EXTRUSION_WIDTH_ADJUST_ZERO_LENGTH_WALL_FIXED', {
                    wallId: wall.tempId,
                    originalStart: wall.start,
                    originalEnd: wall.end,
                    reason: 'Zero-length wall fixed to maintain room boundary integrity'
                });
                
                // Fix zero-length wall by extending it slightly in a direction
                // This maintains the wall count while preserving room boundary
                const fixedEnd = {
                    x: wall.start.x + 1, // Extend 1 unit to the right
                    y: wall.start.y
                };
                
                return {
                    ...wall,
                    end: fixedEnd
                };
            }
            
            return wall;
        });

        const allUpdatedWalls = [...otherWalls, ...updatedExtrusionWalls];

        // 🔧 CRITICAL: Verify wall count integrity
        onDebug('EXTRUSION_WIDTH_WALL_COUNT_VERIFICATION', {
            originalWallCount: currentWalls.length,
            wallsToReplaceCount: wallsToReplace.size,
            otherWallsCount: otherWalls.length,
            updatedExtrusionWallsCount: updatedExtrusionWalls.length,
            finalWallCount: allUpdatedWalls.length,
            expectedFinalCount: currentWalls.length, // Should be same as original
            wallCountIntact: allUpdatedWalls.length === currentWalls.length,
            wallsToReplaceIds: Array.from(wallsToReplace),
            updatedExtrusionWallIds: updatedExtrusionWalls.map(w => w.tempId)
        });

        // 🚨 SAFETY CHECK: If wall count doesn't match, abort the resize to prevent room breakdown
        if (allUpdatedWalls.length !== currentWalls.length) {
            onDebug('EXTRUSION_WIDTH_ADJUST_WALL_COUNT_MISMATCH', {
                message: 'Wall count mismatch detected - aborting resize to prevent room breakdown',
                expected: currentWalls.length,
                actual: allUpdatedWalls.length,
                missing: currentWalls.length - allUpdatedWalls.length
            });
            
            // Revert to safe state
            setTempWalls(null);
            setActivelyResizedWalls(null);
            return;
        }

        // Add debugging for what walls are being set
        onDebug('[ExtrusionWidthAdjust] WALL_UPDATES_DETAILED', {
            totalOriginalWalls: currentWalls.length,
            totalUpdatedWalls: allUpdatedWalls.length,
            excludedWallCount: 3, // main + 2 connectors
            otherWallsCount: otherWalls.length,
            originalMainSegment: { 
                tempId: mainSegment.tempId,
                start: mainSegment.start, 
                end: mainSegment.end 
            },
            updatedMainSegment: { 
                tempId: newMainSegment.tempId,
                start: newMainSegment.start, 
                end: newMainSegment.end 
            },
            wallMovement: {
                mainSegmentMoved: !pointsAreEqual(mainSegment.start, newMainSegment.start, 0.1) || !pointsAreEqual(mainSegment.end, newMainSegment.end, 0.1),
                draggedConnectorMoved: !pointsAreEqual(originalDraggedConnectorInstance.start, newDraggedConnector.start, 0.1) || !pointsAreEqual(originalDraggedConnectorInstance.end, newDraggedConnector.end, 0.1),
                otherConnectorsMoved: otherConnectors.some((original, i) => {
                    const newConnector = newOtherConnectors[i];
                    return !pointsAreEqual(original.start, newConnector.start, 0.1) || !pointsAreEqual(original.end, newConnector.end, 0.1);
                })
            }
        });

        onDebug('[ExtrusionWidthAdjust] FINAL WALLS UPDATE', {
            totalWalls: allUpdatedWalls.length,
            updatedWallIds: [newMainSegment.tempId, newDraggedConnector.tempId, ...newOtherConnectors.map(c => c.tempId)],
            mainSegmentFinal: { start: newMainSegment.start, end: newMainSegment.end },
            draggedConnectorFinal: { start: newDraggedConnector.start, end: newDraggedConnector.end },
            otherConnectorsFinal: newOtherConnectors.map(c => ({ start: c.start, end: c.end }))
        });

        // 🔧 NEW: Update connection points of original room walls
        const wallsWithUpdatedConnections = allUpdatedWalls.map(wall => {
            // Skip the extrusion walls we already updated
            const isExtrusionWall = wall.tempId === newMainSegment.tempId || 
                                   wall.tempId === newDraggedConnector.tempId || 
                                   newOtherConnectors.some(c => c.tempId === wall.tempId);
            if (isExtrusionWall) {
                return wall;
            }

            let updatedWall = { ...wall };
            let wasUpdated = false;

            // Check if this wall connects to the old dragged connector position
            const oldDraggedStart = originalDraggedConnectorInstance.start;
            const oldDraggedEnd = originalDraggedConnectorInstance.end;
            const newDraggedStart = newDraggedConnector.start;
            const newDraggedEnd = newDraggedConnector.end;

            // Update wall endpoints that match old connector positions
            if (Math.abs(wall.start.x - oldDraggedStart.x) < 0.1 && Math.abs(wall.start.y - oldDraggedStart.y) < 0.1) {
                updatedWall.start = { ...newDraggedStart };
                wasUpdated = true;
            }
            if (Math.abs(wall.end.x - oldDraggedStart.x) < 0.1 && Math.abs(wall.end.y - oldDraggedStart.y) < 0.1) {
                updatedWall.end = { ...newDraggedStart };
                wasUpdated = true;
            }
            if (Math.abs(wall.start.x - oldDraggedEnd.x) < 0.1 && Math.abs(wall.start.y - oldDraggedEnd.y) < 0.1) {
                updatedWall.start = { ...newDraggedEnd };
                wasUpdated = true;
            }
            if (Math.abs(wall.end.x - oldDraggedEnd.x) < 0.1 && Math.abs(wall.end.y - oldDraggedEnd.y) < 0.1) {
                updatedWall.end = { ...newDraggedEnd };
                wasUpdated = true;
            }

            if (wasUpdated) {
                onDebug('[ExtrusionWidthAdjust] Updated connection point', {
                    wallId: wall.tempId,
                    oldWall: wall,
                    newWall: updatedWall
                });
            }

            return updatedWall;
        });

        onDebug('[ExtrusionWidthAdjust] ABOUT_TO_SET_TEMP_WALLS', {
            tempWallsCount: wallsWithUpdatedConnections.length,
            activelyResizedWallsCount: 3,
            tempWallsPreview: wallsWithUpdatedConnections.slice(0, 5)
        });

        setTempWalls(wallsWithUpdatedConnections);
        setActivelyResizedWalls([newMainSegment, newDraggedConnector, ...newOtherConnectors]);

        // Add debugging right after setting temp walls
        onDebug('[ExtrusionWidthAdjust] TEMP_WALLS_SET_COMPLETE', {
            message: 'setTempWalls and setActivelyResizedWalls called',
            expectsVisualUpdate: true
        });

        // Add this right after the existing TEMP_WALLS_SET_COMPLETE debug log
        onDebug('[ExtrusionWidthAdjust] VISUAL_DEBUG_CHECK', {
            tempWallsLength: allUpdatedWalls.length,
            activelyResizedWallsLength: [newMainSegment, newDraggedConnector, ...newOtherConnectors].length,
            mainSegmentInTempWalls: allUpdatedWalls.find(w => w.tempId === newMainSegment.tempId),
            draggedConnectorInTempWalls: allUpdatedWalls.find(w => w.tempId === newDraggedConnector.tempId),
            otherConnectorsInTempWalls: newOtherConnectors.map(c => allUpdatedWalls.find(w => w.tempId === c.tempId)),
            currentWallsLength: currentWalls.length,
            tempWallsStateExists: tempWalls !== null,
            activelyResizedWallsStateExists: activelyResizedWalls !== null
        });

    } else if (isExtrudingRoomSegmentRef.current) {
        const originalSegment = draggedWallSegmentRef.current as WallWithTempId; 
        if (!originalSegment) {
            onDebug('EXTRUSION_RESIZE_ERROR', { message: "Original segment missing."});
            return;
        }

        // 🔧 DEBUG: Check wall flags to understand why first-time extrusion logic isn't running
        onDebug('🔧 WALL_FLAGS_DEBUG', {
            wallTempId: originalSegment.tempId,
            isMainExtrusionSegment: originalSegment.isMainExtrusionSegment,
            isSplitOrigin: (originalSegment as any).isSplitOrigin,
            isExtrusionConnector: originalSegment.isExtrusionConnector,
            parentWallId: (originalSegment as any).parentWallId,
            requiresExtrusionOnMove: (originalSegment as any).requiresExtrusionOnMove,
            wallCoords: `(${originalSegment.start.x.toFixed(1)},${originalSegment.start.y.toFixed(1)})->(${originalSegment.end.x.toFixed(1)},${originalSegment.end.y.toFixed(1)})`,
            willEnterFirstTimeLogic: originalSegment.isMainExtrusionSegment !== true
        });

        if (originalSegment.isMainExtrusionSegment === true) {
            // ---- RESIZING AN EXISTING EXTRUSION ----
            onDebug('ROOM_RESIZE_MODIFYING_EXISTING_EXTRUSION', { mainSegmentId: originalSegment.tempId });
            
            // Add this debug at the start
            debugConnectorWalls(currentWalls, 'BEFORE_EXTRUSION_RESIZE');

            const currentMainSegment = originalSegment;
            
            // Use the identifyConnectorWalls function we added
            const connectors = identifyConnectorWalls(currentWalls, currentMainSegment.tempId || '');

            if (connectors.length !== 2) {
                onDebug('MODIFY_EXTRUSION_ERROR', { 
                    message: "Could not find exactly 2 connector walls for main segment.", 
                    mainSegmentId: currentMainSegment.tempId, 
                    foundConnectorsCount: connectors.length,
                    foundConnectors: connectors.map(c => ({ 
                        tempId: c.tempId, 
                        start: c.start, 
                        end: c.end 
                    }))
                });
                setTempWalls(null); 
                setActivelyResizedWalls(null);
                return; 
            }
            
            const [connector1, connector2] = connectors;

            // Find which endpoints of the connectors are connected to the main segment
            const connector1MainConnection = pointsAreEqual(connector1.start, currentMainSegment.start, POINT_MATCH_TOLERANCE) || 
                                             pointsAreEqual(connector1.start, currentMainSegment.end, POINT_MATCH_TOLERANCE) 
                                             ? connector1.start : connector1.end;
            
            const connector2MainConnection = pointsAreEqual(connector2.start, currentMainSegment.start, POINT_MATCH_TOLERANCE) || 
                                             pointsAreEqual(connector2.start, currentMainSegment.end, POINT_MATCH_TOLERANCE) 
                                             ? connector2.start : connector2.end;

            // Find the base points (endpoints not connected to main segment)
            const basePoint1 = pointsAreEqual(connector1.start, connector1MainConnection, POINT_MATCH_TOLERANCE) 
                               ? connector1.end : connector1.start;
            const basePoint2 = pointsAreEqual(connector2.start, connector2MainConnection, POINT_MATCH_TOLERANCE) 
                               ? connector2.end : connector2.start;
            
            onDebug('CONNECTOR_ANALYSIS', {
                connector1: { 
                    tempId: connector1.tempId, 
                    start: connector1.start, 
                    end: connector1.end,
                    mainConnection: connector1MainConnection,
                    basePoint: basePoint1
                },
                connector2: { 
                    tempId: connector2.tempId, 
                    start: connector2.start, 
                    end: connector2.end,
                    mainConnection: connector2MainConnection,
                    basePoint: basePoint2
                }
            });
            
            const baseVector = { x: basePoint2.x - basePoint1.x, y: basePoint2.y - basePoint1.y };
            const baseLength = Math.hypot(baseVector.x, baseVector.y);

            if (baseLength < 0.01) {
                onDebug('MODIFY_EXTRUSION_ERROR', { 
                    message: "Base segment has near-zero length.", 
                    baseP1: basePoint1, 
                    baseP2: basePoint2 
                });
                 return;
            }
            
            const perpToBaseVector = { x: -baseVector.y / baseLength, y: baseVector.x / baseLength };
            const midPointBase = { x: (basePoint1.x + basePoint2.x) / 2, y: (basePoint1.y + basePoint2.y) / 2 };
            const dragVectorFromBaseMid = { x: point.x - midPointBase.x, y: point.y - midPointBase.y };
            const displacement = (dragVectorFromBaseMid.x * perpToBaseVector.x) + (dragVectorFromBaseMid.y * perpToBaseVector.y);

            const mainSegmentLengthCurrent = Math.hypot(currentMainSegment.end.x - currentMainSegment.start.x, currentMainSegment.end.y - currentMainSegment.start.y);
            const newMainSegDir = { x: baseVector.x / baseLength, y: baseVector.y / baseLength };

            const newMainMid_calculated = {
                x: midPointBase.x + perpToBaseVector.x * displacement,
                y: midPointBase.y + perpToBaseVector.y * displacement
            };
            
            const newMainMid_snapped = snapPointToGrid(newMainMid_calculated, CM_SNAP_SIZE);

            const newMainStart = {
                x: newMainMid_snapped.x - (newMainSegDir.x * mainSegmentLengthCurrent / 2),
                y: newMainMid_snapped.y - (newMainSegDir.y * mainSegmentLengthCurrent / 2)
            };
    
            const newMainEnd = {
                x: newMainMid_snapped.x + (newMainSegDir.x * mainSegmentLengthCurrent / 2),
                y: newMainMid_snapped.y + (newMainSegDir.y * mainSegmentLengthCurrent / 2)
            };

            const newMainSegment: WallWithTempId = {
                ...currentMainSegment, 
                start: newMainStart,
                end: newMainEnd,
            };
    
            // *** KEY FIX: Properly update connector endpoints to follow the main segment ***
            
            // Determine which end of the new main segment each connector should connect to
            const distConnector1ToNewStart = Math.hypot(connector1MainConnection.x - newMainStart.x, connector1MainConnection.y - newMainStart.y);
            const distConnector1ToNewEnd = Math.hypot(connector1MainConnection.x - newMainEnd.x, connector1MainConnection.y - newMainEnd.y);

            const connector1ShouldConnectToStart = distConnector1ToNewStart < distConnector1ToNewEnd;
            const connector1NewMainConnection = connector1ShouldConnectToStart ? newMainStart : newMainEnd;
            const connector2NewMainConnection = connector1ShouldConnectToStart ? newMainEnd : newMainStart;

            // Update connector 1
            const newConnector1: WallWithTempId = {
                ...connector1, 
                start: pointsAreEqual(connector1.start, connector1MainConnection, POINT_MATCH_TOLERANCE) 
                       ? connector1NewMainConnection : connector1.start,
                end: pointsAreEqual(connector1.end, connector1MainConnection, POINT_MATCH_TOLERANCE) 
                     ? connector1NewMainConnection : connector1.end,
            };

            // Update connector 2  
            const newConnector2: WallWithTempId = {
                ...connector2, 
                start: pointsAreEqual(connector2.start, connector2MainConnection, POINT_MATCH_TOLERANCE) 
                       ? connector2NewMainConnection : connector2.start,
                end: pointsAreEqual(connector2.end, connector2MainConnection, POINT_MATCH_TOLERANCE) 
                     ? connector2NewMainConnection : connector2.end,
            };

            onDebug('UPDATED_WALLS_DEBUG', {
                originalMainSegment: { start: currentMainSegment.start, end: currentMainSegment.end },
                newMainSegment: { start: newMainSegment.start, end: newMainSegment.end },
                originalConnector1: { start: connector1.start, end: connector1.end },
                newConnector1: { start: newConnector1.start, end: newConnector1.end },
                originalConnector2: { start: connector2.start, end: connector2.end },
                newConnector2: { start: newConnector2.start, end: newConnector2.end }
            });
            
            const wallsForModifiedExtrusion: WallWithTempId[] = [];
            wallsForModifiedExtrusion.push(newMainSegment);
            
            if (!pointsAreEqual(newConnector1.start, newConnector1.end, 0.1)) {
                wallsForModifiedExtrusion.push(newConnector1);
            }
            if (!pointsAreEqual(newConnector2.start, newConnector2.end, 0.1)) {
                wallsForModifiedExtrusion.push(newConnector2);
            }
    
            const otherWalls = currentWalls.filter(w => 
                w.tempId !== currentMainSegment.tempId &&
                w.tempId !== connector1.tempId &&
                w.tempId !== connector2.tempId
            );
            
            const finalTempWalls = [...otherWalls, ...wallsForModifiedExtrusion];
            
            // Add debug after creating updated walls
            debugConnectorWalls(finalTempWalls, 'AFTER_EXTRUSION_RESIZE');
            
            setTempWalls(finalTempWalls);
            setActivelyResizedWalls(wallsForModifiedExtrusion);
            
            onDebug('EXTRUSION_RESIZE_COMPLETE', {
                mainSegmentId: currentMainSegment.tempId,
                displacement,
                wallsCount: finalTempWalls.length,
                extrusionWallsCount: wallsForModifiedExtrusion.length
            });
        } else {
             // ---- This is a FIRST-TIME EXTRUSION from a splitOrigin segment ----
            onDebug('ROOM_RESIZE_EXTRUSION_ACTIVE (First Time)', { originalSegmentId: originalSegment.tempId, targetPoint: point });

            const segmentVector = { x: originalSegment.end.x - originalSegment.start.x, y: originalSegment.end.y - originalSegment.start.y };
            const segmentLength = Math.hypot(segmentVector.x, segmentVector.y);
    
            if (segmentLength === 0) {
                onDebug('EXTRUSION_RESIZE_ERROR', { message: "Original segment has zero length."});
                return;
            }
    
            const perpVector = { x: -segmentVector.y / segmentLength, y: segmentVector.x / segmentLength };
            const midPointOriginal = { x: (originalSegment.start.x + originalSegment.end.x) / 2, y: (originalSegment.start.y + originalSegment.end.y) / 2 };
            const dragVector = { x: point.x - midPointOriginal.x, y: point.y - midPointOriginal.y };
            const displacement = (dragVector.x * perpVector.x) + (dragVector.y * perpVector.y);
    
            const movedSegmentStartPx = originalSegment.start.x + perpVector.x * displacement;
            const movedSegmentStartPy = originalSegment.start.y + perpVector.y * displacement;
            const movedSegmentEndPx = originalSegment.end.x + perpVector.x * displacement;
            const movedSegmentEndPy = originalSegment.end.y + perpVector.y * displacement;
    
            const snappedMovedStart = snapToFineGridOrEndpointsUtil({x: movedSegmentStartPx, y: movedSegmentStartPy}, currentWalls, POINT_SNAP_THRESHOLD, CM_SNAP_SIZE);
            const snappedMovedEnd = snapToFineGridOrEndpointsUtil({x: movedSegmentEndPx, y: movedSegmentEndPy}, currentWalls, POINT_SNAP_THRESHOLD, CM_SNAP_SIZE);
    
            const newMovedSegment: WallWithTempId = {
                ...originalSegment, 
                start: snappedMovedStart,
                end: snappedMovedEnd,
                tempId: originalSegment.tempId, 
                isMainExtrusionSegment: true,
                // 🏠 PRESERVE ROOM OWNERSHIP: Inherit ownership from originalSegment
                originatingRoomId: (originalSegment as any).originatingRoomId,
                assignedRoomIds: (originalSegment as any).assignedRoomIds ? [...(originalSegment as any).assignedRoomIds] : undefined
            };
    
            // 🔧 ENHANCED: Apply the "original room endpoints never use connectors" rule
            // Only create connectors for split points, never for original room endpoints
            
            onDebug('🚨 TESTING: First-time extrusion logic reached!', { 
                originalSegmentId: originalSegment.tempId,
                originalSegmentParentWallId: (originalSegment as any).parentWallId,
                currentWallsCount: currentWalls.length
            });
            
            // Helper function to find split point (shared with another segment from same parent)
            const findSplitPoint = (): Point | null => {
                const tolerance = 0.1;
                const parentWallId = (originalSegment as any).parentWallId;
                
                onDebug('FIRST_TIME_EXTRUSION_SPLIT_POINT_DETECTION', {
                    parentWallId,
                    originalStart: originalSegment.start,
                    originalEnd: originalSegment.end,
                    tolerance
                });
                
                if (!parentWallId) {
                    onDebug('FIRST_TIME_EXTRUSION_NO_PARENT_WALL_ID', { message: 'No parentWallId found' });
                    return null;
                }
                
                // Find other segments from the same parent wall using robust coordinate comparison
                const siblingSegments = currentWalls.filter(wall => {
                    const wallParentId = (wall as any).parentWallId;
                    
                    // Check if this wall has the same parent ID
                    if (wallParentId !== parentWallId) {
                        return false;
                    }
                    
                    // More robust way to exclude self - use coordinate comparison since tempId might be unreliable
                    const isSameWall = (
                        pointsAreEqual(originalSegment.start, wall.start, tolerance) && 
                        pointsAreEqual(originalSegment.end, wall.end, tolerance)
                    ) || (
                        pointsAreEqual(originalSegment.start, wall.end, tolerance) && 
                        pointsAreEqual(originalSegment.end, wall.start, tolerance)
                    );
                    
                    const isSibling = !isSameWall;
                    
                    if (isSibling) {
                        onDebug('FIRST_TIME_EXTRUSION_SIBLING_FOUND', {
                            siblingTempId: wall.tempId,
                            siblingStart: wall.start,
                            siblingEnd: wall.end,
                            parentMatches: wallParentId === parentWallId,
                            isNotSameWall: !isSameWall
                        });
                    }
                    
                    return isSibling;
                });
                
                onDebug('FIRST_TIME_EXTRUSION_SIBLING_COUNT', { siblingCount: siblingSegments.length });
                
                if (siblingSegments.length === 0) {
                    return null;
                }
                
                // Check which endpoint is shared with sibling segments
                for (const sibling of siblingSegments) {
                    // Check if originalSegment.start is shared with sibling
                    const p1MatchesSiblingStart = Math.abs(originalSegment.start.x - sibling.start.x) < tolerance && Math.abs(originalSegment.start.y - sibling.start.y) < tolerance;
                    const p1MatchesSiblingEnd = Math.abs(originalSegment.start.x - sibling.end.x) < tolerance && Math.abs(originalSegment.start.y - sibling.end.y) < tolerance;
                    
                    if (p1MatchesSiblingStart || p1MatchesSiblingEnd) {
                        onDebug('FIRST_TIME_EXTRUSION_SPLIT_POINT_IDENTIFIED', { 
                            splitPoint: originalSegment.start, 
                            reason: 'P1 shared with sibling' 
                        });
                        return originalSegment.start;
                    }
                    
                    // Check if originalSegment.end is shared with sibling
                    const p2MatchesSiblingStart = Math.abs(originalSegment.end.x - sibling.start.x) < tolerance && Math.abs(originalSegment.end.y - sibling.start.y) < tolerance;
                    const p2MatchesSiblingEnd = Math.abs(originalSegment.end.x - sibling.end.x) < tolerance && Math.abs(originalSegment.end.y - sibling.end.y) < tolerance;
                    
                    if (p2MatchesSiblingStart || p2MatchesSiblingEnd) {
                        onDebug('FIRST_TIME_EXTRUSION_SPLIT_POINT_IDENTIFIED', { 
                            splitPoint: originalSegment.end, 
                            reason: 'P2 shared with sibling' 
                        });
                        return originalSegment.end;
                    }
                }
                
                onDebug('FIRST_TIME_EXTRUSION_NO_SPLIT_POINT', { message: 'No shared endpoint found' });
                return null;
            };
            
            // Helper function to identify if an endpoint is an original room endpoint
            const isOriginalRoomEndpoint = (point: Point): boolean => {
                const tolerance = 0.1;
                
                // An endpoint is "original" if it's connected to walls that are NOT split segments from the same parent
                const connectedWalls = currentWalls.filter(wall => {
                    if (wall.tempId === originalSegment.tempId) return false;
                    
                    const connectsAtStart = Math.abs(wall.start.x - point.x) < tolerance && Math.abs(wall.start.y - point.y) < tolerance;
                    const connectsAtEnd = Math.abs(wall.end.x - point.x) < tolerance && Math.abs(wall.end.y - point.y) < tolerance;
                    
                    return connectsAtStart || connectsAtEnd;
                });
                
                onDebug('FIRST_TIME_EXTRUSION_ORIGINAL_ENDPOINT_CHECK', {
                    point,
                    connectedWallsCount: connectedWalls.length,
                    connectedWalls: connectedWalls.map(w => ({
                        tempId: w.tempId,
                        coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
                        parentWallId: (w as any).parentWallId,
                        isExtrusionConnector: w.isExtrusionConnector
                    }))
                });
                
                const parentWallId = (originalSegment as any).parentWallId;
                
                for (const connectedWall of connectedWalls) {
                    // Skip extrusion connectors - they don't count as original walls
                    if (connectedWall.isExtrusionConnector) continue;
                    
                    const connectedWallParentId = (connectedWall as any).parentWallId;
                    
                    // If connected to a regular wall (no parent ID), this is an original endpoint
                    if (!connectedWallParentId) {
                        onDebug('FIRST_TIME_EXTRUSION_ORIGINAL_ENDPOINT_CONFIRMED', { 
                            point, 
                            reason: 'Connected to regular wall' 
                        });
                        return true;
                    }
                    
                    // If connected to a split segment from a DIFFERENT parent wall, this is an original endpoint
                    if (connectedWallParentId !== parentWallId) {
                        onDebug('FIRST_TIME_EXTRUSION_ORIGINAL_ENDPOINT_CONFIRMED', { 
                            point, 
                            reason: `Connected to different parent wall (${connectedWallParentId} vs ${parentWallId})` 
                        });
                        return true;
                    }
                }
                
                onDebug('FIRST_TIME_EXTRUSION_SPLIT_ENDPOINT_CONFIRMED', { 
                    point, 
                    reason: 'Only connects to segments from same parent wall' 
                });
                return false;
            };
            
            const splitPoint = findSplitPoint();
            
            // 🔧 CRITICAL: Create connectors ONLY for split points, NEVER for original room endpoints
            const wallsForExtrusion: WallWithTempId[] = [newMovedSegment];
            
            onDebug('FIRST_TIME_EXTRUSION_ENDPOINT_ANALYSIS', {
                splitPoint: splitPoint ? `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})` : 'Not found',
                startIsOriginal: isOriginalRoomEndpoint(originalSegment.start),
                endIsOriginal: isOriginalRoomEndpoint(originalSegment.end)
            });
            
            if (splitPoint) {
                // Determine which new endpoint corresponds to the split point
                const splitPointNew = (Math.abs(splitPoint.x - originalSegment.start.x) < 0.1 && Math.abs(splitPoint.y - originalSegment.start.y) < 0.1) ? 
                    snappedMovedStart : snappedMovedEnd;
                
                // Double-check: even if we identified a split point, make sure it's not an original room endpoint
                if (!isOriginalRoomEndpoint(splitPoint)) {
                    // Only create connector if it would have meaningful length
                    // 🎯 ROOT CAUSE FIX: Apply precise grid snapping for coordinate accuracy
                    // Use 0.1 precision instead of GRID_SIZE (30) to prevent coordinate drift
                    const precisionSplitPoint = snapPointToGrid(splitPoint, 0.1);
                    const precisionSplitPointNew = snapPointToGrid(splitPointNew, 0.1);
                    
                    if (!pointsAreEqual(precisionSplitPoint, precisionSplitPointNew, 0.1)) {
                        // Use centralized safe connector creation
                        const safeConnectorResult = createSafeConnector({
                            start: precisionSplitPoint,
                            end: precisionSplitPointNew,
                            type: originalSegment.type || 'None',
                            floor: currentFloor,
                            parentWallId: (originalSegment as any).parentWallId,
                            parentRoomIds: (originalSegment as any).parentRoomIds,
                            // 🏠 PRESERVE ROOM OWNERSHIP: Inherit ownership from originalSegment
                            originatingRoomId: (originalSegment as any).originatingRoomId,
                            assignedRoomIds: (originalSegment as any).assignedRoomIds ? [...(originalSegment as any).assignedRoomIds] : undefined
                        }, currentWalls, 0.1);
                        
                        if (safeConnectorResult.wasCreated) {
                            wallsForExtrusion.push(safeConnectorResult.connector! as WallWithTempId);
                        } else {
                            onDebug('EXTRUSION_CONNECTOR_SKIPPED', { reason: safeConnectorResult.reason });
                        }
                        
                        if (safeConnectorResult.wasCreated) {
                            onDebug('FIRST_TIME_EXTRUSION_SPLIT_CONNECTOR_CREATED', {
                                splitPoint: `(${precisionSplitPoint.x.toFixed(1)},${precisionSplitPoint.y.toFixed(1)})`,
                                splitPointNew: `(${precisionSplitPointNew.x.toFixed(1)},${precisionSplitPointNew.y.toFixed(1)})`,
                                connectorTempId: safeConnectorResult.connector!.tempId
                            });
                        }
                    } else {
                        onDebug('FIRST_TIME_EXTRUSION_SPLIT_CONNECTOR_SKIPPED', { 
                            reason: 'Connector would be too short' 
                        });
                    }
                } else {
                    onDebug('FIRST_TIME_EXTRUSION_ORIGINAL_ENDPOINT_PROTECTION', { 
                        splitPoint: `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})`,
                        reason: 'Split point is actually an original room endpoint - no connector created' 
                    });
                }
            } else {
                onDebug('FIRST_TIME_EXTRUSION_NO_SPLIT_POINT_FOUND', { 
                    message: 'No split point found - no connectors will be created' 
                });
            }
            
            // 🔧 CRITICAL: Handle endpoint extension for ALL endpoints (both split and original)
            // This ensures original room endpoints always allow connected walls to extend/retract naturally
            const handleEndpointExtension = (originalEndpoint: Point, newEndpoint: Point, endpointType: string) => {
                const tolerance = 0.1;
                
                // Find walls connected to this endpoint (excluding the segment being moved and extrusion connectors)
                const connectedWalls = currentWalls.filter(wall => {
                    if (wall.tempId === originalSegment.tempId) return false;
                    if (wall.isExtrusionConnector) return false;
                    
                    const connectsAtStart = Math.abs(wall.start.x - originalEndpoint.x) < tolerance && Math.abs(wall.start.y - originalEndpoint.y) < tolerance;
                    const connectsAtEnd = Math.abs(wall.end.x - originalEndpoint.x) < tolerance && Math.abs(wall.end.y - originalEndpoint.y) < tolerance;
                    
                    return connectsAtStart || connectsAtEnd;
                });
                
                onDebug(`FIRST_TIME_EXTRUSION_${endpointType}_ENDPOINT_EXTENSION`, {
                    originalEndpoint: `(${originalEndpoint.x.toFixed(1)},${originalEndpoint.y.toFixed(1)})`,
                    newEndpoint: `(${newEndpoint.x.toFixed(1)},${newEndpoint.y.toFixed(1)})`,
                    connectedWallsCount: connectedWalls.length,
                    connectedWalls: connectedWalls.map(w => `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`)
                });
                
                // Update each connected wall to extend/retract naturally
                connectedWalls.forEach(wall => {
                    const wallConnectsAtStart = Math.abs(wall.start.x - originalEndpoint.x) < tolerance && Math.abs(wall.start.y - originalEndpoint.y) < tolerance;
                    const wallConnectsAtEnd = Math.abs(wall.end.x - originalEndpoint.x) < tolerance && Math.abs(wall.end.y - originalEndpoint.y) < tolerance;
                    
                    if (wallConnectsAtStart) {
                        wall.start.x = newEndpoint.x;
                        wall.start.y = newEndpoint.y;
                        onDebug('FIRST_TIME_EXTRUSION_WALL_START_EXTENDED', {
                            wallTempId: wall.tempId,
                            newStart: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})`,
                            wallEnd: `(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`
                        });
                    } else if (wallConnectsAtEnd) {
                        wall.end.x = newEndpoint.x;
                        wall.end.y = newEndpoint.y;
                        onDebug('FIRST_TIME_EXTRUSION_WALL_END_EXTENDED', {
                            wallTempId: wall.tempId,
                            wallStart: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})`,
                            newEnd: `(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`
                        });
                    }
                });
            };
            
            // Apply extension logic to BOTH endpoints - ensures original room endpoints work correctly
            handleEndpointExtension(originalSegment.start, snappedMovedStart, "START");
            handleEndpointExtension(originalSegment.end, snappedMovedEnd, "END");
            
            const otherWalls = currentWalls.filter(w => w.tempId !== originalSegment.tempId);
            const finalTempWalls = [...otherWalls, ...wallsForExtrusion];
            
            onDebug('FIRST_TIME_EXTRUSION_COMPLETE', {
                originalSegmentId: originalSegment.tempId,
                createdConnectors: wallsForExtrusion.length - 1, // -1 because wallsForExtrusion includes the main segment
                totalWalls: finalTempWalls.length,
                splitPointBehavior: splitPoint ? 'Only split point has connector' : 'No connectors created'
            });
            
            setTempWalls(finalTempWalls);
            setActivelyResizedWalls(wallsForExtrusion);
    
            if (wallsForExtrusion.length > 0) {
                const allRelevantWallsForBounds = [...selectedRoom.walls.map(w => ({...w, floor: w.floor || currentFloor})), ...wallsForExtrusion.map(w => ({...w, floor: w.floor || currentFloor}))];
                const combinedBounds = getRoomBounds(allRelevantWallsForBounds);
                const widthPx = combinedBounds.maxX - combinedBounds.minX;
                const heightPx = combinedBounds.maxY - combinedBounds.minY;
                // Round dimensions to 2 decimal places before calculating area (consistent with calculateArea)
                const widthM = Math.round((widthPx / PIXELS_PER_METER) * 100) / 100;
                const heightM = Math.round((heightPx / PIXELS_PER_METER) * 100) / 100;
                
                // 🔧 CRITICAL FIX: Use proper polygon-based area calculation for extrusion
                const areaM2 = calculateArea(allRelevantWallsForBounds);
                console.log('🔧 EXTRUSION AREA CALCULATION:', {
                  boundingBoxArea: (widthM * heightM).toFixed(2),
                  actualPolygonArea: areaM2.toFixed(2),
                  wallCount: allRelevantWallsForBounds.length,
                  method: 'polygon-based-extrusion'
                });
                
                setResizingRoomLiveDimensions({ width: widthM, height: heightM, area: areaM2 });
            } else {
                setResizingRoomLiveDimensions(null);
            }
        }
    } else { // Standard room resize logic
        if (!activeResizeOriginalWallTempIdsRef.current) {
             onDebug('HANDLE_ROOM_RESIZE_GUARD_FAIL', { message: "activeResizeOriginalWallTempIdsRef missing for standard resize" });
             return;
        }
    const plainDraggedWall = (({ tempId, floor, ...rest }) => ({
      ...rest, 
      floor: floor || currentFloor,
      // Preserve split wall metadata
      ...(rest.isSplitOrigin && { isSplitOrigin: rest.isSplitOrigin }),
      ...(rest.parentWallId && { parentWallId: rest.parentWallId }),
      ...(rest.parentRoomIds && { parentRoomIds: rest.parentRoomIds }),
      ...(rest.requiresExtrusionOnMove && { requiresExtrusionOnMove: rest.requiresExtrusionOnMove }),
      ...(rest.isMainExtrusionSegment && { isMainExtrusionSegment: rest.isMainExtrusionSegment }),
      ...(rest.isExtrusionConnector && { isExtrusionConnector: rest.isExtrusionConnector }),
      // 🔧 CRITICAL FIX: Preserve ownership fields during resize to maintain room membership across multiple operations
      ...(rest.originatingRoomId && { originatingRoomId: rest.originatingRoomId }),
      ...(rest.assignedRoomIds && { assignedRoomIds: rest.assignedRoomIds })
    }))(draggedWallSegmentRef.current as WallWithTempId);

    // 🔧 FIX: Use walls that include existing extrusions from when resize started
    // This prevents "extrusions within extrusions" by allowing calculateResizedWalls to see existing connectors
    const wallsIncludingExistingExtrusions = initialWallsForResizeRef.current || selectedRoom.walls;
    const roomWallsForCalculation = wallsIncludingExistingExtrusions
      .filter(w => {
        // Include walls that belong to this room
        const belongsToRoom = (w as any).roomId === selectedRoom.id;
        
        // 🔧 CRITICAL FIX: Check for room membership using BOTH old and new ownership formats
        // Old format check (for backwards compatibility)
        const isExtrusionForRoomOld = (w as any).parentRoomIds?.includes(selectedRoom.id);
        
        // New format check (for walls created with new ownership logic)
        const isExtrusionForRoomNew = (
          (w as any).originatingRoomId === selectedRoom.id ||
          (w as any).assignedRoomIds?.includes(selectedRoom.id)
        );
        
        // Include extrusion walls that have this room in either format
        const isExtrusionForRoom = isExtrusionForRoomOld || isExtrusionForRoomNew;
        
        // Include extrusion connectors or main segments that are part of this room's extrusions
        const isExtrusionComponent = (w as any).isExtrusionConnector || (w as any).isMainExtrusionSegment;
        
        return belongsToRoom || isExtrusionForRoom || isExtrusionComponent;
      })
      .map(w => ({...w, floor: w.floor || currentFloor}));

    // ⚡ CRITICAL FIX: Include tempWalls and currentWalls for connector detection during wall splits
    const wallsForConnectorDetection = tempWalls && tempWalls.length > 0 
      ? [...currentWalls, ...tempWalls] 
      : currentWalls;

    // 🔒 ENHANCED WALL OWNERSHIP (STANDARD): Filter walls to only include those belonging to this room
    const standardSafeWallArray = createSafeWallArrayForResize(selectedRoom, wallsForConnectorDetection);
    
    if (standardSafeWallArray.warnings.length > 0) {
      console.warn('⚠️ STANDARD RESIZE WALL OWNERSHIP WARNINGS:', {
        roomId: selectedRoom.stableId,
        warnings: standardSafeWallArray.warnings
      });
    }

    console.log('🔒 STANDARD RESIZE WALL OWNERSHIP FILTERING APPLIED:', {
      roomId: selectedRoom.stableId,
      originalWallCount: wallsForConnectorDetection.length,
      safeWallCount: standardSafeWallArray.safeWalls.length,
      excludedWallCount: standardSafeWallArray.excludedWalls.length
    });

    // 🛡️ ENHANCED RESIZE VALIDATION: Validate standard resize operation
    const draggedWallForStandardValidation = draggedWallSegmentRef.current;
    
    try {
      // Validate the standard resize operation to prevent coordinate collisions and disconnections
      const validation = validateWallResize(
        draggedWallForStandardValidation,
        point, // Using point as new start
        { x: draggedWallForStandardValidation.end.x + (point.x - draggedWallForStandardValidation.start.x), 
          y: draggedWallForStandardValidation.end.y + (point.y - draggedWallForStandardValidation.start.y) }, // Calculate new end
        selectedRoom.stableId || selectedRoom.id || 'unknown',
        currentWalls,
        [selectedRoom] // Pass current room for validation
      );

      if (!validation.canProceed) {
        console.warn('🚫 STANDARD RESIZE OPERATION BLOCKED:', {
          roomId: selectedRoom.stableId,
          wallId: draggedWallForStandardValidation.tempId,
          blockers: validation.blockers,
          point: point
        });
        // Don't proceed with resize if validation fails
        return;
      }

      if (validation.warnings.length > 0) {
        console.warn('⚠️ STANDARD RESIZE WARNINGS:', {
          roomId: selectedRoom.stableId,
          wallId: draggedWallForStandardValidation.tempId,
          warnings: validation.warnings
        });
      }

      console.log('✅ STANDARD RESIZE VALIDATION PASSED:', {
        roomId: selectedRoom.stableId,
        wallId: draggedWallForStandardValidation.tempId,
        affectedRooms: validation.affectedRooms,
        sharedWallsCount: validation.sharedWallsCount
      });

    } catch (error) {
      console.error('❌ STANDARD RESIZE VALIDATION ERROR:', error);
      // Continue with standard resize as fallback
    }

    // 🔒 ENHANCED STANDARD RESIZE: Use only walls that belong to this room to prevent coordinate collisions
    const standardRoomSpecificWallsForResize = getResizeEligibleWalls(selectedRoom, standardSafeWallArray.safeWalls);
    
    console.log('🔍 STANDARD_BEFORE_CALCULATE_RESIZED_WALLS:', {
      roomName: selectedRoom.name,
      roomWallsCount: selectedRoom.walls.length,
      roomSpecificWallsCount: standardRoomSpecificWallsForResize.length,
      safeWallsCount: standardSafeWallArray.safeWalls.length,
      roomSpecificWallIds: standardRoomSpecificWallsForResize.map(w => w.tempId || 'NO_ID')
    });
    
    let newlyResizedPlainWalls = calculateResizedWalls(
      standardRoomSpecificWallsForResize.map(w => ({...w, floor: w.floor || currentFloor})), 
      plainDraggedWall,   
          resizeHandle!, 
      point,
      false, // isFinalAdjustment
      false, // maintainSquareness
      undefined, // originalRoomAspectRatio
      // Only force standard resize for isSplitOrigin walls when room has existing extrusions
      // Never force it for requiresExtrusionOnMove walls as they need extrusion logic
      !isExtrudingRoomSegmentRef.current && !isResizingExtrusionWidthRef.current && !(plainDraggedWall as any).requiresExtrusionOnMove, // forceStandardResize
      standardRoomSpecificWallsForResize // 🔒 ENHANCED: Use room-specific walls to prevent coordinate collisions
    );
    
    console.log('🔍 STANDARD_AFTER_CALCULATE_RESIZED_WALLS:', {
      roomName: selectedRoom.name,
      inputWallsCount: standardRoomSpecificWallsForResize.length,
      outputWallsCount: newlyResizedPlainWalls.length,
      wallCountChange: newlyResizedPlainWalls.length - standardRoomSpecificWallsForResize.length,
      outputWallIds: newlyResizedPlainWalls.map((w, i) => `${i}: ${(w as any).tempId || 'NO_ID'}`)
    });

    // 🔒 STANDARD POST-RESIZE VALIDATION: Ensure only the correct room's walls were affected
    const standardPostResizeValidation = validatePostResize(
      wallsForConnectorDetection,
      newlyResizedPlainWalls,
      selectedRoom.stableId || selectedRoom.id || 'unknown'
    );

    if (!standardPostResizeValidation.isValid) {
      console.warn('⚠️ STANDARD POST-RESIZE VALIDATION WARNING:', {
        roomId: selectedRoom.stableId,
        unexpectedChanges: standardPostResizeValidation.unexpectedChanges.length,
        summary: standardPostResizeValidation.summary
      });
    } else {
      console.log('✅ STANDARD POST-RESIZE VALIDATION PASSED:', {
        roomId: selectedRoom.stableId,
        summary: standardPostResizeValidation.summary
      });
    }

    setActivelyResizedWalls(newlyResizedPlainWalls);

    const originalTempIds = activeResizeOriginalWallTempIdsRef.current;

    // 🔧 FIX: Don't reassign tempIds for extrusion walls - preserve their identity
    const newlyResizedWallsWithTempIds: WallWithTempId[] = newlyResizedPlainWalls.map((resizedWall, index) => {
      // Check if this is an extrusion-related wall that already has its proper tempId/identity
      const isExtrusionWall = (resizedWall as any).isMainExtrusionSegment || 
                              (resizedWall as any).isExtrusionConnector || 
                              (resizedWall as any).isSplitOrigin;
      
      if (isExtrusionWall && (resizedWall as any).tempId) {
        // Keep existing tempId and properties for extrusion walls
        return {
          ...resizedWall,
          floor: currentFloor 
        } as WallWithTempId;
      }
      
      // For regular walls, try to match by coordinates first, then fall back to index
      let originalTempId = originalTempIds[index];
      
      // Try to find the original wall by coordinates if index mapping fails
      if (!originalTempId && selectedRoom) {
        const matchingOriginalWall = selectedRoom.walls.find(origWall => {
          const tolerance = 0.1;
          return Math.abs(origWall.start.x - resizedWall.start.x) < tolerance &&
                 Math.abs(origWall.start.y - resizedWall.start.y) < tolerance &&
                 Math.abs(origWall.end.x - resizedWall.end.x) < tolerance &&
                 Math.abs(origWall.end.y - resizedWall.end.y) < tolerance;
        });
        
        if (matchingOriginalWall) {
          originalTempId = (matchingOriginalWall as any).tempId;
        }
      }
      
      if (!originalTempId) {
        console.error("Error in handleRoomResize: Missing original tempId for resized wall at index", index, resizedWall);
        return { ...resizedWall, tempId: `error-tempId-${Date.now()}-${index}`, floor: currentFloor };
      }
      
      return {
        ...resizedWall,
        tempId: originalTempId,
        floor: currentFloor 
      };
    });
    
    const tempIdsToExclude = activeResizeOriginalWallTempIdsRef.current;
    
    // Check if this is a standard room resize (not extrusion-specific)
    const isStandardResize = !isExtrudingRoomSegmentRef.current && !isResizingExtrusionWidthRef.current;
    
    if (isStandardResize) {
      if (!initialWallsForResizeRef.current) { 
        onDebug('HANDLE_ROOM_RESIZE_STD_ERR', { message: "initialWallsForResizeRef.current is null" });
        setTempWalls(null);
        return;
      }
      const allInitialWalls = initialWallsForResizeRef.current;

      const originalStatesOfMovedBaseWalls = allInitialWalls.filter(wall => 
        activeResizeOriginalWallTempIdsRef.current?.includes(wall.tempId!) &&
        !(wall as any).isExtrusionConnector && 
        !(wall as any).isMainExtrusionSegment
      );
      
      // Only include actual base walls (not extrusion parts) as "moved"
      const newStatesOfMovedBaseWalls = newlyResizedWallsWithTempIds.filter(wall =>
        activeResizeOriginalWallTempIdsRef.current?.includes(wall.tempId!) &&
        !(wall as any).isExtrusionConnector && 
        !(wall as any).isMainExtrusionSegment
      );
      
      const otherInitialWallsNotPartOfSelectedRoom = allInitialWalls.filter(
        wall => !activeResizeOriginalWallTempIdsRef.current?.includes(wall.tempId!)
      );

      const wallsAfterBaseResize = [...otherInitialWallsNotPartOfSelectedRoom, ...newlyResizedWallsWithTempIds];
      
      const wallsWithMovedExtrusions = updateConnectedExtrusionsForStandardResize(
        allInitialWalls,                 
        wallsAfterBaseResize,            
        originalStatesOfMovedBaseWalls,  
        newStatesOfMovedBaseWalls, // Now this variable exists
        onDebug // Add this missing parameter
      );
      onDebug('[HOOK handleRoomResize] Walls after extrusion update:', { count: wallsWithMovedExtrusions.length });
      
      // 🔒 OWNERSHIP-AWARE SHARED WALL SYNC: Synchronize walls while respecting room boundaries
      // This prevents coordinate collision issues during extrusion resize operations
      const extrusionSyncResult = synchronizeSharedWallsWithOwnership(
        newlyResizedWallsWithTempIds,
        wallsWithMovedExtrusions,
        selectedRoom.stableId || selectedRoom.id || 'unknown',
        POINT_MATCH_TOLERANCE
      );

      // Validate that room boundaries were respected during extrusion sync
      const extrusionBoundaryValidation = validateSynchronizationRespectedBoundaries(
        extrusionSyncResult,
        selectedRoom.stableId || selectedRoom.id || 'unknown'
      );

      if (!extrusionBoundaryValidation.isValid) {
        console.warn('⚠️ EXTRUSION SYNC BOUNDARY VIOLATION:', {
          roomId: selectedRoom.stableId,
          violations: extrusionBoundaryValidation.violations
        });
      }

      const synchronizedWalls = extrusionSyncResult.synchronizedWalls;
      
      onDebug('SHARED_WALL_SYNC_APPLIED', { 
        originalWallsCount: wallsWithMovedExtrusions.length,
        synchronizedWallsCount: synchronizedWalls.length,
        resizedRoomWallsCount: newlyResizedWallsWithTempIds.length
      });
      
      setTempWalls(synchronizedWalls);
    } else {
      // This else branch implies it's an extrusion-specific resize (depth or width)
      // The logic for setting tempWalls in extrusion-specific cases is handled within their respective if blocks.
      // If it's not standard resize and not extrusion width/depth, currentWalls are used to get otherWalls.
      // This might need to use initialWallsForResizeRef.current as well if this path is complex.
      const otherWallsFromCurrentContext = (initialWallsForResizeRef.current || currentWalls).filter(
        wall => !tempIdsToExclude.includes(wall.tempId!)
      );
      
      const allWallsForExtrusionResize = [...otherWallsFromCurrentContext, ...newlyResizedWallsWithTempIds];
      
      // 🔒 OWNERSHIP-AWARE SHARED WALL SYNC: Synchronize walls for extrusion resizes with ownership protection
      const extrusionOnlySyncResult = synchronizeSharedWallsWithOwnership(
        newlyResizedWallsWithTempIds,
        allWallsForExtrusionResize,
        selectedRoom.stableId || selectedRoom.id || 'unknown',
        POINT_MATCH_TOLERANCE
      );

      // Validate that room boundaries were respected during extrusion-only sync
      const extrusionOnlyBoundaryValidation = validateSynchronizationRespectedBoundaries(
        extrusionOnlySyncResult,
        selectedRoom.stableId || selectedRoom.id || 'unknown'
      );

      if (!extrusionOnlyBoundaryValidation.isValid) {
        console.warn('⚠️ EXTRUSION-ONLY SYNC BOUNDARY VIOLATION:', {
          roomId: selectedRoom.stableId,
          violations: extrusionOnlyBoundaryValidation.violations
        });
      }

      const synchronizedExtrusionWalls = extrusionOnlySyncResult.synchronizedWalls;
      
      onDebug('SHARED_WALL_SYNC_APPLIED_EXTRUSION', { 
        originalWallsCount: allWallsForExtrusionResize.length,
        synchronizedWallsCount: synchronizedExtrusionWalls.length,
        resizedRoomWallsCount: newlyResizedWallsWithTempIds.length
      });
      
      setTempWalls(synchronizedExtrusionWalls);
    }

    if (newlyResizedPlainWalls && newlyResizedPlainWalls.length > 0) {
      const bounds = getRoomBounds(newlyResizedPlainWalls);
      const widthPx = bounds.maxX - bounds.minX;
      const heightPx = bounds.maxY - bounds.minY;
      
      const widthM = Math.round((widthPx / PIXELS_PER_METER) * 100) / 100;
      const heightM = Math.round((heightPx / PIXELS_PER_METER) * 100) / 100;
      
      // 🔧 CRITICAL FIX: Use proper polygon-based area calculation for final resize
      const areaM2 = calculateArea(newlyResizedPlainWalls);
      console.log('🔧 FINAL RESIZE AREA CALCULATION:', {
        boundingBoxArea: (widthM * heightM).toFixed(2),
        actualPolygonArea: areaM2.toFixed(2),
        wallCount: newlyResizedPlainWalls.length,
        method: 'polygon-based-final'
      });

      setResizingRoomLiveDimensions({ width: widthM, height: heightM, area: areaM2 });
    } else {
      setResizingRoomLiveDimensions(null);
        }
    }
  }, [
    selectedRoom, resizeHandle, draggedWallSegmentRef, currentWalls, setTempWalls, setActivelyResizedWalls, 
    currentFloor, activeResizeOriginalWallTempIdsRef, onDebug, setResizingRoomLiveDimensions, PIXELS_PER_METER,
    isExtrudingRoomSegmentRef, // Added ref
    isResizingExtrusionWidthRef, // <<< ADDED
    snapToFineGridOrEndpointsUtil, // Added for extrusion snapping
    POINT_SNAP_THRESHOLD, CM_SNAP_SIZE, // Added for extrusion snapping
    pointsAreEqual, // Added for filtering zero-length walls
    isDragging // Added for performance optimization
  ]);

  const handleWallSplit = useCallback((pointOnWallToSplit: Point, onSplitComplete?: (newSegments: WallWithTempId[]) => void, wallOverride?: WallWithTempId) => {
    const splitTimestamp = Date.now();
    const wallToSplitFromState = wallOverride || selectedWall;
    onDebug('HANDLE_WALL_SPLIT_INITIATED', { pointOnWallToSplit, selectedWallId: wallToSplitFromState?.tempId, wallOverride: !!wallOverride });

    if (!wallToSplitFromState) {
      console.error('❌ No selectedWall available for splitting');
      return;
    }

    // 🔧 CRITICAL FIX: Ensure wall has ownership before splitting
    let wallToSplit = wallToSplitFromState;
    const hasOwnership = (wallToSplit as any).originatingRoomId || (wallToSplit as any).assignedRoomIds;
    
    if (!hasOwnership) {
      console.log('🔧 WALL_SPLIT_OWNERSHIP_REPAIR: Wall lacks ownership, assigning to containing room');
      
      // Find which room contains this wall
      const rooms = memoizedCalculateRooms(currentWalls, roomCustomLabels, 'geometryUpdate', currentFloor);
      const containingRoom = rooms.find(room => 
        room.walls.some(roomWall => 
          (pointsAreEqual(roomWall.start, wallToSplit.start, 0.1) && pointsAreEqual(roomWall.end, wallToSplit.end, 0.1)) ||
          (pointsAreEqual(roomWall.start, wallToSplit.end, 0.1) && pointsAreEqual(roomWall.end, wallToSplit.start, 0.1))
        )
      );
      
      if (containingRoom) {
        console.log('🏠 WALL_SPLIT_OWNERSHIP_ASSIGNED', { 
          wallId: wallToSplit.tempId, 
          roomId: containingRoom.stableId,
          roomName: containingRoom.name 
        });
        
        // Update the wall with ownership info
        wallToSplit = {
          ...wallToSplit,
          originatingRoomId: containingRoom.stableId,
          assignedRoomIds: [containingRoom.stableId]
        } as any;
        
      } else {
        console.warn('⚠️ WALL_SPLIT_OWNERSHIP_REPAIR_FAILED: Could not find containing room for wall', wallToSplit.tempId);
      }
    }
    
    if (!pointOnWallToSplit) {
      onDebug('HANDLE_WALL_SPLIT_EARLY_RETURN', { hasSelectedWall: !!selectedWall, hasPointOnWallToSplit: !!pointOnWallToSplit });
      return;
    }

    // Set flag to skip expensive room calculations during splitting
    setIsWallSplitting(true);

    if (lastOperationTypeRef) lastOperationTypeRef.current = 'geometryUpdate';
    recordUndoState?.(); // Record state before split

    // Find all elements attached to the wall being split
    // Elements can reference walls by either tempId or stableId
    const attachedElements = elements.filter(element => {
      if (!('wallId' in element)) return false;
      const elementWallId = (element as any).wallId;
      return elementWallId === wallToSplit.tempId || 
             elementWallId === (wallToSplit as any).stableId;
    });

    onDebug('HANDLE_WALL_SPLIT_FOUND_ATTACHED_ELEMENTS', { 
      selectedWallId: wallToSplit.tempId, 
      splitPoint: pointOnWallToSplit,
      attachedElementsCount: attachedElements.length,
      attachedElements: attachedElements.map(el => ({ 
        id: (el as any).id || 'no-id',
        type: el.type,
        position: (el as any).position || 'no-position'
      }))
    });

    // Use the proper wall splitting logic from wallUtils.ts to preserve room IDs
    const plainCurrentWalls = currentWalls.map(({ tempId, ...wall }) => ({
      ...wall, 
      floor: wall.floor || currentFloor,
      // Preserve split wall metadata
      ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
      ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
      ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
      ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
      ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
      ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
    }));
          const plainSelectedWall = selectedWall ? (({ tempId, ...wall }) => ({
        ...wall, 
        floor: wall.floor || currentFloor,
        // Preserve split wall metadata
        ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
        ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
        ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
        ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
        ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
        ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
      }))(selectedWall) : null;
    
    if (!plainSelectedWall) {
      onDebug('HANDLE_WALL_SPLIT_NO_PLAIN_WALL', { selectedWall });
      return;
    }

    onDebug('HANDLE_WALL_SPLIT_CALLING_UTILS', {
      plainSelectedWall,
      splitPoint: pointOnWallToSplit,
      roomLabelsCount: Object.keys(roomCustomLabels).length,
      selectedRoomId: selectedRoom?.stableId
    });

    // Use the proper room-preserving wall split function
    const result: WallSplitResult = splitWallAndProcessState({
      selectedWall: plainSelectedWall,
      splitPoint: pointOnWallToSplit,
      currentWallsForFloor: plainCurrentWalls,
      allRoomCustomLabels: roomCustomLabels,
      currentSelectedRoom: selectedRoom,
      currentFloorNumber: currentFloor,
    });

    onDebug('HANDLE_WALL_SPLIT_UTILS_RESULT', {
      newWallsCount: result.newWallsForFloor.length,
      originalWallsCount: plainCurrentWalls.length,
      newRoomLabelsCount: Object.keys(result.newRoomCustomLabels).length,
      originalRoomLabelsCount: Object.keys(roomCustomLabels).length,
      hasNewSelectedRoom: !!result.newSelectedRoomAfterSplit,
      originalSelectedRoomId: selectedRoom?.stableId,
      newSelectedRoomId: result.newSelectedRoomAfterSplit?.stableId
    });

    // 🔧 PROACTIVE ROOM OWNERSHIP: Establish room ownership for split segments even if parent wall lacks it
    let parentRoomOwnership = {
      originatingRoomId: (wallToSplit as any).originatingRoomId,
      assignedRoomIds: (wallToSplit as any).assignedRoomIds ? [...(wallToSplit as any).assignedRoomIds] : undefined
    };

    console.log('🔧 SPLIT_WALL_OWNERSHIP: Starting proactive ownership check', {
      wallId: wallToSplit.tempId,
      parentOriginatingRoomId: parentRoomOwnership.originatingRoomId,
      parentAssignedRoomIds: parentRoomOwnership.assignedRoomIds,
      wallCoords: `(${wallToSplit.start.x.toFixed(1)},${wallToSplit.start.y.toFixed(1)})->(${wallToSplit.end.x.toFixed(1)},${wallToSplit.end.y.toFixed(1)})`
    });

    // If parent wall lacks room ownership, find it proactively to prevent orphaned segments
    if (!parentRoomOwnership.originatingRoomId && (!parentRoomOwnership.assignedRoomIds || parentRoomOwnership.assignedRoomIds.length === 0)) {
      console.log('🔧 SPLIT_WALL_OWNERSHIP: Parent wall lacks room ownership, finding room proactively');
      
      // Calculate current rooms to find which room contains this wall
      const currentRoomState = calculateRooms(currentWalls, roomCustomLabels, 'geometryUpdate', currentFloor);
      
      // Find room containing this wall by coordinates
      const containingRoom = currentRoomState.find(room => {
        return room.walls.some(roomWall => {
          const tolerance = 0.1;
          const startMatches = Math.abs(roomWall.start.x - wallToSplit.start.x) < tolerance && 
                              Math.abs(roomWall.start.y - wallToSplit.start.y) < tolerance;
          const endMatches = Math.abs(roomWall.end.x - wallToSplit.end.x) < tolerance && 
                            Math.abs(roomWall.end.y - wallToSplit.end.y) < tolerance;
          const reverseMatches = Math.abs(roomWall.start.x - wallToSplit.end.x) < tolerance && 
                                Math.abs(roomWall.start.y - wallToSplit.end.y) < tolerance &&
                                Math.abs(roomWall.end.x - wallToSplit.start.x) < tolerance && 
                                Math.abs(roomWall.end.y - wallToSplit.start.y) < tolerance;
          return (startMatches && endMatches) || reverseMatches;
        });
      });

      if (containingRoom) {
        parentRoomOwnership = {
          originatingRoomId: containingRoom.stableId || containingRoom.id,
          assignedRoomIds: [containingRoom.stableId || containingRoom.id]
        };
        
        console.log('🔧 SPLIT_WALL_OWNERSHIP: Found containing room for wall', {
          wallId: wallToSplit.tempId,
          roomName: containingRoom.name,
          roomId: containingRoom.stableId || containingRoom.id
        });
      } else {
        console.log('⚠️ SPLIT_WALL_OWNERSHIP: Could not find containing room for wall', {
          wallId: wallToSplit.tempId,
          wallCoords: `(${wallToSplit.start.x.toFixed(1)},${wallToSplit.start.y.toFixed(1)})->(${wallToSplit.end.x.toFixed(1)},${wallToSplit.end.y.toFixed(1)})`
        });
      }
    }

    // Create the two new wall segments with both tempIds and stableIds for proper element tracking
    const segment1: WallWithTempId = { 
      start: wallToSplit.start, 
      end: pointOnWallToSplit, 
      type: wallToSplit.type, 
      floor: currentFloor,
      isSplitOrigin: true,
      parentWallId: wallToSplit.tempId, // Track the original wall ID
      tempId: `split-${wallToSplit.tempId}-1-${splitTimestamp}`,
      stableId: `wall_${wallToSplit.start.x.toFixed(2)}_${wallToSplit.start.y.toFixed(2)}_${pointOnWallToSplit.x.toFixed(2)}_${pointOnWallToSplit.y.toFixed(2)}`,
      // 🏠 GUARANTEED ROOM OWNERSHIP: Use proactively determined ownership
      originatingRoomId: parentRoomOwnership.originatingRoomId,
      assignedRoomIds: parentRoomOwnership.assignedRoomIds
    };
    
    const segment2: WallWithTempId = { 
      start: pointOnWallToSplit, 
      end: wallToSplit.end, 
      type: wallToSplit.type, 
      floor: currentFloor,
      isSplitOrigin: true,
      parentWallId: wallToSplit.tempId, // Track the original wall ID
      tempId: `split-${wallToSplit.tempId}-2-${splitTimestamp}`,
      stableId: `wall_${pointOnWallToSplit.x.toFixed(2)}_${pointOnWallToSplit.y.toFixed(2)}_${wallToSplit.end.x.toFixed(2)}_${wallToSplit.end.y.toFixed(2)}`,
      // 🏠 GUARANTEED ROOM OWNERSHIP: Use proactively determined ownership
      originatingRoomId: parentRoomOwnership.originatingRoomId,
      assignedRoomIds: parentRoomOwnership.assignedRoomIds
    };

    onDebug('HANDLE_WALL_SPLIT_CREATED_NEW_SEGMENTS', {
      segment1: { tempId: segment1.tempId, start: segment1.start, end: segment1.end },
      segment2: { tempId: segment2.tempId, start: segment2.start, end: segment2.end }
    });

    // Convert the walls from splitWallAndProcessState to walls with tempIds that match our segments
    const wallsWithMatchingTempIds: WallWithTempId[] = result.newWallsForFloor.map((wall, index) => {
      // Try to find if this is one of our split segments by checking coordinates
      const isSegment1 = wall.start.x === wallToSplit.start.x && wall.start.y === wallToSplit.start.y &&
                         wall.end.x === pointOnWallToSplit.x && wall.end.y === pointOnWallToSplit.y;
      const isSegment2 = wall.start.x === pointOnWallToSplit.x && wall.start.y === pointOnWallToSplit.y &&
                         wall.end.x === wallToSplit.end.x && wall.end.y === wallToSplit.end.y;
      
      let tempId: string;
      let stableId: string | undefined;
      if (isSegment1) {
        tempId = segment1.tempId || `fallback-segment1-${splitTimestamp}-${index}`;
        stableId = segment1.stableId;
      } else if (isSegment2) {
        tempId = segment2.tempId || `fallback-segment2-${splitTimestamp}-${index}`;
        stableId = segment2.stableId;
      } else {
        // For other walls, generate a tempId or preserve existing one
        tempId = `wall-${splitTimestamp}-${index}`;
        stableId = (wall as any).stableId || undefined;
      }
      
      // 🔧 CRITICAL FIX: Preserve ALL split wall metadata, don't override it
      const baseWallData = { ...wall, tempId, stableId };
      
      if (isSegment1 || isSegment2) {
        // Preserve all the metadata that was set in createSplitSegmentWithRoomInfo
        return {
          ...baseWallData,
          // Preserve existing split wall metadata
          isSplitOrigin: (wall as any).isSplitOrigin ?? true,
          parentWallId: (wall as any).parentWallId ?? wallToSplit.tempId,
          parentRoomIds: (wall as any).parentRoomIds,
          requiresExtrusionOnMove: (wall as any).requiresExtrusionOnMove ?? true,
          // Preserve any other metadata
          ...(wall as any).isMainExtrusionSegment && { isMainExtrusionSegment: (wall as any).isMainExtrusionSegment },
          ...(wall as any).isExtrusionConnector && { isExtrusionConnector: (wall as any).isExtrusionConnector }
        } as WallWithTempId;
      } else {
        return baseWallData as WallWithTempId;
      }
    });

    onDebug('HANDLE_WALL_SPLIT_CREATED_WALLS_WITH_TEMPIDS', {
      originalWallsFromSplit: result.newWallsForFloor.length,
      wallsWithMatchingTempIds: wallsWithMatchingTempIds.length,
      segment1TempId: segment1.tempId,
      segment2TempId: segment2.tempId,
      wallsWithTempIds: wallsWithMatchingTempIds.map(w => ({ tempId: w.tempId, start: w.start, end: w.end }))
    });

    // Find the actual split segments from the walls that will be saved
    const actualSegment1 = wallsWithMatchingTempIds.find(wall => 
      wall.start.x === wallToSplit.start.x && wall.start.y === wallToSplit.start.y &&
      wall.end.x === pointOnWallToSplit.x && wall.end.y === pointOnWallToSplit.y
    );
    const actualSegment2 = wallsWithMatchingTempIds.find(wall => 
      wall.start.x === pointOnWallToSplit.x && wall.start.y === pointOnWallToSplit.y &&
      wall.end.x === wallToSplit.end.x && wall.end.y === wallToSplit.end.y
    );

    onDebug('HANDLE_WALL_SPLIT_FOUND_ACTUAL_SEGMENTS', {
      actualSegment1: actualSegment1 ? { tempId: actualSegment1.tempId, stableId: actualSegment1.stableId } : null,
      actualSegment2: actualSegment2 ? { tempId: actualSegment2.tempId, stableId: actualSegment2.stableId } : null,
      attachedElementsCount: attachedElements.length
    });

    // Update floor plans with the walls with matching tempIds
    setFloorPlans(prev => ({ ...prev, [currentFloor]: wallsWithMatchingTempIds }));

    // Update room labels with the preserved/migrated labels
    setRoomCustomLabels(result.newRoomCustomLabels);

    // Update selected room to the properly tracked room after split
    setSelectedRoom(result.newSelectedRoomAfterSplit);

    // Reassign elements to the appropriate new wall segment
    if (attachedElements.length > 0 && actualSegment1 && actualSegment2) {
      const updatedElements = elements.map(element => {
        if (!('wallId' in element)) return element;
        
        const elementWallId = (element as any).wallId;
        const isAttachedToSelectedWall = elementWallId === wallToSplit.tempId || 
                                        elementWallId === (wallToSplit as any).stableId;
        
        if (!isAttachedToSelectedWall) {
          return element; // Keep elements that aren't attached to the split wall
        }

        // Calculate which segment this element should be attached to based on positionOnWall
        const elementPos = (element as any).position;
        const positionOnWall = (element as any).positionOnWall;
        
        if (!elementPos || positionOnWall === undefined) {
          onDebug('HANDLE_WALL_SPLIT_ELEMENT_MISSING_DATA', {
            elementId: (element as any).id,
            elementType: element.type,
            hasPosition: !!elementPos,
            positionOnWall: positionOnWall
          });
          return element;
        }

        // Calculate the split position as a ratio on the original wall
        const originalWallLength = Math.hypot(
          wallToSplit.end.x - wallToSplit.start.x, 
          wallToSplit.end.y - wallToSplit.start.y
        );
        const splitDistanceFromStart = Math.hypot(
          pointOnWallToSplit.x - wallToSplit.start.x,
          pointOnWallToSplit.y - wallToSplit.start.y
        );
        const splitRatio = splitDistanceFromStart / originalWallLength;

        // Determine which segment the element belongs to based on its positionOnWall
        const useSegment1 = positionOnWall <= splitRatio;
        const selectedSegment = useSegment1 ? actualSegment1 : actualSegment2;
        
        // Recalculate positionOnWall for the new segment to maintain same physical position
        let newPositionOnWall: number;
        if (useSegment1) {
          // Element is on segment1: scale up the position since segment1 is shorter
          const segment1Length = Math.hypot(actualSegment1.end.x - actualSegment1.start.x, actualSegment1.end.y - actualSegment1.start.y);
          newPositionOnWall = (positionOnWall * originalWallLength) / segment1Length;
        } else {
          // Element is on segment2: adjust position relative to new start point
          const segment2Length = Math.hypot(actualSegment2.end.x - actualSegment2.start.x, actualSegment2.end.y - actualSegment2.start.y);
          const distanceFromSplitPoint = (positionOnWall - splitRatio) * originalWallLength;
          newPositionOnWall = distanceFromSplitPoint / segment2Length;
        }
        
        // Use stableId if the element was originally using stableId, otherwise use tempId
        const newWallId = elementWallId === (selectedWall as any).stableId 
          ? selectedSegment.stableId 
          : selectedSegment.tempId;
        
        // Ensure newWallId is valid before reassigning
        if (!newWallId) {
          onDebug('HANDLE_WALL_SPLIT_INVALID_WALLID', {
            elementId: (element as any).id,
            elementType: element.type,
            originalWallId: elementWallId,
            selectedSegmentTempId: selectedSegment.tempId,
            selectedSegmentStableId: selectedSegment.stableId,
            useSegment1: useSegment1
          });
          return element; // Keep original element if newWallId is invalid
        }

        onDebug('HANDLE_WALL_SPLIT_REASSIGNING_ELEMENT', {
          elementId: (element as any).id,
          elementType: element.type,
          oldWallId: elementWallId,
          newWallId: newWallId,
          oldPositionOnWall: positionOnWall,
          newPositionOnWall: newPositionOnWall,
          splitRatio: splitRatio,
          selectedSegment: useSegment1 ? 1 : 2
        });

        // Recalculate the actual position coordinates for the element on the new wall segment
        const newPosition = {
          x: selectedSegment.start.x + (selectedSegment.end.x - selectedSegment.start.x) * newPositionOnWall,
          y: selectedSegment.start.y + (selectedSegment.end.y - selectedSegment.start.y) * newPositionOnWall
        };

        // For radiators and boilers, we need to maintain the proper offset from the wall
        if (element.type === 'radiator' || element.type === 'boiler') {
          const wallDx = selectedSegment.end.x - selectedSegment.start.x;
          const wallDy = selectedSegment.end.y - selectedSegment.start.y;
          const wallAngle = Math.atan2(wallDy, wallDx);
          const perpVector = { x: -Math.sin(wallAngle), y: Math.cos(wallAngle) };
          
          const pointOnWall = {
            x: selectedSegment.start.x + wallDx * newPositionOnWall,
            y: selectedSegment.start.y + wallDy * newPositionOnWall,
          };

          // Use the element's existing side and height values
          const elementSide = (element as any).side || 1;
          const itemHeight = element.type === 'radiator' ? ((element as any).height || 10) : 0;
          const offsetConstant = element.type === 'radiator' ? RADIATOR_WALL_OFFSET : BOILER_WALL_OFFSET;
          const wallThickness = WALL_THICKNESS;

          const totalOffset = (wallThickness / 2 + offsetConstant + itemHeight / 2) * elementSide;

          newPosition.x = pointOnWall.x + perpVector.x * totalOffset;
          newPosition.y = pointOnWall.y + perpVector.y * totalOffset;
        }

        onDebug('HANDLE_WALL_SPLIT_ELEMENT_POSITION_UPDATED', {
          elementId: (element as any).id,
          elementType: element.type,
          oldPosition: elementPos,
          newPosition: newPosition,
          newPositionOnWall: newPositionOnWall
        });

        return { ...element, wallId: newWallId, positionOnWall: newPositionOnWall, position: newPosition };
      });

      onDebug('HANDLE_WALL_SPLIT_UPDATING_ELEMENTS', {
        originalElementsCount: elements.length,
        updatedElementsCount: updatedElements.length,
        reassignedElementsCount: attachedElements.length,
        elementsWithNewWallIds: updatedElements.filter((el, index) => {
          const original = elements[index];
          return ('wallId' in el) && ('wallId' in original) && 
                 (el as any).wallId !== (original as any).wallId;
        }).length
      });

      // Update elements with the new wall assignments
      setElements(updatedElements);
    } else if (attachedElements.length > 0) {
      onDebug('HANDLE_WALL_SPLIT_CANNOT_REASSIGN_ELEMENTS', {
        attachedElementsCount: attachedElements.length,
        hasActualSegment1: !!actualSegment1,
        hasActualSegment2: !!actualSegment2,
        reason: !actualSegment1 ? 'Missing actualSegment1' : !actualSegment2 ? 'Missing actualSegment2' : 'Unknown'
      });
    }

    triggerAutosave?.(); // Trigger autosave after wall changes
    
    // If we have a callback, call it with the new segments
    if (onSplitComplete) {
      const newSegments = wallsWithMatchingTempIds.filter(w => 
        w.parentWallId === wallToSplit.tempId && w.isSplitOrigin
      );
      
      onDebug('HANDLE_WALL_SPLIT_CALLBACK_SEGMENTS', {
        newSegmentsCount: newSegments.length,
        newSegments: newSegments.map(s => ({ tempId: s.tempId, coords: `(${s.start.x.toFixed(1)},${s.start.y.toFixed(1)})->(${s.end.x.toFixed(1)},${s.end.y.toFixed(1)})` }))
      });
      
      onSplitComplete(newSegments);
    }
    
    // Clear wall splitting state
    setSelectedWall(null);
    setSplitPoint(null);
    setSplitPreviewDimensions(null); // Clear split preview dimensions
    
    // 🔧 CRITICAL FIX: Trigger window sync after wall splitting to reestablish window-room relationships
    // This fixes the bug where windows on split walls become uneditable and show default values
    setTimeout(() => {
      try {
        // Force a window and radiator sync to reestablish element-room relationships after wallId changes
        window.dispatchEvent(new CustomEvent('elementSyncRequired', { 
          detail: { 
            reason: 'wall_split_completed',
            attachedElementsCount: attachedElements.length,
            elementTypes: ['window', 'radiator']
          } 
        }));
        
        onDebug('WALL_SPLIT_ELEMENT_SYNC_TRIGGERED', {
          reason: 'Triggered window and radiator sync after wall split to fix wallId references',
          attachedElementsCount: attachedElements.length
        });
      } catch (error) {
        onDebug('WALL_SPLIT_WINDOW_SYNC_ERROR', {
          error: error instanceof Error ? error.message : 'Unknown error',
          attachedElementsCount: attachedElements.length
        });
      }
    }, 100); // Short delay to ensure element updates are committed
    
    // Clear the wall splitting flag after a longer delay to ensure all state updates complete
    // This prevents race conditions where room calculations happen before labels are updated
    setTimeout(() => {
      setIsWallSplitting(false);
      onDebug('HANDLE_WALL_SPLIT_FLAG_CLEARED', { 
        message: 'Wall splitting flag cleared, room calculations can resume'
      });
      
      // 🔧 FIX: Check for delayed room label prompt after wall splitting completes
      // This fixes the bug where ALL room creation doesn't show label prompt
      // because wall splitting suppressed it during the critical timing window
      const wasRoomCreationOperation = lastOperationTypeRef?.current === 'explicitRoomCreation' ||
                                       newlyAddedWallsRef.current.length > 0;
      
      if (wasRoomCreationOperation) {
        onDebug('WALL_SPLIT_DELAYED_ROOM_CHECK', {
          message: 'Triggering delayed room label check after wall splitting',
          operationType: lastOperationTypeRef.current,
          newWallsAdded: newlyAddedWallsRef.current.length,
          wasRoomCreation: true
        });
        
        // Use a small additional delay to ensure all state updates are complete
        setTimeout(() => {
          // Force a state update that will trigger the room label effect
          // by temporarily changing the current tool to trigger re-evaluation
          const currentToolValue = currentToolInternalRef.current;
          setCurrentTool('geometryUpdate');
          
          // Immediately restore the original tool
          setTimeout(() => {
            setCurrentTool(currentToolValue);
            
            // Clear the newly added walls ref after processing
            newlyAddedWallsRef.current = [];
          }, 10);
        }, 100);
      }
    }, 800);
    
    onDebug('HANDLE_WALL_SPLIT_OPTIMIZED_COMPLETE', { 
      newWallsCount: result.newWallsForFloor.length,
      elementsReassigned: attachedElements.length
    });
  }, [selectedWall, currentWalls, currentFloor, elements, selectedRoom, roomCustomLabels, setFloorPlans, setElements, setRoomCustomLabels, setSelectedRoom, setSelectedWall, setSplitPoint, setSplitPreviewDimensions, setIsWallSplitting, recordUndoState, lastOperationTypeRef, onDebug, triggerAutosave]);

  const generateStableWallIdFallback = (wall: { start: Point; end: Point }): string => {
    if (generateStableWallId) {
        return generateStableWallId(wall.start, wall.end); 
    }
    // Use coordinate-based format consistent with other wall ID generation
    return `wall_${wall.start.x.toFixed(2)}_${wall.start.y.toFixed(2)}_${wall.end.x.toFixed(2)}_${wall.end.y.toFixed(2)}`;
  };

  const findClickedSegment = (point: Point, segments: Wall[], currentScale: number): Wall | null => {
    const tolerance = HOVER_TOLERANCE / currentScale;
    return segments.find(segment => isPointOnWall(point, segment, tolerance)) || null;
  };

  // Helper function to find overlapping walls at a point
  const findOverlappingWalls = (point: Point, walls: Wall[], tolerance: number = 10): Wall[] => {
    const overlappingWalls: Wall[] = [];
    
    // Find the primary wall at the click point
    const primaryWall = walls.find(wall => isPointOnWall(point, wall, tolerance));
    if (!primaryWall) return [];
    
    console.log('🔍 PRIMARY WALL FOUND', {
      primaryWallId: generateStableWallId(primaryWall.start, primaryWall.end),
      primaryWallCoords: `${primaryWall.start.x.toFixed(1)},${primaryWall.start.y.toFixed(1)} -> ${primaryWall.end.x.toFixed(1)},${primaryWall.end.y.toFixed(1)}`,
      primaryWallType: primaryWall.type
    });
    
    // Find all walls that overlap with the primary wall
    for (const wall of walls) {
      if (wall === primaryWall) {
        overlappingWalls.push(wall);
        continue;
      }
      
      const wallId = generateStableWallId(wall.start, wall.end);
      const wallCoords = `${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)} -> ${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)}`;
      
      // Enhanced overlap detection with multiple approaches
      let isOverlapping = false;
      let overlapReason = '';
      
      // Method 1: Check if walls are nearly identical (coordinates within tolerance)
      const startDistance = Math.hypot(wall.start.x - primaryWall.start.x, wall.start.y - primaryWall.start.y);
      const endDistance = Math.hypot(wall.end.x - primaryWall.end.x, wall.end.y - primaryWall.end.y);
      const reverseStartDistance = Math.hypot(wall.start.x - primaryWall.end.x, wall.start.y - primaryWall.end.y);
      const reverseEndDistance = Math.hypot(wall.end.x - primaryWall.start.x, wall.end.y - primaryWall.start.y);
      
      const isNearlyIdentical = (startDistance < tolerance && endDistance < tolerance) ||
                               (reverseStartDistance < tolerance && reverseEndDistance < tolerance);
      
      if (isNearlyIdentical) {
        isOverlapping = true;
        overlapReason = `nearly identical coordinates (distance: ${Math.min(startDistance + endDistance, reverseStartDistance + reverseEndDistance).toFixed(1)}px)`;
      } else {
        // Method 2: Check if walls are collinear and overlapping
        const areCollinear = distanceToLine(wall.start, primaryWall.start, primaryWall.end) < tolerance &&
                            distanceToLine(wall.end, primaryWall.start, primaryWall.end) < tolerance;
        
        if (areCollinear) {
          // Check for actual overlap using projection
          const primaryVector = { x: primaryWall.end.x - primaryWall.start.x, y: primaryWall.end.y - primaryWall.start.y };
          const primaryLength = Math.sqrt(primaryVector.x * primaryVector.x + primaryVector.y * primaryVector.y);
          
          if (primaryLength > 0) {
            const primaryUnit = { x: primaryVector.x / primaryLength, y: primaryVector.y / primaryLength };
            
            // Project wall endpoints onto primary wall
            const wallStartProj = (wall.start.x - primaryWall.start.x) * primaryUnit.x + (wall.start.y - primaryWall.start.y) * primaryUnit.y;
            const wallEndProj = (wall.end.x - primaryWall.start.x) * primaryUnit.x + (wall.end.y - primaryWall.start.y) * primaryUnit.y;
            
            const wallMin = Math.min(wallStartProj, wallEndProj);
            const wallMax = Math.max(wallStartProj, wallEndProj);
            
            // Check if there's overlap with primary wall (0 to primaryLength)
            const overlapStart = Math.max(0, wallMin);
            const overlapEnd = Math.min(primaryLength, wallMax);
            const overlapLength = overlapEnd - overlapStart;
            
            if (overlapLength > 1) { // Require at least 1px overlap
              isOverlapping = true;
              overlapReason = `collinear with overlap (${overlapLength.toFixed(1)}px overlap)`;
            }
          }
        }
      }
      
      if (isOverlapping) {
        overlappingWalls.push(wall);
        console.log('🔍 OVERLAPPING WALL FOUND', {
          wallId,
          wallCoords,
          wallType: wall.type,
          overlapReason
        });
      }
    }
    
    console.log('🔍 OVERLAP DETECTION COMPLETE', {
      totalWallsChecked: walls.length,
      overlappingWallsFound: overlappingWalls.length,
      overlappingWallTypes: overlappingWalls.map(w => w.type)
    });
    
    return overlappingWalls;
  };

  const handleWallTypeConversion = (point: Point, newType: WallType) => {
    console.log('🔧 WALL TYPE CONVERSION START', {
      type: newType,
      point,
      timestamp: new Date().toISOString()
    });

    onDebug('SEGMENT_WALL_CONVERSION_START', {
      type: newType,
      point,
      timestamp: new Date().toISOString()
    });

    // Step 1: Find overlapping walls at the click point
    const allWallsForFloor = floorPlans[currentFloor] || [];
    // Use a minimum tolerance to ensure consistent click detection regardless of zoom level
    // Convert screen-space minimum to world-space, then take the larger of the two
    const worldTolerance = SNAP_THRESHOLD / scale;
    const screenTolerance = MIN_CLICK_TOLERANCE / scale;
    const effectiveTolerance = Math.max(worldTolerance, screenTolerance);
    const overlappingWalls = findOverlappingWalls(point, allWallsForFloor, effectiveTolerance);
    
    // 🚨 SPECIAL LOGGING FOR PW CONVERSION: Enhanced wall identification
    if (newType === 'PW') {
      console.log(`🔵🚨🔵 PARTY WALL (PW) CONVERSION DETECTED! 🔵🚨🔵`, {
        clickPoint: point,
        overlappingWallsCount: overlappingWalls.length,
        timestamp: new Date().toISOString()
      });
      
      overlappingWalls.forEach((wall, index) => {
        const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
        const wallLengthMeters = (wallLength / 100).toFixed(2);
        
        console.log(`🔵 PW TARGET WALL ${index + 1}:`, {
          tempId: wall.tempId,
          coordinates: `${wall.start.x},${wall.start.y} → ${wall.end.x},${wall.end.y}`,
          wallLength: wallLength.toFixed(1),
          wallLengthMeters: `${wallLengthMeters}m`,
          exactLength: wallLength,
          currentType: wall.type,
          willBecome: 'PW (Party Wall)',
          is058mWall: wallLength >= 50 && wallLength <= 65 ? '🚨 POTENTIAL 0.58m WALL!' : false
        });
      });
    }
    
    console.log('🔍 OVERLAPPING WALLS DETECTED', {
      clickPoint: point,
      overlappingWallsCount: overlappingWalls.length,
      overlappingWallIds: overlappingWalls.map(w => generateStableWallId(w.start, w.end))
    });

    // 🔧 REMOVED: Logic that changed ALL overlapping walls - now using precise segmentation instead

    // ENHANCED WALL SECTIONS: Apply type change to specific exposed sections only
    // This uses aligned room endpoints for natural segmentation without wall splitting
    const useWallSections = true; // Enable the new simplified approach
    
    if (useWallSections && overlappingWalls.length >= 1) {
      // CRITICAL FIX: Select the wall that actually contains the click point for projection
      // Sort by length descending to prefer full walls over segments
      const sortedWalls = [...overlappingWalls].sort((a, b) => {
        const lengthA = Math.hypot(a.end.x - a.start.x, a.end.y - a.start.y);
        const lengthB = Math.hypot(b.end.x - b.start.x, b.end.y - b.start.y);
        return lengthB - lengthA; // Descending order - longest first
      });

      // Select the longest wall that contains the click point within its bounds
      let targetWall = sortedWalls[0]; // Default to longest wall
      
      // Verify that the click point actually projects within the wall bounds
      for (const wall of sortedWalls) {
        const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
        const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
        
        if (wallLength > 0) {
          const wallUnit = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
          const clickVector = { x: point.x - wall.start.x, y: point.y - wall.start.y };
          const projection = clickVector.x * wallUnit.x + clickVector.y * wallUnit.y;
          
          // Check if projection is within wall bounds (0 to wallLength)
          if (projection >= -1 && projection <= wallLength + 1) { // Small tolerance for floating point
            targetWall = wall;
            console.log('🎯 SELECTED WALL FOR SECTIONS (contains click point)', {
              wallId: generateStableWallId(wall.start, wall.end),
              wallLength: wallLength.toFixed(1),
              projection: projection.toFixed(4),
              projectionValid: true
            });
            break;
          } else {
            console.log('🔍 WALL REJECTED (click point outside bounds)', {
              wallId: generateStableWallId(wall.start, wall.end),
              wallLength: wallLength.toFixed(1),
              projection: projection.toFixed(4),
              projectionValid: false
            });
          }
        }
      }

      console.log('🎯 USING WALL SECTIONS for exposed segment control', {
        wallId: generateStableWallId(targetWall.start, targetWall.end),
        newType,
        clickPoint: point,
        overlappingWallsCount: overlappingWalls.length,
        selectedWallLength: Math.hypot(targetWall.end.x - targetWall.start.x, targetWall.end.y - targetWall.start.y).toFixed(1)
      });
      
      // Use the imported wall section utilities
      const currentRooms = calculateRooms(allWallsForFloor, roomCustomLabels, 'geometryUpdate', currentFloor);
      
      // Create or get existing sections for this wall
      let wallSections = targetWall.sections;
      if (!wallSections) {
        wallSections = createWallSections(targetWall, currentRooms, allWallsForFloor);
        console.log('🔧 WALL SECTIONS CREATED', {
          wallId: generateStableWallId(targetWall.start, targetWall.end),
          wallLength: Math.hypot(targetWall.end.x - targetWall.start.x, targetWall.end.y - targetWall.start.y).toFixed(1),
          sectionsCreated: wallSections.length,
          sections: wallSections.map((section, index) => ({
            index,
            startPercent: section.startPercent.toFixed(3),
            endPercent: section.endPercent.toFixed(3),
            type: section.type,
            isExterior: section.isExterior
          })),
          roomsCount: currentRooms.length,
          roomNames: currentRooms.map(r => r.name)
        });
      } else {
        console.log('🔧 USING EXISTING WALL SECTIONS', {
          wallId: generateStableWallId(targetWall.start, targetWall.end),
          sectionsCount: wallSections.length
        });
      }
      
      // Find which section was clicked
      console.log('🔧 FINDING SECTION AT POINT', {
        clickPoint: point,
        wallStart: targetWall.start,
        wallEnd: targetWall.end,
        wallLength: Math.hypot(targetWall.end.x - targetWall.start.x, targetWall.end.y - targetWall.start.y).toFixed(1),
        sectionsAvailable: wallSections.length
      });
      
      const clickedSection = findSectionAtPoint(point, targetWall, wallSections);
      
      console.log('🔧 SECTION SEARCH RESULT', {
        found: !!clickedSection,
        clickedSectionInfo: clickedSection ? {
          startPercent: clickedSection.startPercent.toFixed(3),
          endPercent: clickedSection.endPercent.toFixed(3),
          type: clickedSection.type,
          isExterior: clickedSection.isExterior
        } : null
      });
      
      if (clickedSection) {
        console.log('🎯 SECTION FOUND', {
          sectionIndex: wallSections.indexOf(clickedSection),
          currentType: clickedSection.type,
          newType,
          isExterior: clickedSection.isExterior,
          exposureLevel: clickedSection.exposureLevel
        });
        
        // Update the specific section type
        const sectionIndex = wallSections.indexOf(clickedSection);
        const updatedSections = updateSectionType(wallSections, sectionIndex, newType);
        
        // Apply the change to the wall without splitting
        recordUndoState?.();
        onWallOperation?.(); // Track wall operation for reinitialization guard
        const updatedWalls = allWallsForFloor.map(wall => {
          const wallId = generateStableWallId(wall.start, wall.end);
          const targetWallId = generateStableWallId(targetWall.start, targetWall.end);
          
          if (wallId === targetWallId) {
            return { 
              ...wall, 
              sections: updatedSections,
              // Keep primary type unchanged to maintain compatibility
              primaryType: wall.type
            };
          }
          return wall;
        });
        
        setFloorPlans(prev => ({ ...prev, [currentFloor]: updatedWalls }));
        // Delay autosave to ensure React state update completes
        setTimeout(() => {
          triggerAutosave?.();
        }, 10);
        
        console.log('✅ WALL SECTION TYPE CONVERSION COMPLETE', {
          wallId: generateStableWallId(targetWall.start, targetWall.end),
          sectionIndex,
          newType,
          wallCountUnchanged: true,
          sectionsCount: updatedSections.length
        });
        
        return;
      } else {
        console.log('⚠️ NO SECTION FOUND AT CLICK POINT, using entire wall fallback');
        // Fallback to entire wall change if no section found
      }
    }

    // FALLBACK: Apply type change to entire wall if sections approach fails
    if (overlappingWalls.length >= 1) {
      // FIXED: Always use the primary wall (first in array) instead of shortest wall
      // The primary wall is the one that was actually clicked, which is what users expect
      const targetWall = overlappingWalls[0]; // Primary wall is always first in the array

      console.log('🔄 APPLYING TYPE CHANGE TO ENTIRE WALL (fallback)', {
        wallId: generateStableWallId(targetWall.start, targetWall.end),
        newType,
        overlappingWallsCount: overlappingWalls.length,
        selectedWallLength: Math.hypot(targetWall.end.x - targetWall.start.x, targetWall.end.y - targetWall.start.y).toFixed(1)
      });
      
      // Apply type change to the entire wall directly
      recordUndoState?.();
      onWallOperation?.(); // Track wall operation for reinitialization guard
      const updatedWalls = allWallsForFloor.map(wall => {
        const wallId = generateStableWallId(wall.start, wall.end);
        const targetWallId = generateStableWallId(targetWall.start, targetWall.end);
        
        if (wallId === targetWallId) {
          return { ...wall, type: newType };
        }
        return wall;
      });
      
      setFloorPlans(prev => ({ ...prev, [currentFloor]: updatedWalls }));
      // Delay autosave to ensure React state update completes
      setTimeout(() => {
        triggerAutosave?.();
      }, 10);
      
      console.log('✅ ENTIRE WALL TYPE CONVERSION COMPLETE', {
        wallId: generateStableWallId(targetWall.start, targetWall.end),
        newType,
        wallCountUnchanged: true
      });
      
      return;
    }

    // Step 2: Analyze wall segmentation for current floor (single wall case)
    const segmentationResult = analyzeWallSegmentation(allWallsForFloor);
    
    const intersectionEntries = Array.from(segmentationResult.intersectionMap.entries()) as [string, any[]][];
    onDebug('SEGMENTATION_ANALYSIS_COMPLETE', {
      originalWalls: segmentationResult.originalWalls.length,
      totalSegments: segmentationResult.segments.length,
      wallsWithIntersections: intersectionEntries
        .filter(([_, intersections]) => intersections.length > 0).length
    });

    // Step 2: Find the specific segment at the clicked point
    // Use the same effective tolerance for segment detection
    const segmentTolerance = Math.max(SNAP_THRESHOLD / scale, MIN_CLICK_TOLERANCE / scale);
    console.log('🔍 SEARCHING FOR SEGMENT AT POINT', {
      point,
      tolerance: segmentTolerance,
      availableSegments: segmentationResult.segments.length,
      segmentDetails: segmentationResult.segments.map(s => ({
        id: s.id,
        start: s.start,
        end: s.end,
        type: s.type
      }))
    });
    
    const clickedSegment = findSegmentAtPoint(point, segmentationResult.segments, segmentTolerance);
    
    console.log('🔍 SEGMENT SEARCH RESULT', {
      found: !!clickedSegment,
      clickedSegmentId: clickedSegment?.id || 'none',
      clickedSegmentInfo: clickedSegment ? {
        id: clickedSegment.id,
        start: clickedSegment.start,
        end: clickedSegment.end,
        type: clickedSegment.type,
        parentWallId: clickedSegment.parentWallId
      } : null
    });
    
    if (!clickedSegment) {
      console.log('❌ NO SEGMENT FOUND', { 
        reason: "No wall segment found at click point", 
        point,
        availableSegments: segmentationResult.segments.length
      });
      onDebug("SEGMENT_NOT_FOUND", { 
        reason: "No wall segment found at click point", 
        point,
        availableSegments: segmentationResult.segments.length
      });
      return;
    }

    console.log('✅ SEGMENT FOUND - PROCESSING', {
      segmentId: clickedSegment.id,
      parentWallId: clickedSegment.parentWallId,
      segmentIndex: clickedSegment.segmentIndex,
      isMainSegment: clickedSegment.isMainSegment,
      currentType: clickedSegment.type,
      newType,
      segmentBounds: {
        start: clickedSegment.start,
        end: clickedSegment.end,
        length: clickedSegment.length.toFixed(1)
      }
    });

    onDebug('SEGMENT_FOUND', {
      segmentId: clickedSegment.id,
      parentWallId: clickedSegment.parentWallId,
      segmentIndex: clickedSegment.segmentIndex,
      isMainSegment: clickedSegment.isMainSegment,
      currentType: clickedSegment.type,
      newType,
      segmentBounds: {
        start: clickedSegment.start,
        end: clickedSegment.end,
        length: clickedSegment.length.toFixed(1)
      }
    });

    // Step 3: Update only the clicked segment's type
    console.log('🔄 UPDATING SEGMENT TYPE', {
      segmentId: clickedSegment.id,
      fromType: clickedSegment.type,
      toType: newType
    });
    
    const updatedSegments = updateSegmentType(segmentationResult.segments, clickedSegment.id, newType);
    
    console.log('✅ SEGMENT TYPE UPDATED', {
      totalSegments: updatedSegments.length,
      updatedSegment: updatedSegments.find(s => s.id === clickedSegment.id)
    });
    
    // Step 4: Smart wall update - only replace the affected wall while preserving metadata
    const affectedParentWallId = clickedSegment.parentWallId;
    const currentWallsWithTempId = floorPlans[currentFloor] || [];
    
    console.log('🔍 FINDING AFFECTED WALL', {
      affectedParentWallId,
      currentWallsCount: currentWallsWithTempId.length
    });
    
    // 🔧 ENHANCED WALL FINDING: Try multiple approaches to find the wall
    // This fixes room order dependency issues by being more flexible in wall identification
    let originalWallIndex = -1;
    let matchMethod = '';
    
    // Method 1: Try stable ID match (most reliable)
    originalWallIndex = currentWallsWithTempId.findIndex(wall => {
      const wallId = generateStableWallId(wall.start, wall.end);
      const match = wallId === affectedParentWallId;
      if (match) matchMethod = 'stableId';
      return match;
    });
    
    // Method 2: If stable ID fails, try tempId match (for newer walls)
    if (originalWallIndex === -1) {
      originalWallIndex = currentWallsWithTempId.findIndex(wall => {
        const wallTyped = wall as any;
        const match = wallTyped.tempId === affectedParentWallId;
        if (match) matchMethod = 'tempId';
        return match;
      });
    }
    
    // Method 3: If both fail, try coordinate proximity match (fallback)
    if (originalWallIndex === -1) {
      originalWallIndex = currentWallsWithTempId.findIndex(wall => {
        // Extract coordinates from parentWallId if it's a coordinate string
        if (affectedParentWallId.includes(',') && affectedParentWallId.includes('-')) {
          const [startStr, endStr] = affectedParentWallId.split('-');
          const [startX, startY] = startStr.split(',').map(Number);
          const [endX, endY] = endStr.split(',').map(Number);
          
          const coordsMatch = (
            Math.abs(wall.start.x - startX) < 0.1 && Math.abs(wall.start.y - startY) < 0.1 &&
            Math.abs(wall.end.x - endX) < 0.1 && Math.abs(wall.end.y - endY) < 0.1
          ) || (
            Math.abs(wall.start.x - endX) < 0.1 && Math.abs(wall.start.y - endY) < 0.1 &&
            Math.abs(wall.end.x - startX) < 0.1 && Math.abs(wall.end.y - startY) < 0.1
          );
          
          if (coordsMatch) matchMethod = 'coordinates';
          return coordsMatch;
        }
        return false;
      });
    }
    
    console.log('🔍 ENHANCED WALL SEARCH RESULT', {
      originalWallIndex,
      found: originalWallIndex !== -1,
      matchMethod,
      affectedParentWallId,
      totalWallsChecked: currentWallsWithTempId.length
    });
    
    if (originalWallIndex === -1) {
      console.log('❌ WALL NOT FOUND WITH ANY METHOD', { 
        reason: "Could not find original wall using any identification method", 
        affectedParentWallId,
        availableWalls: currentWallsWithTempId.length,
        availableWallIds: currentWallsWithTempId.map(w => ({
          stableId: generateStableWallId(w.start, w.end),
          tempId: (w as any).tempId,
          coords: `${w.start.x.toFixed(1)},${w.start.y.toFixed(1)}-${w.end.x.toFixed(1)},${w.end.y.toFixed(1)}`
        }))
      });
      onDebug("SEGMENT_CONVERSION_ERROR", { 
        reason: "Could not find original wall using any identification method", 
        affectedParentWallId,
        availableWalls: currentWallsWithTempId.length
      });
      return;
    }

    // Get the original wall with all its metadata
    const originalWall = currentWallsWithTempId[originalWallIndex] as WallWithTempId;
    
    // Get segments for the affected parent only
    const affectedSegments = updatedSegments.filter(seg => seg.parentWallId === affectedParentWallId);
    
    // SMART MERGING: Try to minimize wall creation by merging consecutive segments with same type
    console.log('🧠 SMART MERGE ANALYSIS', {
      affectedParentWallId,
      totalSegments: affectedSegments.length,
      segmentTypes: affectedSegments.map(s => ({ id: s.id, type: s.type, index: s.segmentIndex }))
    });
    
    // Check if we can merge segments to avoid extra walls
    const canMergeSegments = affectedSegments.length > 1 && affectedSegments.every(seg => seg.type === newType);
    const hasOnlyOneTypeChange = affectedSegments.filter(seg => seg.type !== originalWall.type).length === 1;
    
    if (canMergeSegments) {
      console.log('🔄 ALL SEGMENTS SAME TYPE - CHANGING ENTIRE WALL', {
        parentWallId: affectedParentWallId,
        sharedType: newType,
        segmentCount: affectedSegments.length
      });
      
      // All segments have the same type now, so just change the entire wall type
      const updatedWalls = currentWallsWithTempId.map(wall => {
        const wallId = generateStableWallId(wall.start, wall.end);
        if (wallId === affectedParentWallId) {
          return { ...wall, type: newType };
        }
        return wall;
      });
      
      setFloorPlans(prev => ({ ...prev, [currentFloor]: updatedWalls }));
      triggerAutosave?.();
      
      console.log('✅ SMART MERGE COMPLETE - NO WALL SPLITTING', {
        parentWallId: affectedParentWallId,
        newType,
        wallCountUnchanged: true
      });
      
      return;
    } else if (hasOnlyOneTypeChange && affectedSegments.length === 2) {
      console.log('🔄 MINIMAL SPLITTING - ONLY ONE SEGMENT CHANGED', {
        parentWallId: affectedParentWallId,
        changedSegments: affectedSegments.filter(seg => seg.type !== originalWall.type).length,
        totalSegments: affectedSegments.length
      });
    }
    
    // Proceed with standard segmentation if smart merging isn't applicable
    const reconstructedWallsFromParent = segmentsToWalls(affectedSegments);
    
    // Create new walls with preserved metadata
    const newWallsWithMetadata: WallWithTempId[] = reconstructedWallsFromParent.map((wall, index) => ({
      ...wall,
      tempId: index === 0 ? originalWall.tempId : `${originalWall.tempId}_seg_${index}`,
      stableId: index === 0 ? originalWall.stableId : `${originalWall.stableId}_seg_${index}`
    } as WallWithTempId));
    
    // Replace only the affected wall while preserving all others
    const updatedWallsArray = [...currentWallsWithTempId];
    console.log('🔄 REPLACING WALL', {
      originalWallIndex,
      removingWalls: 1,
      addingWalls: newWallsWithMetadata.length,
      newWalls: newWallsWithMetadata.map(w => ({
        tempId: w.tempId,
        type: w.type,
        start: w.start,
        end: w.end
      }))
    });
    
    updatedWallsArray.splice(originalWallIndex, 1, ...newWallsWithMetadata);
    
    console.log('🔄 WALL ARRAY UPDATED', {
      originalWallCount: currentWallsWithTempId.length,
      newWallCount: updatedWallsArray.length,
      wallChange: updatedWallsArray.length - currentWallsWithTempId.length
    });
    
    // Step 5: Update the floor plan with smart wall replacement
    console.log('💾 SAVING TO FLOOR PLAN', {
      currentFloor,
      newWallCount: updatedWallsArray.length
    });

    recordUndoState?.();
    setFloorPlans(prev => ({ ...prev, [currentFloor]: updatedWallsArray }));
    triggerAutosave?.();
    
    console.log('✅ WALL TYPE CONVERSION COMPLETE', {
      segmentId: clickedSegment.id,
      fromType: clickedSegment.type,
      toType: newType,
      wallsAfterUpdate: updatedWallsArray.length
    });

    onDebug('SEGMENT_CONVERSION_COMPLETE', {
      segmentId: clickedSegment.id,
      from: clickedSegment.type,
      to: newType,
      affectedSegment: {
        start: clickedSegment.start,
        end: clickedSegment.end,
        length: clickedSegment.length
      },
      totalWallsAfterConversion: updatedWallsArray.length,
      message: `Successfully converted segment ${clickedSegment.segmentIndex} of wall ${clickedSegment.parentWallId}`
    });
    
    // Re-evaluate element room assignments after wall type change
    setTimeout(async () => {
      if (!roomCustomLabels || !elements) return;
      
      // Import reassignElementsAfterWallTypeChange dynamically since it's not imported at the top
      const { reassignElementsAfterWallTypeChange } = await import('../services/roomSyncService');
      
      const currentRooms = memoizedCalculateRooms(
        updatedWallsArray, 
        roomCustomLabels, 
        'wallTypeModification', 
        currentFloor
      );
      
      const reassignmentResult = reassignElementsAfterWallTypeChange(
        elements, 
        currentRooms, 
        {} as any, // Empty site notes data since it's not available in this hook
        currentFloor
      );
      
      if (reassignmentResult.updatedElements !== elements) {
        setElements?.(reassignmentResult.updatedElements);
        onDebug('WALL_TYPE_CHANGE_REASSIGNMENT', {
          report: reassignmentResult.reassignmentReport,
          elementsUpdated: reassignmentResult.updatedElements.length
        });
      }
      
      // Note: siteNotesData updates would need to be handled in the parent component
    }, 100);
    
    onDebug('SMART_WALL_CONVERSION_COMPLETE', {
      from: clickedSegment.type,
      to: newType,
      segmentsConverted: 1,
      segmentId: clickedSegment.id,
      parentWallId: clickedSegment.parentWallId,
      segmentIndex: clickedSegment.segmentIndex,
      clickedSegment: { start: clickedSegment.start, end: clickedSegment.end },
      timestamp: new Date().toISOString()
    });
  };
  
  const handleExtrusion = (
    baseWall: WallWithTempId,
    newType: WallType,
    onDebug: (event: string, data: any) => void
  ): WallWithTempId => {
    const newWall: WallWithTempId = {
      ...baseWall,
      type: newType,
      stableId: baseWall.stableId || generateStableWallId(baseWall.start, baseWall.end),
    };
    onDebug('[ExtrusionWidthAdjust] New wall created', { newWall });
    return newWall;
  };

  const handleTextBoxCreation = useCallback((point: Point) => {
    const newTextBox: TextBox = {
      id: `text-${Date.now()}`, content: '', position: { x: point.x - 100, y: point.y - 50 },
      width: 200, height: 100, fontSize: 16, isDragging: false, isResizing: false, isEditing: true,
      floor: currentFloor
    };
    recordUndoState?.(); // Record state before creation
    setTextBoxes(prev => [...prev, newTextBox]);
    setSelectedTextBox(newTextBox);
  }, [currentFloor, setTextBoxes, setSelectedTextBox, recordUndoState, triggerAutosave]);

  const handlePan = useCallback((clientX: number, clientY: number) => {
    if (!panStart || !canvasRef.current) return;
    const dx = clientX - panStart.x;
    const dy = clientY - panStart.y;
    setViewOffset(prev => {
      const containerWidth = canvasRef.current?.clientWidth || 750;
      const newX = prev.x - dx / scale;
      const newY = prev.y - dy / scale;
      return constrainViewOffsetUtil({ x: newX, y: newY }, scale, containerWidth);
    });
    setPanStart({ x: clientX, y: clientY });
  }, [panStart, scale, canvasRef, setViewOffset, setPanStart]);



  const handleCanvasClick = useCallback((point: Point) => {
    const toolForThisInteraction = currentToolInternalRef.current;
    onDebug('CANVAS_CLICK', { tool: toolForThisInteraction, point });
    if (!toolForThisInteraction) return;

    switch (toolForThisInteraction) {
      case 'select':
        // Handle element and room selection for the select tool
        // First check for elements (they have priority over rooms)
        const elementAtClickPoint = findElementAtPoint(point, elements, currentFloor);
        
        // Debug logging to help diagnose coordinate issues
        onDebug('SELECT_TOOL_CLICK_DEBUG', {
          clickPoint: point,
          currentFloor,
          elementsCount: elements.length,
          elementsOnCurrentFloor: elements.filter(el => (el as any).floor === currentFloor || (el as any).floor === undefined).length,
          elementFound: elementAtClickPoint ? {
            type: elementAtClickPoint.type,
            id: hasId(elementAtClickPoint) ? elementAtClickPoint.id : 'no-id',
            position: (elementAtClickPoint as any).position,
            bounds: getElementBounds(elementAtClickPoint)
          } : null
        });
        
        if (elementAtClickPoint) {
          setSelectedElement(elementAtClickPoint);
          setSelectedRoom(null); // Clear room selection when element is selected
          onDebug('SELECT_TOOL_ELEMENT_SELECTED', { 
            elementId: hasId(elementAtClickPoint) ? elementAtClickPoint.id : 'no-id', 
            elementType: elementAtClickPoint.type,
            clickPoint: point 
          });
          
          // 🚪 DOOR EDITING: Trigger door editing modal when door is selected
          if (elementAtClickPoint.type === 'door') {
            // Use a callback to trigger door editing in the parent component
            // This will be handled by the component that uses this hook
            onDebug('DOOR_SELECTED_FOR_EDITING', { 
              doorId: hasId(elementAtClickPoint) ? elementAtClickPoint.id : 'no-id',
              doorElement: elementAtClickPoint
            });
          }
        } else {
          // No element found, check for rooms
          const allRooms = memoizedCalculateRooms(currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor})), roomCustomLabels, 'geometryUpdate');
          const roomAtClickPoint = findRoomAtPoint(point, allRooms);
          
          if (roomAtClickPoint) {
            setSelectedRoom(roomAtClickPoint);
            setSelectedElement(null); // Clear element selection when room is selected
            onDebug('SELECT_TOOL_ROOM_SELECTED', { 
              roomId: roomAtClickPoint.stableId, 
              roomName: (roomAtClickPoint.stableId ? roomCustomLabels[roomAtClickPoint.stableId] : null) || roomAtClickPoint.name,
              clickPoint: point 
            });
          } else if (selectedRoom || selectedElement) {
            // Clicked outside any room or element while select tool is active, deselect both
            setSelectedRoom(null);
            setSelectedElement(null);
                         onDebug('SELECT_TOOL_DESELECTED', { 
               previousRoomId: selectedRoom?.stableId,
               previousElementId: selectedElement && hasId(selectedElement) ? selectedElement.id : 'no-id'
             });
          }
        }
        break;
      case 'wall':
        if (roomStartRef.current) {
          // Use enhanced wall snapping for the end point with scale awareness
          const targetWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
          const endSnapResult = snapToWalls(point, targetWalls, scale, false);
          
          let finalEndPoint = endSnapResult.type !== 'none' ? endSnapResult.point : point;
          
          onDebug('SIMPLE_WALL_END_SNAP', {
            rawPoint: point,
            snappedPoint: finalEndPoint,
            snapType: endSnapResult.type,
            message: `Wall end snapped to ${endSnapResult.type}`
          });
          
          if (Math.abs(roomStartRef.current.x - finalEndPoint.x) > 1 || 
              Math.abs(roomStartRef.current.y - finalEndPoint.y) > 1) {
            
            // Create wall with simple snapping
              const newWall: Wall = {
                start: { ...roomStartRef.current },
                end: { ...finalEndPoint },
                type: 'None',
                floor: currentFloor
              };
              
              onDebug('SIMPLE_WALL_CREATION', {
                startPoint: roomStartRef.current,
                endPoint: finalEndPoint,
              snapType: endSnapResult.type,
                wallCreated: true
              });
              
              // Check if the new wall intersects existing walls
              if (!doesWallIntersect(newWall, currentWalls.map(({tempId,...w})=>({...w, floor: w.floor || currentFloor })))) {
                recordUndoState?.();
                const finalWalls = [...currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor})), newWall];
                
                // 🔧 ROOM MERGE FIX: Only set explicitRoomCreation for actual enclosed room creation
                const mightCreateRoom = detectPotentialRoomCreation(newWall, finalWalls);
                if (mightCreateRoom && lastOperationTypeRef) {
                  lastOperationTypeRef.current = 'explicitRoomCreation';
                } else if (lastOperationTypeRef) {
                  lastOperationTypeRef.current = 'geometryUpdate';
                }
                
                checkForNewRoomsAndCommit(finalWalls);
            }
          }
          setRoomStart(null); setCurrentPoint(null); setIsDraggingRoom(false);
        } else {
          // Use enhanced wall snapping for the start point with scale awareness
          const targetWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
          const startSnapResult = snapToWalls(point, targetWalls, scale, false);
          
          let finalStartPoint = startSnapResult.type !== 'none' ? startSnapResult.point : point;
          
          // 🔧 NEW: Only reset operation type if we're NOT starting from an endpoint (mouse)
          // If we're starting from an endpoint, preserve the explicitRoomCreation context
          if (lastOperationTypeRef && startSnapResult.type !== 'endpoint') {
            lastOperationTypeRef.current = 'geometryUpdate';
            console.log('🔧 WALL TOOL: Reset operation type for new wall creation (mouse)');
          } else if (startSnapResult.type === 'endpoint') {
            console.log('🔧 WALL TOOL: Preserving operation type for endpoint-based wall creation (mouse)');
          }
          
          onDebug('SIMPLE_WALL_START_SNAP', {
            rawPoint: point,
            snappedPoint: finalStartPoint,
            snapType: startSnapResult.type,
            message: `Wall start snapped to ${startSnapResult.type}`
          });
          
          // Set up for wall creation (matches touch behavior)
          setIsDraggingRoom(true);
          setRoomStart(finalStartPoint); 
          setCurrentPoint(finalStartPoint);
        }
        break;
      case 'hlp': handleWallTypeConversion(point, 'HLP'); break;
      case 'pw': handleWallTypeConversion(point, 'PW'); break;
      case 'sw': handleWallTypeConversion(point, 'SW'); break;
      case 'eraser': eraseAtPoint(point); break;
      case 'text': handleTextBoxCreation(point); break;
    }
  }, [
    onDebug, currentToolInternalRef, roomStartRef, currentFloor, currentWalls, floorPlans, checkForNewRoomsAndCommit,
    setRoomStart, setCurrentPoint, handleWallTypeConversion, eraseAtPoint, handleTextBoxCreation, lastOperationTypeRef,
    calculateRooms, roomCustomLabels, findRoomAtPoint, elements, selectedRoom, selectedElement, setSelectedRoom, setSelectedElement, getElementBounds
  ]);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    isMouseDownRef.current = true;
    const point = getCanvasCoordinates(e);
    const toolForThisInteraction = currentToolInternalRef.current;

    onDebug('CANVAS_MOUSE_DOWN', { tool: toolForThisInteraction, point, selectedRoomId: selectedRoom?.stableId, selectedWallId: selectedWall?.tempId, isResizing: isResizingRef.current });

    if (e.button === 2) { // Right click
      onDebug('MOUSE_DOWN_RIGHT_CLICK', { point });
      return;
    }

    justResizedRef.current = false;
    
    // 🚨 CRITICAL FIX: Don't reset extrusion width state during ongoing resize operations
    // Only reset if we're not currently in an active extrusion width resize
    if (!isResizingExtrusionWidthRef.current || !isResizingRef.current) {
    isExtrudingRoomSegmentRef.current = false; // Reset at the start of interaction
    isResizingExtrusionWidthRef.current = false; // Reset for width resize too
      onDebug('EXTRUSION_STATE_RESET', { 
        reason: 'New interaction starting',
        wasResizingExtrusionWidth: isResizingExtrusionWidthRef.current,
        wasResizing: isResizingRef.current
      });
    } else {
      onDebug('EXTRUSION_STATE_PRESERVED', { 
        reason: 'Active extrusion width resize in progress',
        isResizingExtrusionWidth: isResizingExtrusionWidthRef.current,
        isResizing: isResizingRef.current
      });
    }
    
    initialWallsForResizeRef.current = null; // Clear at the start of any new mouse down

    // Corner dragging is now available with roomResize tool - check this first with PRIORITY
    if (toolForThisInteraction === 'cornerAdjust' || toolForThisInteraction === 'roomResize') {
        const uniqueCorners = getUniqueCornerPoints(currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor })));
        let clickedCorner: Point | null = null;
        // EXTRA LARGE selection area for corners - prioritize corner grabbing
        for (const corner of uniqueCorners) {
            if (isNearPoint(point, corner, SNAP_THRESHOLD * 10)) {
                clickedCorner = corner;
                break;
            }
        }
        if (clickedCorner) {
            const affectedWallsDataValue = currentWalls
                .filter(wall =>
                    isNearPoint(wall.start, clickedCorner!, POINT_MATCH_TOLERANCE) ||
                    isNearPoint(wall.end, clickedCorner!, POINT_MATCH_TOLERANCE))
                .map(wall => ({
                    tempId: wall.tempId!,
                    isStartPointCorner: isNearPoint(wall.start, clickedCorner!, POINT_MATCH_TOLERANCE)
                }));
            activeCornerInfoRef.current = { originalCornerWorldPoint: clickedCorner, affectedWallsData: affectedWallsDataValue };
            setIsDraggingCorner(true); setIsDraggingRoom(false); setIsResizing(false);
            setTempWalls(currentWalls.map(wall => ({ ...wall, start: { ...wall.start }, end: { ...wall.end }, floor: wall.floor || currentFloor })));
            return;
        }
    }

    if (toolForThisInteraction === 'roomResize') {
      // Block for handling clicks during an ongoing resize (isResizingRef.current is true)
      if (isResizingRef.current && selectedRoom && resizeHandle && draggedWallSegmentRef.current) {
        onDebug('MOUSE_DOWN_DURING_ONGOING_RESIZE', { selectedRoomId: selectedRoom.stableId, resizeHandle });
        // Re-evaluate extrusion/width resize state based on the currently dragged segment
        if (draggedWallSegmentRef.current.isExtrusionConnector) {
            isResizingExtrusionWidthRef.current = true;
            isExtrudingRoomSegmentRef.current = false;
            onDebug('CONTINUE_EXTRUSION_WIDTH_RESIZE_MID_DRAG', { wall: draggedWallSegmentRef.current });
        } else if (draggedWallSegmentRef.current.isMainExtrusionSegment) {
            // Allow modifying existing extrusions
                isExtrudingRoomSegmentRef.current = true;
                isResizingExtrusionWidthRef.current = false;
            onDebug('CONTINUE_EXTRUSION_DEPTH_RESIZE_MID_DRAG', { wall: draggedWallSegmentRef.current });
        } else if (draggedWallSegmentRef.current.isSplitOrigin && !draggedWallSegmentRef.current.isMainExtrusionSegment && !draggedWallSegmentRef.current.isExtrusionConnector) {
            // 🔧 FIX: Allow split walls to be dragged/resized as normal walls unless they're extrusions
            // Split walls should behave like regular walls for drag/resize operations
            // NOTE: Connector detection is now handled in the touch setup phase
            isExtrudingRoomSegmentRef.current = false;
            isResizingExtrusionWidthRef.current = false;
            onDebug('CONTINUE_STANDARD_RESIZE_FOR_SPLIT_WALL', { 
                wall: draggedWallSegmentRef.current,
                reason: 'Split wall treated as normal wall for drag/resize'
            });
        } else if (draggedWallSegmentRef.current.isSplitOrigin) {
            // 🔧 BULLETPROOF EXTRUSION LOGIC: Use comprehensive state tracking (only for split walls that are also extrusions)
            const wallId = draggedWallSegmentRef.current.tempId!;
            // ⚡ FIX: Use expanded wall scope for extrusion decision
            const wallsForExtrusionDecision = tempWalls && tempWalls.length > 0 
              ? [...currentWalls, ...tempWalls] 
              : currentWalls;
            const extrusionDecision = shouldAllowExtrusionCreation(wallId, wallsForExtrusionDecision);
            
            debugExtrusionOperation('MID_DRAG_DECISION', {
              wallId,
              decision: extrusionDecision,
              wallFlags: {
                isSplitOrigin: (draggedWallSegmentRef.current as any).isSplitOrigin,
                isMainExtrusionSegment: (draggedWallSegmentRef.current as any).isMainExtrusionSegment,
                isExtrusionConnector: (draggedWallSegmentRef.current as any).isExtrusionConnector
              }
            });

            if (extrusionDecision.action === 'create-first') {
                // First drag - allow extrusion creation
                isExtrudingRoomSegmentRef.current = true;
                isResizingExtrusionWidthRef.current = false;
                
                // Update both tracking systems
                hasCreatedExtrusionRef.current.add(wallId);
                setExtrusionState(wallId, {
                  hasCreatedExtrusion: true,
                  extrusionType: 'first-creation'
                });
                
                debugExtrusionOperation('FIRST_EXTRUSION_ALLOWED', { wallId });
            } else if (extrusionDecision.action === 'modify-existing') {
                // Existing extrusion - allow modification
                isExtrudingRoomSegmentRef.current = true;
                isResizingExtrusionWidthRef.current = false;
                
                setExtrusionState(wallId, {
                  extrusionType: 'existing-main'
                });
                
                debugExtrusionOperation('EXISTING_EXTRUSION_MODIFICATION', { wallId });
            } else {
                // Blocked or standard resize
                isExtrudingRoomSegmentRef.current = false;
                isResizingExtrusionWidthRef.current = false;
                
                debugExtrusionOperation('EXTRUSION_BLOCKED', { 
                    wallId,
                    reason: extrusionDecision.reason,
                    action: extrusionDecision.action
                });
            }
        } else {
            isExtrudingRoomSegmentRef.current = false; 
            isResizingExtrusionWidthRef.current = false;
            onDebug('CONTINUE_STANDARD_RESIZE_MID_DRAG_CHECK', { wall: draggedWallSegmentRef.current });
      }
      return;
    }

      // Enhanced room selection with simple wall detection
      const allRooms = memoizedCalculateRooms(
        currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor})), 
        roomCustomLabels, 
        'geometryUpdate'
      );
      
      // Check if this click is on an existing wall for individual wall dragging
      const clickedWall = currentWalls.find(wall => 
        isPointOnWall(point, wall, WALL_RESIZE_HOVER_TOLERANCE / scale)
      );
      
      if (clickedWall) {
        // Find the room containing this wall for standard room resize
        const roomContainingWall = allRooms.find(room => 
          room.walls.some(roomWall => 
            (pointsAreEqual(roomWall.start, clickedWall.start, 0.1) && pointsAreEqual(roomWall.end, clickedWall.end, 0.1)) ||
            (pointsAreEqual(roomWall.start, clickedWall.end, 0.1) && pointsAreEqual(roomWall.end, clickedWall.start, 0.1))
          )
        );
        
        if (roomContainingWall) {
          setSelectedRoom(roomContainingWall);
          onDebug('ROOM_RESIZE_WALL_CLICKED', {
            wallClicked: `(${clickedWall.start.x.toFixed(1)},${clickedWall.start.y.toFixed(1)})->(${clickedWall.end.x.toFixed(1)},${clickedWall.end.y.toFixed(1)})`,
            roomSelected: roomContainingWall.stableId
          });
          
          // 🎯 PROFESSIONAL CAD: Enhanced wall analysis for auto-splitting (MOUSE VERSION)
          const wallAnalysis = analyzeWallClick(point, clickedWall, WALL_RESIZE_HOVER_TOLERANCE / scale, 15 / scale);
          
          onDebug('PROFESSIONAL_CAD_ANALYSIS_MOUSE', {
            wallId: clickedWall.tempId,
            clickType: wallAnalysis.clickType,
            shouldAutoSplit: wallAnalysis.shouldAutoSplit,
            splitPoint: wallAnalysis.splitPoint,
            endpointIndex: wallAnalysis.endpointIndex,
            distanceFromEndpoint: wallAnalysis.distanceFromNearestEndpoint
          });
          
          // 🎯 AUTO-SPLIT: Professional CAD behavior - split wall if clicking on middle
          if (wallAnalysis.shouldAutoSplit && wallAnalysis.splitPoint) {
            console.log('🎯 MOUSE AUTO-SPLIT TRIGGERED: Professional CAD behavior detected');
            
            onDebug('PROFESSIONAL_CAD_AUTO_SPLIT_START_MOUSE', {
              originalWallId: clickedWall.tempId,
              splitPoint: wallAnalysis.splitPoint,
              reason: 'User clicked on wall middle - auto-splitting before resize (mouse)'
            });
            
            // Temporarily select the wall for splitting
            setSelectedWall(clickedWall);
            
            // Execute the wall split with callback (pass wall directly to avoid race condition)
            handleWallSplit(wallAnalysis.splitPoint, (newSegments) => {
              if (newSegments.length === 2) {
                onDebug('PROFESSIONAL_CAD_AUTO_SPLIT_COMPLETE_MOUSE', {
                  originalWallId: clickedWall.tempId,
                  newSegment1Id: newSegments[0].tempId,
                  newSegment2Id: newSegments[1].tempId,
                  splitSuccessful: true
                });
                
                console.log('✅ MOUSE AUTO-SPLIT COMPLETE: Wall split into segments', newSegments.map(s => s.tempId));
              } else {
                onDebug('PROFESSIONAL_CAD_AUTO_SPLIT_FAILED_MOUSE', {
                  originalWallId: clickedWall.tempId,
                  expectedSegments: 2,
                  foundSegments: newSegments.length,
                  splitFailed: true
                });
                
                console.log('❌ MOUSE AUTO-SPLIT FAILED: Could not find new segments');
              }
            }, clickedWall);
            
            console.log('✅ MOUSE AUTO-SPLIT INITIATED: Wall will be split at point', wallAnalysis.splitPoint);
          } else {
            console.log('🎯 PROFESSIONAL CAD MOUSE: Standard endpoint resize detected');
          }
        }
      }

      // Standard room selection logic (existing wall endpoint or room selection)
      const roomAtClickPoint = findRoomAtPoint(point, allRooms);

      if (roomAtClickPoint) {
        setSelectedRoom(roomAtClickPoint);
        onDebug('ROOM_RESIZE_ROOM_SELECTED', { 
          roomId: roomAtClickPoint.stableId, 
          startPoint: point 
        });
      } else if (selectedRoom) {
        // Clicked outside any room while roomResize tool is active, deselect room
        setSelectedRoom(null);
        setResizeHandle(null);
        setIsResizing(false); isResizingRef.current = false;
        setTempWalls(null); setResizingRoomInfo(null); setActivelyResizedWalls(null);
        draggedWallSegmentRef.current = null; activeResizeOriginalWallTempIdsRef.current = null;
        onDebug('ROOM_RESIZE_ROOM_DESELECTED', { previousRoomId: selectedRoom.stableId });
      }
      return;
    }

    if (toolForThisInteraction === 'eraser') {
          // Enhanced eraser for touch devices - immediate feedback
          const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
          
          if (isTouchDevice) {
            // For touch devices, provide immediate visual feedback
            eraserPreviewPointRef.current = point;
            
            // Add a small delay to prevent accidental erasing on touch
            setTimeout(() => {
              if (eraserPreviewPointRef.current && 
                  Math.abs(eraserPreviewPointRef.current.x - point.x) < 5 &&
                  Math.abs(eraserPreviewPointRef.current.y - point.y) < 5) {
                eraseAtPoint(point);
              }
            }, 100); // 100ms delay for touch confirmation
          } else {
            // For mouse devices, erase immediately
            eraseAtPoint(point);
          }
          return;
        }
    if (toolForThisInteraction === 'split') {
      if (selectedWall && splitPoint) { handleWallSplit(splitPoint!); }
      return;
    }
     handleCanvasClick(point);
  }, [
    getCanvasCoordinates, isMouseDownRef, onDebug, currentToolInternalRef, roomStartRef, currentFloor, currentWalls, floorPlans, checkForNewRoomsAndCommit,
    setRoomStart, setCurrentPoint, handleWallTypeConversion, eraseAtPoint, handleTextBoxCreation, lastOperationTypeRef,
    scale, roomCustomLabels, recordUndoState, setFloorPlans, setSelectedRoom, setIsResizing, setIsDragging, setResizeHandle, 
    setTempWalls, setResizingRoomInfo, memoizedCalculateRooms
  ]);

  const handleCanvasMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const rawPoint = getCanvasCoordinates(e);
    const toolForThisInteraction = currentToolInternalRef.current;
    const isDraggingRoomCurrent = isDraggingRoomRef.current;
    const roomStartCurrent = roomStartRef.current;
    if (!toolForThisInteraction) return;

    if (isPanning && panStart) { handlePan(e.clientX, e.clientY); return; }

    if (isDraggingExtrusionRef.current && extrusionSegmentRef.current && originalWallLineForExtrusionRef.current && extrusionDragStartPointRef.current) {
      const draggedWall = extrusionSegmentRef.current;
      const originalLine = originalWallLineForExtrusionRef.current; // This is the line of the full original wall (neighbor1.other -> neighbor2.other)

      // Original points of the segment being dragged
      const pA = draggedWall.start; // Let's call it P_drag_orig_start
      const pB = draggedWall.end;   // Let's call it P_drag_orig_end

      // Vector of the dragged segment
      const segDX = pB.x - pA.x;
      const segDY = pB.y - pA.y;
      const segLength = Math.hypot(segDX, segDY);

      if (segLength === 0) return; // Should not happen for a valid wall

      // Normal vector (perpendicular to the segment, pointing "outwards" can be tricky)
      // For simplicity, let's assume horizontal/vertical walls first, or calculate based on rawPoint's side.
      // For a line (x1,y1) to (x2,y2), a perpendicular vector is (y2-y1, x1-x2) or (y1-y2, x2-x1)
      const perpDX = -segDY / segLength; // Normalized perpendicular vector component
      const perpDY = segDX / segLength;  // Normalized perpendicular vector component

      // Project rawPoint onto the line perpendicular to the segment and passing through its midpoint,
      // to find the displacement magnitude.
      // Midpoint of the original dragged segment:
      const midPAX = (pA.x + pB.x) / 2;
      const midPAY = (pA.y + pB.y) / 2;
      
      // Vector from midpoint to rawPoint
      const vecMidToRawX = rawPoint.x - midPAX;
      const vecMidToRawY = rawPoint.y - midPAY;

      // Dot product of this vector with the normalized perpendicular vector gives the displacement distance
      let displacement = (vecMidToRawX * perpDX + vecMidToRawY * perpDY);
      
      // Snap displacement to grid
      displacement = Math.round(displacement / CM_SNAP_SIZE) * CM_SNAP_SIZE;

      // New points for the dragged segment
      const newPA = { x: pA.x + perpDX * displacement, y: pA.y + perpDY * displacement };
      const newPB = { x: pB.x + perpDX * displacement, y: pB.y + perpDY * displacement };

      const tempWallList: WallWithTempId[] = [];

      // 1. The moved segment
      tempWallList.push({
        ...draggedWall,
        start: newPA,
        end: newPB,
        tempId: draggedWall.tempId || `extruded-${Date.now()}` // Ensure it has a tempId
      });

      // 2. Side wall 1
      tempWallList.push({
        start: pA,
        end: newPA,
        type: 'None', // Or inherit? For now, 'None'
        floor: currentFloor,
        tempId: `side1-${draggedWall.tempId}-${Date.now()}`
      });
      
      // 3. Side wall 2
      tempWallList.push({
        start: pB,
        end: newPB,
        type: 'None',
        floor: currentFloor,
        tempId: `side2-${draggedWall.tempId}-${Date.now()}`
      });
      
      // 4. Parts of the originalWallLineForExtrusion
      // originalLine.start and originalLine.end are the far ends of the neighbors.
      // pA and pB are the points where the draggedSegment connected to these neighbors.

      // Segment from originalLine.start to pA (if pA is not originalLine.start)
      if (!pointsAreEqual(originalLine.start, pA)) {
        tempWallList.push({
          start: originalLine.start,
          end: pA,
          type: 'None', // Assuming neighbors were 'None', or get their type
          floor: currentFloor,
          tempId: `origSeg1-${draggedWall.tempId}-${Date.now()}`
        });
      }
      // Segment from pB to originalLine.end (if pB is not originalLine.end)
      if (!pointsAreEqual(pB, originalLine.end)) {
        tempWallList.push({
          start: pB,
          end: originalLine.end,
          type: 'None',
          floor: currentFloor,
          tempId: `origSeg2-${draggedWall.tempId}-${Date.now()}`
        });
      }
      
      // 5. Add all other walls from currentWalls
      // Exclude the original draggedWall and its two original direct neighbors
      // We need to robustly identify those neighbors again.
      const { startNeighbor, endNeighbor } = getCollinearNeighbors(draggedWall, currentWalls);
      const idsToExclude = new Set<string | undefined>();
      idsToExclude.add(draggedWall.tempId);
      if (startNeighbor) idsToExclude.add(startNeighbor.tempId);
      if (endNeighbor) idsToExclude.add(endNeighbor.tempId);

      currentWalls.forEach(wall => {
        if (!idsToExclude.has(wall.tempId)) {
          tempWallList.push(wall);
        }
      });
      
      setTempWalls(tempWallList);
      return;
    }


    if (toolForThisInteraction === 'wall') {
      // If we have a start point, use enhanced line intersection snapping
      if (roomStartCurrent) {
        const targetWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
        const snapResult = snapToWallsWithLineIntersection(rawPoint, targetWalls, roomStartCurrent, scale, false);
        
        // Use snapped point for preview, fallback to raw point
        const previewPoint = snapResult.type !== 'none' ? snapResult.point : rawPoint;
        setCurrentPoint(previewPoint);
        
        // Update visual feedback for snapping
        setCurrentSnapResult(snapResult.type !== 'none' ? snapResult : null);
        
        // Debug line intersection snapping
        onDebug('MOUSE_LINE_INTERSECTION_WALL_SNAP', {
          rawPoint,
          previewPoint,
          snapType: snapResult.type,
          priority: snapResult.priority,
          isLineIntersectionSnap: snapResult.type === 'intersection' && snapResult.priority === 1.5,
          message: `Mouse wall preview snapped to ${snapResult.type} with line intersection support`
        });
      } else {
        // No start point yet, just use raw point
        setCurrentPoint(rawPoint); 
        setCurrentSnapResult(null);
      }
    } else if (toolForThisInteraction !== 'cornerAdjust' && toolForThisInteraction !== 'roomResize' && toolForThisInteraction !== 'eraser' && toolForThisInteraction !== 'split') {
      setCurrentPoint(null);
    }

    if (isDraggingRoomCurrent && roomStartCurrent && toolForThisInteraction === 'wall') {
      // Use enhanced line intersection snapping for room end point as well
      const targetWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
      const snapResult = snapToWallsWithLineIntersection(rawPoint, targetWalls, roomStartCurrent, scale, false);
      const snappedPoint = snapResult.type !== 'none' ? snapResult.point : rawPoint;
      
      console.log("[Hook handleCanvasMouseMove 'wall'] DRAGGING. RawPoint:", rawPoint, "SnappedPoint:", snappedPoint, "SnapType:", snapResult.type, "Current RoomEnd before set:", roomEndRef.current, "isDraggingRoom:", isDraggingRoomCurrent, "roomStart:", roomStartCurrent);
      setRoomEnd(snappedPoint);
      console.log("[Hook handleCanvasMouseMove 'wall'] Using SnappedPoint for roomEnd:", snappedPoint);
    }

    if (isResizingRef.current) {
      if (toolForThisInteraction === 'roomResize' && selectedRoom && resizeHandle) {
        handleRoomResize(rawPoint);
      }
      return;
    }

    // Seamless room switching and auto-start resize for roomResize tool
    if (toolForThisInteraction === 'roomResize' && isMouseDownRef.current && !touchStartPos) {
      // Always check what room we're currently over when mouse is down
              const allRooms = memoizedCalculateRooms(currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor})), roomCustomLabels, 'geometryUpdate');
      const roomAtCurrentPoint = findRoomAtPoint(rawPoint, allRooms);

      // If we're over a room and either no room is selected or it's different from current room
      if (roomAtCurrentPoint && (!selectedRoom || roomAtCurrentPoint.stableId !== selectedRoom.stableId)) {
        onDebug('ROOM_RESIZE_AUTO_SWITCHING_ROOM', { 
          fromRoomId: selectedRoom?.stableId, 
          toRoomId: roomAtCurrentPoint.stableId,
          mousePos: rawPoint 
        });
        
        // Reset any current resize state
        setIsResizing(false);
        isResizingRef.current = false;
        draggedWallSegmentRef.current = null;
        activeResizeOriginalWallTempIdsRef.current = null;
        setResizeHandle(null);
        setTempWalls(null);
        setActivelyResizedWalls(null);
        
        // Set up new room for resizing
        setSelectedRoom(roomAtCurrentPoint);
        
        // Start resize immediately
        const bounds = getRoomBounds(roomAtCurrentPoint.walls);
        
        // Determine resize direction based on which edge of the room is closest to mouse
        const roomCenterX = (bounds.minX + bounds.maxX) / 2;
        const roomCenterY = (bounds.minY + bounds.maxY) / 2;
        
        const deltaX = rawPoint.x - roomCenterX;
        const deltaY = rawPoint.y - roomCenterY;
        
        let resizeDirection: string;
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          resizeDirection = deltaX > 0 ? 'e' : 'w';
        } else {
          resizeDirection = deltaY > 0 ? 's' : 'n';
        }
        
        // Find the appropriate wall to resize
        const roomCandidateWalls: WallWithTempId[] = roomAtCurrentPoint.walls
          .map(rw => currentWalls.find(cw => 
            (pointsAreEqual(cw.start, rw.start) && pointsAreEqual(cw.end, rw.end)) ||
            (pointsAreEqual(cw.start, rw.end) && pointsAreEqual(cw.end, rw.start))
          ))
          .filter(Boolean) as WallWithTempId[];
        
        let wallToResize: WallWithTempId | null = null;
        for (const wall of roomCandidateWalls) {
          const wallDirection = getWallResizeDirection(wall, bounds);
          if (wallDirection === resizeDirection) {
            wallToResize = wall;
            break;
          }
        }
        
        // Fallback: find any wall that can be resized in the desired direction
        if (!wallToResize) {
          for (const wall of roomCandidateWalls) {
            const isHorizontalWall = Math.abs(wall.start.y - wall.end.y) < Math.abs(wall.start.x - wall.end.x);
            
            if ((resizeDirection === 'n' || resizeDirection === 's') && !isHorizontalWall) {
              wallToResize = wall;
              break;
            } else if ((resizeDirection === 'e' || resizeDirection === 'w') && isHorizontalWall) {
              wallToResize = wall;
              break;
            }
          }
        }
        
        if (wallToResize) {
          // Start the resize operation immediately
          resetConnectionLock(); // 🔒 Reset connection lock for new resize operation
          draggedWallSegmentRef.current = wallToResize;
          setResizeHandle(resizeDirection);
          setIsResizing(true);
          isResizingRef.current = true;
          setIsDragging(true);
          
          // Set up wall tracking with deduplication to prevent matching same wall multiple times
          const usedTempIds = new Set<string>();
          const originalWallTempIds = roomAtCurrentPoint.walls.map(roomWall_plain => {
            const match = currentWalls.find(cw => {
              // Skip if already used
              if (cw.tempId && usedTempIds.has(cw.tempId)) return false;
              
              return (pointsAreEqual(cw.start, roomWall_plain.start) && pointsAreEqual(cw.end, roomWall_plain.end)) ||
                     (pointsAreEqual(cw.start, roomWall_plain.end) && pointsAreEqual(cw.end, roomWall_plain.start));
            });
            
            if (match?.tempId) {
              usedTempIds.add(match.tempId);
            }
            return match?.tempId;
          }).filter(Boolean) as string[];
          
          console.log('🔍 INITIAL_WALL_TRACKING_SETUP:', {
            roomWallsCount: roomAtCurrentPoint.walls.length,
            matchedTempIdsCount: originalWallTempIds.length,
            shouldMatch: roomAtCurrentPoint.walls.length === originalWallTempIds.length ? '✅' : '❌'
          });
          
          activeResizeOriginalWallTempIdsRef.current = originalWallTempIds;
          
          // Set up initial states
          setTempWalls(currentWalls.map(w => ({ ...w, start: { ...w.start }, end: { ...w.end }, floor: w.floor || currentFloor })));
          if (roomAtCurrentPoint.stableId) {
            const currentLabel = roomCustomLabels[roomAtCurrentPoint.stableId];
            setResizingRoomInfo(currentLabel ? { originalStableId: roomAtCurrentPoint.stableId, label: currentLabel } : null);
          } else { 
            setResizingRoomInfo(null); 
          }
          initialWallsForResizeRef.current = currentWalls.map(w => ({ ...w, start: { ...w.start }, end: { ...w.end }, floor: w.floor || currentFloor }));
          
          // Reset extrusion flags for standard resize
          isExtrudingRoomSegmentRef.current = false;
          isResizingExtrusionWidthRef.current = false;
          
          onDebug('SEAMLESS_ROOM_RESIZE_INITIATED', { 
            roomId: roomAtCurrentPoint.stableId,
            resizeDirection: resizeDirection,
            wallId: wallToResize.tempId
          });
          
          // Immediately handle the resize
          handleRoomResize(rawPoint);
          return;
        }
      }
    }

    // Check for instant responsive room resize initiation (touch version)
    if (toolForThisInteraction === 'roomResize' && roomResizeStartPointRef.current && pendingRoomForResizeRef.current && !dragThresholdReachedRef.current) {
      const startPoint = roomResizeStartPointRef.current;
      const dragDistance = Math.hypot(rawPoint.x - startPoint.x, rawPoint.y - startPoint.y);
      const minDragThreshold = 15 / scale; // Slightly larger threshold for touch
      
      if (dragDistance > minDragThreshold) {
        dragThresholdReachedRef.current = true;
        const roomToResize = pendingRoomForResizeRef.current;
        const bounds = getRoomBounds(roomToResize.walls);
        
        // Determine drag direction and select appropriate wall/handle
        const dragVector = { x: rawPoint.x - startPoint.x, y: rawPoint.y - startPoint.y };
        const absDragX = Math.abs(dragVector.x);
        const absDragY = Math.abs(dragVector.y);
        
        // Determine resize handle based on drag direction
        let determinedHandle: string;
        if (absDragX > absDragY) {
          // Horizontal drag
          determinedHandle = dragVector.x > 0 ? 'e' : 'w';
        } else {
          // Vertical drag
          determinedHandle = dragVector.y > 0 ? 's' : 'n';
        }
        
        // Find the room's walls and select the appropriate one for resizing
        const roomCandidateWallsWithTempId: WallWithTempId[] = roomToResize.walls
          .map(rw => currentWalls.find(cw => 
            (pointsAreEqual(cw.start, rw.start) && pointsAreEqual(cw.end, rw.end)) ||
            (pointsAreEqual(cw.start, rw.end) && pointsAreEqual(cw.end, rw.start))
          ))
          .filter(Boolean) as WallWithTempId[];
          
        // Find the best wall to resize based on the handle direction
        let wallToResize: WallWithTempId | null = null;
        for (const wall of roomCandidateWallsWithTempId) {
          const wallDirection = getWallResizeDirection(wall, bounds);
          if (wallDirection === determinedHandle) {
            wallToResize = wall;
            break;
          }
        }
        
        // Fallback: find any wall that can be resized in the desired direction
        if (!wallToResize) {
          for (const wall of roomCandidateWallsWithTempId) {
            const isHorizontalWall = Math.abs(wall.start.y - wall.end.y) < Math.abs(wall.start.x - wall.end.x);
            
            if ((determinedHandle === 'n' || determinedHandle === 's') && !isHorizontalWall) {
              wallToResize = wall;
              break;
            } else if ((determinedHandle === 'e' || determinedHandle === 'w') && isHorizontalWall) {
              wallToResize = wall;
              break;
            }
          }
        }
        
        if (wallToResize) {
          // Start the resize operation
          resetConnectionLock(); // 🔒 Reset connection lock for new resize operation
          draggedWallSegmentRef.current = wallToResize;
          setResizeHandle(determinedHandle);
          setIsResizing(true);
          isResizingRef.current = true;
          setIsDragging(true);
          
          // Set up wall tracking with deduplication to prevent matching same wall multiple times
          const usedTempIds = new Set<string>();
          const originalWallTempIds = roomToResize.walls.map(roomWall_plain => {
            const match = currentWalls.find(cw => {
              // Skip if already used
              if (cw.tempId && usedTempIds.has(cw.tempId)) return false;
              
              return (pointsAreEqual(cw.start, roomWall_plain.start) && pointsAreEqual(cw.end, roomWall_plain.end)) ||
                     (pointsAreEqual(cw.start, roomWall_plain.end) && pointsAreEqual(cw.end, roomWall_plain.start));
            });
            
            if (match?.tempId) {
              usedTempIds.add(match.tempId);
            }
            return match?.tempId;
          }).filter(Boolean) as string[];
          
          console.log('🔍 INITIAL_WALL_TRACKING_SETUP_TOUCH:', {
            roomWallsCount: roomToResize.walls.length,
            matchedTempIdsCount: originalWallTempIds.length,
            shouldMatch: roomToResize.walls.length === originalWallTempIds.length ? '✅' : '❌'
          });
          
          activeResizeOriginalWallTempIdsRef.current = originalWallTempIds;
          
          // Reset extrusion flags for standard resize
          isExtrudingRoomSegmentRef.current = false;
          isResizingExtrusionWidthRef.current = false;
          
          onDebug('INSTANT_ROOM_RESIZE_INITIATED_TOUCH', { 
            roomId: roomToResize.stableId,
            dragDirection: determinedHandle,
            wallId: wallToResize.tempId,
            dragDistance: dragDistance.toFixed(2)
          });
          
          // Immediately handle the first resize step
          handleRoomResize(rawPoint);
      return;
        }
      }
    }
    
    if ((toolForThisInteraction === 'cornerAdjust' || toolForThisInteraction === 'roomResize') && isDraggingCorner && activeCornerInfoRef.current && tempWalls) {
        const { affectedWallsData } = activeCornerInfoRef.current;
        let snappedForAnglePoint = { ...rawPoint };
        const tempWallCopies = tempWalls.map(w => ({ ...w, start: { ...w.start }, end: { ...w.end } }));
        const affectedWallEntries = affectedWallsData.map(awd => {
            const wall = tempWallCopies.find(tw => tw.tempId === awd.tempId);
            return wall ? { wall, isStartCorner: awd.isStartPointCorner } : null;
        }).filter(Boolean) as Array<{ wall: WallWithTempId, isStartCorner: boolean }>;

        if (affectedWallEntries.length === 2) {
            const entry1 = affectedWallEntries[0]; const entry2 = affectedWallEntries[1];
            const fixedP1 = entry1.isStartCorner ? entry1.wall.end : entry1.wall.start;
            const fixedP2 = entry2.isStartCorner ? entry2.wall.end : entry2.wall.start;
            const toleranceWorld = SNAP_ANGLE_TOLERANCE_PX / scale;
            let bestSnapPos = { ...rawPoint };
            if (Math.abs(rawPoint.y - fixedP1.y) < toleranceWorld && Math.abs(rawPoint.x - fixedP2.x) < toleranceWorld) bestSnapPos = { x: fixedP2.x, y: fixedP1.y };
            else if (Math.abs(rawPoint.x - fixedP1.x) < toleranceWorld && Math.abs(rawPoint.y - fixedP2.y) < toleranceWorld) bestSnapPos = { x: fixedP1.x, y: fixedP2.y };
            else if (Math.abs(rawPoint.y - fixedP1.y) < toleranceWorld) bestSnapPos = { x: rawPoint.x, y: fixedP1.y };
            else if (Math.abs(rawPoint.x - fixedP1.x) < toleranceWorld) bestSnapPos = { x: fixedP1.x, y: rawPoint.y };
            else if (Math.abs(rawPoint.y - fixedP2.y) < toleranceWorld) bestSnapPos = { x: rawPoint.x, y: fixedP2.y };
            else if (Math.abs(rawPoint.x - fixedP2.x) < toleranceWorld) bestSnapPos = { x: fixedP2.x, y: rawPoint.y };
            snappedForAnglePoint = bestSnapPos;
        }
        lastRawDraggedCornerPosRef.current = { ...rawPoint };
        const newTempWallsNextState = tempWallCopies.map(currentTempWall => {
            const affectionData = affectedWallsData.find(data => data.tempId === currentTempWall.tempId);
            if (affectionData) {
                const updatedWall = { ...currentTempWall, start: { ...currentTempWall.start }, end: { ...currentTempWall.end } };
                if (affectionData.isStartPointCorner) updatedWall.start = { ...snappedForAnglePoint };
                else updatedWall.end = { ...snappedForAnglePoint };
                return updatedWall;
            }
            return currentTempWall;
        });
        setTempWalls(newTempWallsNextState);
        
        // Calculate and set corner drag preview
        const previewData = calculateCornerDragPreview(snappedForAnglePoint, affectedWallsData, currentWalls);
        setCornerDragPreview(previewData);
        return;
    }

    if (toolForThisInteraction === 'wall') {
      // 🆕 ENHANCED: Use simple wall snapping system for mouse (matches touch behavior)
      if (roomStartCurrent) {
        // Snap to nearby walls when drawing wall end point with enhanced detection
        const targetWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
        const snapResult = snapToWalls(rawPoint, targetWalls, scale, false);
        
        // Update visual feedback for snapping
        setCurrentSnapResult(snapResult.type !== 'none' ? snapResult : null);
        
        // Use snapped point for preview, fallback to raw point
        const previewPoint = snapResult.type !== 'none' ? snapResult.point : rawPoint;
        setCurrentPoint(previewPoint);
        
                 onDebug('MOUSE_SIMPLE_WALL_SNAP', {
           rawPoint,
           previewPoint,
           snapType: snapResult.type,
           message: `Mouse wall preview snapped to ${snapResult.type}`
         });
         
         // Also update roomEnd when dragging (matches touch behavior)
         if (isDraggingRoomCurrent) {
           setRoomEnd(previewPoint);
         }
       } else {
         // No start point yet - use basic snapping for hover
         const snappedHoverPoint = snapToFineGridOrEndpointsUtil(rawPoint, currentWalls, POINT_SNAP_THRESHOLD, CM_SNAP_SIZE);
         setCurrentPoint(snappedHoverPoint);
         // Clear snap result when no start point
         setCurrentSnapResult(null);
       }
       return;
     }
    
    if (toolForThisInteraction === 'eraser') {
      eraserPreviewPointRef.current = rawPoint;
    } else {
      eraserPreviewPointRef.current = null;
    }
    
    if (toolForThisInteraction === 'split') {
        let foundWall: WallWithTempId | null = null;
        let splitLocation: Point | null = null;
        for (const wall of currentWalls) {
            const pointOnWall = isPointOnWall(rawPoint, wall);
            if (pointOnWall) { foundWall = wall; splitLocation = pointOnWall; break; }
        }
        if (foundWall !== selectedWall || (splitLocation && (!splitPoint || splitLocation.x !== splitPoint.x || splitLocation.y !== splitPoint.y))) {
            setSelectedWall(foundWall); setSplitPoint(splitLocation);
        }
    }
    
    if ((toolForThisInteraction === 'cornerAdjust' || toolForThisInteraction === 'roomResize') && !isDraggingCorner) {
        const uniqueCorners = getUniqueCornerPoints(currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor })));
        let foundHoveredCorner: Point | null = null;
        // EXTRA LARGE hover detection for corners
        for (const corner of uniqueCorners) {
            if (isNearPoint(rawPoint, corner, SNAP_THRESHOLD * 10 / scale)) {
                foundHoveredCorner = corner;
                break;
            }
        }
        if (hoveredCorner?.x !== foundHoveredCorner?.x || hoveredCorner?.y !== foundHoveredCorner?.y) {
            setHoveredCorner(foundHoveredCorner);
        }
    } else if (toolForThisInteraction !== 'cornerAdjust' && toolForThisInteraction !== 'roomResize' && hoveredCorner) {
        setHoveredCorner(null);
    }
  }, [
    getCanvasCoordinates, isPanning, panStart, handlePan, isResizingRef, currentTool, isDragging, selectedRoom, resizeHandle,
    handleRoomResize, isDraggingCorner, activeCornerInfoRef, tempWalls, scale, lastRawDraggedCornerPosRef, setTempWalls,
    isDraggingRoom, roomStart, currentWalls, setRoomEnd, setCurrentPoint, eraserPreviewPointRef, selectedWall, splitPoint,
    setSelectedWall, setSplitPoint, hoveredCorner, setHoveredCorner, snapToFineGridOrEndpointsUtil,
    snapToEndpointsOrGridUtil, currentToolInternalRef, isDraggingRoomRef, roomStartRef, roomEndRef, currentFloor,
    // Add instant resize dependencies
    roomResizeStartPointRef, pendingRoomForResizeRef, dragThresholdReachedRef, setResizeHandle, setIsResizing, setIsDragging,
    activeResizeOriginalWallTempIdsRef, isExtrudingRoomSegmentRef, isResizingExtrusionWidthRef, draggedWallSegmentRef, onDebug,
    roomCustomLabels, setTempWalls, setResizingRoomInfo, initialWallsForResizeRef,
    // Add new wall snapping dependencies
    setCurrentSnapResult, snapToWalls
  ]);

  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    isMouseDownRef.current = false;
    const toolForThisInteraction = currentToolInternalRef.current;
    const isDraggingRoomCurrent = isDraggingRoomRef.current;
    const roomStartCurrent = roomStartRef.current;
    const roomEndCurrent = roomEndRef.current;
    const wasDraggingExtrusion = isDraggingExtrusionRef.current; // For the 'select' tool wall extrusion
    const wasExtrudingRoomSegment = isExtrudingRoomSegmentRef.current; // For 'roomResize' tool segment extrusion
    const finalResizingTempWalls = tempWalls ? [...tempWalls] : null;


    if (!toolForThisInteraction) return;

    onDebug('MOUSE_UP', { tool: toolForThisInteraction, isResizing: isResizingRef.current, isDraggingRoom: isDraggingRoomCurrent, wasDraggingExtrusion, wasExtrudingRoomSegment });

    if (wasDraggingExtrusion && extrusionSegmentRef.current && finalResizingTempWalls && toolForThisInteraction === 'select') {
      onDebug('EXTRUSION_DRAG_COMMIT (SELECT_TOOL)', { segment: extrusionSegmentRef.current.tempId, finalWalls: finalResizingTempWalls });
      // 🔧 ROOM MERGE FIX: Extrusion operations are geometry updates, not room creation
      if (lastOperationTypeRef) lastOperationTypeRef.current = 'geometryUpdate';
      recordUndoState?.(); // Record state before committing extrusion
      const wallsToCommit = finalResizingTempWalls.filter(
        wall => !pointsAreEqual(wall.start, wall.end, 0.1) // Increased tolerance to filter out very short walls
      ).map(w => {
        const { tempId, ...wallData } = w;
        return { 
          ...wallData, 
          floor: w.floor || currentFloor,
          // Explicitly preserve extrusion properties and their related data
          ...(w.isExtrusionConnector && { 
            isExtrusionConnector: true,
            parentWallId: (w as any).parentWallId,
            parentRoomIds: (w as any).parentRoomIds,
            connectedToSplitWallId: (w as any).connectedToSplitWallId,
            extrusionGroupId: (w as any).extrusionGroupId,
            // 🏠 PRESERVE ROOM OWNERSHIP
            originatingRoomId: (w as any).originatingRoomId,
            assignedRoomIds: (w as any).assignedRoomIds
          }),
          ...(w.isMainExtrusionSegment && { 
            isMainExtrusionSegment: true,
            parentWallId: (w as any).parentWallId,
            parentRoomIds: (w as any).parentRoomIds,
            // 🏠 PRESERVE ROOM OWNERSHIP
            originatingRoomId: (w as any).originatingRoomId,
            assignedRoomIds: (w as any).assignedRoomIds
          }),
          ...(w.isSplitOrigin && { 
            isSplitOrigin: true,
            parentWallId: (w as any).parentWallId,
            parentRoomIds: (w as any).parentRoomIds,
            requiresExtrusionOnMove: (w as any).requiresExtrusionOnMove
          })
        };
      });
      setFloorPlans(prev => ({ ...prev, [currentFloor]: wallsToCommit }));
      // Undo/Redo stack managed by recordUndoState in FloorPlanCreator
      triggerAutosave?.(); // Trigger autosave after wall changes
    } else if (toolForThisInteraction === 'roomResize' && isResizingRef.current && selectedRoom && finalResizingTempWalls) {
        const wasExtrudingRoomSegment = isExtrudingRoomSegmentRef.current; // Capture before reset
        
        // 🔍 RESIZE COMMIT DEBUG: Trace wall metadata before commit
        console.log('🔍 RESIZE_COMMIT_START: Analyzing walls before commit', {
          totalWalls: finalResizingTempWalls.length,
          wasExtrudingRoomSegment,
          selectedRoomId: selectedRoom.stableId
        });
        
        finalResizingTempWalls.forEach((wall, index) => {
          console.log(`🔍 WALL_${index}_BEFORE_COMMIT:`, {
            tempId: wall.tempId,
            coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
            // Metadata preservation check
            originatingRoomId: (wall as any).originatingRoomId,
            assignedRoomIds: (wall as any).assignedRoomIds,
            isSplitOrigin: (wall as any).isSplitOrigin,
            parentWallId: (wall as any).parentWallId,
            parentRoomIds: (wall as any).parentRoomIds,
            isExtrusionConnector: (wall as any).isExtrusionConnector,
            isMainExtrusionSegment: (wall as any).isMainExtrusionSegment,
            requiresExtrusionOnMove: (wall as any).requiresExtrusionOnMove,
            hasValidOwnership: !!((wall as any).originatingRoomId || (wall as any).assignedRoomIds?.length)
          });
        });
        
        recordUndoState?.(); // Record state before resize commit

        if (wasExtrudingRoomSegment) {
            onDebug('ROOM_RESIZE_EXTRUSION_COMMIT', { originalRoomId: selectedRoom.stableId, newWalls: finalResizingTempWalls });
            // 🔧 ROOM MERGE FIX: Room extrusion is geometry update, not room creation
            if (lastOperationTypeRef) lastOperationTypeRef.current = 'geometryUpdate';
      
            const wallsToCommitPlain: Wall[] = finalResizingTempWalls.map((w, mapIndex) => {
                const { tempId, ...wallData } = w;
                
                // 🔍 DEBUG: Before wall mapping
                console.log(`🔍 MAPPING_WALL_${mapIndex} (tempId: ${tempId}):`, {
                  beforeMapping: {
                    tempId,
                    coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
                    originatingRoomId: (w as any).originatingRoomId,
                    assignedRoomIds: (w as any).assignedRoomIds,
                    isSplitOrigin: (w as any).isSplitOrigin,
                    parentWallId: (w as any).parentWallId,
                    isExtrusionConnector: (w as any).isExtrusionConnector,
                    isMainExtrusionSegment: (w as any).isMainExtrusionSegment,
                    requiresExtrusionOnMove: (w as any).requiresExtrusionOnMove
                  },
                  wallDataAfterDestructuring: Object.keys(wallData)
                });
                
                // Explicitly preserve extrusion properties to ensure they're not lost
                const result: Wall = { 
                    ...wallData, 
                    floor: w.floor || currentFloor,
                    // Explicitly preserve extrusion properties and their related data
                    ...(w.isExtrusionConnector && { 
                        isExtrusionConnector: true,
                        parentWallId: (w as any).parentWallId,
                        parentRoomIds: (w as any).parentRoomIds,
                        connectedToSplitWallId: (w as any).connectedToSplitWallId,
                        extrusionGroupId: (w as any).extrusionGroupId,
                        // 🏠 PRESERVE ROOM OWNERSHIP
                        originatingRoomId: (w as any).originatingRoomId,
                        assignedRoomIds: (w as any).assignedRoomIds
                    }),
                    ...(w.isMainExtrusionSegment && { 
                        isMainExtrusionSegment: true,
                        parentWallId: (w as any).parentWallId,
                        parentRoomIds: (w as any).parentRoomIds,
                        // 🏠 PRESERVE ROOM OWNERSHIP
                        originatingRoomId: (w as any).originatingRoomId,
                        assignedRoomIds: (w as any).assignedRoomIds
                    }),
                    ...(w.isSplitOrigin && { 
                        isSplitOrigin: true,
                        parentWallId: (w as any).parentWallId,
                        parentRoomIds: (w as any).parentRoomIds,
                        requiresExtrusionOnMove: (w as any).requiresExtrusionOnMove
                    })
                };
                
                // 🔍 DEBUG: After all metadata preservation logic
                console.log(`🔍 FINAL_MAPPED_WALL_${mapIndex} (tempId: ${tempId}):`, {
                   afterMapping: {
                     coords: `(${result.start.x.toFixed(1)},${result.start.y.toFixed(1)})->(${result.end.x.toFixed(1)},${result.end.y.toFixed(1)})`,
                     originatingRoomId: (result as any).originatingRoomId,
                     assignedRoomIds: (result as any).assignedRoomIds,
                     isSplitOrigin: (result as any).isSplitOrigin,
                     parentWallId: (result as any).parentWallId,
                     parentRoomIds: (result as any).parentRoomIds,
                     isExtrusionConnector: (result as any).isExtrusionConnector,
                     isMainExtrusionSegment: (result as any).isMainExtrusionSegment,
                     requiresExtrusionOnMove: (result as any).requiresExtrusionOnMove,
                     hasValidOwnership: !!((result as any).originatingRoomId || (result as any).assignedRoomIds?.length),
                     allFields: Object.keys(result)
                   },
                   metadataPreservationSuccess: {
                     splitOrigin: (w as any).isSplitOrigin === (result as any).isSplitOrigin,
                     ownership: ((w as any).originatingRoomId === (result as any).originatingRoomId) && 
                               JSON.stringify((w as any).assignedRoomIds) === JSON.stringify((result as any).assignedRoomIds)
                   }
                 });
                 
                 return result;
      });

            let updatedLabels = { ...roomCustomLabels };
            if (resizingRoomInfo && resizingRoomInfo.originalStableId && resizingRoomInfo.label) {
                const originalWallsPlain = currentWalls.map(({ tempId, ...wall }) => ({
        ...wall, 
        floor: wall.floor || currentFloor,
        // Preserve split wall metadata
        ...(wall.isSplitOrigin && { isSplitOrigin: wall.isSplitOrigin }),
        ...(wall.parentWallId && { parentWallId: wall.parentWallId }),
        ...(wall.parentRoomIds && { parentRoomIds: wall.parentRoomIds }),
        ...(wall.requiresExtrusionOnMove && { requiresExtrusionOnMove: wall.requiresExtrusionOnMove }),
        ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: wall.isMainExtrusionSegment }),
        ...(wall.isExtrusionConnector && { isExtrusionConnector: wall.isExtrusionConnector })
      }));
                
                // 🔧 CRITICAL ROOM MERGE FIX: Use complete floor plan walls for label migration
                // During resize, originalWallsPlain/wallsToCommitPlain only contain ~4 walls from resizing room
                // but labelPreservation context in migrateRoomLabels needs ALL floor plan walls to maintain accurate room counts
                const completeOriginalWalls = floorPlans[currentFloor] || [];  // Complete floor plan (before resize)
                const completeNewWalls = wallsToCommitPlain.filter(w => w && typeof w === 'object' && w.start && w.end);  // Complete floor plan (after resize)
                const migrationResult = migrateRoomLabels(
                    completeOriginalWalls,   // Complete floor plan walls (before)
                    completeNewWalls,        // Complete floor plan walls (after) 
                    roomCustomLabels,        // Pass all room labels
                    currentFloor
                );

                // 🔧 CRITICAL DEBUG: Log label migration details to identify state corruption
                console.log('🔧 LABEL MIGRATION DEBUG:', {
                    beforeMigration: {
                        updatedLabelsCount: Object.keys(updatedLabels).length,
                        updatedLabelsKeys: Object.keys(updatedLabels),
                        roomCustomLabelsCount: Object.keys(roomCustomLabels).length,
                        roomCustomLabelsKeys: Object.keys(roomCustomLabels)
                    },
                    migrationResult: {
                        labelsCount: Object.keys(migrationResult.labels).length,
                        labelsKeys: Object.keys(migrationResult.labels),
                        labelsValues: Object.values(migrationResult.labels),
                        migrationMapSize: Object.keys(migrationResult.migrationMap).length
                    }
                });
                
                // Update the main labels map with all migrated labels
                updatedLabels = { ...updatedLabels, ...migrationResult.labels };
                
                console.log('🔧 AFTER LABEL MERGE:', {
                    finalUpdatedLabelsCount: Object.keys(updatedLabels).length,
                    finalUpdatedLabelsKeys: Object.keys(updatedLabels),
                    finalUpdatedLabelsValues: Object.values(updatedLabels)
                });
                
                onDebug('ROOM_RESIZE_LABEL_MIGRATION_COMPLETE', {
                    originalLabelsCount: Object.keys(roomCustomLabels).length,
                    migratedLabelsCount: Object.keys(migrationResult.labels).length,
                    resizedRoomId: resizingRoomInfo.originalStableId,
                    migrationMapSize: Object.keys(migrationResult.migrationMap).length
                });
            }
            setRoomCustomLabels(updatedLabels);
            
            const checkedWallsToCommitPlain = wallsToCommitPlain.filter(
                w => w && typeof w === 'object' && w.start && w.end
            );
            if (checkedWallsToCommitPlain.length !== wallsToCommitPlain.length && onDebug) {
                onDebug("HOOK_MOUSEUP_EXTRUSION_INVALID_WALL_FILTERED", {
                    originalCount: wallsToCommitPlain.length,
                    newCount: checkedWallsToCommitPlain.length,
                });
            }
            
            // 🔍 CRITICAL DEBUG: What's being committed to state
            console.log('🔍 COMMITTING_TO_STATE:', {
              totalWalls: checkedWallsToCommitPlain.length,
              currentFloor,
              wallsBeingCommitted: checkedWallsToCommitPlain.map((wall, idx) => ({
                index: idx,
                coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
                hasOwnership: !!((wall as any).originatingRoomId || (wall as any).assignedRoomIds?.length),
                originatingRoomId: (wall as any).originatingRoomId,
                assignedRoomIds: (wall as any).assignedRoomIds,
                isSplitOrigin: (wall as any).isSplitOrigin,
                parentWallId: (wall as any).parentWallId,
                isExtrusionConnector: (wall as any).isExtrusionConnector,
                isMainExtrusionSegment: (wall as any).isMainExtrusionSegment,
                requiresExtrusionOnMove: (wall as any).requiresExtrusionOnMove,
                allMetadataFields: Object.keys(wall).filter(k => !['start', 'end', 'type', 'floor'].includes(k))
              }))
            });
            
            setFloorPlans(prev => {
              const newFloorPlan = { ...prev, [currentFloor]: checkedWallsToCommitPlain };
              
              // 🔍 DEBUG: State after commit
              console.log('🔍 STATE_AFTER_COMMIT:', {
                floorPlan: newFloorPlan[currentFloor]?.map((wall, idx) => ({
                  index: idx,
                  coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
                  hasOwnership: !!((wall as any).originatingRoomId || (wall as any).assignedRoomIds?.length),
                  originatingRoomId: (wall as any).originatingRoomId,
                  assignedRoomIds: (wall as any).assignedRoomIds,
                  isSplitOrigin: (wall as any).isSplitOrigin,
                  parentWallId: (wall as any).parentWallId,
                  metadataPreserved: !!Object.keys(wall).find(k => ['isSplitOrigin', 'parentWallId', 'originatingRoomId', 'assignedRoomIds'].includes(k))
                }))
              });
              
              return newFloorPlan;
            });
            // Undo/Redo stack managed by recordUndoState in FloorPlanCreator
            triggerAutosave?.(); // Trigger autosave after wall changes
            // The lines below were removed as previousFPState is not defined in this scope,
            // and recordUndoState() (called earlier) is expected to handle undo/redo stack updates.
            // setUndoStack(prev => [...prev, previousFPState]);
            // setRedoStack([]);
            
            // 🔧 CRITICAL ROOM MERGE FIX: Use complete floor plan walls, not just resizing room's walls
            // During resize, wallsToCommitPlain only contains ~4 walls from the resizing room
            // but labelPreservation context needs ALL floor plan walls to maintain accurate room counts
            const completeFloorWalls = floorPlans[currentFloor] || [];
            const newRoomsAfterExtrusion = calculateRooms(completeFloorWalls, updatedLabels, 'labelPreservation');
            let newSelectedRoom: Room | null = null;

            if (resizingRoomInfo && resizingRoomInfo.originalStableId) {
                // Try to find the room by the (potentially migrated) new stable ID
                const migratedKeys = Object.keys(updatedLabels).filter(k => updatedLabels[k] === resizingRoomInfo.label);
                if (migratedKeys.length > 0) {
                    newSelectedRoom = newRoomsAfterExtrusion.find(r => r.stableId === migratedKeys[0]) || null;
                }
            }
            
            // Fallback if direct stable ID match fails after migration
            if (!newSelectedRoom && draggedWallSegmentRef.current) {
                const movedSegmentOriginalTempId = draggedWallSegmentRef.current.tempId; // this might be the ID of the wall *before* extrusion
                // The issue here is that draggedWallSegmentRef.current may not exist with the same tempId in finalResizingTempWalls
                // especially if it was replaced. Let's try finding a room near the original room's center.
                 if (selectedRoom.center) {
                     newSelectedRoom = findRoomAtPoint(selectedRoom.center, newRoomsAfterExtrusion);
                 }
            }
            
            setSelectedRoom(newSelectedRoom || null);
            justResizedRef.current = true;
            
            // 🔧 FIX: Add delayed state cleanup to prevent permanent corruption
            setTimeout(() => {
                console.log('🔧 RESIZE_COMPLETION_CLEANUP: Ensuring justResizedRef and isWallSplitting are reset after resize');
                justResizedRef.current = false;
                setIsWallSplitting(false); // 🔧 CRITICAL: Reset wall splitting flag to restore labeling
            }, 500); // Longer delay to ensure all effects have processed

        } else { // Standard room resize commit
            onDebug('ROOM_RESIZE_STANDARD_COMMIT', { originalRoom: selectedRoom, newWalls: finalResizingTempWalls });
            
            // 🔍 STANDARD RESIZE COMMIT DEBUG: Trace wall metadata before commit
            console.log('🔍 STANDARD_RESIZE_COMMIT_START: Analyzing walls before commit', {
              totalWalls: finalResizingTempWalls.length,
              selectedRoomId: selectedRoom.stableId,
              selectedRoomName: selectedRoom.stableId ? (roomCustomLabels[selectedRoom.stableId] || 'Unknown') : 'NoStableId'
            });
            
            finalResizingTempWalls.forEach((wall, index) => {
              console.log(`🔍 STD_WALL_${index}_BEFORE_COMMIT:`, {
                tempId: wall.tempId,
                coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
                // Metadata preservation check
                originatingRoomId: (wall as any).originatingRoomId,
                assignedRoomIds: (wall as any).assignedRoomIds,
                isSplitOrigin: (wall as any).isSplitOrigin,
                parentWallId: (wall as any).parentWallId,
                parentRoomIds: (wall as any).parentRoomIds,
                isExtrusionConnector: (wall as any).isExtrusionConnector,
                isMainExtrusionSegment: (wall as any).isMainExtrusionSegment,
                requiresExtrusionOnMove: (wall as any).requiresExtrusionOnMove,
                hasValidOwnership: !!((wall as any).originatingRoomId || (wall as any).assignedRoomIds?.length)
              });
            });
            
            if (lastOperationTypeRef) lastOperationTypeRef.current = 'geometryUpdate';
            recordUndoState?.(); // Record state before resize commit
            
            // const previousFPState = { ...floorPlans }; // Not a complete UndoRedoState, remove if not used for that.
            
            const originalStableId = selectedRoom.stableId;
            const originalLabel = originalStableId ? roomCustomLabels[originalStableId] : null;
            
            const finalPlainWallsSource = finalResizingTempWalls.map(({ tempId, ...w }, mapIndex) => {
                // 🔍 DEBUG: Before wall mapping in standard resize
                console.log(`🔍 STD_MAPPING_WALL_${mapIndex} (tempId: ${tempId}):`, {
                  beforeMapping: {
                    tempId,
                    coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
                    originatingRoomId: (w as any).originatingRoomId,
                    assignedRoomIds: (w as any).assignedRoomIds,
                    isSplitOrigin: (w as any).isSplitOrigin,
                    parentWallId: (w as any).parentWallId,
                    isExtrusionConnector: (w as any).isExtrusionConnector,
                    isMainExtrusionSegment: (w as any).isMainExtrusionSegment,
                    requiresExtrusionOnMove: (w as any).requiresExtrusionOnMove
                  },
                  wallDataAfterDestructuring: Object.keys(w)
                });
                
                const result = {
                ...w, 
                floor: w.floor || currentFloor,
                // Explicitly preserve extrusion properties and their related data
                ...(w.isExtrusionConnector && { 
                    isExtrusionConnector: true,
                    parentWallId: (w as any).parentWallId,
                    parentRoomIds: (w as any).parentRoomIds,
                    connectedToSplitWallId: (w as any).connectedToSplitWallId,
                    extrusionGroupId: (w as any).extrusionGroupId,
                    // 🏠 PRESERVE ROOM OWNERSHIP
                    originatingRoomId: (w as any).originatingRoomId,
                    assignedRoomIds: (w as any).assignedRoomIds
                }),
                ...(w.isMainExtrusionSegment && { 
                    isMainExtrusionSegment: true,
                    parentWallId: (w as any).parentWallId,
                    parentRoomIds: (w as any).parentRoomIds,
                    // 🏠 PRESERVE ROOM OWNERSHIP
                    originatingRoomId: (w as any).originatingRoomId,
                    assignedRoomIds: (w as any).assignedRoomIds
                }),
                ...(w.isSplitOrigin && { 
                    isSplitOrigin: true,
                    parentWallId: (w as any).parentWallId,
                    parentRoomIds: (w as any).parentRoomIds,
                    requiresExtrusionOnMove: (w as any).requiresExtrusionOnMove
                })
            };
            
            // 🔍 DEBUG: After all metadata preservation logic in standard resize
            console.log(`🔍 STD_FINAL_MAPPED_WALL_${mapIndex} (tempId: ${tempId}):`, {
               afterMapping: {
                 coords: `(${result.start.x.toFixed(1)},${result.start.y.toFixed(1)})->(${result.end.x.toFixed(1)},${result.end.y.toFixed(1)})`,
                 originatingRoomId: (result as any).originatingRoomId,
                 assignedRoomIds: (result as any).assignedRoomIds,
                 isSplitOrigin: (result as any).isSplitOrigin,
                 parentWallId: (result as any).parentWallId,
                 parentRoomIds: (result as any).parentRoomIds,
                 isExtrusionConnector: (result as any).isExtrusionConnector,
                 isMainExtrusionSegment: (result as any).isMainExtrusionSegment,
                 requiresExtrusionOnMove: (result as any).requiresExtrusionOnMove,
                 hasValidOwnership: !!((result as any).originatingRoomId || (result as any).assignedRoomIds?.length),
                 allFields: Object.keys(result)
               },
               metadataPreservationSuccess: {
                 splitOrigin: (w as any).isSplitOrigin === (result as any).isSplitOrigin,
                 ownership: ((w as any).originatingRoomId === (result as any).originatingRoomId) && 
                           JSON.stringify((w as any).assignedRoomIds) === JSON.stringify((result as any).assignedRoomIds)
               }
             });
             
             return result;
            });
            
            const finalPlainWalls = finalPlainWallsSource.filter(
                w => w && typeof w === 'object' && w.start && w.end
            );
            if (finalPlainWalls.length !== finalPlainWallsSource.length && onDebug) {
                onDebug("HOOK_MOUSEUP_STD_RESIZE_INVALID_WALL_FILTERED", {
                    originalCount: finalPlainWallsSource.length,
                    newCount: finalPlainWalls.length,
                });
            }
            
            setFloorPlans(prev => ({ ...prev, [currentFloor]: finalPlainWalls }));
            // Undo/Redo stack managed by recordUndoState in FloorPlanCreator
            triggerAutosave?.(); // Trigger autosave after wall changes
            
            // Calculate new rooms with current labels
            const newRoomsAfterResize = calculateRooms(finalPlainWalls, roomCustomLabels, 'geometryUpdate');
            
            // Update survey data with new room dimensions if callback provided
            console.log('🔄 [useFloorPlanInteractions] Checking survey update callback:', { hasCallback: !!updateSurveyDataAfterResize, roomCount: newRoomsAfterResize.length });
            if (updateSurveyDataAfterResize) {
                console.log('🎯 [useFloorPlanInteractions] Calling updateSurveyDataAfterResize with rooms:', newRoomsAfterResize.map(r => ({ id: r.stableId, name: r.name })));
                updateSurveyDataAfterResize(newRoomsAfterResize);
            } else {
                console.log('❌ [useFloorPlanInteractions] Survey update callback not available');
            }
            
            // Simple room selection: find room by the resized walls
            let newSelectedRoom: Room | null = null;
            
            if (activelyResizedWalls && activelyResizedWalls.length > 0) {
                // Find the room that contains the actively resized walls
                newSelectedRoom = newRoomsAfterResize.find(room => {
                    return activelyResizedWalls.some(resizedWall => 
                        room.walls.some(roomWall => 
                            (pointsAreEqual(roomWall.start, resizedWall.start, 1) && pointsAreEqual(roomWall.end, resizedWall.end, 1)) ||
                            (pointsAreEqual(roomWall.start, resizedWall.end, 1) && pointsAreEqual(roomWall.end, resizedWall.start, 1))
                        )
                    );
                }) || null;
            }
            
            // If we found the resized room and we have the original label, preserve it
            if (newSelectedRoom && originalLabel && originalStableId) {
                const newStableId = newSelectedRoom.stableId;
                if (newStableId && newStableId !== originalStableId) {
                    // Room ID changed, update the label mapping
                    const updatedLabels = { ...roomCustomLabels };
                    delete updatedLabels[originalStableId]; // Remove old mapping
                    updatedLabels[newStableId] = originalLabel; // Add new mapping
                    setRoomCustomLabels(updatedLabels);
                    
                    onDebug('ROOM_RESIZE_LABEL_MIGRATED', { 
                        from: originalStableId, 
                        to: newStableId, 
                        label: originalLabel 
                    });
                } else if (newStableId === originalStableId) {
                    // ID stayed the same, no need to change labels
                    onDebug('ROOM_RESIZE_ID_PRESERVED', { stableId: originalStableId, label: originalLabel });
                }
            }
            
            setSelectedRoom(newSelectedRoom);
            justResizedRef.current = true;
            
            // 🔧 FIX: Add delayed state cleanup to prevent permanent corruption
            setTimeout(() => {
                console.log('🔧 RESIZE_COMPLETION_CLEANUP: Ensuring justResizedRef and isWallSplitting are reset after resize');
                justResizedRef.current = false;
                setIsWallSplitting(false); // 🔧 CRITICAL: Reset wall splitting flag to restore labeling
            }, 500); // Longer delay to ensure all effects have processed
        }
    }

    if ((toolForThisInteraction === 'cornerAdjust' || toolForThisInteraction === 'roomResize') && isDraggingCorner && activeCornerInfoRef.current && lastRawDraggedCornerPosRef.current) {
        // Use the same snapping logic as the preview - get the position from temp walls which already has angle snapping applied
        let pointToCommit = lastRawDraggedCornerPosRef.current;
        
        // Apply the same angle snapping logic that was used in the preview
        const { affectedWallsData } = activeCornerInfoRef.current;
        let snappedForAnglePoint = { ...pointToCommit };
        
        if (tempWalls && affectedWallsData.length === 2) {
            const tempWallCopies = tempWalls.map(w => ({ ...w, start: { ...w.start }, end: { ...w.end } }));
            const affectedWallEntries = affectedWallsData.map(awd => {
                const wall = tempWallCopies.find(tw => tw.tempId === awd.tempId);
                return wall ? { wall, isStartCorner: awd.isStartPointCorner } : null;
            }).filter(Boolean) as Array<{ wall: WallWithTempId, isStartCorner: boolean }>;

            if (affectedWallEntries.length === 2) {
                const entry1 = affectedWallEntries[0]; 
                const entry2 = affectedWallEntries[1];
                const fixedP1 = entry1.isStartCorner ? entry1.wall.end : entry1.wall.start;
                const fixedP2 = entry2.isStartCorner ? entry2.wall.end : entry2.wall.start;
                const toleranceWorld = SNAP_ANGLE_TOLERANCE_PX / scale;
                
                if (Math.abs(pointToCommit.y - fixedP1.y) < toleranceWorld && Math.abs(pointToCommit.x - fixedP2.x) < toleranceWorld) 
                    snappedForAnglePoint = { x: fixedP2.x, y: fixedP1.y };
                else if (Math.abs(pointToCommit.x - fixedP1.x) < toleranceWorld && Math.abs(pointToCommit.y - fixedP2.y) < toleranceWorld) 
                    snappedForAnglePoint = { x: fixedP1.x, y: fixedP2.y };
                else if (Math.abs(pointToCommit.y - fixedP1.y) < toleranceWorld) 
                    snappedForAnglePoint = { x: pointToCommit.x, y: fixedP1.y };
                else if (Math.abs(pointToCommit.x - fixedP1.x) < toleranceWorld) 
                    snappedForAnglePoint = { x: fixedP1.x, y: pointToCommit.y };
                else if (Math.abs(pointToCommit.y - fixedP2.y) < toleranceWorld) 
                    snappedForAnglePoint = { x: pointToCommit.x, y: fixedP2.y };
                else if (Math.abs(pointToCommit.x - fixedP2.x) < toleranceWorld) 
                    snappedForAnglePoint = { x: fixedP2.x, y: pointToCommit.y };
            }
        }
        
        // Apply only CM grid snapping (same as preview)
        pointToCommit = { 
            x: Math.round(snappedForAnglePoint.x / CM_SNAP_SIZE) * CM_SNAP_SIZE, 
            y: Math.round(snappedForAnglePoint.y / CM_SNAP_SIZE) * CM_SNAP_SIZE 
        };
        
        onDebug('TOUCH_CORNER_ADJUST_COMMIT', { rawPoint: lastRawDraggedCornerPosRef.current, snappedPoint: pointToCommit });
        if (lastOperationTypeRef) lastOperationTypeRef.current = 'explicitRoomCreation';
        recordUndoState?.(); // Record state before corner adjust commit
        // FIXED: Proper corner adjustment logic
        const wallsToCommit: Wall[] = currentWalls.map(wallWithTempId => {
            const affectionData = affectedWallsData.find(data => data.tempId === wallWithTempId.tempId);
            let plainWall = (({ tempId, ...rest }) => ({ ...rest, floor: rest.floor || currentFloor }))(wallWithTempId);
            if (affectionData) {
                const newWallGeom = { ...plainWall, start: { ...plainWall.start }, end: { ...plainWall.end } };
                if (affectionData.isStartPointCorner) newWallGeom.start = { ...pointToCommit! };
                else newWallGeom.end = { ...pointToCommit! };
                return newWallGeom;
            }
            return plainWall;
        });
        setFloorPlans(prev => ({ ...prev, [currentFloor]: wallsToCommit }));
        triggerAutosave?.(); // Trigger autosave after wall changes
        
        // Clear corner dragging state and preview
        setIsDraggingCorner(false);
        activeCornerInfoRef.current = null;
        lastRawDraggedCornerPosRef.current = null;
        setTempWalls(null);
        setCornerDragPreview(null);
    }

    if (toolForThisInteraction === 'wall' && isDraggingRoomCurrent && roomStartCurrent && roomEndCurrent) {
      onDebug("[Hook handleMouseUp 'wall'] Triggered", { 
        roomStart: roomStartCurrent, 
        roomEnd: roomEndCurrent 
      });
      if (Math.abs(roomStartCurrent.x - roomEndCurrent.x) > CM_SNAP_SIZE || Math.abs(roomStartCurrent.y - roomEndCurrent.y) > CM_SNAP_SIZE) {
        const mappedWalls: Wall[] = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
        // 🚫 BYPASS: Temporarily disable strict intersection check to allow room connections
        const BYPASS_STRICT_INTERSECTION_CHECK = true;
        
        if (BYPASS_STRICT_INTERSECTION_CHECK || !(checkRectangleWallIntersectionUniqueName as any)(roomStartCurrent as any, roomEndCurrent as any, mappedWalls as any )) { // Cast to any
          // 🎯 ROOT CAUSE FIX: Apply precision control to room creation
          // Use 0.1 precision instead of GRID_SIZE (30) to prevent coordinate drift
          const precisionRoomStart = snapPointToGrid(roomStartCurrent, 0.1);
          const precisionRoomEnd = snapPointToGrid(roomEndCurrent, 0.1);
          
          // Create walls for the new room (grid-snapped coordinates)
          const gridSnappedWalls: Wall[] = [
            { start: { ...precisionRoomStart }, end: { x: precisionRoomEnd.x, y: precisionRoomStart.y }, type: 'None', floor: currentFloor },
            { start: { x: precisionRoomEnd.x, y: precisionRoomStart.y }, end: { ...precisionRoomEnd }, type: 'None', floor: currentFloor },
            { start: { ...precisionRoomEnd }, end: { x: precisionRoomStart.x, y: precisionRoomEnd.y }, type: 'None', floor: currentFloor },
            { start: { x: precisionRoomStart.x, y: precisionRoomEnd.y }, end: { ...precisionRoomStart }, type: 'None', floor: currentFloor }
          ];

          // 🔬 COORDINATE COLLISION PREVENTION: Apply smart offsets AFTER grid snapping to prevent override
          const existingMappedWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
          const { processedWalls: wallsWithoutOwnership, offsetsApplied, isConnecting } = applySmartCoordinateOffsets(
            gridSnappedWalls,
            existingMappedWalls,
            { enableOffset: true }
          );

          console.log('🔬 ROOM CREATION COORDINATE PROCESSING:', {
            isConnectingRoom: isConnecting,
            offsetsApplied,
            roomBounds: `${precisionRoomStart.x},${precisionRoomStart.y} -> ${precisionRoomEnd.x},${precisionRoomEnd.y}`,
            wallCount: wallsWithoutOwnership.length,
            prevention: offsetsApplied > 0 ? 'Coordinate collisions prevented' : 'No offsets needed'
          });
          
          // 🏠 CRITICAL FIX: Calculate canonical room ID and assign ownership to all walls at creation
          const canonicalRoomId = getPersistentRoomId(wallsWithoutOwnership, currentFloor);
          console.log('🏠 ROOM_CREATION_WITH_OWNERSHIP: Assigning canonical ownership to new walls', {
            canonicalRoomId,
            wallCount: wallsWithoutOwnership.length,
            roomBounds: `${roomStartCurrent.x},${roomStartCurrent.y} -> ${roomEndCurrent.x},${roomEndCurrent.y}`
          });
          
          const walls: Wall[] = wallsWithoutOwnership.map((wall, index) => ({
            ...wall,
            // Assign canonical ownership at creation time
            originatingRoomId: canonicalRoomId,
            assignedRoomIds: [canonicalRoomId]
          }));
          if (lastOperationTypeRef) lastOperationTypeRef.current = 'explicitRoomCreation';
          const previousFPState = { ...floorPlans };
          console.log("[Hook handleMouseUp 'wall'] Creating room with walls:", walls);
          checkForNewRoomsAndCommit([...currentWalls.map(({tempId, ...w})=>({...w, floor: w.floor || currentFloor})), ...walls]);
        } else {
          console.log("[Hook handleMouseUp 'wall'] Room intersects existing walls. Not created.");
        }
      } else {
        console.log("[Hook handleMouseUp 'wall'] Room too small. Not created.");
      }
    }

    // Handle split tool for touch
    if (toolForThisInteraction === 'split' && selectedWall && splitPoint) { // Split regardless of movement - removed !touchMoved
      onDebug('TOUCH_END_SPLIT_ATTEMPTING', { 
        hasSelectedWall: !!selectedWall, 
        selectedWallId: selectedWall?.tempId,
        hasSplitPoint: !!splitPoint, 
        splitPoint: splitPoint,
        toolForThisInteraction,
        touchMoved // Log touchMoved state for clarity
      });
      handleWallSplit(splitPoint);
      onDebug('TOUCH_END_SPLIT_EXECUTED', { splitPoint, touchMoved });
    } else if (toolForThisInteraction === 'split') {
      onDebug('TOUCH_END_SPLIT_FAILED_CONDITIONS', { 
        toolForThisInteraction,
        hasSelectedWall: !!selectedWall, 
        selectedWallId: selectedWall?.tempId,
        hasSplitPoint: !!splitPoint,
        splitPoint,
        touchMoved
      });
    }

    // Reset common states after any interaction
    setIsResizing(false); isResizingRef.current = false;
    setTempWalls(null); 
    setActivelyResizedWalls(null);
    setIsDraggingRoom(false); setRoomStart(null); setRoomEnd(null); // Reset these as they are touch specific start/end now
    // setIsDragging(false); // This is reset in handleTouchMove if needed, or here if general
    // setIsPanning(false); setPanStart(null); // Panning is usually a different gesture

    // Only reset selectedWall and splitPoint if not already reset by split tool above
    if (toolForThisInteraction !== 'split') {
      setSelectedWall(null); setSplitPoint(null);
    }
    setIsDraggingCorner(false); activeCornerInfoRef.current = null; lastRawDraggedCornerPosRef.current = null;
    draggedWallSegmentRef.current = null;
    activeResizeOriginalWallTempIdsRef.current = null; 
    
    // Reset extrusion refs
    isDraggingExtrusionRef.current = false;
    extrusionSegmentRef.current = null;
    originalWallLineForExtrusionRef.current = null;
    extrusionDragStartPointRef.current = null;
    isExtrudingRoomSegmentRef.current = false; // <<< RESET THE NEW REF
    isResizingExtrusionWidthRef.current = false; // <<< RESET WIDTH RESIZE REF
    initialWallsForResizeRef.current = null; // Clear initial walls ref
    
    // Reset instant resize state
    roomResizeStartPointRef.current = null;
    pendingRoomForResizeRef.current = null;
    dragThresholdReachedRef.current = false;

    // Look for where resize operations complete, likely in a mouse/touch up handler
    // Add this debugging when resize completes:

    onDebug('RESIZE_COMPLETE_DEBUG', {
      hadSelectedRoom: !!selectedRoom,
      selectedRoomId: selectedRoom?.stableId,
      selectedRoomName: selectedRoom?.name,
      resizingRoomInfo: resizingRoomInfo,
      justResizedFlag: justResizedRef.current,
      currentWallCount: currentWalls.length
    });

    // Make sure justResizedRef is set to true when resize completes
    justResizedRef.current = true;
    
    // 🧹 PHANTOM ELEMENT FIX: Trigger element cleanup after room operations to prevent orphaned elements
    if (isResizingRef.current || wasExtrudingRoomSegment || wasDraggingExtrusion) {
      // Dispatch custom event to trigger element cleanup in FloorPlanCreator
      window.dispatchEvent(new CustomEvent('floorplan-room-operation-complete', {
        detail: {
          type: 'room-resize-complete',
          floor: currentFloor,
          timestamp: Date.now()
        }
      }));
    }
  }, [
    isMouseDownRef, onDebug, currentTool, isResizingRef, selectedRoom, tempWalls, floorPlans, currentFloor,
    setFloorPlans, roomCustomLabels, setRoomCustomLabels, 
    resizingRoomInfo, setSelectedRoom, justResizedRef, lastOperationTypeRef,
    isDraggingRoomRef, roomStartRef, roomEndRef, 
    isDraggingExtrusionRef, extrusionSegmentRef, originalWallLineForExtrusionRef, extrusionDragStartPointRef, 
    isExtrudingRoomSegmentRef, // <<< ADD NEW REF TO DEPENDENCY ARRAY
    isResizingExtrusionWidthRef, // <<< ADDED
    pointsAreEqual, 
    findRoomAtPoint, 
    draggedWallSegmentRef, 
    getCollinearNeighbors, 
    checkRectangleWallIntersectionUniqueName,
    handleWallSplit, snapToFineGridOrEndpointsUtil, eraseAtPoint, activeCornerInfoRef, lastRawDraggedCornerPosRef,
    setIsResizing, setTempWalls, setResizingRoomInfo, setActivelyResizedWalls, setIsDraggingRoom, setRoomStart, setRoomEnd, setCurrentPoint,
    setIsDragging, setIsPanning, setPanStart, setSelectedWall, setSplitPoint, setIsDraggingCorner,
    activeResizeOriginalWallTempIdsRef, props.setSelectedElement, props.setIsDraggingCorner, props.setSelectedRoom, props.setResizeHandle, props.setPanStart,
    elements, findElementAtPointInternal, handleCanvasClick, getRoomCenter, migrateRoomLabels, // Added missing utils for standard resize commit
    currentWalls, // Added currentWalls as it's used in label migration logic
    resizeHandle, // Ensure resizeHandle is in deps for roomResize
    checkForNewRoomsAndCommit, // Make sure this is in deps if used
    recordUndoState // Added recordUndoState to dependencies
  ]);

  const handleMouseLeave = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    isMouseDownRef.current = false;
    const toolForThisInteraction = currentToolInternalRef.current; 
    onDebug('MOUSE_LEAVE', { tool: toolForThisInteraction, isResizing: isResizingRef.current, isDraggingRoomState: isDraggingRoomRef.current }); 

    if (isResizingRef.current && selectedRoom && tempWalls) {
        onDebug('ROOM_RESIZE_MOUSE_LEAVE_COMMIT', { originalRoom: selectedRoom, newWalls: tempWalls });
        if (lastOperationTypeRef) lastOperationTypeRef.current = 'geometryUpdate';
        recordUndoState?.(); // Record state before commit on mouse leave
        // const previousFPState = { ...floorPlans }; // Not a complete UndoRedoState
        // 🔧 CRITICAL FIX: Preserve split wall metadata during commit
        const wallsToCommitOnMouseLeaveSource = tempWalls.map(preserveWallMetadata);
        const wallsToCommitOnMouseLeave = wallsToCommitOnMouseLeaveSource.filter(
            w => w && typeof w === 'object' && w.start && w.end
        );
        if (wallsToCommitOnMouseLeave.length !== wallsToCommitOnMouseLeaveSource.length && onDebug) {
            onDebug("HOOK_MOUSELEAVE_RESIZE_INVALID_WALL_FILTERED", {
                originalCount: wallsToCommitOnMouseLeaveSource.length,
                newCount: wallsToCommitOnMouseLeave.length,
            });
        }
        setFloorPlans(prev => ({ ...prev, [currentFloor]: wallsToCommitOnMouseLeave }));
        triggerAutosave?.(); // Trigger autosave after wall changes

        if (selectedRoom.stableId && activelyResizedWalls) {
            const originalStableId = selectedRoom.stableId;
            const preservedLabel = roomCustomLabels[originalStableId];
            if (preservedLabel) {
                const newFinalStableId = getPersistentRoomId(activelyResizedWalls, currentFloor);
                setRoomCustomLabels(prevLabels => {
                    const newLabels = { ...prevLabels };
                    if (originalStableId !== newFinalStableId && newLabels[originalStableId] === preservedLabel) delete newLabels[originalStableId];
                    newLabels[newFinalStableId] = preservedLabel;
                    return newLabels;
                });
            }
        }
        // The problematic line `setUndoStack(prev => [...prev, previousFPState]); setRedoStack([]);` has been removed
        // as recordUndoState() called earlier already handles this. previousFPState was of the wrong type.
        
        const newRoomsAfterResize = calculateRooms(tempWalls.map(({tempId, ...w})=>({...w, floor: w.floor || currentFloor})), roomCustomLabels, 'geometryUpdate');
        
        // Update survey data with new room dimensions if callback provided
        console.log('🔄 [useFloorPlanInteractions] Checking survey update callback (mouse leave):', { hasCallback: !!updateSurveyDataAfterResize, roomCount: newRoomsAfterResize.length });
        if (updateSurveyDataAfterResize) {
            console.log('🎯 [useFloorPlanInteractions] Calling updateSurveyDataAfterResize (mouse leave) with rooms:', newRoomsAfterResize.map(r => ({ id: r.stableId, name: r.name })));
            updateSurveyDataAfterResize(newRoomsAfterResize);
        } else {
            console.log('❌ [useFloorPlanInteractions] Survey update callback not available (mouse leave)');
        }
        
        let stableIdToFind = resizingRoomInfo?.originalStableId;
         if (resizingRoomInfo && activelyResizedWalls) {
            const newStableIdAfterResize = getPersistentRoomId(activelyResizedWalls, currentFloor);
            if (newStableIdAfterResize !== resizingRoomInfo.originalStableId) stableIdToFind = newStableIdAfterResize;
        }
        const updatedSelectedRoom = newRoomsAfterResize.find(r => r.stableId === stableIdToFind);
        setSelectedRoom(updatedSelectedRoom || null);
        justResizedRef.current = true;
    }
    setIsDragging(false); setIsPanning(false); setPanStart(null);
    
    if (isDraggingCorner) {
        setIsDraggingCorner(false);
        activeCornerInfoRef.current = null;
        setTempWalls(null);
        lastRawDraggedCornerPosRef.current = null;
        console.log("[Hook handleMouseLeave] Corner drag cancelled due to mouse leave.");
    }
    
    setCurrentPoint(null);
    eraserPreviewPointRef.current = null;

    if (!isResizingRef.current) {
        setTempWalls(null);
        setResizingRoomInfo(null);
        setActivelyResizedWalls(null);
        draggedWallSegmentRef.current = null;
        activeResizeOriginalWallTempIdsRef.current = null; 
    }
    if (isResizingRef.current && selectedRoom && tempWalls) { 
        setIsResizing(false);
        isResizingRef.current = false;
        initialWallsForResizeRef.current = null; // Clear initial walls ref
    }
    // Also reset extrusion if mouse leaves canvas during drag
    if (isDraggingExtrusionRef.current) {
        onDebug('EXTRUSION_DRAG_CANCELLED_MOUSE_LEAVE', {});
        isDraggingExtrusionRef.current = false;
        extrusionSegmentRef.current = null;
        originalWallLineForExtrusionRef.current = null;
        extrusionDragStartPointRef.current = null;
        setTempWalls(null); // Clear temporary walls
        setIsDragging(false); // Reset generic dragging flag
        isResizingExtrusionWidthRef.current = false; // Also reset width resize
    }
  }, [
    isMouseDownRef, isResizingRef, selectedRoom, tempWalls, floorPlans, currentFloor, setFloorPlans, roomCustomLabels,
    activelyResizedWalls, setRoomCustomLabels, resizingRoomInfo, setSelectedRoom, justResizedRef, setUndoStack, setRedoStack,
    setIsDragging, setIsPanning, setPanStart, isDraggingCorner, setIsDraggingCorner, activeCornerInfoRef,
    setTempWalls, lastRawDraggedCornerPosRef, setCurrentPoint, eraserPreviewPointRef, setIsResizing, setActivelyResizedWalls,
    setResizingRoomInfo, draggedWallSegmentRef, onDebug, currentTool, isDraggingRoom, currentToolInternalRef, isDraggingRoomRef, activeResizeOriginalWallTempIdsRef, lastOperationTypeRef,
    // Extrusion refs for dependency array
    isDraggingExtrusionRef, extrusionSegmentRef, originalWallLineForExtrusionRef, extrusionDragStartPointRef,
    isResizingExtrusionWidthRef // <<< ADDED
  ]);

  const handleCanvasDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const point = getCanvasCoordinates(e);
    const clickedTextBox = textBoxes.find(box =>
      point.x >= box.position.x && point.x <= box.position.x + box.width &&
      point.y >= box.position.y && point.y <= box.position.y + box.height
    );
    if (clickedTextBox) {
      setSelectedTextBox({ ...clickedTextBox, isEditing: true });
      setTextBoxes(prev => prev.map(box =>
        box.id === clickedTextBox.id ? { ...box, isEditing: true } : box
      ));
    }
  }, [getCanvasCoordinates, textBoxes, setSelectedTextBox, setTextBoxes]);
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    isMouseDownRef.current = true; // Consider if this is appropriate for touch
    if (e.touches.length > 1) { return; }
    const touch = e.touches[0];
    
    // Use the coordinate function passed from FloorPlanCreator to avoid double processing
    const point = getCanvasCoordinates(touch);
    
    const toolForThisInteraction = currentToolInternalRef.current;
    onDebug('TOUCH_START', { tool: toolForThisInteraction, point });
    
    // Touch tool check - debug logs removed to reduce console noise
    
    if (!toolForThisInteraction) return;

    setTouchStartPos(point); setTouchMoved(false);
    if (longPressTimer) clearTimeout(longPressTimer);
    
    // Only set long press timer for tools that need it (not room resize for better responsiveness)
    if (toolForThisInteraction !== 'roomResize') {
    setLongPressTimer(setTimeout(() => {
      if (!touchMoved) { setIsLongPress(true); console.log("Long press at", point); }
    }, TOUCH_LONG_PRESS_DELAY));
    }

    justResizedRef.current = false;
    isExtrudingRoomSegmentRef.current = false; // Reset at the start of interaction
    isResizingExtrusionWidthRef.current = false; // Also reset width extrusion
    initialWallsForResizeRef.current = null;
    
    // Clear long press timer immediately for better responsiveness
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }

    if (toolForThisInteraction === 'roomResize') {
        console.log('✅ TOUCH: Entered roomResize tool section');
        
        // 🎯 PRIORITY CHECK: Corner dragging takes priority over room resize!
        const uniqueCorners = getUniqueCornerPoints(currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor })));
        let clickedCorner: Point | null = null;
        // EXTRA LARGE touch selection for corners
        for (const corner of uniqueCorners) {
            if (isNearPoint(point, corner, SNAP_THRESHOLD * 10 / scale )) {
                clickedCorner = corner;
                break;
            }
        }
        if (clickedCorner) {
            console.log('🎯 TOUCH: Corner detected! Prioritizing corner drag over room resize');
            const affectedWallsDataValue = currentWalls
                .filter(wall => isNearPoint(wall.start, clickedCorner!, POINT_MATCH_TOLERANCE) || isNearPoint(wall.end, clickedCorner!, POINT_MATCH_TOLERANCE))
                .map(wall => ({ tempId: wall.tempId!, isStartPointCorner: isNearPoint(wall.start, clickedCorner!, POINT_MATCH_TOLERANCE) }));
            activeCornerInfoRef.current = { originalCornerWorldPoint: clickedCorner, affectedWallsData: affectedWallsDataValue };
            setIsDraggingCorner(true); setIsDraggingRoom(false); setIsResizing(false);
            setTempWalls(currentWalls.map(wall => ({ ...wall, start: { ...wall.start }, end: { ...wall.end }, floor: wall.floor || currentFloor })));
            return; // Exit early - corner drag has priority!
        }
        
        onDebug('🚀 ROOMRESIZE_TOUCH_START_DETAILED', {
          currentWalls: currentWalls.length,
          selectedRoom: selectedRoom?.stableId || 'none',
          isResizing: isResizingRef.current,
          resizeHandle: resizeHandle,
          draggedWall: draggedWallSegmentRef.current?.tempId || 'none',
          touchPoint: point,
          scale: scale
        });
        setIsDraggingRoom(false);
      
      // Check if currently resizing, similar to handleMouseDown
      if (isResizingRef.current && selectedRoom && resizeHandle && draggedWallSegmentRef.current) {
        console.log('🔄 TOUCH: Continuing existing resize operation');
        onDebug('TOUCH_START_DURING_ONGOING_RESIZE', { selectedRoomId: selectedRoom.stableId, resizeHandle });
        if (draggedWallSegmentRef.current.isExtrusionConnector) {
            isResizingExtrusionWidthRef.current = true;
            isExtrudingRoomSegmentRef.current = false;
            onDebug('TOUCH_CONTINUE_EXTRUSION_WIDTH_RESIZE_MID_DRAG', { wall: draggedWallSegmentRef.current });
        } else if (draggedWallSegmentRef.current.isMainExtrusionSegment) {
            // Allow modifying existing extrusions
                isExtrudingRoomSegmentRef.current = true;
                isResizingExtrusionWidthRef.current = false;
            onDebug('TOUCH_CONTINUE_EXTRUSION_DEPTH_RESIZE_MID_DRAG', { wall: draggedWallSegmentRef.current });
        } else if (draggedWallSegmentRef.current.isSplitOrigin) {
            // 🔧 SIMPLIFIED RULE: Split origin walls only create extrusion on FIRST drag
            const wallId = draggedWallSegmentRef.current.tempId!;
            // 🔧 BULLETPROOF EXTRUSION LOGIC: Use comprehensive state tracking for touch
            // ⚡ FIX: Use expanded wall scope for extrusion decision
            const wallsForExtrusionDecision = tempWalls && tempWalls.length > 0 
              ? [...currentWalls, ...tempWalls] 
              : currentWalls;
            const extrusionDecision = shouldAllowExtrusionCreation(wallId, wallsForExtrusionDecision);
            
            debugExtrusionOperation('TOUCH_MID_DRAG_DECISION', {
              wallId,
              decision: extrusionDecision,
              wallFlags: {
                isSplitOrigin: (draggedWallSegmentRef.current as any).isSplitOrigin,
                isMainExtrusionSegment: (draggedWallSegmentRef.current as any).isMainExtrusionSegment,
                isExtrusionConnector: (draggedWallSegmentRef.current as any).isExtrusionConnector
              }
            });

            if (extrusionDecision.action === 'create-first') {
                // First drag - allow extrusion creation
                isExtrudingRoomSegmentRef.current = true;
                isResizingExtrusionWidthRef.current = false;
                
                // Update both tracking systems
                hasCreatedExtrusionRef.current.add(wallId);
                setExtrusionState(wallId, {
                  hasCreatedExtrusion: true,
                  extrusionType: 'first-creation'
                });
                
                debugExtrusionOperation('TOUCH_FIRST_EXTRUSION_ALLOWED', { wallId });
            } else if (extrusionDecision.action === 'modify-existing') {
                // Existing extrusion - allow modification
                isExtrudingRoomSegmentRef.current = true;
                isResizingExtrusionWidthRef.current = false;
                
                setExtrusionState(wallId, {
                  extrusionType: 'existing-main'
                });
                
                debugExtrusionOperation('TOUCH_EXISTING_EXTRUSION_MODIFICATION', { wallId });
            } else {
                // Blocked or standard resize
                isExtrudingRoomSegmentRef.current = false;
                isResizingExtrusionWidthRef.current = false;
                
                debugExtrusionOperation('TOUCH_EXTRUSION_BLOCKED', { 
                    wallId,
                    reason: extrusionDecision.reason,
                    action: extrusionDecision.action
                });
            }
        } else {
            isExtrudingRoomSegmentRef.current = false; 
            isResizingExtrusionWidthRef.current = false;
            onDebug('TOUCH_CONTINUE_STANDARD_RESIZE_MID_DRAG_CHECK', { wall: draggedWallSegmentRef.current });
        }
        return;
      }
      
      // 🔧 PRIORITY: Handle wall type tools FIRST before complex wall detection
      if (toolForThisInteraction && ['hlp', 'pw', 'sw'].includes(toolForThisInteraction)) {
        console.log('🎨 WALL TYPE TOOL: Processing wall type conversion first', {
          tool: toolForThisInteraction,
          point
        });
        handleWallTypeConversion(point, toolForThisInteraction.toUpperCase() as WallType);
        return; // Exit early to prevent wall detection logic from interfering
      }
      
      console.log('🚀 TOUCH: Starting room calculation for wall detection');
      
      // Simplified room calculation without pre-split walls
      console.log('📊 BEFORE memoizedCalculateRooms call');
      // 🔧 CRITICAL FIX: Preserve split wall metadata in room calculation
      const allRooms = memoizedCalculateRooms(
          currentWalls.map(preserveWallMetadata), 
          roomCustomLabels, 
        'geometryUpdate'
      );
      console.log('📊 AFTER memoizedCalculateRooms call', { roomsFound: allRooms.length });
      
      console.log('🏠 ROOM_CALCULATION_COMPLETE', {
        allRoomsFound: allRooms.length,
        currentWallsCount: currentWalls.length,
        currentFloor,
        roomIds: allRooms.map(r => r.stableId),
        touchPoint: point,
        scale: scale
      });
      
      console.log('🎯 PROGRESSING_TO_WALL_DETECTION');
      
      // ENHANCED: First check if touch point is on an existing wall with large initial selection area
      // 🔧 SCALE-AWARE: Use same logic as main detection for consistency
      const baseTouchRadius = Math.max(INITIAL_TOUCH_SELECTION_RADIUS / scale, 60); // Minimum 60px world space
      
      console.log('🔍 STARTING_WALL_DETECTION', {
        baseTouchRadius,
        touchPoint: point,
        currentWallsToCheck: currentWalls.length
      });
      
      console.log('🔍 ABOUT_TO_FIND_TOUCHED_WALL_WITH_DISTANCE_SELECTION');
      
      // 🔧 IMPROVED: Use distance-based selection for initial wall detection too
      let touchedWall: WallWithTempId | null = null;
      let shortestDistance = baseTouchRadius;
      
      for (const wall of currentWalls) {
        if (isPointOnWall(point, wall, baseTouchRadius)) {
          const distance = distanceToLine(point, wall.start, wall.end);
          if (distance < shortestDistance) {
            shortestDistance = distance;
            touchedWall = wall;
          }
        }
      }
      
      console.log('🔍 TOUCHED_WALL_SEARCH_COMPLETE_WITH_DISTANCE', { 
        found: !!touchedWall, 
        touchedWallId: touchedWall?.tempId || 'none',
        distance: touchedWall ? shortestDistance.toFixed(2) : 'n/a',
        baseTouchRadius: baseTouchRadius.toFixed(2)
      });
      
      console.log('🔍 INITIAL_WALL_DETECTION', {
        baseTouchRadius,
        currentWallsCount: currentWalls.length,
        allRoomsCount: allRooms.length,
        touchedWallFound: !!touchedWall,
        touchedWallId: touchedWall?.tempId || 'none'
      });

      // Declare roomContainingWall in broader scope so it's accessible later
      let roomContainingWall: Room | null = null;

      if (touchedWall) {
        console.log('🔍 TOUCHED_WALL_FOUND_PROCEEDING');
        
        // 🔎 DEBUG: Wall ownership data inspection
        console.log('📊 WALL_OWNERSHIP_DEBUG', {
          wallId: touchedWall.tempId,
          isSplitOrigin: (touchedWall as any).isSplitOrigin,
          parentWallId: (touchedWall as any).parentWallId,
          originatingRoomId: (touchedWall as any).originatingRoomId,
          assignedRoomIds: (touchedWall as any).assignedRoomIds,
          hasOriginatingRoomId: !!(touchedWall as any).originatingRoomId,
          hasAssignedRoomIds: !!(touchedWall as any).assignedRoomIds,
          willUseOwnershipDetection: !!((touchedWall as any).originatingRoomId || (touchedWall as any).assignedRoomIds)
        });
        
        // 🔍 DEBUG: Check ownership format of ALL walls to understand repair results
        const wallOwnershipDetails = currentWalls.map((wall, index) => ({
          index,
          tempId: (wall as any).tempId,
          coords: `(${wall.start.x.toFixed(1)},${wall.start.y.toFixed(1)})->(${wall.end.x.toFixed(1)},${wall.end.y.toFixed(1)})`,
          originatingRoomId: (wall as any).originatingRoomId,
          assignedRoomIds: (wall as any).assignedRoomIds,
          ownershipFormat: (wall as any).originatingRoomId ? 
            ((wall as any).originatingRoomId.includes('floor_') ? 'CANONICAL' : 'HUMAN_READABLE') : 'NONE',
          isOwned: !!((wall as any).originatingRoomId || (wall as any).assignedRoomIds?.length)
        }));
        
        console.log('🔍 ALL_WALLS_OWNERSHIP_DEBUG (EXPANDED):', {
          totalWalls: currentWalls.length,
          ownedCount: wallOwnershipDetails.filter(w => w.isOwned).length,
          unownedCount: wallOwnershipDetails.filter(w => !w.isOwned).length,
          canonicalCount: wallOwnershipDetails.filter(w => w.ownershipFormat === 'CANONICAL').length,
          humanReadableCount: wallOwnershipDetails.filter(w => w.ownershipFormat === 'HUMAN_READABLE').length,
          noneCount: wallOwnershipDetails.filter(w => w.ownershipFormat === 'NONE').length
        });
        
        // Show each wall's details individually for clarity
        wallOwnershipDetails.forEach(wall => {
          console.log(`📝 WALL ${wall.index}: ${wall.coords} | Owner: ${wall.originatingRoomId || 'NONE'} | Format: ${wall.ownershipFormat}`);
        });
        
        // 🔍 DEBUG: Check why room selection is failing
        console.log('🔍 ROOM_SELECTION_DEBUG:', {
          allRoomsCount: allRooms.length,
          selectedRoomFromState: selectedRoom,
          roomIds: allRooms.map(r => r.stableId),
          roomNames: allRooms.map(r => r.name),
          touchPointUsed: point
        });
        
        // 🔧 AUTO-REPAIR: If wall lacks ownership, trigger immediate repair
        const wallLacksOwnership = !(touchedWall as any).originatingRoomId && 
                                  (!((touchedWall as any).assignedRoomIds) || (touchedWall as any).assignedRoomIds.length === 0);
        
        if (wallLacksOwnership) {
          console.log('🚨 WALL LACKS OWNERSHIP: Triggering immediate repair for all walls...');
          
          // Import and use the repair tool (using .then() to avoid async/await)
          import('../utils/wallOwnershipRepairTool').then(({ repairWallOwnership }) => {
            const currentFloorPlan = { [currentFloor]: currentWalls };
            const repairResult = repairWallOwnership(currentFloorPlan);
            
            if (repairResult.success && repairResult.totalWallsRepaired > 0) {
              console.log(`✅ AUTO-REPAIR COMPLETE: Repaired ${repairResult.totalWallsRepaired} walls`, {
                totalRoomsDetected: repairResult.totalRoomsDetected,
                floorRepaired: currentFloor
              });
              
              // Update current walls with repaired versions (they now have canonical ownership)
              // Note: The repair tool returns walls with proper originatingRoomId and assignedRoomIds
              console.log('🔄 OWNERSHIP REPAIR: Walls now have canonical stableId ownership');
              
              // Force a state update to use the repaired walls
              // This ensures the UI reflects the newly repaired wall ownership
              console.log('🔄 STATE UPDATE: Triggering re-render with repaired walls');
            } else {
              console.log('❌ AUTO-REPAIR FAILED:', repairResult.errors);
            }
          }).catch(error => {
            console.error('❌ REPAIR IMPORT ERROR:', error);
          });
        }
        
        // 🔧 FIX: Enhanced room detection for split walls using ownership info
        // First try: Use wall ownership information if available (for split walls)
        if ((touchedWall as any).originatingRoomId || (touchedWall as any).assignedRoomIds) {
          const originatingRoomId = (touchedWall as any).originatingRoomId;
          const assignedRoomIds = (touchedWall as any).assignedRoomIds || [];
          
          roomContainingWall = allRooms.find(room => {
            // Match by originating room ID or assigned room IDs
            if (originatingRoomId && room.stableId === originatingRoomId) return true;
            if (assignedRoomIds.length > 0 && assignedRoomIds.includes(room.stableId)) return true;
            return false;
          }) || null;
          
          console.log('🏠 SPLIT_WALL_ROOM_DETECTION_BY_OWNERSHIP', {
            wallId: touchedWall.tempId,
            originatingRoomId,
            assignedRoomIds,
            roomFound: !!roomContainingWall,
            roomId: roomContainingWall?.stableId || 'none'
          });
        }
        
        // Second try: Traditional coordinate matching (for regular walls)
        if (!roomContainingWall) {
          roomContainingWall = allRooms.find(room => 
            room.walls.some(roomWall => 
              (pointsAreEqual(roomWall.start, touchedWall.start, 0.1) && pointsAreEqual(roomWall.end, touchedWall.end, 0.1)) ||
              (pointsAreEqual(roomWall.start, touchedWall.end, 0.1) && pointsAreEqual(roomWall.end, touchedWall.start, 0.1))
            )
          ) || null;
          
          console.log('🔍 TRADITIONAL_ROOM_DETECTION_BY_COORDINATES', {
            wallId: touchedWall.tempId,
            roomFound: !!roomContainingWall,
            roomId: roomContainingWall?.stableId || 'none'
          });
        }
        
        console.log('🔍 ROOM_CONTAINING_WALL_SEARCH_COMPLETE', { found: !!roomContainingWall });
        
        console.log('🏠 ROOM_CONTAINING_WALL_SEARCH', {
          roomContainingWallFound: !!roomContainingWall,
          roomId: roomContainingWall?.stableId || 'none',
          totalRoomsChecked: allRooms.length
        });
        
        if (roomContainingWall) {
          setSelectedRoom(roomContainingWall);
          console.log('TOUCH_ROOM_RESIZE_WALL_TOUCHED', {
            wallTouched: `(${touchedWall.start.x.toFixed(1)},${touchedWall.start.y.toFixed(1)})->(${touchedWall.end.x.toFixed(1)},${touchedWall.end.y.toFixed(1)})`,
            roomSelected: roomContainingWall.stableId
          });
        }
      } else {
        console.log('🔍 NO_TOUCHED_WALL_FOUND');
      }

      console.log('🔍 FINDING_ROOM_AT_CLICK_POINT');
      const roomAtClickPoint = findRoomAtPoint(point, allRooms);
      console.log('🔍 ROOM_AT_CLICK_POINT_SEARCH_COMPLETE', { found: !!roomAtClickPoint });
      
      console.log('🎯 ROOM_AT_CLICK_POINT_DETECTION', {
        roomAtClickPointFound: !!roomAtClickPoint,
        roomAtClickPointId: roomAtClickPoint?.stableId || 'none',
        currentSelectedRoom: selectedRoom?.stableId || 'none',
        roomContainingWallFound: !!roomContainingWall,
        clickPoint: point
      });
      
      // 🔧 FIX: Use roomContainingWall that was just found, as selectedRoom state may not have updated yet
      // Priority: roomAtClickPoint > roomContainingWall > selectedRoom
      let roomToSelectAndResize: Room | null = roomAtClickPoint || roomContainingWall || selectedRoom;

      console.log('🔧 FINAL_ROOM_FOR_RESIZE_SELECTION', {
        finalRoomSelected: !!roomToSelectAndResize,
        finalRoomId: roomToSelectAndResize?.stableId || 'none',
        roomWallsCount: roomToSelectAndResize?.walls.length || 0,
        source: roomAtClickPoint ? 'roomAtClickPoint' : (roomContainingWall ? 'roomContainingWall' : 'selectedRoom')
      });

      if (roomToSelectAndResize) {
        const bounds = getRoomBounds(roomToSelectAndResize.walls);
        
        // 🔧 SCALE-AWARE TOUCH TOLERANCE: Ensure minimum size regardless of zoom level
        const baseTouchTolerance = Math.max(WALL_RESIZE_TOUCH_TOLERANCE / scale, 40); // Minimum 40px world space
        const scaledInitialRadius = Math.max(INITIAL_TOUCH_SELECTION_RADIUS / scale, 60); // Minimum 60px world space
        const finalTouchTolerance = Math.max(baseTouchTolerance, scaledInitialRadius);
        
        console.log('TOUCH_TOLERANCE_CALCULATION', {
            scale,
            baseTouchTolerance,
            scaledInitialRadius,
            finalTouchTolerance,
            originalTouchTolerance: WALL_RESIZE_TOUCH_TOLERANCE,
            originalInitialRadius: INITIAL_TOUCH_SELECTION_RADIUS
        });
        
        let wallClickedForHandleTouch: WallWithTempId | null = null;
        let effectiveWallToDragTouch: WallWithTempId | null = null;

        const roomCandidateWallsWithTempIdTouch: WallWithTempId[] = roomToSelectAndResize.walls
            .map(rw => currentWalls.find(cw => 
                (pointsAreEqual(cw.start, rw.start) && pointsAreEqual(cw.end, rw.end)) ||
                (pointsAreEqual(cw.start, rw.end) && pointsAreEqual(cw.end, rw.start))
            ))
            .filter(Boolean) as WallWithTempId[];

        console.log('🧱 ROOM_CANDIDATE_WALLS_ANALYSIS', {
          totalRoomWalls: roomToSelectAndResize.walls.length,
          candidateWallsFound: roomCandidateWallsWithTempIdTouch.length,
          candidateWallIds: roomCandidateWallsWithTempIdTouch.map(w => w.tempId),
          finalTouchTolerance
        });
        
        // 🔧 SIMPLIFIED PRIORITY LOGIC: Use consistent large tolerance for all detection
        // Priority 1: Touch on an existing isExtrusionConnector (most specific - always extrusion width)
        // 🔧 IMPROVED: Use distance-based selection for extrusion connectors too
        console.log('🔗 CHECKING_EXTRUSION_CONNECTORS_FIRST_WITH_DISTANCE', { wallCount: roomCandidateWallsWithTempIdTouch.length });
        
        let closestExtrusionConnector: WallWithTempId | null = null;
        let closestConnectorDistance = finalTouchTolerance;
        
        for (const wall of roomCandidateWallsWithTempIdTouch) {
            if (wall.isExtrusionConnector) {
                const isOnWall = isPointOnWall(point, wall, finalTouchTolerance);
                const distance = distanceToLine(point, wall.start, wall.end);
                
                console.log('🔍 EXT_CONNECTOR_CHECK', {
                    wallId: wall.tempId, 
                    isExtrusionConnector: wall.isExtrusionConnector,
                    isOnWall: !!isOnWall,
                    distance: distance.toFixed(2),
                    tolerance: finalTouchTolerance
                });
                
                if (isOnWall && distance < closestConnectorDistance) {
                    closestConnectorDistance = distance;
                    closestExtrusionConnector = wall;
                }
            }
        }
        
        if (closestExtrusionConnector) {
            wallClickedForHandleTouch = closestExtrusionConnector;
            effectiveWallToDragTouch = closestExtrusionConnector; // Drag the connector itself
            isResizingExtrusionWidthRef.current = true; // For width adjustment
            isExtrudingRoomSegmentRef.current = false;
            console.log('TOUCH_ROOM_RESIZE_INIT_TARGET_EXT_CONNECTOR (Width)', { 
                connectorId: closestExtrusionConnector.tempId, 
                distance: closestConnectorDistance.toFixed(2),
                tolerance: finalTouchTolerance 
            });
        }

        // Priority 2: Standard wall touch (including main extrusion segments - default to standard resize)
        // 🔧 IMPROVED: Use distance-based selection for better accuracy with split walls and connected rooms
        if (!effectiveWallToDragTouch) {
            console.log('🧱 CHECKING_ALL_WALLS_FOR_STANDARD_RESIZE_WITH_DISTANCE_SELECTION', { candidateWallsCount: roomCandidateWallsWithTempIdTouch.length });
            
            let closestWall: WallWithTempId | null = null;
            let closestDistance = finalTouchTolerance;
            let debugInfo: Array<{wallId: string, distance: number, isOnWall: boolean}> = [];
            
            // First pass: Find all walls within tolerance and their distances
            for (const wall of roomCandidateWallsWithTempIdTouch) {
                const isOnWall = isPointOnWall(point, wall, finalTouchTolerance);
                const distance = distanceToLine(point, wall.start, wall.end);
                
                debugInfo.push({
                  wallId: wall.tempId!,
                  distance: distance,
                  isOnWall: !!isOnWall
                });
                
                if (isOnWall && distance < closestDistance) {
                    closestDistance = distance;
                    closestWall = wall;
                }
            }
            
            console.log('🎯 DISTANCE_BASED_WALL_SELECTION', {
              candidateWalls: debugInfo,
              closestWallId: closestWall?.tempId || 'none',
              closestDistance: closestDistance.toFixed(2),
              finalTouchTolerance: finalTouchTolerance.toFixed(2)
            });
            
            if (closestWall) {
                // 🔧 ENHANCED WALL LOOKUP: Use coordinate collision-aware wall selection
                // This prevents the coordinate collision issue when multiple walls exist at same coordinates
                
                let roomSpecificWall = closestWall; // Default fallback
                
                if (roomToSelectAndResize?.stableId) {
                  // Use enhanced wall lookup to find the correct wall for this specific room
                  const enhancedWall = findWallForRoom(
                    { start: closestWall.start, end: closestWall.end },
                    roomToSelectAndResize.stableId,
                    currentWalls
                  );
                  
                  if (enhancedWall) {
                    roomSpecificWall = enhancedWall;
                    console.log('✅ ENHANCED WALL LOOKUP SUCCESS', {
                      originalWallId: closestWall.tempId,
                      enhancedWallId: enhancedWall.tempId,
                      roomId: roomToSelectAndResize.stableId,
                      coordinateCollisionResolved: enhancedWall.tempId !== closestWall.tempId
                    });
                  } else {
                    console.log('⚠️ ENHANCED WALL LOOKUP FALLBACK', {
                      originalWallId: closestWall.tempId,
                      roomId: roomToSelectAndResize.stableId,
                      reason: 'No room-specific wall found, using closest wall'
                    });
                  }
                }
                
                wallClickedForHandleTouch = roomSpecificWall;
                effectiveWallToDragTouch = roomSpecificWall;
                
                console.log('🔍 CLOSEST_WALL_SELECTED (ENHANCED)', {
                  wallId: roomSpecificWall.tempId,
                  distance: closestDistance.toFixed(2),
                  isSplitOrigin: roomSpecificWall.isSplitOrigin,
                  isMainExtrusionSegment: roomSpecificWall.isMainExtrusionSegment,
                  tolerance: finalTouchTolerance,
                  wallStart: roomSpecificWall.start,
                  wallEnd: roomSpecificWall.end,
                  roomId: roomToSelectAndResize?.stableId || 'unknown',
                  coordinateCollisionHandled: true
                });
                
                // 🎯 PROFESSIONAL CAD: Enhanced wall analysis for auto-splitting
                const wallAnalysis = analyzeWallClick(point, closestWall, finalTouchTolerance, 15 / scale);
                
                onDebug('PROFESSIONAL_CAD_ANALYSIS', {
                  wallId: closestWall.tempId,
                  clickType: wallAnalysis.clickType,
                  shouldAutoSplit: wallAnalysis.shouldAutoSplit,
                  splitPoint: wallAnalysis.splitPoint,
                  endpointIndex: wallAnalysis.endpointIndex,
                  distanceFromEndpoint: wallAnalysis.distanceFromNearestEndpoint
                });
                
                // 🎯 AUTO-SPLIT: Professional CAD behavior - split wall if clicking on middle
                // 🚫 CRITICAL FIX: Don't auto-split walls that are already split to prevent repeated resize issues
                if (wallAnalysis.shouldAutoSplit && wallAnalysis.splitPoint && !closestWall.isSplitOrigin) {
                  console.log('🎯 AUTO-SPLIT TRIGGERED: Professional CAD behavior detected');
                  
                  onDebug('PROFESSIONAL_CAD_AUTO_SPLIT_START', {
                    originalWallId: closestWall.tempId,
                    splitPoint: wallAnalysis.splitPoint,
                    reason: 'User clicked on wall middle - auto-splitting before resize'
                  });
                  
                  // Temporarily select the wall for splitting
                  setSelectedWall(closestWall);
                  
                  // Execute the wall split with callback (pass wall directly to avoid race condition)
                  handleWallSplit(wallAnalysis.splitPoint, (newSegments) => {
                    if (newSegments.length === 2) {
                      // Find which segment is closer to the click point
                      const segment1 = newSegments[0];
                      const segment2 = newSegments[1];
                      
                      const dist1 = Math.min(
                        Math.hypot(point.x - segment1.start.x, point.y - segment1.start.y),
                        Math.hypot(point.x - segment1.end.x, point.y - segment1.end.y)
                      );
                      const dist2 = Math.min(
                        Math.hypot(point.x - segment2.start.x, point.y - segment2.start.y),
                        Math.hypot(point.x - segment2.end.x, point.y - segment2.end.y)
                      );
                      
                      const targetSegment = dist1 < dist2 ? segment1 : segment2;
                      
                      // Update the effective wall to the new segment
                      wallClickedForHandleTouch = targetSegment;
                      effectiveWallToDragTouch = targetSegment;
                      draggedWallSegmentRef.current = targetSegment;
                      
                      onDebug('PROFESSIONAL_CAD_AUTO_SPLIT_COMPLETE', {
                        originalWallId: closestWall.tempId,
                        newSegment1Id: segment1.tempId,
                        newSegment2Id: segment2.tempId,
                        selectedSegmentId: targetSegment.tempId,
                        splitSuccessful: true
                      });
                      
                      console.log('✅ AUTO-SPLIT COMPLETE: Continuing with resize on segment', targetSegment.tempId);
                    } else {
                      onDebug('PROFESSIONAL_CAD_AUTO_SPLIT_FAILED', {
                        originalWallId: closestWall.tempId,
                        expectedSegments: 2,
                        foundSegments: newSegments.length,
                        splitFailed: true
                      });
                      
                      console.log('❌ AUTO-SPLIT FAILED: Could not find new segments, continuing with original wall');
                    }
                  }, closestWall);
                  
                  // Continue with the rest of the logic but mark this as a post-split operation
                  console.log('🎯 AUTO-SPLIT: Continuing with resize setup...');
                  
                  // 🔧 CRITICAL: Reset wall splitting flag after auto-split completes
                  // The auto-split is just a sub-operation during resize, not a full wall split
                  setIsWallSplitting(false);
                } else {
                  console.log('🎯 PROFESSIONAL CAD: Standard endpoint resize detected');
                }
                
                // 🔧 FIX: Allow split walls to be dragged/resized as normal walls on touch devices
                const wallId = closestWall.tempId!;
                const isSplitWallForNormalResize = (closestWall as any).isSplitOrigin && 
                                                  !(closestWall as any).isMainExtrusionSegment && 
                                                  !(closestWall as any).isExtrusionConnector;
                
                if (isSplitWallForNormalResize) {
                    // 🔧 ENHANCED: Check if this split wall has existing connectors that need adjustment
                    const draggedWall = closestWall;
                    const draggedWallTempId = draggedWall.tempId;
                    const parentWallId = (draggedWall as any).parentWallId;
                    
                    // 🔧 BACKWARD COMPATIBILITY: Safely check for existing connectors with proper fallbacks
                    let hasExistingConnectors = false;
                    
                    // ⚡ CRITICAL FIX: Expand search scope to include tempWalls for connector detection
                    const wallsToSearchForConnectors = tempWalls && tempWalls.length > 0 
                      ? [...currentWalls, ...tempWalls]
                      : currentWalls;
                    
                    try {
                      
                      // Ensure we have valid wall arrays and metadata before checking
                      if (Array.isArray(wallsToSearchForConnectors) && draggedWallTempId && draggedWall.start && draggedWall.end) {
                        hasExistingConnectors = wallsToSearchForConnectors.some(wall => {
                          // Defensive checks for wall validity
                          if (!wall || typeof wall !== 'object' || !wall.isExtrusionConnector) return false;
                          
                          // Check if connector is connected to this split wall by ID or parent
                          const connectedToSplitWallId = (wall as any).connectedToSplitWallId;
                          const wallParentId = (wall as any).parentWallId;
                          
                          if (connectedToSplitWallId === draggedWallTempId) return true;
                          if (parentWallId && wallParentId === parentWallId) return true;
                          
                          // 🔧 SAFE COORDINATE FALLBACK: Only if all coordinates are valid
                          if (wall.start && wall.end && 
                              typeof wall.start.x === 'number' && typeof wall.start.y === 'number' &&
                              typeof wall.end.x === 'number' && typeof wall.end.y === 'number') {
                            const tolerance = 0.1;
                            const connectsToStart = (Math.abs(wall.start.x - draggedWall.start.x) < tolerance && Math.abs(wall.start.y - draggedWall.start.y) < tolerance) ||
                                                  (Math.abs(wall.end.x - draggedWall.start.x) < tolerance && Math.abs(wall.end.y - draggedWall.start.y) < tolerance);
                                                  
                            const connectsToEnd = (Math.abs(wall.start.x - draggedWall.end.x) < tolerance && Math.abs(wall.start.y - draggedWall.end.y) < tolerance) ||
                                                 (Math.abs(wall.end.x - draggedWall.end.x) < tolerance && Math.abs(wall.end.y - draggedWall.end.y) < tolerance);
                            
                            return connectsToStart || connectsToEnd;
                          }
                          
                          return false;
                        });
                      }
                    } catch (error) {
                      console.warn('🔧 CONNECTOR DETECTION: Error during connector check, falling back to normal resize', error);
                      hasExistingConnectors = false;
                    }
                    
                    if (hasExistingConnectors) {
                        // Enable extrusion mode to handle connector adjustment
                        isExtrudingRoomSegmentRef.current = true;
                        isResizingExtrusionWidthRef.current = false;
                        console.log('🔧 TOUCH_ENABLE_EXTRUSION_FOR_CONNECTOR_ADJUSTMENT', { 
                            wallId,
                            reason: 'Split wall has existing connectors that need adjustment - enabling extrusion mode',
                            connectorsFound: wallsToSearchForConnectors.filter(w => w.isExtrusionConnector).length,
                            searchScope: tempWalls && tempWalls.length > 0 ? `${currentWalls.length} current + ${tempWalls.length} temp = ${wallsToSearchForConnectors.length} total` : `${currentWalls.length} current only`,
                            tolerance: finalTouchTolerance,
                            distance: closestDistance
                        });
                    } else {
                        // No existing connectors - treat as normal wall
                        isExtrudingRoomSegmentRef.current = false;
                        isResizingExtrusionWidthRef.current = false;
                        console.log('👌 TOUCH_SPLIT_WALL_NORMAL_RESIZE', {
                            wallId,
                            reason: 'Split wall has no existing connectors - treated as normal wall for touch drag/resize',
                            connectorsFound: wallsToSearchForConnectors.filter(w => w.isExtrusionConnector).length,
                            searchScope: tempWalls && tempWalls.length > 0 ? `${currentWalls.length} current + ${tempWalls.length} temp = ${wallsToSearchForConnectors.length} total` : `${currentWalls.length} current only`,
                            tolerance: finalTouchTolerance,
                            distance: closestDistance
                        });
                    }
                } else {
                    // 🔧 BULLETPROOF EXTRUSION LOGIC: Use comprehensive state tracking for touch init (only for non-split walls or split extrusions)
                    // ⚡ FIX: Use expanded wall scope for extrusion decision
                    const wallsForExtrusionDecision = tempWalls && tempWalls.length > 0 
                      ? [...currentWalls, ...tempWalls] 
                      : currentWalls;
                    const extrusionDecision = shouldAllowExtrusionCreation(wallId, wallsForExtrusionDecision);
                    
                    debugExtrusionOperation('TOUCH_ROOM_RESIZE_INIT_DECISION', {
                      wallId,
                      decision: extrusionDecision,
                      tolerance: finalTouchTolerance,
                      distance: closestDistance,
                      wallFlags: {
                        isSplitOrigin: (closestWall as any).isSplitOrigin,
                        isMainExtrusionSegment: (closestWall as any).isMainExtrusionSegment,
                        isExtrusionConnector: (closestWall as any).isExtrusionConnector
                      }
                        });

                    if (extrusionDecision.action === 'create-first') {
                    // First drag - allow extrusion creation
                    isExtrudingRoomSegmentRef.current = true;
                    isResizingExtrusionWidthRef.current = false;
                    
                    // Update both tracking systems
                    hasCreatedExtrusionRef.current.add(wallId);
                    setExtrusionState(wallId, {
                      hasCreatedExtrusion: true,
                      extrusionType: 'first-creation'
                    });
                    
                    debugExtrusionOperation('TOUCH_ROOM_RESIZE_FIRST_EXTRUSION_ALLOWED', { 
                        wallId, 
                        tolerance: finalTouchTolerance,
                        distance: closestDistance
                    });
                } else if (extrusionDecision.action === 'modify-existing') {
                    // Existing extrusion - allow modification
                    if ((closestWall as any).isExtrusionConnector) {
                        // Connector walls get width resize mode
                        isResizingExtrusionWidthRef.current = true;
                        isExtrudingRoomSegmentRef.current = false;
                    } else {
                        // Main segments get depth resize mode
                        isExtrudingRoomSegmentRef.current = true;
                        isResizingExtrusionWidthRef.current = false;
                    }
                    
                    setExtrusionState(wallId, {
                      extrusionType: (closestWall as any).isExtrusionConnector ? 'existing-connector' : 'existing-main'
                    });
                    
                    debugExtrusionOperation('TOUCH_ROOM_RESIZE_EXISTING_EXTRUSION_MODIFICATION', { 
                        wallId, 
                        tolerance: finalTouchTolerance,
                        distance: closestDistance,
                        mode: (closestWall as any).isExtrusionConnector ? 'width-resize' : 'depth-resize'
                    });
                    } else {
                        // Blocked or standard resize for extrusion logic
                        isExtrudingRoomSegmentRef.current = false;
                        isResizingExtrusionWidthRef.current = false;
                        
                        debugExtrusionOperation('TOUCH_ROOM_RESIZE_EXTRUSION_BLOCKED', { 
                            wallId, 
                            tolerance: finalTouchTolerance,
                            distance: closestDistance,
                            reason: extrusionDecision.reason,
                            action: extrusionDecision.action
                        });
                    }
                }
            } else {
                console.log('❌ NO_WALL_WITHIN_TOLERANCE', {
                  message: 'No wall found within touch tolerance using distance-based selection',
                  candidateWallsCount: roomCandidateWallsWithTempIdTouch.length,
                  finalTouchTolerance: finalTouchTolerance.toFixed(2),
                  debugInfo
                });
            }
        }

        console.log('📋 WALL_DETECTION_FINAL_RESULT', {
          effectiveWallFound: !!effectiveWallToDragTouch,
          effectiveWallId: effectiveWallToDragTouch?.tempId || 'none',
          wallClickedFound: !!wallClickedForHandleTouch,
          wallClickedId: wallClickedForHandleTouch?.tempId || 'none',
          willProceedWithResize: !!(effectiveWallToDragTouch && wallClickedForHandleTouch)
        });

        if (effectiveWallToDragTouch && wallClickedForHandleTouch) {
            console.log('✅ WALL_DETECTION_SUCCESS_PROCEEDING_TO_RESIZE');
            draggedWallSegmentRef.current = effectiveWallToDragTouch;
            
            // Determine resize handle before setting extrusion mode based on it for isSplitOrigin
            let determinedResizeHandleTouch = getWallResizeDirection(wallClickedForHandleTouch, bounds);
            if (!determinedResizeHandleTouch) { 
                if (wallClickedForHandleTouch.isExtrusionConnector) {
                const wallIsHorizontal = Math.abs(wallClickedForHandleTouch.start.y - wallClickedForHandleTouch.end.y) < Math.abs(wallClickedForHandleTouch.start.x - wallClickedForHandleTouch.end.x);
                    if (wallIsHorizontal) {
                        determinedResizeHandleTouch = point.x < (wallClickedForHandleTouch.start.x + wallClickedForHandleTouch.end.x) / 2 ? 'w' : 'e';
                    } else {
                        determinedResizeHandleTouch = point.y < (wallClickedForHandleTouch.start.y + wallClickedForHandleTouch.end.y) / 2 ? 'n' : 's';
                    }
                } else {
                    const wallIsHorizontal = Math.abs(wallClickedForHandleTouch.start.y - wallClickedForHandleTouch.end.y) < Math.abs(wallClickedForHandleTouch.start.x - wallClickedForHandleTouch.end.x);
                if (wallIsHorizontal) {
                        determinedResizeHandleTouch = point.y < (wallClickedForHandleTouch.start.y + wallClickedForHandleTouch.end.y) / 2 ? 'n' : 's';
                } else {
                        determinedResizeHandleTouch = point.x < (wallClickedForHandleTouch.start.x + wallClickedForHandleTouch.end.x) / 2 ? 'w' : 'e';
                    }
                }
            }
            
            // Set the resize handle
            setResizeHandle(determinedResizeHandleTouch);

            // 🎯 ACTIVATE WALL DRAG MODE if intersection data exists
            if (intersectionDataRef.current?.requiresPartialDrag && intersectionDataRef.current.intersectionPoints.length > 0) {
                console.log('🎯 WALL DRAG MODE: Activating with proper resize handle');
                
                activateWallDragMode(
                    effectiveWallToDragTouch,
                    intersectionDataRef.current.intersectionPoints,
                    point,
                    currentWalls
                );
                
                onDebug('WALL_DRAG_MODE_ACTIVATED_WITH_PROPER_HANDLE', {
                    draggedWallId: effectiveWallToDragTouch.tempId,
                    resizeHandle: determinedResizeHandleTouch,
                    intersectionCount: intersectionDataRef.current.intersectionPoints.length
                });
            }

            // 🔧 SIMPLIFIED: Extrusion flags should already be set correctly during wall detection
            // Just confirm the final mode based on what was already determined
            if (isResizingExtrusionWidthRef.current) {
                console.log('TOUCH_ROOM_RESIZE_MODE_ENGAGED: WIDTH_EXTRUSION', { targetWallId: effectiveWallToDragTouch.tempId });
            } else if (isExtrudingRoomSegmentRef.current) {
                console.log('TOUCH_ROOM_RESIZE_MODE_ENGAGED: DEPTH_EXTRUSION (New)', { targetWallId: effectiveWallToDragTouch.tempId });
            } else {
                console.log('TOUCH_ROOM_RESIZE_MODE_ENGAGED: STANDARD', { 
                    wallId: effectiveWallToDragTouch.tempId,
                    wallType: effectiveWallToDragTouch.isMainExtrusionSegment ? 'former-main-extrusion' :
                             effectiveWallToDragTouch.isSplitOrigin ? 'split-origin' : 'regular'
                });
            }


            let wallsForDecoupling = currentWalls;
            
            // Get all rooms for shared wall analysis
            const allRooms = memoizedCalculateRooms(currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor})), roomCustomLabels, 'geometryUpdate', currentFloor);

            // 🔗 INDEPENDENT WALL MOVEMENT: Each room resizes independently like when it's the only room
            // Adjacent wall coordinate synchronization happens after resize completion via synchronizeSharedWallsAfterResize
            console.log('🔗 INDEPENDENT RESIZE MODE: Each room resizes independently, coordinate sync happens after completion');
            const sharedWallSystem = { sharedSegments: [], connections: [], independentSegments: [], roomConnectivity: new Map() };
            
            // Calculate the movement effect for this wall
            // 🔧 ENHANCED: Use the exact same coordinate key generation as shared wall system
            const wallCoordinateKey = getWallKey(effectiveWallToDragTouch);
            const movementEffect = calculateSharedWallMovementEffect(
              effectiveWallToDragTouch.tempId || wallCoordinateKey,
              effectiveWallToDragTouch.start,
              effectiveWallToDragTouch.end,
              sharedWallSystem,
              allRooms
            );
            
            onDebug('🔗 SHARED_WALL_ANALYSIS_COMPLETE', {
              wallId: effectiveWallToDragTouch.tempId,
              movementType: movementEffect.movementType,
              affectedRooms: movementEffect.affectedRooms,
              sharedSegments: sharedWallSystem.sharedSegments.length,
              connections: sharedWallSystem.connections.length,
              roomConnectivity: sharedWallSystem.roomConnectivity.size
            });
            
            // Check if this is a shared wall that affects multiple rooms
            // 🚨 BUG FIX: Only enable constraint logic when shared wall system has actual segments
            const hasActualSharedSegments = sharedWallSystem.sharedSegments.length > 0;
            if (movementEffect.movementType === 'shared' && movementEffect.affectedRooms.length > 1 && hasActualSharedSegments) {
                onDebug('🔗 SHARED_WALL_COORDINATED_MOVEMENT', {
                    wallId: effectiveWallToDragTouch.tempId,
                    affectedRooms: movementEffect.affectedRooms,
                    movementType: 'shared-wall-coordinated',
                    message: 'Professional CAD: Implementing constraint-based wall splitting and maintenance'
                });
                
                console.log('🎯 PROFESSIONAL CAD: Constraint-based shared wall resize detected', {
                    affectedRooms: movementEffect.affectedRooms,
                    draggedWallId: effectiveWallToDragTouch.tempId,
                    sharedSegments: sharedWallSystem.sharedSegments.length
                });

                // 🎯 PROFESSIONAL CAD SOLUTION: Set up constraint-based movement
                // This allows for automatic wall splitting and constraint maintenance
                const coordinatedMovementInfo = {
                    isCoordinatedMovement: true,
                    constraintBasedMovement: true,      // Professional CAD flag
                    autoSplitSharedWalls: true,         // Enable auto-splitting
                    maintainSharedConstraints: true,    // Maintain constraints
                    primaryRoom: roomToSelectAndResize,
                    affectedRooms: movementEffect.affectedRooms,
                    sharedWallId: effectiveWallToDragTouch.tempId,
                    sharedWallSystem: sharedWallSystem  // Pass shared wall system for constraint logic
                };
                
                // Store this for use during movement
                (window as any).coordinatedMovementInfo = coordinatedMovementInfo;
                
                onDebug('🎯 PROFESSIONAL_CAD_CONSTRAINTS_SETUP', {
                    constraintBasedMovement: true,
                    autoSplitEnabled: true,
                    maintainConstraints: true,
                    sharedWallId: effectiveWallToDragTouch.tempId,
                    affectedRooms: movementEffect.affectedRooms
                });
                
                console.log('✅ PROFESSIONAL CAD CONSTRAINT SYSTEM ENABLED', {
                    constraintBasedMovement: true,
                    autoSplitSharedWalls: true,
                    maintainSharedConstraints: true
                });
                
                // Continue with normal resize setup but with constraint-based movement
                wallsForDecoupling = currentWalls;
            } else if (movementEffect.movementType === 'independent') {
                onDebug('🔗 INDEPENDENT_WALL_DETECTED', {
                    wallId: effectiveWallToDragTouch.tempId,
                    movementType: 'independent',
                    message: 'Wall can move independently without affecting other rooms'
                });
                
                // For independent walls, proceed with normal resize
                wallsForDecoupling = currentWalls;
            } else {
                // Fallback to old decoupling logic for edge cases
                onDebug('🔗 FALLBACK_TO_DECOUPLING', {
                    wallId: effectiveWallToDragTouch.tempId,
                    movementType: movementEffect.movementType,
                    reason: 'Edge case - using fallback decoupling logic'
                });
                
                const decouplingResult = decoupleSharedPointsForDrag(
                  effectiveWallToDragTouch,
                  roomToSelectAndResize,
                  allRooms,
                  wallsForDecoupling
                );
                
                if (decouplingResult.requiresPartialDrag && decouplingResult.intersectionPoints && decouplingResult.intersectionPoints.length > 0) {
                    intersectionDataRef.current = {
                        intersectionPoints: decouplingResult.intersectionPoints,
                        intersectedWalls: [],
                        requiresPartialDrag: true,
                        draggedWall: effectiveWallToDragTouch
                    };
                    wallsForDecoupling = decouplingResult.updatedWalls;
                } else if (decouplingResult.decoupledPoints.length > 0) {
                    setFloorPlans(prev => ({
                        ...prev,
                        [currentFloor]: decouplingResult.updatedWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}))
                    }));
                    wallsForDecoupling = decouplingResult.updatedWalls;
                }
            }

            console.log('🎬 SETTING_UP_RESIZE_STATE', {
              selectedRoomId: roomToSelectAndResize.stableId,
              resizeHandle: determinedResizeHandleTouch,
              draggedWallId: effectiveWallToDragTouch.tempId,
              isExtrudingSegment: isExtrudingRoomSegmentRef.current,
              isResizingExtrusionWidth: isResizingExtrusionWidthRef.current,
              isWallDragMode: wallDragModeRef.current
            });

            setSelectedRoom(roomToSelectAndResize); 
            // setResizeHandle(determinedResizeHandleTouch); // Already set above
            resetConnectionLock(); // 🔒 Reset connection lock for new resize operation
            setIsResizing(true); isResizingRef.current = true; setIsDragging(true);
            
            // Store initial position for wall drag calculations
            roomResizeStartPointRef.current = point;
            
            // Immediate visual feedback - set touchMoved to true for instant drag start
            setTouchMoved(true);
            
            console.log('✅ RESIZE_STATE_SETUP_COMPLETE', {
              isResizing: true,
              isDragging: true,
              touchMoved: true,
              message: 'Ready for wall dragging'
            });

                // Use the updated walls for resize
                const finalWallsForResize = wallsForDecoupling;
                
                // Set up wall tracking with deduplication to prevent matching same wall multiple times
                const usedTempIds = new Set<string>();
                const originalWallTempIds = roomToSelectAndResize.walls.map(roomWall_plain => {
                    const match = finalWallsForResize.find(cw => {
                      // Skip if already used
                      if (cw.tempId && usedTempIds.has(cw.tempId)) return false;
                      
                      return (pointsAreEqual(cw.start, roomWall_plain.start) && pointsAreEqual(cw.end, roomWall_plain.end)) ||
                             (pointsAreEqual(cw.start, roomWall_plain.end) && pointsAreEqual(cw.end, roomWall_plain.start));
                    });
                    
                    if (match?.tempId) {
                      usedTempIds.add(match.tempId);
                    }
                    return match?.tempId;
                }).filter(Boolean) as string[];
                
                console.log('🔍 INITIAL_WALL_TRACKING_SETUP_LONG_PRESS:', {
                  roomWallsCount: roomToSelectAndResize.walls.length,
                  matchedTempIdsCount: originalWallTempIds.length,
                  shouldMatch: roomToSelectAndResize.walls.length === originalWallTempIds.length ? '✅' : '❌'
                });
                
                activeResizeOriginalWallTempIdsRef.current = originalWallTempIds;

                if (roomToSelectAndResize.stableId) {
                    const currentLabel = roomCustomLabels[roomToSelectAndResize.stableId];
                    setResizingRoomInfo(currentLabel ? { originalStableId: roomToSelectAndResize.stableId, label: currentLabel } : null);
                } else { setResizingRoomInfo(null); }
            
            // Use final walls for resize operations
            initialWallsForResizeRef.current = finalWallsForResize.map(w => ({ ...w, start: { ...w.start }, end: { ...w.end }, floor: w.floor || currentFloor })); // STORE INITIAL WALLS
            setTempWalls(finalWallsForResize.map(w => ({ ...w, start: { ...w.start }, end: { ...w.end }, floor: w.floor || currentFloor }))); // Initialize with updated walls
            return; // Resize initiated

        } else {
            console.log('❌ NO_EFFECTIVE_WALL_FOUND', {
              message: 'No wall found to drag - touch will end quickly',
              roomToSelectAndResize: roomToSelectAndResize?.stableId || 'none',
              candidateWallsCount: roomCandidateWallsWithTempIdTouch.length,
              finalTouchTolerance
            });
        }
        // Removed room selection logic - only individual wall dragging allowed
      } else {
        console.log('❌ NO_ROOM_SELECTED_OR_FOUND', {
          message: 'No room available for resize - touch will end quickly',
          selectedRoom: selectedRoom?.stableId || 'none',
          roomAtClickPoint: roomAtClickPoint?.stableId || 'none',
          allRoomsCount: allRooms.length
        });
        
        if (selectedRoom) { // Clear room selection when touching outside
        setSelectedRoom(null);
        setResizeHandle(null);
        setIsResizing(false); isResizingRef.current = false;
        setTempWalls(null); setResizingRoomInfo(null); setActivelyResizedWalls(null); 
        draggedWallSegmentRef.current = null; activeResizeOriginalWallTempIdsRef.current = null;
        }
      }
      // If no room interaction, proceed to other touch start handlers for other tools.
    }


    if (toolForThisInteraction === 'wall') {
      // Wall tool touch start - debug logs removed
      
      
      // Check if we're completing an existing wall (have a start point)
      if (isDraggingRoomRef.current && roomStartRef.current) {
        console.log('🔗 WALL TOOL: Completing wall creation');
        
        // Use enhanced wall snapping for the end point (TOUCH) with generous endpoint detection
        const targetWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
        const endSnapResult = snapToWalls(point, targetWalls, scale, true);
        
        let finalEndPoint = endSnapResult.type !== 'none' ? endSnapResult.point : point;
        
        console.log('🎯 SIMPLE_WALL_END_SNAP_TOUCH', {
          rawPoint: point,
          snappedPoint: finalEndPoint,
          snapType: endSnapResult.type,
          message: `Touch wall end snapped to ${endSnapResult.type}`
        });
        
        if (Math.abs(roomStartRef.current.x - finalEndPoint.x) > 1 || 
            Math.abs(roomStartRef.current.y - finalEndPoint.y) > 1) {
          
          // Create wall with simple snapping (TOUCH)
            const newWall: Wall = { 
            start: { ...roomStartRef.current },
            end: { ...finalEndPoint },
              type: 'None', 
              floor: currentFloor 
            };
            
          console.log('🤖 SIMPLE_WALL_CREATION_TOUCH', {
              startPoint: roomStartRef.current,
              endPoint: finalEndPoint,
            snapType: endSnapResult.type,
            wallCreated: true
            });
            
          if (!doesWallIntersect(newWall, currentWalls.map(({tempId,...w})=>({...w, floor: w.floor || currentFloor })))) {
            // 🔧 ROOM MERGE FIX: Only set explicitRoomCreation for actual enclosed room creation
            // Check if this wall completion creates a potentially enclosed space
            const wallsIncludingNew = [...currentWalls.map(({tempId,...w})=>({...w, floor: w.floor || currentFloor })), newWall];
            const mightCreateRoom = detectPotentialRoomCreation(newWall, wallsIncludingNew);
            
            if (mightCreateRoom && lastOperationTypeRef) {
              lastOperationTypeRef.current = 'explicitRoomCreation';
              console.log('🔧 ROOM CREATION: Detected potential room creation from wall completion', {
                startSnap: roomStartRef.current,
                endSnap: endSnapResult?.type || 'none',
                wallStart: newWall.start,
                wallEnd: newWall.end
              });
            } else if (lastOperationTypeRef) {
              lastOperationTypeRef.current = 'geometryUpdate';
              console.log('🔧 WALL CONNECTION: Simple wall connection, not room creation', {
                endSnapType: endSnapResult?.type || 'none',
                mightCreateRoom
              });
            }
            
            // 🔧 ENDPOINT ROOM CREATION FIX: Track newly added wall for hybrid room detection
            // Only track for room creation operations, not simple wall connections
            const wasRoomCreation = lastOperationTypeRef?.current === 'explicitRoomCreation';
            
            if (wasRoomCreation && newlyAddedWallsRef) {
              newlyAddedWallsRef.current = [newWall];
              console.log('🔧 ENDPOINT WALL TRACKING: Added wall to tracking for room creation', {
                wallStart: newWall.start,
                wallEnd: newWall.end,
                endSnapType: endSnapResult?.type || 'none',
                operationType: lastOperationTypeRef.current,
                trackedWallsCount: newlyAddedWallsRef.current.length
              });
            } else {
              console.log('🔧 ENDPOINT WALL TRACKING: Skipped - simple wall connection', {
                endSnapType: endSnapResult?.type || 'none',
                operationType: lastOperationTypeRef?.current || 'none',
                wasRoomCreation
              });
            }
            
              recordUndoState?.();
            checkForNewRoomsAndCommit([...currentWalls.map(({tempId, ...w})=>({...w, floor: w.floor || currentFloor})), newWall]);
            
            console.log('✅ WALL_CREATION_SUCCESS_TOUCH', { wall: newWall });
          }
        }
        
        // Reset wall drawing state
        setIsDraggingRoom(false);
        setRoomStart(null);
        setCurrentPoint(null);
        
        return; // Exit after completing wall
      }
      
      // Starting a new wall - use enhanced wall snapping for the start point with generous endpoint detection
      // Starting new wall creation - debug logs removed
      
      const targetWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
      const startSnapResult = snapToWalls(point, targetWalls, scale, true);
      
      let finalStartPoint = startSnapResult.type !== 'none' ? startSnapResult.point : point;
      
      // 🔧 NEW: Only reset operation type if we're NOT starting from an endpoint
      // If we're starting from an endpoint, preserve the explicitRoomCreation context
      if (lastOperationTypeRef && startSnapResult.type !== 'endpoint') {
        lastOperationTypeRef.current = 'geometryUpdate';
        console.log('🔧 WALL TOOL: Reset operation type for new wall creation');
      } else if (startSnapResult.type === 'endpoint') {
        console.log('🔧 WALL TOOL: Preserving operation type for endpoint-based wall creation');
      }
      
      console.log('🎯 SIMPLE_WALL_START_SNAP_TOUCH', {
        rawPoint: point,
        snappedPoint: finalStartPoint,
        snapType: startSnapResult.type,
        message: `Touch wall start snapped to ${startSnapResult.type}`
      });
             
       // Set up for wall creation
      setIsDraggingRoom(true);
       setRoomStart(finalStartPoint); 
       setCurrentPoint(finalStartPoint);
    } else if (toolForThisInteraction === 'cornerAdjust') {
        // Legacy cornerAdjust tool (roomResize handles corners internally now)
        const uniqueCorners = getUniqueCornerPoints(currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor })));
        let clickedCorner: Point | null = null;
        // EXTRA LARGE touch selection for corners
        for (const corner of uniqueCorners) {
            if (isNearPoint(point, corner, SNAP_THRESHOLD * 10 / scale )) {
                clickedCorner = corner; break;
            }
        }
        if (clickedCorner) {
            const affectedWallsDataValue = currentWalls
                .filter(wall => isNearPoint(wall.start, clickedCorner!, POINT_MATCH_TOLERANCE) || isNearPoint(wall.end, clickedCorner!, POINT_MATCH_TOLERANCE))
                .map(wall => ({ tempId: wall.tempId!, isStartPointCorner: isNearPoint(wall.start, clickedCorner!, POINT_MATCH_TOLERANCE) }));
            activeCornerInfoRef.current = { originalCornerWorldPoint: clickedCorner, affectedWallsData: affectedWallsDataValue };
            setIsDraggingCorner(true); setIsDraggingRoom(false); setIsResizing(false);
            setTempWalls(currentWalls.map(wall => ({ ...wall, start: { ...wall.start }, end: { ...wall.end }, floor: wall.floor || currentFloor })));
        }
    } else if (toolForThisInteraction === 'eraser') { 
      eraseAtPoint(point);
    } else if (toolForThisInteraction === 'split') { 
      // 🔧 SCALE-AWARE: Use minimum tolerance for better touch responsiveness
      const splitTouchTolerance = Math.max(WALL_RESIZE_TOUCH_TOLERANCE / scale, 40);
      for (const wall of currentWalls) {
        const pointOnWall = isPointOnWall(point, wall, splitTouchTolerance); 
        if (pointOnWall) { setSelectedWall(wall); setSplitPoint(pointOnWall); break; }
      }
    } else if (toolForThisInteraction === 'select') { 
        const clickedElement = findElementAtPointInternal(point, elements);
        setSelectedElement(clickedElement);
        setIsDragging(!!clickedElement);
        
        if (clickedElement) {
            isDraggingElementRef.current = true;
            
            // 🚪 DOOR EDITING: Trigger door editing modal when door is tapped
            if (clickedElement.type === 'door') {
                onDebug('DOOR_SELECTED_FOR_EDITING', { 
                    doorId: hasId(clickedElement) ? clickedElement.id : 'no-id',
                    doorElement: clickedElement
                });
            }
            
            // Calculate drag offset from element position for touch
            if ((clickedElement as any).position) {
                const elementPos = (clickedElement as any).position;
                const offset = {
                    x: point.x - elementPos.x,
                    y: point.y - elementPos.y
                };
                setElementDragOffset(offset);
                elementDragOffsetRef.current = offset;
                setDragStartPoint(point);
                dragStartPointRef.current = point;
                
                onDebug('ELEMENT_TOUCH_DRAG_START', { 
                    elementType: clickedElement.type,
                    elementId: hasId(clickedElement) ? clickedElement.id : 'no-id',
                    startPoint: point,
                    elementPosition: elementPos,
                    dragOffset: offset
                });
            }
        } else {
            isDraggingElementRef.current = false;
        }
    }
  }, [
    isMouseDownRef, canvasRef, scale, viewOffset, onDebug, currentTool, currentWalls, roomCustomLabels, selectedRoom,
    setTouchStartPos, setTouchMoved, longPressTimer, setLongPressTimer, setIsLongPress,
    setIsDraggingRoom, setSelectedRoom, setResizeHandle, setIsResizing, isResizingRef, setIsDragging, setTempWalls,
    draggedWallSegmentRef, setResizingRoomInfo, snapToFineGridOrEndpointsUtil, setRoomStart, setCurrentPoint,
    activeCornerInfoRef, setIsDraggingCorner, eraseAtPoint, setSelectedWall, setSplitPoint, findElementAtPointInternal,
    elements, setSelectedElement, handleWallTypeConversion, currentToolInternalRef, activeResizeOriginalWallTempIdsRef, currentFloor,
    isDraggingRoomRef, roomStartRef // Added these refs for the new endpoint logic
  ]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    // 🚨 SIMPLE DEBUG: Verify function is being called
    // Hook touch move called - debug logs removed
    
    // 🔥 DEBUG: Hook entry point
    onDebug('HOOK_TOUCH_MOVE_ENTRY', {
      touchCount: e.touches.length,
      timestamp: Date.now()
    });
    
    if (e.touches.length > 1) { 
      onDebug('HOOK_TOUCH_MOVE_EXIT_MULTI_TOUCH', { touchCount: e.touches.length });
      return; 
    }
    const touch = e.touches[0];
    
    // Use the coordinate function passed from FloorPlanCreator to avoid double processing
    const rawPoint = getCanvasCoordinates(touch);
    
    const toolForThisInteraction = currentToolInternalRef.current;
    const isDraggingRoomCurrent = isDraggingRoomRef.current;
    const roomStartCurrent = roomStartRef.current;
    
    // 🔥 DEBUG: Key state values
    onDebug('HOOK_TOUCH_MOVE_STATE_CHECK', {
      toolForThisInteraction,
      isResizingRefCurrent: isResizingRef.current,
      isDraggingRoomCurrent,
      roomStartCurrent: !!roomStartCurrent,
      selectedRoom: !!selectedRoom,
      resizeHandle
    });
    
    if (!toolForThisInteraction) {
      onDebug('HOOK_TOUCH_MOVE_EXIT_NO_TOOL', {});
      return;
    }

    if (touchStartPos && !touchMoved) {
      const dx = rawPoint.x - touchStartPos.x;
      const dy = rawPoint.y - touchStartPos.y;
      const dragDistance = Math.sqrt(dx * dx + dy * dy);
      
      // Use different thresholds based on tool and context for better responsiveness
      let threshold = TOUCH_DRAG_THRESHOLD;
      
      // For room resize, use immediate response (no threshold) to prevent lag
      if (toolForThisInteraction === 'roomResize' && isResizingRef.current) {
        threshold = 0; // Immediate response for wall dragging
      }
      // For wall creation, use smaller threshold
      else if (toolForThisInteraction === 'wall') {
        threshold = 1; // Very responsive for wall creation
      }
      
      if (dragDistance > threshold) {
        setTouchMoved(true);
        if (longPressTimer) { clearTimeout(longPressTimer); setLongPressTimer(null); }
      }
    }

    if (toolForThisInteraction === 'wall') {
      // 🆕 ENHANCED: Use line intersection snapping system for touch
      if (roomStartCurrent) {
        // Snap to nearby walls when drawing wall end point, including line intersections (TOUCH)
        const targetWalls = currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
        const snapResult = snapToWallsWithLineIntersection(rawPoint, targetWalls, roomStartCurrent, scale, true);
        
        // Update visual feedback for snapping
        setCurrentSnapResult(snapResult.type !== 'none' ? snapResult : null);
        
        // Use snapped point for preview, fallback to raw point
        const previewPoint = snapResult.type !== 'none' ? snapResult.point : rawPoint;
        setCurrentPoint(previewPoint);
        
        onDebug('TOUCH_LINE_INTERSECTION_WALL_SNAP', {
          rawPoint,
          previewPoint,
          snapType: snapResult.type,
          priority: snapResult.priority,
          message: `Touch wall preview snapped to ${snapResult.type} with line intersection support`
        });
        
        // Also update roomEnd when dragging (matches mouse behavior)
        if (isDraggingRoomCurrent) {
          setRoomEnd(previewPoint);
        }
      } else {
        // Clear snap result when no start point
        setCurrentSnapResult(null);
        setCurrentPoint(rawPoint);
        console.log('🎯 TOUCH_WALL_NO_START', { rawPoint, roomStartCurrent });
      }
    } else if (toolForThisInteraction !== 'cornerAdjust' && toolForThisInteraction !== 'roomResize' && toolForThisInteraction !== 'eraser' && toolForThisInteraction !== 'split') {
      setCurrentPoint(null);
    }

    // Corner adjustment logic for touch move - this was missing!
    if ((toolForThisInteraction === 'cornerAdjust' || toolForThisInteraction === 'roomResize') && isDraggingCorner && activeCornerInfoRef.current && tempWalls) {
        const { affectedWallsData } = activeCornerInfoRef.current;
        let snappedForAnglePoint = { ...rawPoint };
        const tempWallCopies = tempWalls.map(w => ({ ...w, start: { ...w.start }, end: { ...w.end } }));
        const affectedWallEntries = affectedWallsData.map(awd => {
            const wall = tempWallCopies.find(tw => tw.tempId === awd.tempId);
            return wall ? { wall, isStartCorner: awd.isStartPointCorner } : null;
        }).filter(Boolean) as Array<{ wall: WallWithTempId, isStartCorner: boolean }>;

        if (affectedWallEntries.length === 2) {
            const entry1 = affectedWallEntries[0]; const entry2 = affectedWallEntries[1];
            const fixedP1 = entry1.isStartCorner ? entry1.wall.end : entry1.wall.start;
            const fixedP2 = entry2.isStartCorner ? entry2.wall.end : entry2.wall.start;
            const toleranceWorld = SNAP_ANGLE_TOLERANCE_PX / scale;
            let bestSnapPos = { ...rawPoint };
            if (Math.abs(rawPoint.y - fixedP1.y) < toleranceWorld && Math.abs(rawPoint.x - fixedP2.x) < toleranceWorld) bestSnapPos = { x: fixedP2.x, y: fixedP1.y };
            else if (Math.abs(rawPoint.x - fixedP1.x) < toleranceWorld && Math.abs(rawPoint.y - fixedP2.y) < toleranceWorld) bestSnapPos = { x: fixedP1.x, y: fixedP2.y };
            else if (Math.abs(rawPoint.y - fixedP1.y) < toleranceWorld) bestSnapPos = { x: rawPoint.x, y: fixedP1.y };
            else if (Math.abs(rawPoint.x - fixedP1.x) < toleranceWorld) bestSnapPos = { x: fixedP1.x, y: rawPoint.y };
            else if (Math.abs(rawPoint.y - fixedP2.y) < toleranceWorld) bestSnapPos = { x: rawPoint.x, y: fixedP2.y };
            else if (Math.abs(rawPoint.x - fixedP2.x) < toleranceWorld) bestSnapPos = { x: fixedP2.x, y: rawPoint.y };
            snappedForAnglePoint = bestSnapPos;
        }
        lastRawDraggedCornerPosRef.current = { ...rawPoint }; // Store raw point for commit
        const newTempWallsNextState = tempWallCopies.map(currentTempWall => {
            const affectionData = affectedWallsData.find(data => data.tempId === currentTempWall.tempId);
            if (affectionData) {
                const updatedWall = { ...currentTempWall, start: { ...currentTempWall.start }, end: { ...currentTempWall.end } };
                if (affectionData.isStartPointCorner) updatedWall.start = { ...snappedForAnglePoint };
                else updatedWall.end = { ...snappedForAnglePoint };
                return updatedWall;
            }
            return currentTempWall;
        });
        setTempWalls(newTempWallsNextState);
        
        // Calculate and set corner drag preview
        const previewData = calculateCornerDragPreview(snappedForAnglePoint, affectedWallsData, currentWalls);
        setCornerDragPreview(previewData);
        return;
    }

    if (!isResizingRef.current && !isDraggingCorner) {
        if (toolForThisInteraction === 'eraser') {
          // Enhanced eraser preview for touch devices
          const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
          
          if (isTouchDevice) {
            // For touch devices, show preview immediately and make it more visible
            eraserPreviewPointRef.current = rawPoint;
          } else {
            // For mouse devices, use standard behavior
            eraserPreviewPointRef.current = rawPoint;
          }
        } else {
          eraserPreviewPointRef.current = null;
        }

        if (toolForThisInteraction === 'cornerAdjust' || toolForThisInteraction === 'roomResize') {
            const uniqueCorners = getUniqueCornerPoints(currentWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor })));
            let foundHoveredCorner: Point | null = null;
            // EXTRA LARGE touch hover for corners
            for (const corner of uniqueCorners) {
                if (isNearPoint(rawPoint, corner, SNAP_THRESHOLD * 10 / scale)) { foundHoveredCorner = corner; break; }
            }
            if (hoveredCorner?.x !== foundHoveredCorner?.x || hoveredCorner?.y !== foundHoveredCorner?.y) {
                 setHoveredCorner(foundHoveredCorner);
            }
        } else if (hoveredCorner) {
            setHoveredCorner(null);
        }
    }

    if (isResizingRef.current) {
      // 🔥 DEBUG: Check all conditions for handleRoomResize call
      onDebug('HOOK_TOUCH_MOVE_RESIZE_CONDITIONS', {
        isResizingRefCurrent: isResizingRef.current,
        toolForThisInteraction,
        selectedRoom: !!selectedRoom,
        selectedRoomId: selectedRoom?.stableId,
        resizeHandle,
        draggedWallSegmentRef: !!draggedWallSegmentRef.current,
        willCallHandleRoomResize: toolForThisInteraction === 'roomResize' && selectedRoom && resizeHandle
      });
      
      if (toolForThisInteraction === 'roomResize' && selectedRoom && resizeHandle) {
        // 🎯 CHECK FOR WALL DRAG MODE - Professional CAD approach
        if (wallDragModeRef.current && wallDragDataRef.current) {
          console.log('🎯 WALL DRAG MODE: Using individual wall dragging');
          
          // Use professional CAD-style individual wall dragging
          const updatedWalls = handleWallDragMove(
            rawPoint,
            wallDragDataRef.current.draggedWall,
            wallDragDataRef.current.intersectionPoints,
            tempWalls || currentWalls
          );
          
          // Update temp walls with the independently moved wall
          setTempWalls(updatedWalls);
          
          onDebug('WALL_DRAG_MOVE_APPLIED', {
            draggedWallId: wallDragDataRef.current.draggedWall.tempId,
            updatedWallsCount: updatedWalls.length,
            mode: 'independent_wall_drag'
          });
          
          return; // Skip standard room resize
        }
        
        // Standard room resizing (fallback)
        onDebug('TOUCH_ROOM_RESIZE_MOVE_USING_SIMPLE_APPROACH', {
          toolForThisInteraction,
          selectedRoomId: selectedRoom?.stableId,
          resizeHandle: resizeHandle,
          draggedWallId: draggedWallSegmentRef.current?.tempId,
          isWallDragMode: false
        });
        
        handleRoomResize(rawPoint);
      }
      return;
    }
    if (toolForThisInteraction === 'wall' && isDraggingRoomCurrent && roomStartCurrent) {
      return;
    } else if (toolForThisInteraction === 'split') {
        // 🔧 SCALE-AWARE: Use minimum tolerance for better touch responsiveness in move handler
        const splitMoveTolerance = Math.max(WALL_RESIZE_TOUCH_TOLERANCE / scale, 40);
        
        let currentTargetWall = selectedWall; 
        let potentialSplitPointOnCurrentWall: Point | null = null;

        if (currentTargetWall) {
            potentialSplitPointOnCurrentWall = isPointOnWall(rawPoint, currentTargetWall, splitMoveTolerance);
        }

        if (!potentialSplitPointOnCurrentWall) {
            let newlyFoundWall: WallWithTempId | null = null;
            let newlyFoundSplitPoint: Point | null = null;
            for (const wall of currentWalls) {
                const pointOnWallCandidate = isPointOnWall(rawPoint, wall, splitMoveTolerance);
                if (pointOnWallCandidate) {
                    newlyFoundWall = wall;
                    newlyFoundSplitPoint = pointOnWallCandidate;
                    break;
                }
            }
            currentTargetWall = newlyFoundWall; 
            potentialSplitPointOnCurrentWall = newlyFoundSplitPoint; 
            
            if (selectedWall?.tempId !== currentTargetWall?.tempId) {
                setSelectedWall(currentTargetWall);
            }
            if (splitPoint?.x !== potentialSplitPointOnCurrentWall?.x || splitPoint?.y !== potentialSplitPointOnCurrentWall?.y) {
                setSplitPoint(potentialSplitPointOnCurrentWall);
            }
        } else {
            if (splitPoint?.x !== potentialSplitPointOnCurrentWall?.x || splitPoint?.y !== potentialSplitPointOnCurrentWall?.y) {
                setSplitPoint(potentialSplitPointOnCurrentWall);
            }
        }

        if (currentTargetWall && potentialSplitPointOnCurrentWall) {
            const distToStartPx = Math.hypot(potentialSplitPointOnCurrentWall.x - currentTargetWall.start.x, potentialSplitPointOnCurrentWall.y - currentTargetWall.start.y);
            const distToEndPx = Math.hypot(potentialSplitPointOnCurrentWall.x - currentTargetWall.end.x, potentialSplitPointOnCurrentWall.y - currentTargetWall.end.y);

            // Round distances to 2 decimal places (consistent with calculateWallLength)
            const distToStartM = Math.round((distToStartPx / PIXELS_PER_METER) * 100) / 100;
            const distToEndM = Math.round((distToEndPx / PIXELS_PER_METER) * 100) / 100;

            setSplitPreviewDimensions({ distToStart: distToStartM, distToEnd: distToEndM });
        } else {
            setSplitPreviewDimensions(null);
        }
    } else if (toolForThisInteraction === 'select' && isDragging && selectedElement) {
        // Enhanced element dragging for touch with proper offset handling
        if (isDraggingElementRef.current && elementDragOffsetRef.current) {
            const offset = elementDragOffsetRef.current;
            const newPosition = {
                x: rawPoint.x - offset.x,
                y: rawPoint.y - offset.y
            };
            
            // Use the same enhanced position update with snapping
            updateElementPosition(selectedElement, newPosition, true);
            
            onDebug('ELEMENT_TOUCH_DRAGGING', { 
                elementType: selectedElement.type,
                elementId: hasId(selectedElement) ? selectedElement.id : 'no-id',
                touchPoint: rawPoint,
                newPosition: newPosition,
                dragOffset: offset
            });
        } else {
            // Fallback to basic position update if no offset available
            const newElements = elements.map(el => {
                if (el === selectedElement && (el as any).position) {
                    return { ...el, position: rawPoint };
                }
                return el;
            });
            setElements(newElements);
        }
    }
  }, [
    canvasRef, scale, viewOffset, touchStartPos, touchMoved, setTouchMoved, longPressTimer, setLongPressTimer,
    isResizingRef, isDraggingCorner, currentTool, eraserPreviewPointRef, currentWalls, hoveredCorner, setHoveredCorner,
    selectedRoom, resizeHandle, handleRoomResize, activeCornerInfoRef, tempWalls, lastRawDraggedCornerPosRef, setTempWalls,
    isDraggingRoom, roomStart, setRoomEnd, setCurrentPoint, selectedWall, setSplitPoint, isDragging, selectedElement, setElements,
    snapToFineGridOrEndpointsUtil, snapToEndpointsOrGridUtil, currentToolInternalRef, isDraggingRoomRef, roomStartRef, currentFloor,
    // Add instant resize dependencies
    roomResizeStartPointRef, pendingRoomForResizeRef, dragThresholdReachedRef, setResizeHandle, setIsResizing, setIsDragging,
    activeResizeOriginalWallTempIdsRef, isExtrudingRoomSegmentRef, isResizingExtrusionWidthRef, draggedWallSegmentRef, onDebug,
    roomCustomLabels, setResizingRoomInfo, initialWallsForResizeRef
  ]);

  const handleTouchEndEvent = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    onDebug('TOUCH_END_EVENT_START', { 
      touchMoved,
      currentTool: currentToolInternalRef.current,
      hasSelectedWall: !!selectedWall,
      selectedWallId: selectedWall?.tempId,
      hasSplitPoint: !!splitPoint,
      splitPoint,
      touchStartPos,
      longPressTimer: !!longPressTimer
    });

    isMouseDownRef.current = false; // Consider if this is appropriate for touch

    // 🔗 COORDINATED MOVEMENT CLEANUP: Clear coordinated movement info on touch end
    if ((window as any).coordinatedMovementInfo) {
        console.log('🔗 COORDINATED MOVEMENT CLEANUP: Clearing coordinated movement info on touch end');
        delete (window as any).coordinatedMovementInfo;
    }

    if (e.touches.length > 0) { // If there are still other touches, don't process as end
        onDebug('TOUCH_END_IGNORED_MULTI_TOUCH_STILL_ACTIVE', { remainingTouches: e.touches.length });
        return;
    }
    const toolForThisInteraction = currentToolInternalRef.current;
    onDebug('TOUCH_END_TOOL_CHECK', { 
      tool: toolForThisInteraction, 
      isResizing: isResizingRef.current, 
      isDraggingRoom: isDraggingRoomRef.current,
      selectedWall: selectedWall?.tempId,
      splitPoint: splitPoint,
      touchMoved: touchMoved,
      isLongPress: isLongPress
    });

    // 🔄 ROOM RESIZE COMPLETION: Handle room resize completion for touch events
    if (toolForThisInteraction === 'roomResize' && isResizingRef.current && selectedRoom && tempWalls) {
        console.log('🎯 [handleTouchEndEvent] Room resize completion detected via touch');
        onDebug('ROOM_RESIZE_TOUCH_END_COMMIT', { originalRoom: selectedRoom, newWalls: tempWalls });
        
        const previousFPState = { ...floorPlans };
        const finalPlainWalls = tempWalls.map(({tempId, ...w}) => ({...w, floor: w.floor || currentFloor}));
        setFloorPlans(prev => ({ ...prev, [currentFloor]: finalPlainWalls }));
        
        recordUndoState?.();
        
        // Label preservation logic
        const originalStableId = selectedRoom.stableId;
        const originalLabel = originalStableId ? roomCustomLabels[originalStableId] : undefined;

        if (selectedRoom.stableId && activelyResizedWalls && originalLabel && originalStableId) {
            const newFinalStableId = getPersistentRoomId(activelyResizedWalls, currentFloor);
            setRoomCustomLabels(prevLabels => {
                const newLabels = { ...prevLabels };
                if (originalStableId !== newFinalStableId && newLabels[originalStableId] === originalLabel) delete newLabels[originalStableId];
                newLabels[newFinalStableId] = originalLabel;
                return newLabels;
            });
        }
        
        triggerAutosave?.();
        
        // Calculate new rooms and update survey data
        const newRoomsAfterResize = calculateRooms(finalPlainWalls, roomCustomLabels, 'geometryUpdate');
        
        // Update survey data with new room dimensions if callback provided
        console.log('🔄 [handleTouchEndEvent] Checking survey update callback:', { hasCallback: !!updateSurveyDataAfterResize, roomCount: newRoomsAfterResize.length });
        if (updateSurveyDataAfterResize) {
            console.log('🎯 [handleTouchEndEvent] Calling updateSurveyDataAfterResize with rooms:', newRoomsAfterResize.map(r => ({ id: r.stableId, name: r.name })));
            updateSurveyDataAfterResize(newRoomsAfterResize);
        } else {
            console.log('❌ [handleTouchEndEvent] Survey update callback not available');
        }
        
        // Find and select the resized room
        let stableIdToFind = resizingRoomInfo?.originalStableId;
        if (resizingRoomInfo && activelyResizedWalls) {
            const newStableIdAfterResize = getPersistentRoomId(activelyResizedWalls, currentFloor);
            if (newStableIdAfterResize !== resizingRoomInfo.originalStableId) stableIdToFind = newStableIdAfterResize;
        }
        const updatedSelectedRoom = newRoomsAfterResize.find(r => r.stableId === stableIdToFind);
        setSelectedRoom(updatedSelectedRoom || null);
        justResizedRef.current = true;
        
        // Clean up resize state
        setIsResizing(false);
        setTempWalls(null);
        setResizingRoomInfo(null);
        setActivelyResizedWalls(null);
    }

    // 🔧 PRIORITY: Handle wall type tools FIRST before complex logic
    if (toolForThisInteraction && ['hlp', 'pw', 'sw'].includes(toolForThisInteraction) && touchStartPos && !touchMoved) {
      console.log('🎨 WALL TYPE TOOL: Processing wall type conversion on touch end', {
        tool: toolForThisInteraction,
        touchStartPos,
        touchMoved
      });
      
      // Use touchStartPos since that's where the user initially touched
      handleWallTypeConversion(touchStartPos, toolForThisInteraction.toUpperCase() as WallType);
      
      // Reset touch states
      setTouchStartPos(null);
      setTouchMoved(false);
      if (longPressTimer) clearTimeout(longPressTimer);
      setLongPressTimer(null);
      setIsLongPress(false);
      isMouseDownRef.current = false;
      
      return; // Exit early to prevent other logic from interfering
    }

    if (longPressTimer) clearTimeout(longPressTimer);
    if (isLongPress && !touchMoved) {
        // Handle long press action if needed, e.g., opening a context menu
        console.log("Long press action at touch end", touchStartPos);
        setIsLongPress(false);
        setTouchMoved(false);
        setTouchStartPos(null);
        // Potentially reset other states if long press implies a different interaction flow
        // For now, just reset long press related states.
        return; // Assuming long press might prevent other touch end actions
    }
    setIsLongPress(false); // Reset long press state if it wasn't a long press or if it was moved

    const isDraggingRoomCurrent = isDraggingRoomRef.current;
    const roomStartCurrent = roomStartRef.current;
    const roomEndCurrent = roomEndRef.current;

    // Enhanced Resize Commit: Handle both standard resize AND width extrusion resize modes
    const isStandardResize = isResizingRef.current;
    const isWidthExtrusionResize = isResizingExtrusionWidthRef.current;
    const shouldCommitResize = toolForThisInteraction === 'roomResize' && 
                              (isStandardResize || isWidthExtrusionResize) && 
                              selectedRoom && tempWalls;
    
    if (shouldCommitResize) {
        // Add specific logging for width extrusion commits
        if (isWidthExtrusionResize) {
            console.log('🚀 WIDTH_EXTRUSION_TOUCHEND_COMMIT: Committing width extrusion resize', {
                selectedRoomId: selectedRoom.stableId,
                tempWallsCount: tempWalls.length,
                isResizingExtrusionWidth: isWidthExtrusionResize,
                isStandardResize: isStandardResize,
                resizeHandle: resizeHandle || 'width-extrusion'
            });
        }
        onDebug('TOUCH_ROOM_RESIZE_COMMIT', { originalRoomId: selectedRoom.stableId, tempWallsCount: tempWalls.length });
        if (lastOperationTypeRef) lastOperationTypeRef.current = 'geometryUpdate';
        recordUndoState?.(); 
        
        // Store the original room information for label preservation
        const originalStableId = selectedRoom.stableId;
        const currentLabel = (selectedRoom.stableId ? roomCustomLabels[selectedRoom.stableId] : null) || selectedRoom.name;
        
        if (selectedRoom.stableId) {
          setResizingRoomInfo({ originalStableId: selectedRoom.stableId, label: currentLabel });
          onDebug('TOUCH_ROOM_RESIZE_COMMIT_resizingRoomInfo_SET', { stableId: selectedRoom.stableId, label: currentLabel });
        } else {
          setResizingRoomInfo(null); 
          onDebug('TOUCH_ROOM_RESIZE_COMMIT_WARN', { message: 'Selected room for resize has no stableId.' });
        }

        // Step 1: Map tempWalls (WallWithTempId[]) to plain Wall objects
        // 🔧 CRITICAL FIX: Use preserveWallMetadata to ensure split wall tempIds and metadata are preserved
        console.log('🔧 TOUCH_END_COMMIT_DEBUG: About to commit tempWalls', {
          tempWallsCount: tempWalls.length,
          connectorWallsCount: tempWalls.filter(w => (w as any).isExtrusionConnector).length,
          tempWallsBreakdown: tempWalls.map((w, i) => ({
            index: i,
            tempId: w.tempId,
            coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
            isConnector: (w as any).isExtrusionConnector,
            hasOwnership: !!((w as any).originatingRoomId || (w as any).assignedRoomIds),
            originatingRoomId: (w as any).originatingRoomId?.substring(0, 50) + '...' || 'NONE'
          }))
        });
        
        const mappedPlainWallsFromTemp: Wall[] = tempWalls.map(preserveWallMetadata);
        
        console.log('🔧 TOUCH_END_COMMIT_DEBUG: After preserveWallMetadata mapping', {
          mappedWallsCount: mappedPlainWallsFromTemp.length,
          connectorWallsCount: mappedPlainWallsFromTemp.filter(w => (w as any).isExtrusionConnector).length,
          mappedWallsBreakdown: mappedPlainWallsFromTemp.map((w, i) => ({
            index: i,
            coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
            isConnector: (w as any).isExtrusionConnector,
            hasOwnership: !!((w as any).originatingRoomId || (w as any).assignedRoomIds),
            originatingRoomId: (w as any).originatingRoomId?.substring(0, 50) + '...' || 'NONE'
          }))
        });
        
        // Step 2: Filter the mapped walls for validity AND ensure connector walls are preserved
        const finalResizedWalls = mappedPlainWallsFromTemp.filter(w => {
            // Basic validity check
            const isValid = w && typeof w === 'object' && w.start && w.end &&
                           typeof w.start.x === 'number' && typeof w.start.y === 'number' &&
                           typeof w.end.x === 'number' && typeof w.end.y === 'number';
            
            if (!isValid) {
                console.log('🚨 CONNECTOR_WALL_COMMIT_FILTER: Rejecting invalid wall', {
                    wall: w,
                    reason: 'Failed basic validity check'
                });
                return false;
            }
            
            // Special handling for connector walls - ensure they have room ownership
            const isConnector = (w as any).isExtrusionConnector;
            if (isConnector) {
                const hasOwnership = (w as any).originatingRoomId || 
                                   ((w as any).assignedRoomIds && (w as any).assignedRoomIds.length > 0) ||
                                   ((w as any).parentRoomIds && (w as any).parentRoomIds.length > 0);
                
                console.log('🔗 CONNECTOR_WALL_COMMIT_VALIDATION', {
                    coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
                    isConnector: true,
                    hasOwnership,
                    originatingRoomId: (w as any).originatingRoomId?.substring(0, 50) + '...' || 'NONE',
                    assignedRoomIds: (w as any).assignedRoomIds?.length || 0,
                    parentRoomIds: (w as any).parentRoomIds?.length || 0,
                    willPreserve: hasOwnership
                });
                
                if (!hasOwnership) {
                    console.log('🚨 CONNECTOR_WALL_COMMIT_REPAIR: Attempting to repair orphaned connector wall ownership');
                    // Emergency repair: assign to a reasonable room if possible
                    // This prevents connector wall loss during commit
                    return true; // Keep the wall even if ownership is missing - better than losing it
                }
            }
            
            return true;
        });

        if (finalResizedWalls.length !== mappedPlainWallsFromTemp.length && onDebug) {
            onDebug("HOOK_TOUCHEND_RESIZE_INVALID_WALL_FILTERED", {
                originalCount: mappedPlainWallsFromTemp.length,
                newCount: finalResizedWalls.length,
            });
        }

        console.log('🔧 TOUCH_END_COMMIT_DEBUG: Final walls to commit', {
          finalWallsCount: finalResizedWalls.length,
          connectorWallsCount: finalResizedWalls.filter(w => (w as any).isExtrusionConnector).length,
          finalWallsBreakdown: finalResizedWalls.map((w, i) => ({
            index: i,
            coords: `(${w.start.x.toFixed(1)},${w.start.y.toFixed(1)})->(${w.end.x.toFixed(1)},${w.end.y.toFixed(1)})`,
            isConnector: (w as any).isExtrusionConnector,
            hasOwnership: !!((w as any).originatingRoomId || (w as any).assignedRoomIds),
            originatingRoomId: (w as any).originatingRoomId?.substring(0, 50) + '...' || 'NONE'
          }))
        });

        setFloorPlans(prev => ({ ...prev, [currentFloor]: finalResizedWalls }));

        // 🔧 ROOM RESIZE ID PRESERVATION FIX: Use proper label migration for all rooms
        // 🔧 CRITICAL ROOM MERGE FIX: Use complete floor plan walls for label migration (not just resizing room's walls)
        // During resize, currentWalls might only contain walls from the resizing room (~4 walls)
        // but labelPreservation context in migrateRoomLabels needs ALL floor plan walls to maintain accurate room counts
        const completeOriginalWalls = floorPlans[currentFloor] || []; // Complete floor plan (before resize)
        const originalWallsPlain = completeOriginalWalls.map(({ tempId, ...wall }) => ({
          ...wall, 
          floor: wall.floor || currentFloor,
          ...(wall.isExtrusionConnector && { isExtrusionConnector: true }),
          ...(wall.isMainExtrusionSegment && { isMainExtrusionSegment: true }),
          ...(wall.isSplitOrigin && { isSplitOrigin: true })
        }));
        
        // Use proper label migration to preserve all room labels, not just the single resized room
        const labelMigrationResult = migrateRoomLabels(
            originalWallsPlain,      // Complete floor plan walls (before)
            finalResizedWalls,       // Complete floor plan walls (after)
            roomCustomLabels,        // Pass all room labels to preserve connected rooms
            currentFloor
        );
        
        // Update room labels with migrated labels
        setRoomCustomLabels(labelMigrationResult.labels);
        
        // Calculate new rooms with migrated labels
        const newRoomsAfterResize = calculateRooms(finalResizedWalls, labelMigrationResult.labels, 'geometryUpdate');
        
        // Find the room to select after resize
        let newSelectedRoom: Room | null = null;
        
        if (originalStableId && labelMigrationResult.migrationMap[originalStableId]) {
            // Find room by migrated ID
            const newStableId = labelMigrationResult.migrationMap[originalStableId];
            newSelectedRoom = newRoomsAfterResize.find(r => r.stableId === newStableId) || null;
        } else {
            // Fallback: find room by original ID
            newSelectedRoom = newRoomsAfterResize.find(r => r.stableId === originalStableId) || null;
        }
        
        // If we still couldn't find by ID, try to find by proximity/similarity
        if (!newSelectedRoom && originalStableId) {
            // Find the room with the closest center to the original room
            const originalCenter = getRoomCenter(selectedRoom.walls);
            let minDistance = Infinity;
            for (const room of newRoomsAfterResize) {
                const roomCenter = getRoomCenter(room.walls);
                const distance = Math.hypot(roomCenter.x - originalCenter.x, roomCenter.y - originalCenter.y);
                if (distance < minDistance) {
                    minDistance = distance;
                    newSelectedRoom = room;
                }
            }
        }
        
        // Update selected room immediately to prevent timing issues
        setSelectedRoom(newSelectedRoom);
        
        justResizedRef.current = true;
        setResizeHandle(null); 

        onDebug('TOUCH_ROOM_RESIZE_LABEL_MIGRATION_COMPLETE', {
            originalLabelsCount: Object.keys(roomCustomLabels).length,
            migratedLabelsCount: Object.keys(labelMigrationResult.labels).length,
            resizedRoomId: originalStableId,
            migrationMapSize: Object.keys(labelMigrationResult.migrationMap).length,
            selectedRoomAfterResize: newSelectedRoom?.stableId || null
        });
    }

    if ((toolForThisInteraction === 'cornerAdjust' || toolForThisInteraction === 'roomResize') && isDraggingCorner && activeCornerInfoRef.current && lastRawDraggedCornerPosRef.current) {
        // Use the same snapping logic as the preview - get the position from temp walls which already has angle snapping applied
        let pointToCommit = lastRawDraggedCornerPosRef.current;
        
        // Apply the same angle snapping logic that was used in the preview
        const { affectedWallsData } = activeCornerInfoRef.current;
        let snappedForAnglePoint = { ...pointToCommit };
        
        if (tempWalls && affectedWallsData.length === 2) {
            const tempWallCopies = tempWalls.map(w => ({ ...w, start: { ...w.start }, end: { ...w.end } }));
            const affectedWallEntries = affectedWallsData.map(awd => {
                const wall = tempWallCopies.find(tw => tw.tempId === awd.tempId);
                return wall ? { wall, isStartCorner: awd.isStartPointCorner } : null;
            }).filter(Boolean) as Array<{ wall: WallWithTempId, isStartCorner: boolean }>;

            if (affectedWallEntries.length === 2) {
                const entry1 = affectedWallEntries[0]; 
                const entry2 = affectedWallEntries[1];
                const fixedP1 = entry1.isStartCorner ? entry1.wall.end : entry1.wall.start;
                const fixedP2 = entry2.isStartCorner ? entry2.wall.end : entry2.wall.start;
                const toleranceWorld = SNAP_ANGLE_TOLERANCE_PX / scale;
                
                if (Math.abs(pointToCommit.y - fixedP1.y) < toleranceWorld && Math.abs(pointToCommit.x - fixedP2.x) < toleranceWorld) 
                    snappedForAnglePoint = { x: fixedP2.x, y: fixedP1.y };
                else if (Math.abs(pointToCommit.x - fixedP1.x) < toleranceWorld && Math.abs(pointToCommit.y - fixedP2.y) < toleranceWorld) 
                    snappedForAnglePoint = { x: fixedP1.x, y: fixedP2.y };
                else if (Math.abs(pointToCommit.y - fixedP1.y) < toleranceWorld) 
                    snappedForAnglePoint = { x: pointToCommit.x, y: fixedP1.y };
                else if (Math.abs(pointToCommit.x - fixedP1.x) < toleranceWorld) 
                    snappedForAnglePoint = { x: fixedP1.x, y: pointToCommit.y };
                else if (Math.abs(pointToCommit.y - fixedP2.y) < toleranceWorld) 
                    snappedForAnglePoint = { x: pointToCommit.x, y: fixedP2.y };
                else if (Math.abs(pointToCommit.x - fixedP2.x) < toleranceWorld) 
                    snappedForAnglePoint = { x: fixedP2.x, y: pointToCommit.y };
            }
        }
        
        // Apply only CM grid snapping (same as preview)
        pointToCommit = { 
            x: Math.round(snappedForAnglePoint.x / CM_SNAP_SIZE) * CM_SNAP_SIZE, 
            y: Math.round(snappedForAnglePoint.y / CM_SNAP_SIZE) * CM_SNAP_SIZE 
        };
        
        onDebug('TOUCH_CORNER_ADJUST_COMMIT', { rawPoint: lastRawDraggedCornerPosRef.current, snappedPoint: pointToCommit });
        if (lastOperationTypeRef) lastOperationTypeRef.current = 'explicitRoomCreation';
        recordUndoState?.(); // Record state before corner adjust commit
        // FIXED: Proper corner adjustment logic
        const wallsToCommit: Wall[] = currentWalls.map(wallWithTempId => {
            const affectionData = affectedWallsData.find(data => data.tempId === wallWithTempId.tempId);
            let plainWall = (({ tempId, ...rest }) => ({ ...rest, floor: rest.floor || currentFloor }))(wallWithTempId);
            if (affectionData) {
                const newWallGeom = { ...plainWall, start: { ...plainWall.start }, end: { ...plainWall.end } };
                if (affectionData.isStartPointCorner) newWallGeom.start = { ...pointToCommit! };
                else newWallGeom.end = { ...pointToCommit! };
                return newWallGeom;
            }
            return plainWall;
        });
        setFloorPlans(prev => ({ ...prev, [currentFloor]: wallsToCommit }));
        // PERFORMANCE FIX: Defer autosave to prevent 12+ second TouchEnd delays
        setTimeout(() => triggerAutosave?.(), 100);
        
        // Clear corner dragging state and preview
        setIsDraggingCorner(false);
        activeCornerInfoRef.current = null;
        lastRawDraggedCornerPosRef.current = null;
        setTempWalls(null);
        setCornerDragPreview(null);
    }

    if (toolForThisInteraction === 'wall' && isDraggingRoomCurrent && roomStartCurrent && roomEndCurrent && !touchMoved) { // Create individual wall on tap
      onDebug("[Hook handleTouchEndEvent 'wall'] Simple tap detected - creating individual wall", { roomStart: roomStartCurrent, roomEnd: roomEndCurrent });
      
      // For taps, create a single wall directly without smart connections to prevent wall splitting
      if (Math.abs(roomStartCurrent.x - roomEndCurrent.x) > 1 || Math.abs(roomStartCurrent.y - roomEndCurrent.y) > 1) {
        // Create wall without ownership first to calculate canonical ID
        const wallWithoutOwnership: Wall = {
          start: { ...roomStartCurrent },
          end: { ...roomEndCurrent },
          type: 'None',
          floor: currentFloor
        };
        
        // 🏠 CRITICAL FIX: For single walls, we need to predict what room this will create/belong to
        const mappedExistingWalls: Wall[] = currentWalls.map(({tempId, ...w})=>({...w, floor: w.floor || currentFloor}));
        const temporaryWallSet = [...mappedExistingWalls, wallWithoutOwnership];
        
        // Calculate what rooms would exist with this wall added
        const potentialRooms = calculateRooms(temporaryWallSet, {}, 'labelPreservation', currentFloor);
        let canonicalRoomId: string;
        
        if (potentialRooms.length > 0) {
          // Find the room that would contain this wall
          const roomContainingWall = potentialRooms.find(room => 
            room.walls.some(roomWall => 
              Math.abs(roomWall.start.x - wallWithoutOwnership.start.x) < 0.1 &&
              Math.abs(roomWall.start.y - wallWithoutOwnership.start.y) < 0.1 &&
              Math.abs(roomWall.end.x - wallWithoutOwnership.end.x) < 0.1 &&
              Math.abs(roomWall.end.y - wallWithoutOwnership.end.y) < 0.1
            )
          );
          canonicalRoomId = roomContainingWall?.stableId || getPersistentRoomId([wallWithoutOwnership], currentFloor);
        } else {
          // Fallback: use the wall itself to generate an ID
          canonicalRoomId = getPersistentRoomId([wallWithoutOwnership], currentFloor);
        }
        
        console.log('🏠 SINGLE_WALL_CREATION_WITH_OWNERSHIP: Assigning canonical ownership', {
          canonicalRoomId,
          wallCoords: `${roomStartCurrent.x},${roomStartCurrent.y} -> ${roomEndCurrent.x},${roomEndCurrent.y}`,
          potentialRoomsFound: potentialRooms.length
        });
        
        // Create the final wall with canonical ownership
        const newWall: Wall = {
          ...wallWithoutOwnership,
          // Assign canonical ownership at creation time
          originatingRoomId: canonicalRoomId,
          assignedRoomIds: [canonicalRoomId]
        };
        
        const finalWalls = [...mappedExistingWalls, newWall];
        
        if (lastOperationTypeRef) lastOperationTypeRef.current = 'explicitRoomCreation';
        recordUndoState?.();
        checkForNewRoomsAndCommit(finalWalls);
        onDebug("[Hook handleTouchEndEvent 'wall'] Wall created via touch tap", { 
          wallCreated: 1
        });
      }
    } else if (toolForThisInteraction === 'wall' && isDraggingRoomCurrent && roomStartCurrent && roomEndCurrent && touchMoved) {
        onDebug("[Hook handleTouchEndEvent 'wall' after drag] Triggered with smart room creation", { roomStart: roomStartCurrent, roomEnd: roomEndCurrent });
      if (Math.abs(roomStartCurrent.x - roomEndCurrent.x) > CM_SNAP_SIZE || Math.abs(roomStartCurrent.y - roomEndCurrent.y) > CM_SNAP_SIZE) {
            const mappedExistingWalls: Wall[] = currentWalls.map(({tempId, ...w})=>({...w, floor: w.floor || currentFloor}));
            
            // Standard room creation logic with intersection check
            // 🚫 BYPASS: Temporarily disable strict intersection check to allow room connections
            const BYPASS_STRICT_INTERSECTION_CHECK = true;
            
            if (BYPASS_STRICT_INTERSECTION_CHECK || !(checkRectangleWallIntersectionUniqueName as any)(roomStartCurrent as any, roomEndCurrent as any, mappedExistingWalls as any )) {
              // 🎯 ROOT CAUSE FIX: Apply precision control to room creation
              // Use 0.1 precision instead of GRID_SIZE (30) to prevent coordinate drift
              const precisionRoomStart = snapPointToGrid(roomStartCurrent, 0.1);
              const precisionRoomEnd = snapPointToGrid(roomEndCurrent, 0.1);
              
              // Create basic room walls with grid-snapped coordinates first
              const gridSnappedTouchWalls: Wall[] = [
                { start: { ...precisionRoomStart }, end: { x: precisionRoomEnd.x, y: precisionRoomStart.y }, type: 'None' as WallType, floor: currentFloor },
                { start: { x: precisionRoomEnd.x, y: precisionRoomStart.y }, end: { ...precisionRoomEnd }, type: 'None' as WallType, floor: currentFloor },
                { start: { ...precisionRoomEnd }, end: { x: precisionRoomStart.x, y: precisionRoomEnd.y }, type: 'None' as WallType, floor: currentFloor },
                { start: { x: precisionRoomStart.x, y: precisionRoomEnd.y }, end: { ...precisionRoomStart }, type: 'None' as WallType, floor: currentFloor }
              ];

              // 🔬 COORDINATE COLLISION PREVENTION: Apply smart offsets AFTER grid snapping for touch-based room creation
              const { processedWalls: offsetWalls, offsetsApplied: touchOffsetsApplied, isConnecting: isTouchConnecting } = applySmartCoordinateOffsets(
                gridSnappedTouchWalls,
                mappedExistingWalls,
                { enableOffset: true }
              );

              console.log('🔬 TOUCH ROOM CREATION COORDINATE PROCESSING:', {
                isConnectingRoom: isTouchConnecting,
                offsetsApplied: touchOffsetsApplied,
                roomBounds: `${precisionRoomStart.x},${precisionRoomStart.y} -> ${precisionRoomEnd.x},${precisionRoomEnd.y}`,
                wallCount: offsetWalls.length,
                prevention: touchOffsetsApplied > 0 ? 'Coordinate collisions prevented' : 'No offsets needed'
              });

              // Apply tempIds to processed walls
              const wallsWithoutOwnership: Wall[] = offsetWalls.map(wall => ({
                ...wall,
                tempId: generateStableWallId(wall.start, wall.end) // ⚡ FIX: Assign proper tempId to prevent diagnostic issues
              }));
              
              // 🏠 CRITICAL FIX: Calculate canonical room ID and assign ownership to all walls at creation
              const canonicalRoomId = getPersistentRoomId(wallsWithoutOwnership, currentFloor);
              console.log('🏠 TOUCH_ROOM_CREATION_WITH_OWNERSHIP: Assigning canonical ownership to new walls', {
                canonicalRoomId,
                wallCount: wallsWithoutOwnership.length,
                roomBounds: `${roomStartCurrent.x},${roomStartCurrent.y} -> ${roomEndCurrent.x},${roomEndCurrent.y}`,
                wallTempIds: wallsWithoutOwnership.map(w => (w as any).tempId) // ⚡ DEBUG: Log tempIds for diagnostic verification
              });
              
              const newRoomWalls: Wall[] = wallsWithoutOwnership.map((wall, index) => ({
                ...wall,
                // Assign canonical ownership at creation time
                originatingRoomId: canonicalRoomId,
                assignedRoomIds: [canonicalRoomId]
              }));
              
              // Add room walls directly without smart connections to prevent wall splitting
              let finalWalls = [...mappedExistingWalls, ...newRoomWalls];
              let totalWallsAdded = newRoomWalls.length;
              
              if (lastOperationTypeRef) lastOperationTypeRef.current = 'explicitRoomCreation';
              checkForNewRoomsAndCommit(finalWalls);
              
              onDebug("[Hook handleTouchEndEvent 'wall'] Room created via touch drag", { 
                roomWallsCreated: newRoomWalls.length,
                totalWallsAdded: totalWallsAdded,
                finalWallCount: finalWalls.length
              });
            } else {
              console.log("[Hook handleTouchEndEvent 'wall'] Room intersects existing walls. Not created.");
            }
      } else {
        console.log("[Hook handleTouchEndEvent 'wall'] Room too small. Not created.");
      }
    }

    // Split tool logic for touch end - execute the split when finger is lifted
    if (toolForThisInteraction === 'split' && selectedWall && splitPoint) {
      try {
        handleWallSplit(splitPoint);
      } catch (error) {
        console.error('Split tool error:', error);
      }
    }

    // Reset common states after any interaction
    setIsResizing(false); isResizingRef.current = false;
    setTempWalls(null); 
    setActivelyResizedWalls(null);
    setIsDraggingRoom(false); setRoomStart(null); setRoomEnd(null); // Reset these as they are touch specific start/end now
    // setIsDragging(false); // This is reset in handleTouchMove if needed, or here if general
    // setIsPanning(false); setPanStart(null); // Panning is usually a different gesture

    // Only reset selectedWall and splitPoint if not already reset by split tool above
    if (toolForThisInteraction !== 'split') {
      setSelectedWall(null); setSplitPoint(null);
    }
    setIsDraggingCorner(false); activeCornerInfoRef.current = null; lastRawDraggedCornerPosRef.current = null;
    draggedWallSegmentRef.current = null;
    activeResizeOriginalWallTempIdsRef.current = null;
    
    // Clear intersection data for partial wall dragging
    intersectionDataRef.current = null;
    
    // 🎯 DEACTIVATE WALL DRAG MODE - Professional CAD cleanup
    if (wallDragModeRef.current) {
      deactivateWallDragMode();
    }

    // Reset extrusion refs
    isDraggingExtrusionRef.current = false;
    extrusionSegmentRef.current = null;
    originalWallLineForExtrusionRef.current = null;
    extrusionDragStartPointRef.current = null;
    isExtrudingRoomSegmentRef.current = false;
    isResizingExtrusionWidthRef.current = false;

    // Reset touch-specific states
    setTouchStartPos(null);
    setTouchMoved(false);
    // setIsLongPress(false); // Already reset earlier
    if (longPressTimer) clearTimeout(longPressTimer); // Clear timer again just in case
    setLongPressTimer(null);

    // Handle element drag finalization for select tool
    if (toolForThisInteraction === 'select' && isDragging && selectedElement && isDraggingElementRef.current) {
        // Finalize element drag for touch
        if (elementDragOffsetRef.current && dragStartPointRef.current) {
            // Record undo state for element movement
            recordUndoState?.();
            
            onDebug('ELEMENT_TOUCH_DRAG_END', { 
                elementType: selectedElement.type,
                elementId: hasId(selectedElement) ? selectedElement.id : 'no-id',
                finalPosition: (selectedElement as any).position,
                dragStartPoint: dragStartPointRef.current
            });
        }
        
        // Reset drag state
        setIsDragging(false);
        isDraggingElementRef.current = false;
        setElementDragOffset(null);
        elementDragOffsetRef.current = null;
        setDragStartPoint(null);
        dragStartPointRef.current = null;
    }

    // Handle touch double-tap for element editing (select tool)
    if (toolForThisInteraction === 'select' && !touchMoved && touchStartPos) {
      // Check for double-tap (two quick touches in the same location)
      const now = Date.now();
      const lastTouchTime = (window as any).__lastTouchTime || 0;
      const lastTouchPoint = (window as any).__lastTouchPoint || null;
      const doubleTapThreshold = 500; // ms
      const doubleTapDistance = 50; // pixels tolerance
      
      if (lastTouchTime && 
          now - lastTouchTime < doubleTapThreshold && 
          lastTouchPoint &&
          Math.abs(touchStartPos.x - lastTouchPoint.x) < doubleTapDistance &&
          Math.abs(touchStartPos.y - lastTouchPoint.y) < doubleTapDistance) {
        
        // Double-tap detected - check for elements to edit
        const elementAtPoint = findElementAtPointInternal(touchStartPos, elements);
        
        if (elementAtPoint) {
          onDebug('TOUCH_DOUBLE_TAP_EDIT', {
            elementId: hasId(elementAtPoint) ? elementAtPoint.id : 'no-id',
            elementType: elementAtPoint.type,
            touchPoint: touchStartPos
          });
          
          // Trigger element editing based on type
          switch (elementAtPoint.type) {
            case 'window':
              // Trigger window edit modal
              if (typeof (window as any).__triggerWindowEdit === 'function') {
                (window as any).__triggerWindowEdit(elementAtPoint);
              }
              break;
            case 'radiator':
              // Trigger radiator edit modal  
              if (typeof (window as any).__triggerRadiatorEdit === 'function') {
                (window as any).__triggerRadiatorEdit(elementAtPoint);
              }
              break;
            case 'door':
              // Trigger door edit modal
              if (typeof (window as any).__triggerDoorEdit === 'function') {
                (window as any).__triggerDoorEdit(elementAtPoint);
              }
              break;
            case 'boiler':
              // Trigger boiler edit modal
              if (typeof (window as any).__triggerBoilerEdit === 'function') {
                (window as any).__triggerBoilerEdit(elementAtPoint);
              }
              break;
          }
        }
        
        // Clear double-tap detection data
        delete (window as any).__lastTouchTime;
        delete (window as any).__lastTouchPoint;
      } else {
        // Store current touch for double-tap detection
        (window as any).__lastTouchTime = now;
        (window as any).__lastTouchPoint = { x: touchStartPos.x, y: touchStartPos.y };
      }
    }

    // setCurrentPoint(null); // This might be set by other tools, consider context.
    // Reset isMouseDownRef, as touch has ended
    isMouseDownRef.current = false;
    onDebug('TOUCH_END_FINAL_STATE_RESET_COMPLETE', { currentTool: toolForThisInteraction });

  }, [
    isMouseDownRef, onDebug, currentToolInternalRef, isResizingRef, selectedRoom, tempWalls, floorPlans, currentFloor,
    setFloorPlans, setUndoStack, setRedoStack, roomCustomLabels, setRoomCustomLabels,
    resizingRoomInfo, setSelectedRoom, justResizedRef, lastOperationTypeRef,
    isDraggingRoomRef, roomStartRef, roomEndRef, // Use refs for current drag state
    isDraggingExtrusionRef, extrusionSegmentRef, originalWallLineForExtrusionRef, extrusionDragStartPointRef,
    isExtrudingRoomSegmentRef, isResizingExtrusionWidthRef,
    pointsAreEqual, findRoomAtPoint, getRoomCenter, migrateRoomLabels,
    draggedWallSegmentRef, getCollinearNeighbors,
    checkRectangleWallIntersectionUniqueName,
    handleWallSplit, snapToFineGridOrEndpointsUtil, eraseAtPoint, activeCornerInfoRef, lastRawDraggedCornerPosRef,
    setIsResizing, setTempWalls, setResizingRoomInfo, setActivelyResizedWalls, setIsDraggingRoom, setRoomStart, setRoomEnd, // setCurrentPoint,
    /* setIsDragging, setIsPanning, setPanStart, */ setSelectedWall, setSplitPoint, setIsDraggingCorner, 
    activeResizeOriginalWallTempIdsRef, props.setSelectedElement, props.setIsDraggingCorner, props.setSelectedRoom, props.setResizeHandle, /* props.setPanStart, */
    elements, findElementAtPointInternal, handleCanvasClick, // Added handleCanvasClick for select tool consistency
    // Touch specific states and setters
    touchStartPos, touchMoved, isLongPress, longPressTimer,
    setTouchStartPos, setTouchMoved, setIsLongPress, setLongPressTimer,
    currentWalls, // Added currentWalls as it's used in label migration logic
    resizeHandle, // Ensure resizeHandle is in deps for roomResize
    checkForNewRoomsAndCommit, // Make sure this is in deps if used
    recordUndoState, // Added recordUndoState to dependencies
    calculateRooms, // Added for long press room deletion
    deleteSelectedRoom // Added for long press room deletion
  ]);

  const handleZoomIn = useCallback(() => {
    setScale(prev => {
      const newScale = Math.min(prev * SCALE_FACTOR, MAX_SCALE);
      const containerWidth = canvasRef.current?.clientWidth || 750;
      setViewOffset(current => constrainViewOffsetUtil(current, newScale, containerWidth));
      return newScale;
    });
  }, [setScale, setViewOffset, canvasRef]);

  const handleZoomOut = useCallback(() => {
    setScale(prev => {
      const newScale = Math.max(prev / SCALE_FACTOR, INITIAL_SCALE);
      const containerWidth = canvasRef.current?.clientWidth || 750;
      setViewOffset(current => constrainViewOffsetUtil(current, newScale, containerWidth));
      return newScale;
    });
  }, [setScale, setViewOffset, canvasRef]);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) { 
      const delta = -Math.sign(e.deltaY);
      if (delta > 0) handleZoomIn();
      else handleZoomOut();
    } else { 
      setViewOffset(prev => {
        const newX = prev.x + (e.deltaX / scale);
        const newY = prev.y + (e.deltaY / scale);
        const containerWidth = canvasRef.current?.clientWidth || 750;
        return constrainViewOffsetUtil({ x: newX, y: newY }, scale, containerWidth);
      });
    }
  }, [scale, setViewOffset, canvasRef, handleZoomIn, handleZoomOut]);

  // Add this debug function inside the hook where onDebug is available
  const debugExtrusionConnectors = (walls: Wall[], mainSegmentId: string, context: string) => {
    const connectors = identifyConnectorWalls(walls, mainSegmentId);
    
    onDebug(`[ExtrusionConnectorDebug] ${context}`, {
      mainSegmentId,
      connectorCount: connectors.length,
      connectors: connectors.map(c => ({
        id: c.tempId,
        start: c.start,
        end: c.end,
        // Remove the properties that don't exist on Wall type
      }))
    });
  };

  // Helper function to find connectors for a specific main extrusion segment
  const findConnectorsForMainSegment = (
    mainSegment: WallWithTempId,
    allPotentialConnectors: WallWithTempId[]
  ): { connectors: WallWithTempId[] } => {
    const connectors: WallWithTempId[] = [];
    
    for (const pConnector of allPotentialConnectors) {
        // Ensure we are only checking actual connector walls from the provided list
        if (!pConnector.isExtrusionConnector) continue;

        // A connector is attached if one of its ends matches one of the main segment's ends.
        if (pointsAreEqual(pConnector.start, mainSegment.start) || pointsAreEqual(pConnector.start, mainSegment.end) ||
            pointsAreEqual(pConnector.end, mainSegment.start) || pointsAreEqual(pConnector.end, mainSegment.end)) {
            // Ensure not already added (though usually tempId is unique for distinct walls)
            if (!connectors.find(c => c.tempId === pConnector.tempId)) {
                 connectors.push(pConnector);
            }
        }
    }
    return { connectors };
  };

  // Add this function after the existing extrusion logic, around line 900
  const updateConnectedExtrusionsForStandardResize = (
    originalRoomWallsBeforeAnyResize: WallWithTempId[], 
    currentWallsAfterBaseWallResize: WallWithTempId[], 
    originalStatesOfDirectlyMovedBaseWalls: WallWithTempId[], 
    newStatesOfDirectlyMovedBaseWalls: WallWithTempId[],
    onDebug: (event: string, data: any) => void // Added onDebug here
  ): WallWithTempId[] => {
    
    // 🔧 DEBUG: Track function entry to see if this is being called for split wall resize
    onDebug('🔧 [updateConnectedExtrusionsForStandardResize] ENTRY', {
      originalWallsCount: originalRoomWallsBeforeAnyResize.length,
      currentWallsCount: currentWallsAfterBaseWallResize.length,
      movedWallsCount: newStatesOfDirectlyMovedBaseWalls.length,
      context: 'This function may be creating unwanted connectors for split walls'
    });
    onDebug('UPDATE_CONNECTED_EXTRUSIONS_START', {
      originalRoomWallsCount: originalRoomWallsBeforeAnyResize.length,
      wallsAfterBaseResizeCount: currentWallsAfterBaseWallResize.length,
      originalMovedBaseWallsCount: originalStatesOfDirectlyMovedBaseWalls.length,
        newMovedBaseWallsCount: newStatesOfDirectlyMovedBaseWalls.length,
    });

    let finalWalls = [...currentWallsAfterBaseWallResize];
    const updatedExtrusionWallIds = new Set<string>();
    
    // Helper function to create additional connector walls for bridging gaps
    const createBridgingConnectors = (
      extrusionWalls: WallWithTempId[],
      roomWalls: WallWithTempId[],
      baseWallMovement: { originalStart: Point; originalEnd: Point; newStart: Point; newEnd: Point }
    ): WallWithTempId[] => {
      
      // 🔧 DEBUG: Track when bridging connectors are being created
      onDebug('🔧 [createBridgingConnectors] ENTRY', {
        extrusionWallsCount: extrusionWalls.length,
        roomWallsCount: roomWalls.length,
        baseWallMovement: {
          originalStart: `(${baseWallMovement.originalStart.x.toFixed(1)},${baseWallMovement.originalStart.y.toFixed(1)})`,
          originalEnd: `(${baseWallMovement.originalEnd.x.toFixed(1)},${baseWallMovement.originalEnd.y.toFixed(1)})`,
          newStart: `(${baseWallMovement.newStart.x.toFixed(1)},${baseWallMovement.newStart.y.toFixed(1)})`,
          newEnd: `(${baseWallMovement.newEnd.x.toFixed(1)},${baseWallMovement.newEnd.y.toFixed(1)})`
        },
        context: 'Applying original endpoint protection for bridging connectors'
      });
      
      const bridgingConnectors: WallWithTempId[] = [];
      
      // Calculate movement vectors
      const startMovement = {
        x: baseWallMovement.newStart.x - baseWallMovement.originalStart.x,
        y: baseWallMovement.newStart.y - baseWallMovement.originalStart.y
      };
      const endMovement = {
        x: baseWallMovement.newEnd.x - baseWallMovement.originalEnd.x,
        y: baseWallMovement.newEnd.y - baseWallMovement.originalEnd.y
      };
      
      // Check if this is a significant cross-directional movement
      const isSignificantMovement = (Math.abs(startMovement.x) > 5 && Math.abs(startMovement.y) > 5) ||
                                   (Math.abs(endMovement.x) > 5 && Math.abs(endMovement.y) > 5);
      
      if (!isSignificantMovement) {
        return bridgingConnectors; // No bridging needed for small movements
      }
      
      onDebug('UPDATE_CONNECTED_EXTRUSIONS_CROSS_DIRECTIONAL_DETECTED', {
        startMovement, endMovement, isSignificantMovement
      });
      
      // 🔧 CRITICAL FIX: Add split point detection and original endpoint protection
      
      // Function to find which endpoint is the split point for a main segment
      const findSplitPointForMainSegment = (mainSegment: WallWithTempId): Point | null => {
        const mainParentId = (mainSegment as any).parentWallId;
        if (!mainParentId) return null;
        
        // Find sibling segments from the same parent wall using robust coordinate comparison
        const siblingSegments = [...extrusionWalls, ...roomWalls].filter(wall => {
          const wallParentId = (wall as any).parentWallId;
          
          // Check if this wall has the same parent ID
          if (wallParentId !== mainParentId) {
            return false;
          }
          
          // More robust way to exclude self - use coordinate comparison since tempId might be unreliable
          const isSameWall = (
            pointsAreEqual(mainSegment.start, wall.start, 0.1) && 
            pointsAreEqual(mainSegment.end, wall.end, 0.1)
          ) || (
            pointsAreEqual(mainSegment.start, wall.end, 0.1) && 
            pointsAreEqual(mainSegment.end, wall.start, 0.1)
          );
          
          return !isSameWall;
        });
        
        // Check which endpoint is shared with sibling segments
        for (const sibling of siblingSegments) {
          if (pointsAreEqual(mainSegment.start, sibling.start, 0.1) || pointsAreEqual(mainSegment.start, sibling.end, 0.1)) {
            return mainSegment.start; // start is the split point
          }
          if (pointsAreEqual(mainSegment.end, sibling.start, 0.1) || pointsAreEqual(mainSegment.end, sibling.end, 0.1)) {
            return mainSegment.end; // end is the split point
          }
        }
        return null;
      };
      
      // Function to check if an endpoint is an original room endpoint
      const isOriginalRoomEndpoint = (point: Point, mainSegment: WallWithTempId): boolean => {
        const connectedWalls = [...extrusionWalls, ...roomWalls].filter(wall => 
          pointsAreEqual(wall.start, point, 0.1) || pointsAreEqual(wall.end, point, 0.1)
        );
        
        const mainParentId = (mainSegment as any).parentWallId;
        
        return connectedWalls.some(wall => {
          // If connected to a regular wall (no parent ID), this is an original endpoint
          if (!(wall as any).parentWallId) return true;
          
          // If connected to a wall from a different parent, this is an original endpoint
          return (wall as any).parentWallId !== mainParentId;
        });
      };
      
      // Find extrusion main segments that might need additional connectors
      const mainExtrusionSegments = extrusionWalls.filter(w => w.isMainExtrusionSegment);
      
      mainExtrusionSegments.forEach(mainSegment => {
        // Identify the split point for this main segment
        const splitPoint = findSplitPointForMainSegment(mainSegment);
        
        // Check both endpoints
        const endpoints = [mainSegment.start, mainSegment.end];
        
        endpoints.forEach(endpoint => {
          // 🔧 CRITICAL: Only create connectors for split points, NEVER for original room endpoints
          const isSplitPoint = splitPoint && pointsAreEqual(endpoint, splitPoint, 0.1);
          const isOriginalEndpoint = isOriginalRoomEndpoint(endpoint, mainSegment);
          
          onDebug('🔧 [createBridgingConnectors] ENDPOINT_ANALYSIS', {
            endpoint: `(${endpoint.x.toFixed(1)},${endpoint.y.toFixed(1)})`,
            splitPoint: splitPoint ? `(${splitPoint.x.toFixed(1)},${splitPoint.y.toFixed(1)})` : 'Not found',
            isSplitPoint,
            isOriginalEndpoint,
            mainSegmentId: (mainSegment as any).tempId,
            decision: isSplitPoint ? 'Will try to create connector' : isOriginalEndpoint ? 'Will extend walls naturally (no connector)' : 'Unclear endpoint type - skip'
          });
          
          // Skip creating connectors for original room endpoints
          if (isOriginalEndpoint) {
            onDebug('🔧 [createBridgingConnectors] ORIGINAL_ENDPOINT_PROTECTION', {
              endpoint: `(${endpoint.x.toFixed(1)},${endpoint.y.toFixed(1)})`,
              reason: 'This is an original room endpoint - no bridging connector will be created'
            });
            return; // Skip this endpoint
          }
          
          // Only proceed with connector creation for split points
          if (!isSplitPoint) {
            onDebug('🔧 [createBridgingConnectors] NON_SPLIT_ENDPOINT_SKIPPED', {
              endpoint: `(${endpoint.x.toFixed(1)},${endpoint.y.toFixed(1)})`,
              reason: 'This is neither a split point nor clearly an original endpoint - skipping'
            });
            return; // Skip this endpoint
          }
          
          // Find the closest room wall endpoint that isn't already connected
          let closestRoomPoint: Point | null = null;
          let minDistance = Infinity;
          
          roomWalls.forEach(roomWall => {
            if (roomWall.isExtrusionConnector || roomWall.isMainExtrusionSegment) return;
            
            const roomPoints = [roomWall.start, roomWall.end];
            roomPoints.forEach(roomPoint => {
              const distance = Math.sqrt(
                Math.pow(roomPoint.x - endpoint.x, 2) + 
                Math.pow(roomPoint.y - endpoint.y, 2)
              );
              
              if (distance < minDistance && distance > 1.0) { // Avoid connecting to same point
                minDistance = distance;
                closestRoomPoint = roomPoint;
              }
            });
          });
          
          // Check if there's already a connector between these points
          const hasExistingConnector = closestRoomPoint ? (() => {
            const validPoint: Point = closestRoomPoint;
            return extrusionWalls.some(wall => 
              wall.isExtrusionConnector &&
              ((pointsAreEqual(wall.start, endpoint, 1.0) && pointsAreEqual(wall.end, validPoint, 1.0)) ||
               (pointsAreEqual(wall.start, validPoint, 1.0) && pointsAreEqual(wall.end, endpoint, 1.0)))
            );
          })() : false;
          
          // Create bridging connector only for split points
          if (closestRoomPoint && 
              minDistance > 10.0 && minDistance < 200.0 && // Reasonable distance range
              !hasExistingConnector) {
            
            // Create a properly typed local variable for TypeScript
            const validClosestPoint: Point = closestRoomPoint;
            
            // 🔧 ENHANCED: Log connector creation with split point confirmation
            const newConnector = {
              start: { ...validClosestPoint },
              end: { ...endpoint },
              type: mainSegment.type || 'interior',
              floor: mainSegment.floor,
              isExtrusionConnector: true,
              parentWallId: (mainSegment as any).parentWallId,
              parentRoomIds: (mainSegment as any).parentRoomIds,
              connectedToSplitWallId: (mainSegment as any).tempId,
              extrusionGroupId: `bridge-standard-resize-${Date.now()}`,
              thickness: WALL_THICKNESS,
              tempId: `bridge-connector-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              // 🏠 INHERIT ROOM OWNERSHIP from main segment
              originatingRoomId: (mainSegment as any).originatingRoomId,
              assignedRoomIds: (mainSegment as any).assignedRoomIds ? [...(mainSegment as any).assignedRoomIds] : undefined
            } as WallWithTempId;
            
            onDebug('🔧 [createBridgingConnectors] SPLIT_POINT_CONNECTOR_CREATED', {
              connectorCoords: `(${validClosestPoint.x.toFixed(1)},${validClosestPoint.y.toFixed(1)})->(${endpoint.x.toFixed(1)},${endpoint.y.toFixed(1)})`,
              mainSegmentId: (mainSegment as any).tempId,
              splitPoint: `(${splitPoint!.x.toFixed(1)},${splitPoint!.y.toFixed(1)})`,
              distance: minDistance.toFixed(1),
              connectorTempId: newConnector.tempId,
              reason: 'Split point endpoint - connector creation allowed'
            });
            
            bridgingConnectors.push(newConnector);
            
            onDebug('UPDATE_CONNECTED_EXTRUSIONS_CREATED_BRIDGE', {
              from: validClosestPoint,
              to: endpoint,
              distance: minDistance.toFixed(1),
              mainSegmentId: (mainSegment as any).tempId
            });
          } else if (closestRoomPoint) {
            // Type assertion to help TypeScript understand closestRoomPoint is not null here
            const validClosestRoomPoint: Point = closestRoomPoint;
            onDebug('🔧 [createBridgingConnectors] SPLIT_POINT_CONNECTOR_SKIPPED', {
              endpoint: `(${endpoint.x.toFixed(1)},${endpoint.y.toFixed(1)})`,
              closestRoomPoint: `(${validClosestRoomPoint.x.toFixed(1)},${validClosestRoomPoint.y.toFixed(1)})`,
              distance: minDistance.toFixed(1),
              hasExistingConnector,
              reason: hasExistingConnector ? 'Connector already exists' : minDistance <= 10.0 ? 'Distance too small' : 'Distance too large'
            });
          }
        });
      });
      
      onDebug('🔧 [createBridgingConnectors] SUMMARY', {
        mainSegmentsProcessed: mainExtrusionSegments.length,
        bridgingConnectorsCreated: bridgingConnectors.length,
        originalEndpointProtectionApplied: true
      });
      
      return bridgingConnectors;
    };

    for (let i = 0; i < newStatesOfDirectlyMovedBaseWalls.length; i++) {
        const newMovedBaseWall = newStatesOfDirectlyMovedBaseWalls[i];
        const originalBaseWall = originalStatesOfDirectlyMovedBaseWalls.find(w => w.tempId === newMovedBaseWall.tempId);

        if (!originalBaseWall) {
            onDebug('UPDATE_CONNECTED_EXTRUSIONS_WARN', { message: "Could not find original state for a moved base wall", movedWallId: newMovedBaseWall.tempId });
        continue;
      }
        onDebug('UPDATE_CONNECTED_EXTRUSIONS_PROCESSING_BASE_WALL', { originalBaseWallId: originalBaseWall.tempId, newBaseWallId: newMovedBaseWall.tempId });

        const { mainSegments, connectors: allConnectors } = checkForConnectedExtrusions(originalBaseWall, originalRoomWallsBeforeAnyResize);
        onDebug('UPDATE_CONNECTED_EXTRUSIONS_FOUND_PARTS_FOR_BASE', { baseWallId: originalBaseWall.tempId, mainSegmentsConnectedCount: mainSegments.length, allInitialConnectorsCount: allConnectors.length });

        if (mainSegments.length === 0 || allConnectors.length < 2) {
          // 🔧 NEW: Even if we don't have full extrusions to update, check for bridging needs
          const extrusionWalls = finalWalls.filter(w => w.isExtrusionConnector || w.isMainExtrusionSegment);
          if (extrusionWalls.length > 0) {
            const bridgingConnectors = createBridgingConnectors(
              extrusionWalls,
              finalWalls,
              {
                originalStart: originalBaseWall.start,
                originalEnd: originalBaseWall.end,
                newStart: newMovedBaseWall.start,
                newEnd: newMovedBaseWall.end
              }
            );
            
            if (bridgingConnectors.length > 0) {
              finalWalls = [...finalWalls, ...bridgingConnectors];
              onDebug('UPDATE_CONNECTED_EXTRUSIONS_ADDED_BRIDGES_ONLY', {
                bridgeCount: bridgingConnectors.length,
                baseWallId: originalBaseWall.tempId
              });
            }
          }
          continue;
        }

        const mainExtrusionSegmentOrig = mainSegments[0];
        const relevantConnectorsOrig = allConnectors.filter(conn => {
            const connEndPoints = [conn.start, conn.end];
            const mainSegPoints = [mainExtrusionSegmentOrig.start, mainExtrusionSegmentOrig.end];
            const connectsToMainSeg = connEndPoints.some(pConn => mainSegPoints.some(pMain => pointsAreEqual(pConn, pMain, POINT_MATCH_TOLERANCE)));
            // Further check: ensure one end is on originalBaseWall (or was, for robustness)
            const connectsToOrigBase = connEndPoints.some(pConn => 
                isPointOnWall(pConn, originalBaseWall, POINT_MATCH_TOLERANCE * 2) || // Wider tolerance for initial check
                pointsAreEqual(pConn, originalBaseWall.start, POINT_MATCH_TOLERANCE * 2) ||
                pointsAreEqual(pConn, originalBaseWall.end, POINT_MATCH_TOLERANCE * 2)
            );
            return connectsToMainSeg && connectsToOrigBase;
        }).slice(0, 2);


        if (relevantConnectorsOrig.length < 2) {
            onDebug('UPDATE_CONNECTED_EXTRUSIONS_WARN', { message: "Not enough relevant connectors for main segment", mainSegmentId: mainExtrusionSegmentOrig.tempId, foundConnectorsCount: relevantConnectorsOrig.length });
          continue;
        }

        const connectorLength = distance(relevantConnectorsOrig[0].start, relevantConnectorsOrig[0].end);

        const originalBaseMid = { x: (originalBaseWall.start.x + originalBaseWall.end.x) / 2, y: (originalBaseWall.start.y + originalBaseWall.end.y) / 2 };
        const originalExtrusionMid = { x: (mainExtrusionSegmentOrig.start.x + mainExtrusionSegmentOrig.end.x) / 2, y: (mainExtrusionSegmentOrig.start.y + mainExtrusionSegmentOrig.end.y) / 2 };
        const extrusionSideVector = subtractPoints(originalExtrusionMid, originalBaseMid);

        const updatedConnectorsData: Array<{ id: string, newStart: Point, newEnd: Point }> = [];

        for (const connectorOrig of relevantConnectorsOrig) {
            let P_attach_orig_conn: Point | null = null;

            if (pointsAreEqual(connectorOrig.start, originalBaseWall.start, POINT_MATCH_TOLERANCE) || pointsAreEqual(connectorOrig.start, originalBaseWall.end, POINT_MATCH_TOLERANCE) || isPointOnWall(connectorOrig.start, originalBaseWall, POINT_MATCH_TOLERANCE)) {
                P_attach_orig_conn = connectorOrig.start;
            } else if (pointsAreEqual(connectorOrig.end, originalBaseWall.start, POINT_MATCH_TOLERANCE) || pointsAreEqual(connectorOrig.end, originalBaseWall.end, POINT_MATCH_TOLERANCE) || isPointOnWall(connectorOrig.end, originalBaseWall, POINT_MATCH_TOLERANCE)) {
                P_attach_orig_conn = connectorOrig.end;
            }

            if (!P_attach_orig_conn) {
                 onDebug("UPDATE_CONNECTED_EXTRUSIONS_WARN_CONNECTOR_ATTACHMENT_NF", {connectorId: connectorOrig.tempId, baseWallId: originalBaseWall.tempId});
                continue;
            }
            
            const totalLenOrigBase = distance(originalBaseWall.start, originalBaseWall.end);
            let fractionAlongBase = 0.5; // Default to midpoint if calculation fails
            if (totalLenOrigBase > 0.001) {
                 const distStartToAttach = distance(originalBaseWall.start, P_attach_orig_conn);
                 fractionAlongBase = distStartToAttach / totalLenOrigBase;
                 fractionAlongBase = Math.max(0, Math.min(1, fractionAlongBase)); // Clamp
            } else { // Zero length original base wall
                if (pointsAreEqual(P_attach_orig_conn, originalBaseWall.start)) fractionAlongBase = 0;
                else if (pointsAreEqual(P_attach_orig_conn, originalBaseWall.end)) fractionAlongBase = 1; // Should be same point
            }
            
            const P_attach_new_conn: Point = {
                x: newMovedBaseWall.start.x + (newMovedBaseWall.end.x - newMovedBaseWall.start.x) * fractionAlongBase,
                y: newMovedBaseWall.start.y + (newMovedBaseWall.end.y - newMovedBaseWall.start.y) * fractionAlongBase,
            };

            const dxNewBase = newMovedBaseWall.end.x - newMovedBaseWall.start.x;
            const dyNewBase = newMovedBaseWall.end.y - newMovedBaseWall.start.y;
            let normalToNewBase = normalizePoint({ x: -dyNewBase, y: dxNewBase });

            const tempExtrusionVector = subtractPoints(addPoints(P_attach_new_conn, scalePoint(normalToNewBase, connectorLength)), P_attach_new_conn);
            if (dotProductPoints(extrusionSideVector, tempExtrusionVector) < 0) { // If dot product is negative, normal points wrong way
                normalToNewBase = scalePoint(normalToNewBase, -1);
            }
            
            const P_outer_new_conn: Point = addPoints(P_attach_new_conn, scalePoint(normalToNewBase, connectorLength));
            
            updatedConnectorsData.push({ id: connectorOrig.tempId || `connector-${Date.now()}`, newStart: P_attach_new_conn, newEnd: P_outer_new_conn });
        }

        if (updatedConnectorsData.length === 2) {
            updatedConnectorsData.forEach(ucd => {
                const wallIndex = finalWalls.findIndex(w => w.tempId === ucd.id);
                if (wallIndex !== -1) {
                    // Preserve all original wall properties, especially the type
                    const originalWallAtIndex = finalWalls[wallIndex];
                    finalWalls[wallIndex] = { 
                        ...originalWallAtIndex, 
                        start: ucd.newStart, 
                        end: ucd.newEnd, 
                        isExtrusionConnector: true,
                        // Explicitly preserve the type if it exists
                        type: originalWallAtIndex.type || 'None',
                        // 🏠 PRESERVE ROOM OWNERSHIP
                        originatingRoomId: (originalWallAtIndex as any).originatingRoomId,
                        assignedRoomIds: (originalWallAtIndex as any).assignedRoomIds
                    };
                    updatedExtrusionWallIds.add(ucd.id);
                }
            });

            const mainSegmentIndex = finalWalls.findIndex(w => w.tempId === mainExtrusionSegmentOrig.tempId);
            if (mainSegmentIndex !== -1) {
                // Preserve all original main segment properties, especially the type
                const originalMainSegment = finalWalls[mainSegmentIndex];
                finalWalls[mainSegmentIndex] = {
                    ...originalMainSegment,
                    start: updatedConnectorsData[0].newEnd, 
                    end: updatedConnectorsData[1].newEnd,   
                    isMainExtrusionSegment: true,
                    // Explicitly preserve the type if it exists  
                    type: originalMainSegment.type || 'None',
                    // 🏠 PRESERVE ROOM OWNERSHIP
                    originatingRoomId: (originalMainSegment as any).originatingRoomId,
                    assignedRoomIds: (originalMainSegment as any).assignedRoomIds
                };
                if (mainExtrusionSegmentOrig.tempId) {
                    updatedExtrusionWallIds.add(mainExtrusionSegmentOrig.tempId);
                }
                onDebug('UPDATE_CONNECTED_EXTRUSIONS_REBUILT_SEGMENT', { 
                    mainSegmentId: mainExtrusionSegmentOrig.tempId, 
                    newStart: updatedConnectorsData[0].newEnd, 
                    newEnd: updatedConnectorsData[1].newEnd,
                    preservedType: originalMainSegment.type 
                });
            }
            
            // 🔧 NEW: Check if additional bridging connectors are needed after standard update
            const extrusionWalls = finalWalls.filter(w => w.isExtrusionConnector || w.isMainExtrusionSegment);
            const bridgingConnectors = createBridgingConnectors(
              extrusionWalls,
              finalWalls,
              {
                originalStart: originalBaseWall.start,
                originalEnd: originalBaseWall.end,
                newStart: newMovedBaseWall.start,
                newEnd: newMovedBaseWall.end
              }
            );
            
            if (bridgingConnectors.length > 0) {
              finalWalls = [...finalWalls, ...bridgingConnectors];
              onDebug('UPDATE_CONNECTED_EXTRUSIONS_ADDED_BRIDGES_AFTER_UPDATE', {
                bridgeCount: bridgingConnectors.length,
                baseWallId: originalBaseWall.tempId
              });
            }
          } else {
            onDebug('UPDATE_CONNECTED_EXTRUSIONS_WARN_COMMIT', { message: "Could not update extrusion; not enough valid connector data after processing.", needed: 2, got: updatedConnectorsData.length, originalRelevantConnectors: relevantConnectorsOrig.length });
        }
    }
    onDebug('UPDATE_CONNECTED_EXTRUSIONS_END', { finalWallCount: finalWalls.length, updatedIds: Array.from(updatedExtrusionWallIds) });
    return finalWalls;
  };

  // Helper function to check if a point is connected to a wall
  const isPointConnectedToWall = (point: Point, wall: WallWithTempId): boolean => {
    return isNearPoint(point, wall.start, POINT_MATCH_TOLERANCE) || isNearPoint(point, wall.end, POINT_MATCH_TOLERANCE);
  };

  // REVISED checkForConnectedExtrusions function
  const checkForConnectedExtrusions = (
    wallBeingResized: WallWithTempId,
    allWalls: WallWithTempId[]
  ): { mainSegments: WallWithTempId[]; connectors: WallWithTempId[] } => {
    const directlyConnectedParts: WallWithTempId[] = [];

    allWalls.forEach(wall => {
      if (!wall.isExtrusionConnector && !wall.isMainExtrusionSegment) return;
      if (wall.tempId === wallBeingResized.tempId) return; 

      // Check connection using a slightly larger tolerance for base wall connection to ensure robustness
      const connectionTolerance = POINT_MATCH_TOLERANCE + 0.1; 
      if (
        isNearPoint(wall.start, wallBeingResized.start, connectionTolerance) ||
        isNearPoint(wall.start, wallBeingResized.end, connectionTolerance) ||
        isNearPoint(wall.end, wallBeingResized.start, connectionTolerance) ||
        isNearPoint(wall.end, wallBeingResized.end, connectionTolerance) ||
        (isPointOnWall(wall.start, wallBeingResized, connectionTolerance) !== null) ||
        (isPointOnWall(wall.end, wallBeingResized, connectionTolerance) !== null)
      ) {
        if (!directlyConnectedParts.find(p => p.tempId === wall.tempId)) {
            directlyConnectedParts.push(wall);
        }
      }
    });
    
    const connectors = directlyConnectedParts.filter(w => w.isExtrusionConnector);
    const mainSegmentsFound: WallWithTempId[] = [];

    const allMainSegmentsInPlan = allWalls.filter(w => w.isMainExtrusionSegment);
    for (const ms of allMainSegmentsInPlan) {
        for (const conn of connectors) {
            // A main segment is connected to a connector if they share an endpoint
            if (pointsAreEqual(ms.start, conn.start, POINT_MATCH_TOLERANCE) || 
                pointsAreEqual(ms.start, conn.end, POINT_MATCH_TOLERANCE) ||
                pointsAreEqual(ms.end, conn.start, POINT_MATCH_TOLERANCE) || 
                pointsAreEqual(ms.end, conn.end, POINT_MATCH_TOLERANCE)) {
                if (!mainSegmentsFound.find(foundMs => foundMs.tempId === ms.tempId)) {
                    mainSegmentsFound.push(ms);
                }
                break; 
            }
        }
    }
    
    onDebug('CHECK_FOR_CONNECTED_EXTRUSIONS_RESULT', {
      wallResizedId: wallBeingResized.tempId,
      directlyConnectedPartsCount: directlyConnectedParts.length,
      foundConnectors: connectors.map(c => ({id: c.tempId, start: c.start, end: c.end})),
      foundMainSegments: mainSegmentsFound.map(m => ({id: m.tempId, start: m.start, end: m.end})),
    });
    return { mainSegments: mainSegmentsFound, connectors };
  };

  // Helper function to calculate corner drag preview data
  const calculateCornerDragPreview = useCallback((
    cornerPosition: Point,
    affectedWallsData: Array<{ tempId: string; isStartPointCorner: boolean }>,
    currentWalls: WallWithTempId[]
  ): CornerDragPreview | null => {
    if (affectedWallsData.length !== 2) return null;

    const affectedWalls = affectedWallsData.map(awd => {
      const wall = currentWalls.find(w => w.tempId === awd.tempId);
      if (!wall) return null;

      // Calculate original length
      const originalLength = Math.sqrt(
        Math.pow(wall.end.x - wall.start.x, 2) + Math.pow(wall.end.y - wall.start.y, 2)
      ) / PIXELS_PER_METER;

      // Calculate new length with the corner moved
      const fixedPoint = awd.isStartPointCorner ? wall.end : wall.start;
      const newLength = Math.sqrt(
        Math.pow(fixedPoint.x - cornerPosition.x, 2) + Math.pow(fixedPoint.y - cornerPosition.y, 2)
      ) / PIXELS_PER_METER;

      // Calculate wall angle (in degrees)
      const angle = Math.atan2(
        fixedPoint.y - cornerPosition.y,
        fixedPoint.x - cornerPosition.x
      ) * 180 / Math.PI;

      return {
        wallId: awd.tempId,
        newLength,
        angle: angle >= 0 ? angle : angle + 360,
        originalLength,
        lengthChange: newLength - originalLength
      };
    }).filter((wall): wall is NonNullable<typeof wall> => wall !== null);

    if (affectedWalls.length !== 2) return null;

    // Calculate angle between the two walls
    const angleDiff = Math.abs(affectedWalls[1].angle - affectedWalls[0].angle);
    const cornerAngle = angleDiff > 180 ? 360 - angleDiff : angleDiff;

    return {
      cornerPosition,
      affectedWalls,
      cornerAngle
    };
  }, []);

  // Enhanced element position updating with snapping
  const updateElementPosition = useCallback((element: FloorPlanElement, newPosition: Point, enableSnapping: boolean = true) => {
    let finalPosition = { ...newPosition };
    
    if (enableSnapping) {
      // Snap to grid for precise placement
      finalPosition = {
        x: Math.round(newPosition.x / CM_SNAP_SIZE) * CM_SNAP_SIZE,
        y: Math.round(newPosition.y / CM_SNAP_SIZE) * CM_SNAP_SIZE
      };
      
      // For wall-mounted elements, try to snap to walls
      if (['radiator', 'window', 'door'].includes(element.type)) {
        const nearestWall = findNearestWallForElement(finalPosition, currentWalls);
        if (nearestWall) {
          const snapPoint = getSnapPointOnWall(finalPosition, nearestWall);
          if (snapPoint) {
            finalPosition = snapPoint;
            // Update wallId if element has one
            if ('wallId' in element) {
              (element as any).wallId = nearestWall.tempId;
            }
          }
        }
      }
    }
    
    const newElements = elements.map(el => {
      if (el === element && (el as any).position) {
        return { ...el, position: finalPosition };
      }
      return el;
    });
    
    setElements(newElements);
    onDebug('ELEMENT_POSITION_UPDATED', { 
      elementType: element.type, 
      elementId: hasId(element) ? element.id : 'no-id',
      oldPosition: (element as any).position,
      newPosition: finalPosition,
      snappingEnabled: enableSnapping
    });
  }, [elements, setElements, onDebug, currentWalls]);

  // Helper function to find nearest wall for element snapping
  const findNearestWallForElement = useCallback((point: Point, walls: WallWithTempId[]): WallWithTempId | null => {
    let nearestWall: WallWithTempId | null = null;
    let minDistance = Infinity;
    const maxSnapDistance = 50; // Maximum distance to snap to wall
    
    for (const wall of walls) {
      const distance = distanceToLine(point, wall.start, wall.end);
      if (distance < minDistance && distance < maxSnapDistance) {
        minDistance = distance;
        nearestWall = wall;
      }
    }
    
    return nearestWall;
  }, []);

  // Helper function to get snap point on wall
  const getSnapPointOnWall = useCallback((point: Point, wall: WallWithTempId): Point | null => {
    const snapTolerance = 30; // Distance tolerance for snapping to wall
    const distance = distanceToLine(point, wall.start, wall.end);
    
    if (distance <= snapTolerance) {
      // Project point onto wall line
      const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
      const pointVector = { x: point.x - wall.start.x, y: point.y - wall.start.y };
      
      const wallLength = Math.sqrt(wallVector.x * wallVector.x + wallVector.y * wallVector.y);
      if (wallLength === 0) return null;
      
      const normalizedWall = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
      const projection = pointVector.x * normalizedWall.x + pointVector.y * normalizedWall.y;
      
      // Clamp projection to wall bounds
      const clampedProjection = Math.max(0, Math.min(wallLength, projection));
      
      return {
        x: wall.start.x + normalizedWall.x * clampedProjection,
        y: wall.start.y + normalizedWall.y * clampedProjection
      };
    }
    
    return null;
  }, []);

  // Add mouse event handlers for element dragging
  const handleCanvasMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const point = getCanvasCoordinates(e);
    isMouseDownRef.current = true;
    onDebug('MOUSE_DOWN', { tool: currentTool, point });

    if (currentTool === 'select') {
      const clickedElement = findElementAtPointInternal(point, elements);
      
      if (clickedElement) {
        setSelectedElement(clickedElement);
        setIsDragging(true);
        isDraggingElementRef.current = true;
        
        // Calculate drag offset from element position
        if ((clickedElement as any).position) {
          const elementPos = (clickedElement as any).position;
          const offset = {
            x: point.x - elementPos.x,
            y: point.y - elementPos.y
          };
          setElementDragOffset(offset);
          elementDragOffsetRef.current = offset;
          setDragStartPoint(point);
          dragStartPointRef.current = point;
          
          onDebug('ELEMENT_DRAG_START', { 
            elementType: clickedElement.type,
            elementId: hasId(clickedElement) ? clickedElement.id : 'no-id',
            startPoint: point,
            elementPosition: elementPos,
            dragOffset: offset
          });
        }
      } else {
        // Clicked on empty space - start panning
        setSelectedElement(null);
        setIsDragging(false);
        isDraggingElementRef.current = false;
        setIsPanning(true);
        setPanStart({ x: e.clientX, y: e.clientY });
      }
      return;
    }

    // Handle other tools...
    // ... existing mouseDown logic ...
  }, [getCanvasCoordinates, onDebug, currentTool, findElementAtPointInternal, elements, setSelectedElement, setIsDragging, setIsPanning, setPanStart]);

  const handleCanvasMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    isMouseDownRef.current = false;
    onDebug('MOUSE_UP', { tool: currentTool });

    if (currentTool === 'select' && isDragging && selectedElement && isDraggingElementRef.current) {
      // Finalize element drag
      const point = getCanvasCoordinates(e);
      const offset = elementDragOffsetRef.current || { x: 0, y: 0 };
      const finalPosition = {
        x: point.x - offset.x,
        y: point.y - offset.y
      };
      
      // Final position update with snapping
      updateElementPosition(selectedElement, finalPosition, true);
      
      // Record undo state for element movement
      recordUndoState?.();
      
      onDebug('ELEMENT_DRAG_END', { 
        elementType: selectedElement.type,
        elementId: hasId(selectedElement) ? selectedElement.id : 'no-id',
        finalPosition: finalPosition,
        dragStartPoint: dragStartPointRef.current
      });
      
      // Reset drag state
      setIsDragging(false);
      isDraggingElementRef.current = false;
      setElementDragOffset(null);
      elementDragOffsetRef.current = null;
      setDragStartPoint(null);
      dragStartPointRef.current = null;
      return;
    }

    // Reset panning state
    if (currentTool === 'select') {
      setIsPanning(false);
      setPanStart(null);
    }

    // Handle other tools...
    // ... existing mouseUp logic ...
  }, [currentTool, isDragging, selectedElement, getCanvasCoordinates, updateElementPosition, recordUndoState, onDebug, setIsDragging, setIsPanning, setPanStart]);

  return {
    handleMouseDown,
    handleCanvasMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleCanvasClick,
    handleCanvasDoubleClick,
    handleTouchStart,
    handleTouchMove,
    handleTouchEndEvent,
    handleWheel,
    handleWallTypeConversion,
    handleWallSplit,
    handleTextBoxCreation,
    deleteSelectedRoom, // Added missing room deletion function
    // New enhanced mouse event handlers for element dragging
    handleCanvasMouseDown,
    handleCanvasMouseUp,
    // Helper functions for element manipulation
    findElementAtPointInternal,
    updateElementPosition
  };
};


